// src/cadDimension/vec.js
var sub = (a, b) => ({ x: a.x - b.x, y: a.y - b.y });
var add = (a, b) => ({ x: a.x + b.x, y: a.y + b.y });
var mul = (a, s) => ({ x: a.x * s, y: a.y * s });
var dot = (a, b) => a.x * b.x + a.y * b.y;
var len = (a) => Math.hypot(a.x, a.y);
var dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
var norm = (a) => {
  const l = len(a);
  return l ? { x: a.x / l, y: a.y / l } : { x: 0, y: 0 };
};
var perp = (a) => ({ x: -a.y, y: a.x });
function ptSeg(p, a, b) {
  const vx = b.x - a.x;
  const vy = b.y - a.y;
  const l2 = vx * vx + vy * vy;
  if (l2 === 0) return dist(p, a);
  let t = ((p.x - a.x) * vx + (p.y - a.y) * vy) / l2;
  t = Math.max(0, Math.min(1, t));
  return dist(p, { x: a.x + t * vx, y: a.y + t * vy });
}

// src/cadDimension/measure.js
function linearOrientation(p1, p2, linePoint) {
  const midX = (p1.x + p2.x) / 2;
  const midY = (p1.y + p2.y) / 2;
  return Math.abs(linePoint.y - midY) >= Math.abs(linePoint.x - midX) ? "horizontal" : "vertical";
}
function ordinateAxis(feature, leader) {
  return Math.abs(leader.x - feature.x) >= Math.abs(leader.y - feature.y) ? "y" : "x";
}
function makeDimension(opts) {
  const {
    dimType,
    textOverride = null,
    textHeight = 1,
    arrowSize,
    autoText = true
  } = opts;
  const common = {
    type: "dimension",
    textOverride,
    autoText,
    // true ⇒ text/arrow auto-size from the measured length
    textHeight,
    // used as the manual height when autoText === false
    arrowSize: arrowSize || textHeight,
    dimStyle: opts.dimStyle || "Standard",
    // named DIMSTYLE this dim was created under
    // V4c associativity: the entity ids this dimension measures. A move/rotate/scale
    // of any of them drags this dimension along (see attachedDimensionIds). Empty for
    // a dimension drawn in free space.
    sourceIds: Array.isArray(opts.sourceIds) ? opts.sourceIds.slice() : []
  };
  if (dimType === "angular") {
    return {
      ...common,
      dimType: "angular",
      vertex: { x: opts.vertex.x, y: opts.vertex.y },
      p1: { x: opts.p1.x, y: opts.p1.y },
      p2: { x: opts.p2.x, y: opts.p2.y }
    };
  }
  if (dimType === "radius" || dimType === "diameter") {
    return {
      ...common,
      dimType,
      center: { x: opts.center.x, y: opts.center.y },
      radius: opts.radius,
      leaderPoint: { x: opts.leaderPoint.x, y: opts.leaderPoint.y }
    };
  }
  if (dimType === "arclength") {
    return {
      ...common,
      dimType: "arclength",
      center: { x: opts.center.x, y: opts.center.y },
      radius: opts.radius,
      startAngle: opts.startAngle,
      endAngle: opts.endAngle,
      linePoint: { x: opts.linePoint.x, y: opts.linePoint.y }
    };
  }
  if (dimType === "ordinate") {
    const ord = {
      ...common,
      dimType: "ordinate",
      feature: { x: opts.feature.x, y: opts.feature.y },
      leader: { x: opts.leader.x, y: opts.leader.y },
      datum: { x: opts.datum && opts.datum.x || 0, y: opts.datum && opts.datum.y || 0 },
      axis: opts.axis === "x" ? "x" : "y"
    };
    if (opts.featureRef) ord.featureRef = { ...opts.featureRef };
    return ord;
  }
  const type = dimType === "aligned" ? "aligned" : "linear";
  const dim = {
    ...common,
    dimType: type,
    p1: { x: opts.p1.x, y: opts.p1.y },
    p2: { x: opts.p2.x, y: opts.p2.y },
    linePoint: { x: opts.linePoint.x, y: opts.linePoint.y }
  };
  if (opts.p1Ref) dim.p1Ref = { ...opts.p1Ref };
  if (opts.p2Ref) dim.p2Ref = { ...opts.p2Ref };
  if (type === "linear") dim.orientation = linearOrientation(opts.p1, opts.p2, opts.linePoint);
  return dim;
}
function baselineLinePoint(base, n, spacing) {
  const lp = base.linePoint;
  const off = n * spacing;
  if (base.dimType === "linear" && base.orientation === "vertical") {
    const sign = lp.x >= base.p1.x ? 1 : -1;
    return { x: lp.x + sign * off, y: lp.y };
  }
  if (base.dimType === "linear") {
    const sign = lp.y >= base.p1.y ? 1 : -1;
    return { x: lp.x, y: lp.y + sign * off };
  }
  const ux = base.p2.x - base.p1.x;
  const uy = base.p2.y - base.p1.y;
  const L = Math.hypot(ux, uy) || 1;
  const nx = -uy / L;
  const ny = ux / L;
  const side = (lp.x - base.p1.x) * nx + (lp.y - base.p1.y) * ny >= 0 ? 1 : -1;
  return { x: lp.x + nx * side * off, y: lp.y + ny * side * off };
}
function makeDimChain(dim, isBaseline) {
  const baseDim = {
    id: dim.id,
    dimType: dim.dimType,
    orientation: dim.orientation,
    dimStyle: dim.dimStyle || "Standard",
    p1: { x: dim.p1.x, y: dim.p1.y },
    p2: { x: dim.p2.x, y: dim.p2.y },
    linePoint: { x: dim.linePoint.x, y: dim.linePoint.y },
    p1Ref: dim.p1Ref ? { ...dim.p1Ref } : null,
    p2Ref: dim.p2Ref ? { ...dim.p2Ref } : null
  };
  const chain = isBaseline ? { fromPoint: { ...baseDim.p1 }, fromRef: baseDim.p1Ref ? { ...baseDim.p1Ref } : null, n: 1 } : { fromPoint: { ...baseDim.p2 }, fromRef: baseDim.p2Ref ? { ...baseDim.p2Ref } : null, n: 1 };
  return { baseDim, chain };
}
function chainStep(baseDim, chain, click, isBaseline, spacing) {
  return {
    p1: isBaseline ? { ...baseDim.p1 } : { ...chain.fromPoint },
    p2: { x: click.x, y: click.y },
    linePoint: isBaseline ? baselineLinePoint(baseDim, chain.n, spacing) : { ...baseDim.linePoint },
    p1Ref: chain.fromRef ? { ...chain.fromRef } : null
  };
}
function attachedDimensionIds(entities, ids) {
  const sel = new Set(ids);
  const out = [];
  for (const e of entities) {
    if (e.type !== "dimension" || sel.has(e.id)) continue;
    if (Array.isArray(e.sourceIds) && e.sourceIds.some((sid) => sel.has(sid))) out.push(e.id);
  }
  return out;
}
function dimensionMeasure(e) {
  if (e.dimType === "angular") return angularMeasure(e);
  if (e.dimType === "radius") return e.radius;
  if (e.dimType === "diameter") return e.radius * 2;
  if (e.dimType === "ordinate") {
    return e.axis === "x" ? e.feature.x - (e.datum && e.datum.x || 0) : e.feature.y - (e.datum && e.datum.y || 0);
  }
  if (e.dimType === "arclength") {
    const sweep = ((e.endAngle - e.startAngle) % 360 + 360) % 360;
    return e.radius * (sweep * Math.PI / 180);
  }
  if (e.dimType === "aligned") return dist(e.p1, e.p2);
  if (e.orientation === "vertical") return Math.abs(e.p2.y - e.p1.y);
  return Math.abs(e.p2.x - e.p1.x);
}
function angularMeasure(e) {
  const v1 = sub(e.p1, e.vertex);
  const v2 = sub(e.p2, e.vertex);
  const l1 = len(v1);
  const l2 = len(v2);
  if (l1 < 1e-9 || l2 < 1e-9) return 0;
  let cos = dot(v1, v2) / (l1 * l2);
  cos = Math.max(-1, Math.min(1, cos));
  return Math.acos(cos) * 180 / Math.PI;
}
var DIM_TEXT_K = 0.28;
var DIM_TEXT_MIN = 0.05;
var DIM_TEXT_MAX = 1e4;
function autoTextHeight(measure) {
  const h = DIM_TEXT_K * Math.sqrt(Math.max(measure, 0));
  return Math.min(DIM_TEXT_MAX, Math.max(DIM_TEXT_MIN, h));
}

// src/cadAnnotation.js
var PAPER_TEXT_MM = 2.5;
var PAPER_WIDTH_MM = 180;
var ANNO_SCALE_MIN = 1e-4;
var ANNO_SCALE_MAX = 1e4;
var clampScale = (v) => Math.min(ANNO_SCALE_MAX, Math.max(ANNO_SCALE_MIN, v));
function autoAnnotationScale(bounds) {
  if (!bounds) return 1;
  const w = bounds.maxX - bounds.minX;
  const h = bounds.maxY - bounds.minY;
  if (!Number.isFinite(w) || !Number.isFinite(h)) return 1;
  const extent = Math.max(Math.abs(w), Math.abs(h));
  if (!(extent > 0)) return 1;
  return clampScale(extent / PAPER_WIDTH_MM);
}
function resolveAnnotationScale(doc, bounds) {
  const v = doc && doc.annoScale;
  if (Number.isFinite(v) && v > 0) return clampScale(v);
  return autoAnnotationScale(bounds);
}
function annotationTextHeight(doc, bounds) {
  return PAPER_TEXT_MM * resolveAnnotationScale(doc, bounds);
}
function viewportAnnotationScale(scale, unitMm) {
  if (!Number.isFinite(scale) || scale <= 0) return 1;
  if (!Number.isFinite(unitMm) || unitMm <= 0) return 1;
  return 1 / (unitMm * scale);
}
function documentAnnotationScale(doc) {
  const v = doc && doc.annoScale;
  return Number.isFinite(v) && v > 0 ? clampScale(v) : 1;
}
function sizingAnnoScale(sizing) {
  const v = sizing && sizing.annoScale;
  return Number.isFinite(v) && v > 0 ? v : 1;
}
function isAnnotative(e, resolvedStyle) {
  if (e && typeof e.annotative === "boolean") return e.annotative;
  return !!(resolvedStyle && resolvedStyle.annotative === true);
}
function paperMmToModel(paperMm, annoScale) {
  const mm = Number.isFinite(paperMm) && paperMm > 0 ? paperMm : PAPER_TEXT_MM;
  return mm * (Number.isFinite(annoScale) && annoScale > 0 ? annoScale : 1);
}
var MIRROR_FIELD = { text: "height", mtext: "height", table: "textHeight" };
function entityPaperHeight(e, fallbackMm) {
  const v = e && e.paperHeight;
  if (Number.isFinite(v) && v > 0) return v;
  return Number.isFinite(fallbackMm) && fallbackMm > 0 ? fallbackMm : PAPER_TEXT_MM;
}
function syncAnnotativeEntity(e, annoScale, resolvedStyle) {
  if (!isAnnotative(e, resolvedStyle)) return e;
  const field = MIRROR_FIELD[e.type];
  if (!field) return e;
  const paper = Number.isFinite(e.paperHeight) && e.paperHeight > 0 ? e.paperHeight : resolvedStyle && resolvedStyle.fixedHeight;
  const next = paperMmToModel(paper, annoScale);
  return e[field] === next ? e : { ...e, [field]: next };
}
function annotativeStyleOf(resolveTextStyle2, textStyleDefs, currentStyle) {
  const defs = textStyleDefs || {};
  const anyAnnotativeStyle = Object.keys(defs).some((k) => defs[k] && defs[k].annotative === true);
  if (!anyAnnotativeStyle) return () => null;
  return (e) => resolveTextStyle2({ textStyle: e && e.textStyle }, defs, currentStyle);
}
function syncAnnotativeHeights(doc, resolveStyle) {
  const entities = doc && doc.entities;
  const anyAnnotative = (e) => e && (e.annotative === true || typeof resolveStyle === "function" && isAnnotative(e, resolveStyle(e)));
  if (!Array.isArray(entities) || !entities.some(anyAnnotative)) return doc;
  const annoScale = documentAnnotationScale(doc);
  let mutated = false;
  const next = entities.map((e) => {
    const s = syncAnnotativeEntity(e, annoScale, typeof resolveStyle === "function" ? resolveStyle(e) : null);
    if (s !== e) mutated = true;
    return s;
  });
  return mutated ? { ...doc, entities: next } : doc;
}
function migrateAnnotativeEntity(e) {
  if (!e || e.annotative !== true) return e;
  if (Number.isFinite(e.paperHeight) && e.paperHeight > 0) return e;
  const field = MIRROR_FIELD[e.type];
  const legacy = field ? e[field] : e.textHeight;
  if (!Number.isFinite(legacy) || legacy <= 0) return e;
  return { ...e, paperHeight: legacy };
}
function boxesOverlap(a, b, gap = 0) {
  return a.x - gap < b.x + b.width && a.x + a.width + gap > b.x && a.y - gap < b.y + b.height && a.y + a.height + gap > b.y;
}
function resolveTextCollision(box, placed, dir, opts = {}) {
  const gap = Number.isFinite(opts.gap) ? opts.gap : box.height * 0.25;
  const step = Number.isFinite(opts.step) && opts.step > 0 ? opts.step : box.height * 1.4;
  const maxSteps = Number.isFinite(opts.maxSteps) ? opts.maxSteps : 8;
  const list = Array.isArray(placed) ? placed.filter(Boolean) : [];
  if (!list.length) return { ...box, offset: 0 };
  const len3 = Math.hypot(dir && dir.x ? dir.x : 0, dir && dir.y ? dir.y : 0);
  const ux = len3 > 1e-9 ? dir.x / len3 : 0;
  const uy = len3 > 1e-9 ? dir.y / len3 : 1;
  for (let i = 0; i <= maxSteps; i += 1) {
    const d2 = i * step;
    const cand = { ...box, x: box.x + ux * d2, y: box.y + uy * d2 };
    if (!list.some((p) => boxesOverlap(cand, p, gap))) return { ...cand, offset: d2 };
  }
  const d = maxSteps * step;
  return { ...box, x: box.x + ux * d, y: box.y + uy * d, offset: d };
}
var CHAR_W_RATIO = 0.6;
function textBox(text, height, center) {
  const width = Math.max(1, String(text == null ? "" : text).length) * height * CHAR_W_RATIO;
  return { x: center.x - width / 2, y: center.y - height / 2, width, height };
}
function deconflictLabels(labels) {
  const out = {};
  if (!Array.isArray(labels) || labels.length < 2) return out;
  const placed = [];
  const ordered = [...labels.filter((l) => l && l.fixed), ...labels.filter((l) => !l || !l.fixed)];
  for (const l of ordered) {
    if (!l || !l.pos || !Number.isFinite(l.height) || l.height <= 0) continue;
    const box = textBox(l.text, l.height, l.pos);
    if (l.fixed) {
      placed.push({ x: box.x, y: box.y, width: box.width, height: box.height });
      continue;
    }
    const moved = resolveTextCollision(box, placed, l.dir || { x: 0, y: 1 });
    placed.push({ x: moved.x, y: moved.y, width: moved.width, height: moved.height });
    if (moved.offset > 0) {
      out[l.id] = { x: moved.x + moved.width / 2, y: moved.y + moved.height / 2 };
    }
  }
  return out;
}

// src/cadDimension/overrides.js
var STYLE_ONLY_FIELDS = Object.freeze(["baselineSpacing"]);
var LEGACY_CARRIER_FIELDS = Object.freeze([
  "textHeight",
  "arrowSize",
  "textColor",
  "annotative"
]);
var isNum = (v) => typeof v === "number" && Number.isFinite(v);
var nonNegative = (v) => isNum(v) && v >= 0;
var isBool = (v) => v === true || v === false;
var precision = (v) => Number.isInteger(v) && v >= 0 && v <= 8;
var oneOf = (list) => (v) => typeof v === "string" && list.includes(v);
var EXT_LINE_DIMS = ["linear", "aligned", "angular", "arclength"];
var TEXT_GAP_DIMS = ["linear", "aligned", "angular", "arclength", "radius", "diameter"];
var ARROW_DIMS = ["linear", "aligned", "angular", "arclength", "radius", "diameter"];
var dimOfKind = (kinds) => (e) => !!e && e.type === "dimension" && kinds.includes(e.dimType || "linear");
var DIM_OVERRIDE_FIELDS = Object.freeze([
  {
    key: "extOffset",
    label: "Ext offset",
    dimVar: "DIMEXO",
    kind: "number",
    scaled: true,
    valid: nonNegative,
    applies: dimOfKind(EXT_LINE_DIMS)
  },
  {
    key: "extExtend",
    label: "Ext extend",
    dimVar: "DIMEXE",
    kind: "number",
    scaled: true,
    valid: nonNegative,
    applies: dimOfKind(EXT_LINE_DIMS)
  },
  {
    key: "textGap",
    label: "Text gap",
    dimVar: "DIMGAP",
    kind: "number",
    scaled: true,
    valid: nonNegative,
    applies: dimOfKind(TEXT_GAP_DIMS)
  },
  {
    key: "unitsPrecision",
    label: "Precision",
    dimVar: "DIMDEC",
    kind: "integer",
    scaled: false,
    valid: precision,
    // Every dim kind EXCEPT angular, whose label is degrees and reads DIMADEC.
    applies: (e) => !!e && e.type === "dimension" && (e.dimType || "linear") !== "angular"
  },
  {
    key: "anglePrecision",
    label: "Angle precision",
    dimVar: "DIMADEC",
    kind: "integer",
    scaled: false,
    valid: precision,
    applies: dimOfKind(["angular"])
  },
  {
    key: "unitsFormat",
    label: "Units",
    dimVar: "DIMLUNIT",
    kind: "enum",
    scaled: false,
    options: ["decimal", "architectural"],
    valid: oneOf(["decimal", "architectural"]),
    // Same rule as unitsPrecision: architectural feet-and-inches is meaningless for a
    // bearing, and dimensionLabel's angular branch never consults it.
    applies: (e) => !!e && e.type === "dimension" && (e.dimType || "linear") !== "angular"
  },
  {
    key: "suppressExt1",
    label: "Suppress ext 1",
    dimVar: "DIMSE1",
    kind: "boolean",
    scaled: false,
    valid: isBool,
    applies: dimOfKind(EXT_LINE_DIMS)
  },
  {
    key: "suppressExt2",
    label: "Suppress ext 2",
    dimVar: "DIMSE2",
    kind: "boolean",
    scaled: false,
    valid: isBool,
    applies: dimOfKind(EXT_LINE_DIMS)
  },
  {
    key: "arrowType",
    label: "Arrow type",
    dimVar: "DIMBLK",
    kind: "enum",
    scaled: false,
    options: ["filled", "open", "tick", "dot", "none"],
    valid: oneOf(["filled", "open", "tick", "dot", "none"]),
    // The one field a LEADER shares: cadLeader.js reads style.arrowType for its own
    // head. Everything else above is dimension geometry a leader has none of.
    applies: (e) => !!e && (e.type === "leader" || dimOfKind(ARROW_DIMS)(e))
  }
]);
var FIELD_BY_KEY = new Map(DIM_OVERRIDE_FIELDS.map((f) => [f.key, f]));
var DIM_OVERRIDE_KEYS = Object.freeze(DIM_OVERRIDE_FIELDS.map((f) => f.key));
function isDimOverrideKey(key) {
  return FIELD_BY_KEY.has(key);
}
function isValidDimOverride(key, value) {
  const f = FIELD_BY_KEY.get(key);
  return !!f && f.valid(value);
}
function sanitizeDimOverrides(bag) {
  const out = {};
  if (!bag || typeof bag !== "object" || Array.isArray(bag)) return out;
  for (const f of DIM_OVERRIDE_FIELDS) {
    if (!Object.prototype.hasOwnProperty.call(bag, f.key)) continue;
    const v = bag[f.key];
    if (f.valid(v)) out[f.key] = v;
  }
  return out;
}
function dimOverrides(e) {
  return sanitizeDimOverrides(e && e.styleOverrides);
}
function hasDimOverrides(e) {
  return Object.keys(dimOverrides(e)).length > 0;
}
function withDimOverride(e, key, value) {
  if (!isDimOverrideKey(key)) return null;
  const next = dimOverrides(e);
  if (value === null || value === void 0) {
    if (!Object.prototype.hasOwnProperty.call(next, key)) return null;
    delete next[key];
  } else {
    if (!isValidDimOverride(key, value)) return null;
    if (next[key] === value) return null;
    next[key] = value;
  }
  return Object.keys(next).length ? next : void 0;
}
function dimOverrideDisplay(e, key, resolved) {
  if (!isDimOverrideKey(key)) return null;
  const own = dimOverrides(e);
  const isOwn = Object.prototype.hasOwnProperty.call(own, key);
  const inherited = resolved && key in resolved ? resolved[key] : void 0;
  return { value: isOwn ? own[key] : inherited, overridden: isOwn };
}
function dimOverrideFieldsFor(e) {
  return DIM_OVERRIDE_FIELDS.filter((f) => f.applies(e));
}
function supportsDimOverrides(e) {
  return dimOverrideFieldsFor(e).length > 0;
}

// src/cadDimension/dimstyle.js
var DIMSTYLE_DEFAULTS = {
  textHeight: 2.5,
  // only used when a style explicitly sets it (else auto-size)
  arrowSize: 2.5,
  //   "
  extOffset: 0,
  // DIMEXO: gap from the measured point to the ext-line start (× dimScale)
  // DIMEXE: overshoot of the extension line beyond the dim line (× dimScale). 1.25 is
  // chosen for visual parity: the old renderer hard-coded the overshoot as
  // `arrowSize * 0.5`, which at the default arrow size (2.5) is exactly 1.25. (It is
  // also AutoCAD's metric DIMEXE default.) See geometry.js for where it now drives the
  // ext lines instead of the hard-coded constant.
  extExtend: 1.25,
  // DIMGAP: the true gap between the dim line and the NEAR edge of the value text
  // (× dimScale). 0.25 is chosen for visual parity: the old renderer placed the text
  // CENTRE at `textHeight * 0.6` off the line, i.e. an edge gap of `0.6h − h/2 = 0.1h`,
  // which at the default height (2.5) is exactly 0.25. The renderer now adds textHeight/2
  // back so this value is the edge gap (see geometry.js). The old 0.625 was dead
  // storage (written to DXF DIMGAP but never rendered).
  textGap: 0.25,
  unitsPrecision: 2,
  // decimal places for the measured value
  // DIMADEC: decimal places for an ANGULAR dimension's label. Separate from
  // unitsPrecision because AutoCAD keeps them separate, and because they are not the
  // same question: 2 dp is right for a length in millimetres and needlessly precise
  // for a bearing. The default is 1 — the value dimensionLabel hard-coded before
  // [BUG-A2-09] was fixed — so every drawing that already exists reads EXACTLY as it
  // did, and the bug closes because the field can now reach the label at all.
  anglePrecision: 1,
  unitsFormat: "decimal",
  // 'decimal' | 'architectural'
  suppressExt1: false,
  // DIMSE1: suppress the first extension line
  suppressExt2: false,
  // DIMSE2: suppress the second extension line
  textColor: null,
  // DIMCLRT: value-text colour; null ⇒ follow the dim's stroke
  arrowType: "filled",
  // DIMBLK: 'filled' | 'open' | 'tick' | 'dot' | 'none'
  // DIMDLI (Spec 12): the offset between successive BASELINE dimension lines (×
  // dimScale). 3.75 is AutoCAD's metric default. A LIVE setting from day one (unlike
  // the once-dead knobs) — DIMBASELINE reads it via effectiveBaselineSpacing.
  baselineSpacing: 3.75,
  // ANNOTATIVE (ROADMAP 2.2 criterion 1). When true, every LENGTH in this style —
  // textHeight, arrowSize, extOffset, extExtend, textGap, baselineSpacing — is read as
  // PAPER MILLIMETRES and multiplied by the annotation scale in force where the dim is
  // drawn, instead of by DIMSCALE. That is what makes one dimension measure the same
  // on a 1:50 sheet and a 1:100 one. Default false, so every drawing that exists keeps
  // its stored model-unit behaviour exactly.
  annotative: false
};
function rawStyle(dim, dimStyleDefs, currentStyle) {
  if (!dimStyleDefs) return {};
  const cur = currentStyle && dimStyleDefs[currentStyle] || {};
  const named = dim && dim.dimStyle && dimStyleDefs[dim.dimStyle] || {};
  return { ...cur, ...named };
}
function resolveDimStyle(dim, dimStyleDefs, currentStyle) {
  const merged = {
    ...DIMSTYLE_DEFAULTS,
    ...rawStyle(dim, dimStyleDefs, currentStyle),
    ...dimOverrides(dim)
  };
  merged.annotative = merged.annotative === true;
  if (dim) {
    if (dim.autoText === false && Number.isFinite(dim.textHeight) && dim.textHeight > 0) merged.textHeight = dim.textHeight;
    if (dim.autoText === false && Number.isFinite(dim.arrowSize) && dim.arrowSize > 0) merged.arrowSize = dim.arrowSize;
    if (dim.textColorOverride != null) merged.textColor = dim.textColorOverride;
    merged.annotative = isAnnotative(dim, merged);
  }
  return merged;
}
var DIM_AUTOBASE_RATIO = 50;
var dimScaleOf = (s) => s && Number.isFinite(s.dimScale) && s.dimScale > 0 ? s.dimScale : 1;
var autoBaseOf = (s) => s && Number.isFinite(s.autoBaseHeight) && s.autoBaseHeight > 0 ? s.autoBaseHeight : DIMSTYLE_DEFAULTS.textHeight;
function autoDimBaseHeight(bounds) {
  const def = DIMSTYLE_DEFAULTS.textHeight;
  if (!bounds) return def;
  const w = bounds.maxX - bounds.minX;
  const h = bounds.maxY - bounds.minY;
  const diag = Math.hypot(Number.isFinite(w) ? w : 0, Number.isFinite(h) ? h : 0);
  if (!(diag > 0) || !Number.isFinite(diag)) return def;
  return annotationTextHeight(null, bounds);
}
function resolveUserDimScale(doc) {
  const v = doc && doc.dimScale;
  return Number.isFinite(v) && v > 0 ? v : 1;
}
function makeDimSizing(doc, bounds, annoScale) {
  return {
    dimScale: resolveUserDimScale(doc),
    autoBaseHeight: annotationTextHeight(doc, bounds),
    // The ANNOTATIVE scale: a viewport's override, else the document setting. NOT the
    // extents-derived value — see documentAnnotationScale for the feedback loop that
    // closes. `autoBaseHeight` above still uses the extents, which is correct: it sizes
    // NON-annotative dimension text and nothing feeds back into it.
    annoScale: Number.isFinite(annoScale) && annoScale > 0 ? annoScale : documentAnnotationScale(doc)
  };
}
var lengthScaleOf = (style, sizing) => style.annotative ? sizingAnnoScale(sizing) : dimScaleOf(sizing);
function effectiveTextHeight(e, dimStyleDefs, currentStyle, sizing) {
  return textHeightFrom(resolveDimStyle(e, dimStyleDefs, currentStyle), e, dimStyleDefs, currentStyle, sizing);
}
function textHeightFrom(resolved, e, dimStyleDefs, currentStyle, sizing) {
  if (resolved.annotative) {
    const paper = Number.isFinite(e && e.paperHeight) && e.paperHeight > 0 ? e.paperHeight : resolved.textHeight;
    return paperMmToModel(paper, sizingAnnoScale(sizing));
  }
  const scale = dimScaleOf(sizing);
  if (e.autoText === false) {
    return Number.isFinite(e.textHeight) && e.textHeight > 0 ? e.textHeight : 1;
  }
  const raw = rawStyle(e, dimStyleDefs, currentStyle);
  if (Number.isFinite(raw.textHeight) && raw.textHeight > 0) return raw.textHeight * scale;
  return autoBaseOf(sizing) * scale;
}
function effectiveArrowSize(e, dimStyleDefs, currentStyle, sizing) {
  const resolved = resolveDimStyle(e, dimStyleDefs, currentStyle);
  const height = () => textHeightFrom(resolved, e, dimStyleDefs, currentStyle, sizing);
  if (resolved.annotative) {
    const paper = Number.isFinite(e && e.paperArrowSize) && e.paperArrowSize > 0 ? e.paperArrowSize : resolved.arrowSize;
    return paperMmToModel(paper, sizingAnnoScale(sizing));
  }
  const scale = dimScaleOf(sizing);
  if (e.autoText === false) {
    return Number.isFinite(e.arrowSize) && e.arrowSize > 0 ? e.arrowSize : height();
  }
  const raw = rawStyle(e, dimStyleDefs, currentStyle);
  if (Number.isFinite(raw.arrowSize) && raw.arrowSize > 0) return raw.arrowSize * scale;
  return height();
}
function effectiveExtOffset(e, dimStyleDefs, currentStyle, sizing) {
  const resolved = resolveDimStyle(e, dimStyleDefs, currentStyle);
  return resolved.extOffset * lengthScaleOf(resolved, sizing);
}
function effectiveExtExtend(e, dimStyleDefs, currentStyle, sizing) {
  const resolved = resolveDimStyle(e, dimStyleDefs, currentStyle);
  return resolved.extExtend * lengthScaleOf(resolved, sizing);
}
function effectiveTextGap(e, dimStyleDefs, currentStyle, sizing) {
  const resolved = resolveDimStyle(e, dimStyleDefs, currentStyle);
  return resolved.textGap * lengthScaleOf(resolved, sizing);
}
function effectiveBaselineSpacing(e, dimStyleDefs, currentStyle, sizing) {
  const resolved = resolveDimStyle(e, dimStyleDefs, currentStyle);
  return resolved.baselineSpacing * lengthScaleOf(resolved, sizing);
}
function isTextColorSet(c) {
  if (c == null) return false;
  if (typeof c !== "string") return false;
  const t = c.trim().toLowerCase();
  return t !== "" && t !== "byblock" && t !== "bylayer";
}
function dimensionTextColor(e, dimStyleDefs, currentStyle, fallback) {
  const c = resolveDimStyle(e, dimStyleDefs, currentStyle).textColor;
  return isTextColorSet(c) ? c : fallback;
}

// src/cadDimension/format.js
function formatArchitectural(value) {
  const sign = value < 0 ? "-" : "";
  let total16 = Math.round(Math.abs(value) * 16);
  const feet = Math.floor(total16 / (12 * 16));
  total16 -= feet * 12 * 16;
  const inches = Math.floor(total16 / 16);
  const num2 = total16 - inches * 16;
  let frac = "";
  if (num2 > 0) {
    let n = num2;
    let den = 16;
    while (n % 2 === 0) {
      n /= 2;
      den /= 2;
    }
    frac = ` ${n}/${den}`;
  }
  return `${sign}${feet}'-${inches}${frac}"`;
}
function formatMeasure(measure, override) {
  if (override != null && override !== "") return String(override);
  return measure.toFixed(2);
}
function precisionOf(v, fallback) {
  return Number.isInteger(v) && v >= 0 && v <= 100 ? v : fallback;
}
function dimensionLabel(e, dimStyleDefs, currentStyle) {
  if (e.textOverride != null && e.textOverride !== "") return String(e.textOverride);
  const m = dimensionMeasure(e);
  const st = resolveDimStyle(e, dimStyleDefs, currentStyle);
  if (e.dimType === "angular") return `${m.toFixed(precisionOf(st.anglePrecision, 1))}\xB0`;
  const prec = precisionOf(st.unitsPrecision, 2);
  const num2 = st.unitsFormat === "architectural" ? formatArchitectural(m) : m.toFixed(prec);
  if (e.dimType === "radius") return `R${num2}`;
  if (e.dimType === "diameter") return `\xD8${num2}`;
  if (e.dimType === "arclength") return `\u2312 ${num2}`;
  return num2;
}

// src/cadDimension/geometry.js
function dimensionGeometry(e, dimStyleDefs, currentStyle, sizing) {
  const p1 = e.p1;
  const p2 = e.p2;
  const lp = e.linePoint;
  const arrow = effectiveArrowSize(e, dimStyleDefs, currentStyle, sizing);
  let dl1;
  let dl2;
  let measure;
  let textAngle;
  if (e.dimType === "aligned") {
    const u = norm(sub(p2, p1));
    const n = perp(u);
    const off2 = dot(sub(lp, p1), n);
    const shift = mul(n, off2);
    dl1 = add(p1, shift);
    dl2 = add(p2, shift);
    measure = dist(p1, p2);
    textAngle = Math.atan2(u.y, u.x) * 180 / Math.PI;
  } else if (e.orientation === "vertical") {
    dl1 = { x: lp.x, y: p1.y };
    dl2 = { x: lp.x, y: p2.y };
    measure = Math.abs(p2.y - p1.y);
    textAngle = 90;
  } else {
    dl1 = { x: p1.x, y: lp.y };
    dl2 = { x: p2.x, y: lp.y };
    measure = Math.abs(p2.x - p1.x);
    textAngle = 0;
  }
  textAngle = (textAngle % 180 + 180) % 180;
  if (textAngle > 90) textAngle -= 180;
  const dlu = norm(sub(dl2, dl1));
  const style = resolveDimStyle(e, dimStyleDefs, currentStyle);
  const arrows = [
    arrowShape(dl1, mul(dlu, -1), arrow, style.arrowType),
    // outward, away from dl2
    arrowShape(dl2, dlu, arrow, style.arrowType)
  ];
  const off = effectiveExtOffset(e, dimStyleDefs, currentStyle, sizing);
  const over = effectiveExtExtend(e, dimStyleDefs, currentStyle, sizing);
  const extLine = (p, dl) => {
    const u = norm(sub(dl, p));
    const start = dist(p, dl) > off ? add(p, mul(u, off)) : dl;
    return [start, add(dl, mul(u, over))];
  };
  const ext1 = style.suppressExt1 ? null : extLine(p1, dl1);
  const ext2 = style.suppressExt2 ? null : extLine(p2, dl2);
  const th = effectiveTextHeight(e, dimStyleDefs, currentStyle, sizing);
  const mid = { x: (dl1.x + dl2.x) / 2, y: (dl1.y + dl2.y) / 2 };
  let nrm = perp(dlu);
  const pmid = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
  if (dot(nrm, sub(pmid, mid)) > 0) nrm = mul(nrm, -1);
  const textPos = withTextOffset(add(mid, mul(nrm, effectiveTextGap(e, dimStyleDefs, currentStyle, sizing) + th / 2)), e);
  return { ext1, ext2, dimLine: [dl1, dl2], arrows, textPos, textAngle, measure };
}
function dimTextOffset(e) {
  const o = e && e.textOffset;
  if (!o || typeof o !== "object") return null;
  const x = o.x;
  const y = o.y;
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  if (x === 0 && y === 0) return null;
  return { x, y };
}
function withTextOffset(pos, e) {
  const o = dimTextOffset(e);
  return o ? { x: pos.x + o.x, y: pos.y + o.y } : pos;
}
function angularGeometry(e, dimStyleDefs, currentStyle, sizing) {
  const vertex = e.vertex;
  const u1 = norm(sub(e.p1, vertex));
  const u2 = norm(sub(e.p2, vertex));
  const R = dist(vertex, e.p2);
  const arrow = effectiveArrowSize(e, dimStyleDefs, currentStyle, sizing);
  const deg = (v) => Math.atan2(v.y, v.x) * 180 / Math.PI;
  let a1 = deg(u1);
  let a2 = deg(u2);
  let sweep = ((a2 - a1) % 360 + 360) % 360;
  if (sweep > 180) {
    const t = a1;
    a1 = a2;
    a2 = t;
    sweep = 360 - sweep;
  }
  const onArc = (degAng) => ({
    x: vertex.x + R * Math.cos(degAng * Math.PI / 180),
    y: vertex.y + R * Math.sin(degAng * Math.PI / 180)
  });
  const startPt = onArc(a1);
  const endPt = onArc(a2);
  const midPt = onArc(a1 + sweep / 2);
  const style = resolveDimStyle(e, dimStyleDefs, currentStyle);
  const off = effectiveExtOffset(e, dimStyleDefs, currentStyle, sizing);
  const over = effectiveExtExtend(e, dimStyleDefs, currentStyle, sizing);
  const ext1 = style.suppressExt1 ? null : [add(vertex, mul(u1, R + off)), add(vertex, mul(u1, R + over))];
  const ext2 = style.suppressExt2 ? null : [add(vertex, mul(u2, R + off)), add(vertex, mul(u2, R + over))];
  const phi = arrow / Math.max(R, 1e-9) * 180 / Math.PI;
  const fits = sweep >= phi * 2.5;
  const chordDir = (tipDeg, backDeg) => norm(sub(onArc(tipDeg), onArc(backDeg)));
  const pairs = fits ? [
    { tip: startPt, dir: chordDir(a1, a1 + phi) },
    // body inside the sweep
    { tip: endPt, dir: chordDir(a2, a2 - phi) }
  ] : [
    { tip: startPt, dir: chordDir(a1, a1 - phi) },
    // flipped outside
    { tip: endPt, dir: chordDir(a2, a2 + phi) }
  ];
  const arrows = pairs.map((ar) => arrowShape(ar.tip, ar.dir, arrow, style.arrowType));
  const th = effectiveTextHeight(e, dimStyleDefs, currentStyle, sizing);
  const textPos = withTextOffset(add(vertex, mul(norm(sub(midPt, vertex)), R + effectiveTextGap(e, dimStyleDefs, currentStyle, sizing) + th / 2)), e);
  return {
    kind: "angular",
    arc: { cx: vertex.x, cy: vertex.y, r: R, startAngle: a1, endAngle: a2 },
    ext1,
    ext2,
    arrows,
    textPos,
    measure: sweep
  };
}
function lineIntersection(a, b, c, d) {
  const r = sub(b, a);
  const s = sub(d, c);
  const denom = r.x * s.y - r.y * s.x;
  if (Math.abs(denom) < 1e-9) return null;
  const t = ((c.x - a.x) * s.y - (c.y - a.y) * s.x) / denom;
  return { x: a.x + t * r.x, y: a.y + t * r.y };
}
function angularFromTwoLines(l1, l2, placePt) {
  const vertex = lineIntersection(l1.a, l1.b, l2.a, l2.b);
  if (!vertex) return null;
  const u1 = norm(sub(l1.b, l1.a));
  const u2 = norm(sub(l2.b, l2.a));
  const toP = sub(placePt, vertex);
  const R = Math.max(len(toP), 1e-6);
  const deg = (v) => Math.atan2(v.y, v.x) * 180 / Math.PI;
  const n360 = (a) => (a % 360 + 360) % 360;
  const rays = [u1, mul(u1, -1), u2, mul(u2, -1)];
  const pAng = deg(toP);
  let cw = rays[0];
  let ccw = rays[0];
  let cwGap = Infinity;
  let ccwGap = Infinity;
  for (const r of rays) {
    const a = deg(r);
    const g1 = n360(pAng - a);
    const g2 = n360(a - pAng);
    if (g1 < cwGap) {
      cwGap = g1;
      cw = r;
    }
    if (g2 < ccwGap) {
      ccwGap = g2;
      ccw = r;
    }
  }
  return { vertex, p1: add(vertex, mul(cw, R)), p2: add(vertex, mul(ccw, R)) };
}
function arclengthGeometry(e, dimStyleDefs, currentStyle, sizing) {
  const c = e.center;
  const srcR = e.radius;
  const a1 = e.startAngle;
  const sweep = ((e.endAngle - a1) % 360 + 360) % 360;
  if (!(srcR > 0) || sweep <= 1e-9) return null;
  const a2 = a1 + sweep;
  const R = dist(c, e.linePoint);
  if (!(R > 0)) return null;
  const arrow = effectiveArrowSize(e, dimStyleDefs, currentStyle, sizing);
  const rad = (d) => d * Math.PI / 180;
  const onAng = (radius, degAng) => ({ x: c.x + radius * Math.cos(rad(degAng)), y: c.y + radius * Math.sin(rad(degAng)) });
  const style = resolveDimStyle(e, dimStyleDefs, currentStyle);
  const off = effectiveExtOffset(e, dimStyleDefs, currentStyle, sizing);
  const over = effectiveExtExtend(e, dimStyleDefs, currentStyle, sizing);
  const dir1 = { x: Math.cos(rad(a1)), y: Math.sin(rad(a1)) };
  const dir2 = { x: Math.cos(rad(a2)), y: Math.sin(rad(a2)) };
  const extLine = (u) => [add(c, mul(u, srcR + off)), add(c, mul(u, R + over))];
  const ext1 = style.suppressExt1 ? null : extLine(dir1);
  const ext2 = style.suppressExt2 ? null : extLine(dir2);
  const startPt = onAng(R, a1);
  const endPt = onAng(R, a2);
  const phi = arrow / Math.max(R, 1e-9) * 180 / Math.PI;
  const fits = sweep >= phi * 2.5;
  const chordDir = (tipDeg, backDeg) => norm(sub(onAng(R, tipDeg), onAng(R, backDeg)));
  const pairs = fits ? [
    { tip: startPt, dir: chordDir(a1, a1 + phi) },
    { tip: endPt, dir: chordDir(a2, a2 - phi) }
  ] : [
    { tip: startPt, dir: chordDir(a1, a1 - phi) },
    { tip: endPt, dir: chordDir(a2, a2 + phi) }
  ];
  const arrows = pairs.map((ar) => arrowShape(ar.tip, ar.dir, arrow, style.arrowType));
  const th = effectiveTextHeight(e, dimStyleDefs, currentStyle, sizing);
  const textPos = withTextOffset(onAng(R + effectiveTextGap(e, dimStyleDefs, currentStyle, sizing) + th / 2, a1 + sweep / 2), e);
  return {
    kind: "arclength",
    arc: { cx: c.x, cy: c.y, r: R, startAngle: a1, endAngle: a2 },
    ext1,
    ext2,
    arrows,
    textPos,
    measure: srcR * (sweep * Math.PI) / 180
  };
}
function radialGeometry(e, dimStyleDefs, currentStyle, sizing) {
  const c = e.center;
  const lp = e.leaderPoint;
  const off = sub(lp, c);
  const u = len(off) > 1e-9 ? norm(off) : { x: 1, y: 0 };
  const arrow = effectiveArrowSize(e, dimStyleDefs, currentStyle, sizing);
  const h = effectiveTextHeight(e, dimStyleDefs, currentStyle, sizing);
  const type = resolveDimStyle(e, dimStyleDefs, currentStyle).arrowType;
  const gap = effectiveTextGap(e, dimStyleDefs, currentStyle, sizing) + h / 2;
  const label = dimensionLabel(e, dimStyleDefs, currentStyle);
  const estW = Math.max(label.length * h * 0.62, h);
  const near3 = add(c, mul(u, e.radius));
  const far = add(c, mul(u, -e.radius));
  const m = arrow * 0.5;
  const centerMark = [
    [{ x: c.x - m, y: c.y }, { x: c.x + m, y: c.y }],
    [{ x: c.x, y: c.y - m }, { x: c.x, y: c.y + m }]
  ];
  if (dist(lp, c) <= e.radius) {
    const diameter = e.dimType === "diameter";
    return {
      kind: "radial",
      line: [diameter ? far : c, near3],
      landing: null,
      centerMark: diameter ? null : centerMark,
      arrows: diameter ? [arrowShape(near3, u, arrow, type), arrowShape(far, mul(u, -1), arrow, type)] : [arrowShape(near3, u, arrow, type)],
      textPos: withTextOffset(add(lp, mul(perp(u), gap)), e),
      arrow
    };
  }
  const landDir = { x: u.x >= 0 ? 1 : -1, y: 0 };
  const landing = [lp, add(lp, mul(landDir, estW))];
  return {
    kind: "radial",
    line: [near3, lp],
    landing,
    centerMark,
    arrows: [arrowShape(near3, mul(u, -1), arrow, type)],
    // ON the curve, pointing at it along the leader
    textPos: withTextOffset({ x: (landing[0].x + landing[1].x) / 2, y: landing[0].y + gap }, e),
    arrow
  };
}
function ordinateGeometry(e) {
  const f = e.feature;
  const lp = e.leader;
  const elbow = e.axis === "x" ? { x: f.x, y: lp.y } : { x: lp.x, y: f.y };
  return { kind: "ordinate", leader: [f, elbow, lp], textPos: withTextOffset(lp, e), measure: dimensionMeasure(e) };
}
function dimensionTextAnchor(e, dimStyleDefs, currentStyle, sizing) {
  let g;
  if (e.dimType === "angular") g = angularGeometry(e, dimStyleDefs, currentStyle, sizing);
  else if (e.dimType === "radius" || e.dimType === "diameter") g = radialGeometry(e, dimStyleDefs, currentStyle, sizing);
  else if (e.dimType === "ordinate") g = ordinateGeometry(e);
  else if (e.dimType === "arclength") g = arclengthGeometry(e, dimStyleDefs, currentStyle, sizing);
  else g = dimensionGeometry(e, dimStyleDefs, currentStyle, sizing);
  if (!g) return { x: e.center ? e.center.x : 0, y: e.center ? e.center.y : 0, angle: 0, height: effectiveTextHeight(e, dimStyleDefs, currentStyle, sizing) };
  return {
    x: g.textPos.x,
    y: g.textPos.y,
    angle: g.textAngle || 0,
    height: effectiveTextHeight(e, dimStyleDefs, currentStyle, sizing)
  };
}
function arrowTriangle({ tip, dir }, size) {
  const back = { x: tip.x - dir.x * size, y: tip.y - dir.y * size };
  const w = size / 6;
  const px = -dir.y * w;
  const py = dir.x * w;
  return [tip, { x: back.x + px, y: back.y + py }, { x: back.x - px, y: back.y - py }];
}
function arrowShape(tip, dir, size, type) {
  switch (type) {
    case "none":
      return null;
    case "tick": {
      const a = Math.PI / 4;
      const cos = Math.cos(a);
      const sin = Math.sin(a);
      const t = { x: dir.x * cos - dir.y * sin, y: dir.x * sin + dir.y * cos };
      const half = size / 2;
      return {
        kind: "tick",
        seg: [
          { x: tip.x - t.x * half, y: tip.y - t.y * half },
          { x: tip.x + t.x * half, y: tip.y + t.y * half }
        ]
      };
    }
    case "dot":
      return { kind: "dot", c: { x: tip.x, y: tip.y }, r: size / 3 };
    case "open":
      return { kind: "triangle", pts: arrowTriangle({ tip, dir }, size), filled: false };
    case "filled":
    default:
      return { kind: "triangle", pts: arrowTriangle({ tip, dir }, size), filled: true };
  }
}

// src/cadDimension/decompose.js
function dimensionPrimitives(e, dimStyleDefs, style, sizing) {
  if (!e || e.type !== "dimension") return null;
  const lines = [];
  const arcs = [];
  const arrows = [];
  let text = null;
  const height = effectiveTextHeight(e, dimStyleDefs, style, sizing);
  const label = dimensionLabel(e, dimStyleDefs);
  const setText = (pos, rotation) => {
    if (pos) text = { pos, rotation: rotation || 0, height, label };
  };
  const push = (seg) => {
    if (seg && seg[0] && seg[1]) lines.push([seg[0], seg[1]]);
  };
  if (e.dimType === "angular") {
    const g2 = angularGeometry(e, dimStyleDefs, style, sizing);
    if (!g2) return null;
    push(g2.ext1);
    push(g2.ext2);
    if (g2.arc) arcs.push(g2.arc);
    arrows.push(...g2.arrows || []);
    setText(g2.textPos, 0);
    return { lines, arcs, arrows, text };
  }
  if (e.dimType === "radius" || e.dimType === "diameter") {
    const g2 = radialGeometry(e, dimStyleDefs, style, sizing);
    if (!g2) return null;
    push(g2.line);
    push(g2.landing);
    for (const seg of g2.centerMark || []) push(seg);
    arrows.push(...g2.arrows || []);
    setText(g2.textPos, 0);
    return { lines, arcs, arrows, text };
  }
  if (e.dimType === "ordinate") {
    const g2 = ordinateGeometry(e);
    if (!g2) return null;
    for (let i = 0; i < (g2.leader || []).length - 1; i++) push([g2.leader[i], g2.leader[i + 1]]);
    setText(g2.textPos, 0);
    return { lines, arcs, arrows, text };
  }
  if (e.dimType === "arclength") {
    const g2 = arclengthGeometry(e, dimStyleDefs, style, sizing);
    if (!g2) return null;
    push(g2.ext1);
    push(g2.ext2);
    if (g2.arc) arcs.push(g2.arc);
    arrows.push(...g2.arrows || []);
    setText(g2.textPos, 0);
    return { lines, arcs, arrows, text };
  }
  const g = dimensionGeometry(e, dimStyleDefs, style, sizing);
  if (!g) return null;
  push(g.ext1);
  push(g.ext2);
  push(g.dimLine);
  arrows.push(...g.arrows || []);
  setText(g.textPos, g.textAngle);
  return { lines, arcs, arrows, text };
}
function arrowStrokes(ar) {
  const lines = [];
  const circles = [];
  if (!ar) return { lines, circles };
  if (ar.kind === "triangle") {
    lines.push([ar.pts[0], ar.pts[1]], [ar.pts[1], ar.pts[2]], [ar.pts[2], ar.pts[0]]);
  } else if (ar.kind === "tick") {
    lines.push([ar.seg[0], ar.seg[1]]);
  } else if (ar.kind === "dot") {
    circles.push({ cx: ar.c.x, cy: ar.c.y, r: ar.r });
  }
  return { lines, circles };
}

// src/cadDimension/hittest.js
function arcDistance(a, p) {
  const ang = (Math.atan2(p.y - a.cy, p.x - a.cx) * 180 / Math.PI % 360 + 360) % 360;
  const sweep = ((a.endAngle - a.startAngle) % 360 + 360) % 360;
  const within = ((ang - (a.startAngle % 360 + 360) % 360) % 360 + 360) % 360 <= sweep;
  return within ? Math.abs(Math.hypot(p.x - a.cx, p.y - a.cy) - a.r) : Math.min(
    dist(p, {
      x: a.cx + a.r * Math.cos(a.startAngle * Math.PI / 180),
      y: a.cy + a.r * Math.sin(a.startAngle * Math.PI / 180)
    }),
    dist(p, {
      x: a.cx + a.r * Math.cos(a.endAngle * Math.PI / 180),
      y: a.cy + a.r * Math.sin(a.endAngle * Math.PI / 180)
    })
  );
}
function extDistance(g, p) {
  let d = Infinity;
  if (g.ext1) d = Math.min(d, ptSeg(p, g.ext1[0], g.ext1[1]));
  if (g.ext2) d = Math.min(d, ptSeg(p, g.ext2[0], g.ext2[1]));
  return d;
}
function distanceToDimension(e, p, ctx) {
  const defs = ctx && ctx.dimStyleDefs;
  const cur = ctx && ctx.dimStyle;
  const sizing = ctx && ctx.sizing;
  if (e.dimType === "ordinate") {
    const g2 = ordinateGeometry(e);
    return Math.min(ptSeg(p, g2.leader[0], g2.leader[1]), ptSeg(p, g2.leader[1], g2.leader[2]));
  }
  if (e.dimType === "arclength") {
    const g2 = arclengthGeometry(e, defs, cur, sizing);
    if (!g2) return Infinity;
    return Math.min(arcDistance(g2.arc, p), extDistance(g2, p));
  }
  if (e.dimType === "radius" || e.dimType === "diameter") {
    const g2 = radialGeometry(e, defs, cur, sizing);
    return ptSeg(p, g2.line[0], g2.line[1]);
  }
  if (e.dimType === "angular") {
    const g2 = angularGeometry(e, defs, cur, sizing);
    return Math.min(arcDistance(g2.arc, p), extDistance(g2, p));
  }
  const g = dimensionGeometry(e, defs, cur, sizing);
  return Math.min(ptSeg(p, g.dimLine[0], g.dimLine[1]), extDistance(g, p));
}

// src/cadLeader.js
function leaderGeometry(e, dimStyleDefs, currentStyle, sizing) {
  const pts = e && Array.isArray(e.points) ? e.points : [];
  if (pts.length < 2) return null;
  const dir = norm(sub(pts[0], pts[1]));
  if (len(sub(pts[0], pts[1])) < 1e-9) return null;
  const arrowSize = effectiveArrowSize(e, dimStyleDefs, currentStyle, sizing);
  const style = resolveDimStyle(e, dimStyleDefs, currentStyle);
  const ownHeight = Number.isFinite(e.textHeight) && e.textHeight > 0 ? e.textHeight : null;
  const textHeight = style.annotative ? paperMmToModel(
    Number.isFinite(e.paperHeight) && e.paperHeight > 0 ? e.paperHeight : style.textHeight,
    sizingAnnoScale(sizing)
  ) : ownHeight != null ? ownHeight : effectiveTextHeight(e, dimStyleDefs, currentStyle, sizing);
  const type = style.arrowType;
  const segs = [];
  for (let i = 0; i < pts.length - 1; i++) segs.push([pts[i], pts[i + 1]]);
  const last = pts[pts.length - 1];
  const prev = pts[pts.length - 2];
  const landDir = last.x - prev.x >= 0 ? 1 : -1;
  const far = { x: last.x + landDir * arrowSize * 2, y: last.y };
  const landing = [last, far];
  const textPos = { x: far.x + landDir * textHeight * 0.25, y: far.y };
  const textAlignRight = landDir < 0;
  return {
    segs,
    arrow: arrowShape(pts[0], dir, arrowSize, type),
    landing,
    textPos,
    textAlignRight,
    textHeight
  };
}

// src/cadText.js
var APPROX_CHAR_WIDTH_FACTOR = 0.6;
function justifyFactors(justify = "BL") {
  const j = typeof justify === "string" ? justify.toUpperCase() : "";
  const v = j[0];
  const h = j[1];
  const fx = h === "C" ? 0.5 : h === "R" ? 1 : 0;
  const fy = v === "T" ? 0 : v === "M" ? 0.5 : 1;
  return { fx, fy };
}
function justifyOffset(justify, width, height) {
  const { fx, fy } = justifyFactors(justify);
  return { dx: fx ? -fx * (width || 0) : 0, dy: fy ? fy * (height || 0) : 0 };
}

// src/cadMText.js
var MTEXT_DEFAULT_LINE_SPACING = 1.5;
var MAX_MTEXT_LINES = 2e3;
var approxMeasure = (t, h) => (t ? String(t).length : 0) * (h || 1) * APPROX_CHAR_WIDTH_FACTOR;
var heightOf = (e) => Number.isFinite(e.height) && e.height > 0 ? e.height : 1;
var spacingOf = (e) => Number.isFinite(e.lineSpacing) && e.lineSpacing > 0 ? e.lineSpacing : MTEXT_DEFAULT_LINE_SPACING;
function layoutMText(e, measure = approxMeasure) {
  const h = heightOf(e);
  const family = e.fontFamily;
  const wrapW = Number.isFinite(e.width) && e.width > 0 ? e.width : null;
  const lines = [];
  for (const para of String(e.text ?? "").split("\n")) {
    if (lines.length >= MAX_MTEXT_LINES) break;
    const words = para.split(/\s+/).filter((w) => w !== "");
    if (!words.length) {
      lines.push("");
      continue;
    }
    let line = "";
    for (const word of words) {
      const candidate = line ? line + " " + word : word;
      if (!line || wrapW == null || measure(candidate, h, family) <= wrapW) {
        line = candidate;
      } else {
        lines.push(line);
        line = word;
        if (lines.length >= MAX_MTEXT_LINES) break;
      }
    }
    if (lines.length < MAX_MTEXT_LINES) lines.push(line);
  }
  const advance = h * spacingOf(e);
  const boxHeight = lines.length ? (lines.length - 1) * advance + h : 0;
  const widths = lines.map((t) => t ? measure(t, h, family) : 0);
  const effWidth = wrapW != null ? wrapW : Math.max(h, ...widths);
  const { fx, fy } = justifyFactors(e.justify || "TL");
  const dy0 = fy ? -fy * boxHeight : 0;
  return {
    lines: lines.map((text, i) => ({
      text,
      dx: fx ? fx * (effWidth - widths[i]) : 0,
      dy: dy0 + i * advance
    })),
    boxHeight,
    height: h,
    width: effWidth
  };
}
function mtextToWorld(e, dx, dy) {
  const rot = (e.rotation || 0) * Math.PI / 180;
  const ca = Math.cos(rot);
  const sa = Math.sin(rot);
  return { x: e.x + dx * ca + dy * sa, y: e.y + dx * sa - dy * ca };
}
function mtextLocal(e, p) {
  const rot = (e.rotation || 0) * Math.PI / 180;
  const ca = Math.cos(rot);
  const sa = Math.sin(rot);
  const ox = p.x - e.x;
  const oy = p.y - e.y;
  return { dx: ox * ca + oy * sa, dy: ox * sa - oy * ca };
}
function mtextBoxCorners(e, measure = approxMeasure) {
  const g = layoutMText(e, measure);
  const top = g.lines.length ? g.lines[0].dy : 0;
  return [
    mtextToWorld(e, 0, top),
    mtextToWorld(e, g.width, top),
    mtextToWorld(e, g.width, top + g.boxHeight),
    mtextToWorld(e, 0, top + g.boxHeight)
  ];
}
function mtextGripPoints(e) {
  const g = layoutMText(e);
  const top = g.lines.length ? g.lines[0].dy : 0;
  return { position: { x: e.x, y: e.y }, width: mtextToWorld(e, g.width, top) };
}
function mtextWidthPatch(e, p) {
  const { dx } = mtextLocal(e, p);
  return { width: Math.max(heightOf(e), dx) };
}

// src/cadTable.js
var TABLE_MAX_CELLS = 1e4;
var TABLE_CELL_KEY_RE = /^\d+,\d+$/;
var textHeightOf = (e) => Number.isFinite(e.textHeight) && e.textHeight > 0 ? e.textHeight : 1;
var tableMinColWidth = (e) => textHeightOf(e);
var tableMinRowHeight = (e) => textHeightOf(e) * 1.4;
var TEXT_INSET_FACTOR = 0.3;
var sanitizeSizes = (arr, min) => Array.isArray(arr) ? arr.map((v) => Number.isFinite(v) && v > min ? v : min) : [];
var edgeOffsets = (sizes) => {
  const out = [0];
  for (const s of sizes) out.push(out[out.length - 1] + s);
  return out;
};
function tableLayout(e) {
  const th = textHeightOf(e);
  const colWidths = sanitizeSizes(e.colWidths, tableMinColWidth(e));
  const rowHeights = sanitizeSizes(e.rowHeights, tableMinRowHeight(e));
  const colX = edgeOffsets(colWidths);
  const rowY = edgeOffsets(rowHeights);
  const width = colX[colX.length - 1];
  const height = rowY[rowY.length - 1];
  const gridSegs = [];
  if (colWidths.length && rowHeights.length) {
    for (const x of colX) gridSegs.push([{ x, y: 0 }, { x, y: height }]);
    for (const y of rowY) gridSegs.push([{ x: 0, y }, { x: width, y }]);
  }
  const cellRects = /* @__PURE__ */ new Map();
  for (let r = 0; r < rowHeights.length; r++) {
    for (let c = 0; c < colWidths.length; c++) {
      cellRects.set(`${r},${c}`, { x: colX[c], y: rowY[r], w: colWidths[c], h: rowHeights[r] });
    }
  }
  const inset = th * TEXT_INSET_FACTOR;
  const cellTextPos = (r, c, justify) => {
    const rect = cellRects.get(`${r},${c}`);
    if (!rect) return null;
    const x = justify === "C" ? rect.x + rect.w / 2 : justify === "R" ? rect.x + rect.w - inset : rect.x + inset;
    return { x, y: rect.y + rect.h / 2 };
  };
  return { width, height, textHeight: th, colX, rowY, gridSegs, cellRects, cellTextPos };
}
function tableToWorld(e, dx, dy) {
  const rot = (e.rotation || 0) * Math.PI / 180;
  const ca = Math.cos(rot);
  const sa = Math.sin(rot);
  return { x: e.x + dx * ca + dy * sa, y: e.y + dx * sa - dy * ca };
}
function tableLocal(e, p) {
  const rot = (e.rotation || 0) * Math.PI / 180;
  const ca = Math.cos(rot);
  const sa = Math.sin(rot);
  const ox = p.x - e.x;
  const oy = p.y - e.y;
  return { dx: ox * ca + oy * sa, dy: ox * sa - oy * ca };
}
function tableCorners(e) {
  const g = tableLayout(e);
  return [
    tableToWorld(e, 0, 0),
    tableToWorld(e, g.width, 0),
    tableToWorld(e, g.width, g.height),
    tableToWorld(e, 0, g.height)
  ];
}
function worldToTableCell(e, p) {
  const g = tableLayout(e);
  const cols = g.colX.length - 1;
  const rows = g.rowY.length - 1;
  if (cols < 1 || rows < 1) return null;
  const { dx, dy } = tableLocal(e, p);
  if (dx < 0 || dx > g.width || dy < 0 || dy > g.height) return null;
  const bisect = (edges, v) => {
    let lo = 0;
    let hi = edges.length - 1;
    while (lo + 1 < hi) {
      const mid = lo + hi >> 1;
      if (edges[mid] <= v) lo = mid;
      else hi = mid;
    }
    return lo;
  };
  return {
    r: Math.min(rows - 1, bisect(g.rowY, dy)),
    c: Math.min(cols - 1, bisect(g.colX, dx))
  };
}
function tableGripPoints(e) {
  const g = tableLayout(e);
  const colEdges = [];
  for (let i = 0; i + 1 < g.colX.length; i++) {
    const w = tableToWorld(e, g.colX[i + 1], 0);
    colEdges.push({ x: w.x, y: w.y, index: i });
  }
  const rowEdges = [];
  for (let j = 0; j + 1 < g.rowY.length; j++) {
    const w = tableToWorld(e, 0, g.rowY[j + 1]);
    rowEdges.push({ x: w.x, y: w.y, index: j });
  }
  const corner = colEdges.length && rowEdges.length ? tableToWorld(e, g.width, g.height) : null;
  return { position: { x: e.x, y: e.y }, colEdges, rowEdges, corner };
}
function tableColEdgePatch(e, index, p) {
  const g = tableLayout(e);
  const cols = g.colX.length - 1;
  if (!Number.isInteger(index) || index < 0 || index >= cols) return null;
  const { dx } = tableLocal(e, p);
  const newW = Math.max(tableMinColWidth(e), dx - g.colX[index]);
  const colWidths = g.colX.slice(1).map((edge, i) => edge - g.colX[i]);
  colWidths[index] = newW;
  return { colWidths };
}
function tableRowEdgePatch(e, index, p) {
  const g = tableLayout(e);
  const rows = g.rowY.length - 1;
  if (!Number.isInteger(index) || index < 0 || index >= rows) return null;
  const { dy } = tableLocal(e, p);
  const newH = Math.max(tableMinRowHeight(e), dy - g.rowY[index]);
  const rowHeights = g.rowY.slice(1).map((edge, j) => edge - g.rowY[j]);
  rowHeights[index] = newH;
  return { rowHeights };
}
var minOf = (arr) => {
  let m = arr[0];
  for (const v of arr) if (v < m) m = v;
  return m;
};
function tableScalePatch(e, p) {
  const g = tableLayout(e);
  const colWidths = g.colX.slice(1).map((edge, i) => edge - g.colX[i]);
  const rowHeights = g.rowY.slice(1).map((edge, j) => edge - g.rowY[j]);
  if (!colWidths.length || !rowHeights.length) return null;
  const den = g.width * g.width + g.height * g.height;
  if (!(den > 0)) return null;
  const { dx, dy } = tableLocal(e, p);
  let f = (dx * g.width + dy * g.height) / den;
  if (!Number.isFinite(f) || f <= 0) return null;
  f = Math.max(
    f,
    tableMinColWidth(e) / minOf(colWidths),
    tableMinRowHeight(e) / minOf(rowHeights)
  );
  return {
    colWidths: colWidths.map((w) => w * f),
    rowHeights: rowHeights.map((h) => h * f),
    textHeight: g.textHeight * f
  };
}

// src/cadModelCore/core.js
var DEFAULT_LAYER = "0";
var CAD_FONTS = [
  "Arial",
  "Helvetica",
  "Verdana",
  "Tahoma",
  "Courier New",
  "Times New Roman"
];
var DEFAULT_FONT = "Arial";
var ENTITY_TYPES = [
  "line",
  "polyline",
  "rectangle",
  "circle",
  "arc",
  "ellipse",
  "spline",
  // V4c: control-point (de Boor) or fit-point (Catmull-Rom) curve; the
  // model stays semantic and tessellates via cadCurves.splineToPolyline.
  "ray",
  // V4c: semi-infinite construction line (base + direction)
  "xline",
  // V4c: infinite construction line (point + direction)
  "revcloud",
  // V4c: revision-cloud markup (scalloped arcs along a path)
  "text",
  "mtext",
  // Spec 05: multiline text BLOCK with a wrap width — word-wrapped and
  // justified inside its box; layout single-sourced in cadMText.js.
  "point",
  "dimension",
  "leader",
  // Spec 04: multi-segment arrow-tipped polyline with a single note line
  // at its tail (points[0] = arrow tip). Render geometry in cadLeader.js.
  "table",
  // Spec 13: model-space grid of text cells — top-left anchor (x,y),
  // rotation, per-axis colWidths/rowHeights arrays, sparse cells map
  // {"r,c": {text, justify?}}. Layout single-sourced in cadTable.js.
  "hatch",
  // v4: cached pattern lines clipped to a closed boundary (see cadHatch.js).
  // v5: derived/associative — rebuilt from boundaryPolylineIds (entity-backed)
  // or regionSeed {point, sourceIds} (interior-point region) on every commit;
  // derivedError {reason} flags a broken boundary (see cadDerive.js).
  "insert",
  // v4: a placed instance of a block definition (doc.blockDefs)
  "xref",
  // V4b P3: a placed instance of an EXTERNAL reference drawing
  // (doc.xrefDefs). Like an insert, but its geometry came from another
  // file and is LOCKED — only the frame (transform) is editable. See
  // attachXref / xrefFrameBounds below and CADCanvasRenderer's xref case.
  "image",
  // [RASTER-IMAGE-1]: a placed raster (PNG/JPEG). The PIXELS live in
  // `doc.imageDefs` keyed by `imageDefId` — write-once, frozen and
  // ALIASED by the undo snapshot, because 60 undo entries x N x ~1 MB
  // of base64 is not affordable. The entity carries only the PLACEMENT,
  // as `origin` + edge vectors `u`/`v` (lower-left, width edge, height
  // edge) so that rotate/scale/mirror are one linear map and a mirrored
  // raster is representable at all. Schema + frame maths in
  // CADModel/image.js; NOT exported to DXF (see DXFExporter).
  "region"
  // C4b: a filled planar area — `rings: [outer, ...holes]`, each ring
  // an Array<{x,y}> IMPLICITLY closed (ring[last] !== ring[0]), the
  // convention cadHatch/boundary.js produces. Ring 0 is the outer
  // boundary and the rest are holes strictly inside it; the fill is
  // EVEN-ODD, so it is winding-blind by design even though the rings
  // are stored outer-CCW/holes-CW. Produced by REGION and by the
  // UNION/SUBTRACT/INTERSECT booleans (see cadBoolean/). A region is a
  // POLYGON: curved sources arrive already tessellated, so REGION of a
  // circle is that circle's 48-gon — cadBoolean/region.js's header
  // states why that is the only consistent answer here.
];
function transformPoint(p, insertionPoint = { x: 0, y: 0 }, scaleX = 1, scaleY = 1, rotationDeg = 0, basePoint = { x: 0, y: 0 }) {
  const lx = (p.x - basePoint.x) * scaleX;
  const ly = (p.y - basePoint.y) * scaleY;
  const a = rotationDeg * Math.PI / 180;
  const c = Math.cos(a);
  const s = Math.sin(a);
  return { x: insertionPoint.x + lx * c - ly * s, y: insertionPoint.y + lx * s + ly * c };
}
var _seq = 0;
function genId(prefix = "e") {
  _seq += 1;
  return `${prefix}-${Date.now().toString(36)}-${_seq.toString(36)}`;
}
function resolveColor(entity, layers) {
  if (entity.color) return entity.color;
  const layer = layers.find((l) => l.name === entity.layer);
  return layer && layer.color || "#ffffff";
}
function getLayer(doc, name) {
  return doc.layers.find((l) => l.name === name) || null;
}
function carryStyle(src, extra) {
  return {
    layer: src.layer,
    color: src.color || null,
    linetype: src.linetype || null,
    lineweight: src.lineweight ?? null,
    ltScale: Number.isFinite(src.ltScale) && src.ltScale > 0 ? src.ltScale : 1,
    ...extra
  };
}

// src/cadCurves.js
var DEG = Math.PI / 180;
var RAD = 180 / Math.PI;
var MAX_SEG = 256;
var norm360 = (a) => (a % 360 + 360) % 360;
function isFullEllipse(e) {
  if (e.startAngle == null || e.endAngle == null) return true;
  return norm360(e.endAngle - e.startAngle) < 1e-9;
}
function ellipseSweep(e) {
  if (e.startAngle == null || e.endAngle == null) return 360;
  const s = norm360(e.endAngle - e.startAngle);
  return s < 1e-9 ? 360 : s;
}
function ellipsePoint(e, tDeg) {
  const t = tDeg * DEG;
  const rot = (e.rotation || 0) * DEG;
  const lx = e.rx * Math.cos(t);
  const ly = e.ry * Math.sin(t);
  const ca = Math.cos(rot);
  const sa = Math.sin(rot);
  return { x: e.cx + lx * ca - ly * sa, y: e.cy + lx * sa + ly * ca };
}
function ellipseParamAt(e, p) {
  const rot = (e.rotation || 0) * DEG;
  const ca = Math.cos(rot);
  const sa = Math.sin(rot);
  const dx = p.x - e.cx;
  const dy = p.y - e.cy;
  const lx = dx * ca + dy * sa;
  const ly = -dx * sa + dy * ca;
  const rx = e.rx || 1e-9;
  const ry = e.ry || 1e-9;
  return norm360(Math.atan2(ly / ry, lx / rx) * RAD);
}
function ellipseQuadrants(e) {
  return [0, 90, 180, 270].map((t) => ellipsePoint(e, t));
}
function ellipseToPolyline(e, tol) {
  const rx = Math.abs(e.rx) || 1e-9;
  const ry = Math.abs(e.ry) || 1e-9;
  const rmax = Math.max(rx, ry);
  const full = isFullEllipse(e);
  const startDeg = full ? 0 : norm360(e.startAngle);
  const sweepDeg = ellipseSweep(e);
  const h = Math.max(tol || rmax / 500, rmax * 1e-6);
  let d\u03B8 = 2 * Math.acos(Math.max(0, Math.min(1, 1 - h / rmax)));
  if (!isFinite(d\u03B8) || d\u03B8 <= 1e-4) d\u03B8 = 1e-4;
  let n = Math.ceil(sweepDeg * DEG / d\u03B8);
  n = Math.max(8, Math.min(MAX_SEG, n));
  const pts = [];
  for (let i = 0; i <= n; i++) pts.push(ellipsePoint(e, startDeg + sweepDeg * i / n));
  return pts;
}
var dist2d = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
function bulgeToArc(p1, p2, bulge) {
  const b = Number(bulge);
  if (!Number.isFinite(b) || b === 0) return null;
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const c = Math.hypot(dx, dy);
  const theta = 4 * Math.atan(b);
  if (!(c > 0) || !Number.isFinite(theta) || Math.abs(theta) < 1e-9) return null;
  const half = theta / 2;
  const sinHalf = Math.sin(half);
  const tanHalf = Math.tan(half);
  if (!Number.isFinite(sinHalf) || Math.abs(sinHalf) < 1e-12) return null;
  const apothem = Number.isFinite(tanHalf) && Math.abs(tanHalf) > 1e-12 ? c / 2 / tanHalf : 0;
  const cx = (p1.x + p2.x) / 2 - apothem * (dy / c);
  const cy = (p1.y + p2.y) / 2 + apothem * (dx / c);
  const r = Math.abs(c / 2 / sinHalf);
  const a0 = Math.atan2(p1.y - cy, p1.x - cx) * 180 / Math.PI;
  const a1 = Math.atan2(p2.y - cy, p2.x - cx) * 180 / Math.PI;
  const ccw = theta > 0;
  return {
    cx,
    cy,
    r,
    startAngle: ccw ? a0 : a1,
    endAngle: ccw ? a1 : a0,
    ccw,
    sweepDeg: theta * 180 / Math.PI
  };
}
function splineDefiningPoints(e) {
  if (Array.isArray(e.fitPoints) && e.fitPoints.length >= 2) return e.fitPoints;
  return e.controlPoints || [];
}
function isFitSpline(e) {
  return Array.isArray(e.fitPoints) && e.fitPoints.length >= 2;
}
function deBoorEval(ctrl, p, knots, u) {
  const n = ctrl.length;
  let k = p;
  while (k < n - 1 && u >= knots[k + 1]) k++;
  const d = [];
  for (let j = 0; j <= p; j++) d.push({ ...ctrl[k - p + j] });
  for (let r = 1; r <= p; r++) {
    for (let j = p; j >= r; j--) {
      const i = k - p + j;
      const denom = knots[i + p - r + 1] - knots[i];
      const a = denom > 1e-12 ? (u - knots[i]) / denom : 0;
      d[j] = { x: (1 - a) * d[j - 1].x + a * d[j].x, y: (1 - a) * d[j - 1].y + a * d[j].y };
    }
  }
  return d[p];
}
function deBoorOpen(ctrl, degree) {
  const n = ctrl.length;
  const p = Math.max(1, Math.min(degree || 3, n - 1));
  const m = n + p + 1;
  const knots = new Array(m);
  for (let i = 0; i < m; i++) {
    if (i <= p) knots[i] = 0;
    else if (i >= n) knots[i] = 1;
    else knots[i] = (i - p) / (n - p);
  }
  const samples = Math.max(16, Math.min(MAX_SEG, n * 16));
  const pts = [];
  for (let s = 0; s <= samples; s++) {
    pts.push(deBoorEval(ctrl, p, knots, Math.min(s / samples, 1 - 1e-12)));
  }
  pts[0] = { x: ctrl[0].x, y: ctrl[0].y };
  pts[pts.length - 1] = { x: ctrl[n - 1].x, y: ctrl[n - 1].y };
  return pts;
}
function crPoint(p0, p1, p2, p3, t, alpha) {
  const t0 = 0;
  const t1 = t0 + Math.pow(dist2d(p0, p1), alpha);
  const t2 = t1 + Math.pow(dist2d(p1, p2), alpha);
  const t3 = t2 + Math.pow(dist2d(p2, p3), alpha);
  const tt = t1 + (t2 - t1) * t;
  const L = (a, b, ta, tb) => {
    const f = tb - ta > 1e-12 ? (tt - ta) / (tb - ta) : 0;
    return { x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f };
  };
  const A1 = L(p0, p1, t0, t1);
  const A2 = L(p1, p2, t1, t2);
  const A3 = L(p2, p3, t2, t3);
  const B1 = L(A1, A2, t0, t2);
  const B2 = L(A2, A3, t1, t3);
  return L(B1, B2, t1, t2);
}
function catmullRom(points, closed) {
  const n = points.length;
  if (n < 2) return points.map((p) => ({ ...p }));
  if (n === 2 && !closed) return [{ ...points[0] }, { ...points[1] }];
  const at = (i) => points[(i % n + n) % n];
  const segCount = closed ? n : n - 1;
  const perSeg = Math.max(4, Math.min(48, Math.floor(MAX_SEG / segCount)));
  const out = [];
  for (let i = 0; i < segCount; i++) {
    const p0 = closed ? at(i - 1) : points[Math.max(0, i - 1)];
    const p1 = points[i];
    const p2 = closed ? at(i + 1) : points[i + 1];
    const p3 = closed ? at(i + 2) : points[Math.min(n - 1, i + 2)];
    for (let s = 0; s < perSeg; s++) out.push(crPoint(p0, p1, p2, p3, s / perSeg, 0.5));
  }
  out.push(closed ? { ...out[0] } : { ...points[n - 1] });
  return out;
}
function polygonPoints(center, radius, sides, inscribed = true, rotationDeg = 0) {
  const n = Math.max(3, Math.floor(sides) || 3);
  const step = 2 * Math.PI / n;
  const rot = rotationDeg * Math.PI / 180;
  const pts = [];
  if (inscribed) {
    for (let i = 0; i < n; i++) {
      const a = rot + i * step;
      pts.push({ x: center.x + radius * Math.cos(a), y: center.y + radius * Math.sin(a) });
    }
  } else {
    const R = radius / Math.cos(Math.PI / n);
    for (let i = 0; i < n; i++) {
      const a = rot + (i + 0.5) * step;
      pts.push({ x: center.x + R * Math.cos(a), y: center.y + R * Math.sin(a) });
    }
  }
  return pts;
}
function revisionCloudBumps(points, closed, arcRadius) {
  const n = points.length;
  if (n < 2) return [];
  const out = [];
  const edges = closed ? n : n - 1;
  for (let i = 0; i < edges; i++) {
    const a = points[i];
    const b = points[(i + 1) % n];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const L = Math.hypot(dx, dy) || 1e-9;
    const r = arcRadius > 0 ? arcRadius : Math.max(L / 8, 1e-6);
    const bumps = Math.max(1, Math.round(L / (2 * r)));
    const ux = dx / L;
    const uy = dy / L;
    const chord = L / bumps;
    const rad = chord / 2;
    const along = Math.atan2(uy, ux) * 180 / Math.PI;
    for (let k = 0; k < bumps; k++) {
      out.push({
        cx: a.x + ux * chord * (k + 0.5),
        cy: a.y + uy * chord * (k + 0.5),
        r: rad,
        startAngle: along,
        endAngle: along + 180,
        ux,
        uy
      });
    }
  }
  return out;
}
function revisionCloudArcs(points, closed, arcRadius) {
  const n = points.length;
  if (n < 2) return points.map((p) => ({ ...p }));
  const out = [];
  const steps = 6;
  for (const b of revisionCloudBumps(points, closed, arcRadius)) {
    const nx = -b.uy;
    const ny = b.ux;
    for (let s = 0; s <= steps; s++) {
      const th = Math.PI * (1 - s / steps);
      const la = Math.cos(th) * b.r;
      const ll = Math.sin(th) * b.r;
      out.push({ x: b.cx + b.ux * la + nx * ll, y: b.cy + b.uy * la + ny * ll });
    }
  }
  return out;
}
function splinePointAt(e, u) {
  const closed = !!e.closed;
  const t = Math.max(0, Math.min(1, u));
  if (isFitSpline(e) || closed) {
    const points = splineDefiningPoints(e);
    const n2 = points.length;
    if (n2 < 2) return null;
    if (n2 === 2 && !closed) return lerpPt(points[0], points[1], t);
    const at = (i2) => points[(i2 % n2 + n2) % n2];
    const segCount = closed ? n2 : n2 - 1;
    const g = Math.min(segCount - 1e-12, t * segCount);
    const i = Math.min(segCount - 1, Math.floor(g));
    const p0 = closed ? at(i - 1) : points[Math.max(0, i - 1)];
    const p1 = points[i];
    const p2 = closed ? at(i + 1) : points[i + 1];
    const p3 = closed ? at(i + 2) : points[Math.min(n2 - 1, i + 2)];
    return crPoint(p0, p1, p2, p3, g - i, 0.5);
  }
  const ctrl = e.controlPoints || [];
  const n = ctrl.length;
  if (n < 2) return null;
  if (n === 2) return lerpPt(ctrl[0], ctrl[1], t);
  const p = Math.max(1, Math.min(e.degree || 3, n - 1));
  const m = n + p + 1;
  const knots = new Array(m);
  for (let i = 0; i < m; i++) {
    if (i <= p) knots[i] = 0;
    else if (i >= n) knots[i] = 1;
    else knots[i] = (i - p) / (n - p);
  }
  if (t <= 0) return { x: ctrl[0].x, y: ctrl[0].y };
  if (t >= 1) return { x: ctrl[n - 1].x, y: ctrl[n - 1].y };
  return deBoorEval(ctrl, p, knots, t);
}
var lerpPt = (a, b, t) => ({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
function splineArcLengthParams(e, count, closed = !!e.closed) {
  const n = Math.max(1, count | 0);
  const DENSE = 4096;
  const pts = [];
  for (let i = 0; i <= DENSE; i++) {
    const q = splinePointAt(e, i / DENSE);
    if (!q) return [];
    pts.push(q);
  }
  const cum = new Float64Array(DENSE + 1);
  for (let i = 0; i < DENSE; i++)
    cum[i + 1] = cum[i] + Math.hypot(pts[i + 1].x - pts[i].x, pts[i + 1].y - pts[i].y);
  const total = cum[DENSE];
  if (!(total > 1e-12)) return [];
  const step = n > 1 ? total / (closed ? n : n - 1) : 0;
  const out = [];
  let k = 0;
  for (let i = 0; i < n; i++) {
    const want = Math.min(step * i, total);
    while (k < DENSE - 1 && cum[k + 1] < want) k++;
    const span = cum[k + 1] - cum[k];
    const f = span > 1e-15 ? (want - cum[k]) / span : 0;
    out.push((k + f) / DENSE);
  }
  return out;
}
function splineToPolyline(e) {
  const closed = !!e.closed;
  if (isFitSpline(e)) return catmullRom(e.fitPoints, closed);
  const ctrl = e.controlPoints || [];
  if (ctrl.length < 2) return ctrl.map((p) => ({ ...p }));
  if (ctrl.length === 2 && !closed) return [{ ...ctrl[0] }, { ...ctrl[1] }];
  if (closed) return catmullRom(ctrl, true);
  return deBoorOpen(ctrl, e.degree);
}

// src/cadGeometry/primitives.js
var XLINE_BIG = 1e7;
var lineDir = (e) => {
  const L = Math.hypot(e.dx, e.dy) || 1;
  return { ux: e.dx / L, uy: e.dy / L };
};
var constructionSegment = (e) => {
  const { ux, uy } = lineDir(e);
  const f = { x: e.x + ux * XLINE_BIG, y: e.y + uy * XLINE_BIG };
  const b = e.type === "xline" ? { x: e.x - ux * XLINE_BIG, y: e.y - uy * XLINE_BIG } : { x: e.x, y: e.y };
  return [b, f];
};
function distance(a, b) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}
function pointToSegment(p, a, b) {
  const vx = b.x - a.x;
  const vy = b.y - a.y;
  const len22 = vx * vx + vy * vy;
  if (len22 === 0) return distance(p, a);
  let t = ((p.x - a.x) * vx + (p.y - a.y) * vy) / len22;
  t = Math.max(0, Math.min(1, t));
  return distance(p, { x: a.x + t * vx, y: a.y + t * vy });
}
function closestPointOnSegment(p, a, b) {
  const vx = b.x - a.x;
  const vy = b.y - a.y;
  const len22 = vx * vx + vy * vy;
  if (len22 === 0) return { x: a.x, y: a.y };
  let t = ((p.x - a.x) * vx + (p.y - a.y) * vy) / len22;
  t = Math.max(0, Math.min(1, t));
  return { x: a.x + t * vx, y: a.y + t * vy };
}
function perpendicularFoot(p, a, b) {
  const vx = b.x - a.x;
  const vy = b.y - a.y;
  const len22 = vx * vx + vy * vy;
  if (len22 === 0) return { x: a.x, y: a.y };
  const t = ((p.x - a.x) * vx + (p.y - a.y) * vy) / len22;
  return { x: a.x + t * vx, y: a.y + t * vy };
}
function pointInPolygon(p, poly) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i].x, yi = poly[i].y;
    const xj = poly[j].x, yj = poly[j].y;
    const hit = yi > p.y !== yj > p.y && p.x < (xj - xi) * (p.y - yi) / (yj - yi) + xi;
    if (hit) inside = !inside;
  }
  return inside;
}
function lineLineIntersection(a1, a2, b1, b2) {
  const d1x = a2.x - a1.x, d1y = a2.y - a1.y;
  const d2x = b2.x - b1.x, d2y = b2.y - b1.y;
  const den = d1x * d2y - d1y * d2x;
  if (Math.abs(den) < 1e-12) return null;
  const t = ((b1.x - a1.x) * d2y - (b1.y - a1.y) * d2x) / den;
  return { x: a1.x + t * d1x, y: a1.y + t * d1y };
}
function segSegIntersection(p1, p2, p3, p4) {
  const d1x = p2.x - p1.x, d1y = p2.y - p1.y;
  const d2x = p4.x - p3.x, d2y = p4.y - p3.y;
  const den = d1x * d2y - d1y * d2x;
  if (Math.abs(den) < 1e-12) return null;
  const t = ((p3.x - p1.x) * d2y - (p3.y - p1.y) * d2x) / den;
  const u = ((p3.x - p1.x) * d1y - (p3.y - p1.y) * d1x) / den;
  if (t < -1e-9 || t > 1 + 1e-9 || u < -1e-9 || u > 1 + 1e-9) return null;
  return { x: p1.x + t * d1x, y: p1.y + t * d1y };
}
function segCircleIntersections(a, b, c, r) {
  const dx = b.x - a.x, dy = b.y - a.y;
  const fx = a.x - c.x, fy = a.y - c.y;
  const A = dx * dx + dy * dy;
  const B = 2 * (fx * dx + fy * dy);
  const C = fx * fx + fy * fy - r * r;
  if (A < 1e-12) return [];
  let disc = B * B - 4 * A * C;
  if (disc < 0) {
    if (disc < -1e-6 * (B * B + 4 * Math.abs(A * C) + 1)) return [];
    disc = 0;
  }
  disc = Math.sqrt(disc);
  const out = [];
  for (const t of [(-B - disc) / (2 * A), (-B + disc) / (2 * A)]) {
    if (t >= -1e-9 && t <= 1 + 1e-9) out.push({ x: a.x + t * dx, y: a.y + t * dy });
  }
  return out;
}
function circleCircleIntersections(c1, r1, c2, r2) {
  const dx = c2.x - c1.x, dy = c2.y - c1.y;
  const d = Math.hypot(dx, dy);
  if (d < 1e-12) return [];
  if (d > r1 + r2 + 1e-9 || d < Math.abs(r1 - r2) - 1e-9) return [];
  const a = (r1 * r1 - r2 * r2 + d * d) / (2 * d);
  const h2 = r1 * r1 - a * a;
  const h = h2 > 0 ? Math.sqrt(h2) : 0;
  const xm = c1.x + a * dx / d;
  const ym = c1.y + a * dy / d;
  if (h === 0) return [{ x: xm, y: ym }];
  const ox = -dy * h / d;
  const oy = dx * h / d;
  return [{ x: xm + ox, y: ym + oy }, { x: xm - ox, y: ym - oy }];
}
function tangentPointsToCircle(from, c, r) {
  const dx = from.x - c.x, dy = from.y - c.y;
  const d2 = dx * dx + dy * dy;
  if (d2 <= r * r + 1e-12) return [];
  const d = Math.sqrt(d2);
  const base = Math.atan2(dy, dx);
  const alpha = Math.acos(r / d);
  return [base + alpha, base - alpha].map((ang) => ({
    x: c.x + r * Math.cos(ang),
    y: c.y + r * Math.sin(ang)
  }));
}
function angleInArcSweep(e, pt2) {
  if (e.type !== "arc") return true;
  const norm3 = (a) => (a % 360 + 360) % 360;
  const ang = norm3(Math.atan2(pt2.y - e.cy, pt2.x - e.cx) * 180 / Math.PI);
  let sweep = norm3(e.endAngle - e.startAngle);
  if (sweep < 1e-9) sweep = 360;
  return norm3(ang - norm3(e.startAngle)) <= sweep + 1e-6;
}
var onCirclePoint = (cx, cy, r, deg) => {
  const a = deg * Math.PI / 180;
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
};
var arcMidAngle = (e) => {
  const sweep = ((e.endAngle - e.startAngle) % 360 + 360) % 360;
  return e.startAngle + sweep / 2;
};

// src/cadBoolean/clip.js
var BOOL_EPS = 1e-6;
var AREA_EPS = 1e-9;
function signedArea(ring) {
  if (!Array.isArray(ring) || ring.length < 3) return 0;
  let a = 0;
  for (let i = 0, n = ring.length; i < n; i++) {
    const p = ring[i];
    const q = ring[(i + 1) % n];
    a += p.x * q.y - q.x * p.y;
  }
  return a / 2;
}
var ringArea = (ring) => Math.abs(signedArea(ring));
function mapRings(rings, fn) {
  return Array.isArray(rings) ? rings.map((ring) => Array.isArray(ring) ? ring.map((p) => fn(p)) : ring) : rings;
}
function cleanRing(ring) {
  if (!Array.isArray(ring)) return [];
  const out = [];
  for (const p of ring) {
    if (!p || !Number.isFinite(p.x) || !Number.isFinite(p.y)) continue;
    const last = out[out.length - 1];
    if (last && Math.hypot(p.x - last.x, p.y - last.y) <= BOOL_EPS) continue;
    out.push({ x: p.x, y: p.y });
  }
  while (out.length > 1) {
    const a = out[0];
    const b = out[out.length - 1];
    if (Math.hypot(a.x - b.x, a.y - b.y) <= BOOL_EPS) out.pop();
    else break;
  }
  return out;
}
function orientRing(ring, ccw = true) {
  const r = cleanRing(ring);
  if (r.length < 3) return r;
  const isCcw = signedArea(r) > 0;
  return isCcw === ccw ? r : r.slice().reverse();
}
function pointInRings(p, rings) {
  let inside = false;
  for (const r of rings) if (r.length >= 3 && pointInPolygon(p, r)) inside = !inside;
  return inside;
}
function pointOnRings(p, rings) {
  for (const r of rings) {
    for (let i = 0, n = r.length; i < n; i++) {
      if (pointToSegment(p, r[i], r[(i + 1) % n]) <= BOOL_EPS) return true;
    }
  }
  return false;
}
var cross = (ox, oy, ax, ay, bx, by) => (ax - ox) * (by - oy) - (ay - oy) * (bx - ox);
function segCuts(a, b, c, d) {
  const d1x = b.x - a.x;
  const d1y = b.y - a.y;
  const d2x = d.x - c.x;
  const d2y = d.y - c.y;
  const len1 = Math.hypot(d1x, d1y);
  const len22 = Math.hypot(d2x, d2y);
  if (len1 <= BOOL_EPS || len22 <= BOOL_EPS) return [];
  const den = d1x * d2y - d1y * d2x;
  const sinTheta = Math.abs(den) / (len1 * len22);
  if (sinTheta > 1e-12) {
    const t = ((c.x - a.x) * d2y - (c.y - a.y) * d2x) / den;
    const u = ((c.x - a.x) * d1y - (c.y - a.y) * d1x) / den;
    const et = BOOL_EPS / len1;
    const eu = BOOL_EPS / len22;
    if (t < -et || t > 1 + et || u < -eu || u > 1 + eu) return [];
    return [Math.min(1, Math.max(0, t))];
  }
  const distC = Math.abs(cross(a.x, a.y, b.x, b.y, c.x, c.y)) / len1;
  if (distC > BOOL_EPS) return [];
  const tOf = (p) => ((p.x - a.x) * d1x + (p.y - a.y) * d1y) / (len1 * len1);
  const tc = tOf(c);
  const td = tOf(d);
  const lo = Math.min(tc, td);
  const hi = Math.max(tc, td);
  const out = [];
  if (hi >= -BOOL_EPS / len1 && lo <= 1 + BOOL_EPS / len1) {
    for (const t of [lo, hi]) if (t > 0 && t < 1) out.push(t);
  }
  return out;
}
var ringEdges = (r) => {
  const out = [];
  for (let i = 0, n = r.length; i < n; i++) out.push([r[i], r[(i + 1) % n]]);
  return out;
};
var ringsBounds = (rings) => {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const r of rings)
    for (const p of r) {
      if (p.x < minX) minX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.x > maxX) maxX = p.x;
      if (p.y > maxY) maxY = p.y;
    }
  return { minX, minY, maxX, maxY };
};
function splitRings(rings, others) {
  const otherEdges = [];
  for (const r of others) for (const e of ringEdges(r)) otherEdges.push(e);
  const ob = ringsBounds(others);
  const out = [];
  for (const r of rings) {
    for (const [a, b] of ringEdges(r)) {
      const ts = [0, 1];
      const eMinX = Math.min(a.x, b.x), eMaxX = Math.max(a.x, b.x);
      const eMinY = Math.min(a.y, b.y), eMaxY = Math.max(a.y, b.y);
      const far = eMaxX < ob.minX - BOOL_EPS || eMinX > ob.maxX + BOOL_EPS || eMaxY < ob.minY - BOOL_EPS || eMinY > ob.maxY + BOOL_EPS;
      if (!far) {
        for (const [c, d] of otherEdges) {
          for (const t of segCuts(a, b, c, d)) if (t > 0 && t < 1) ts.push(t);
        }
      }
      ts.sort((p, q) => p - q);
      const len3 = Math.hypot(b.x - a.x, b.y - a.y);
      for (let i = 0; i < ts.length - 1; i++) {
        const t0 = ts[i];
        const t1 = ts[i + 1];
        if ((t1 - t0) * len3 <= BOOL_EPS) continue;
        out.push({
          a: { x: a.x + (b.x - a.x) * t0, y: a.y + (b.y - a.y) * t0 },
          b: { x: a.x + (b.x - a.x) * t1, y: a.y + (b.y - a.y) * t1 }
        });
      }
    }
  }
  return out;
}
var IN = "in";
var OUT = "out";
var ON_SAME = "on-same";
var ON_OPP = "on-opp";
function classify(edge, otherRings) {
  const mid = { x: (edge.a.x + edge.b.x) / 2, y: (edge.a.y + edge.b.y) / 2 };
  if (!pointOnRings(mid, otherRings)) return pointInRings(mid, otherRings) ? IN : OUT;
  const dx = edge.b.x - edge.a.x;
  const dy = edge.b.y - edge.a.y;
  for (const r of otherRings) {
    for (let i = 0, n = r.length; i < n; i++) {
      const c = r[i];
      const d = r[(i + 1) % n];
      if (pointToSegment(mid, c, d) > BOOL_EPS) continue;
      const dot3 = dx * (d.x - c.x) + dy * (d.y - c.y);
      return dot3 >= 0 ? ON_SAME : ON_OPP;
    }
  }
  return ON_SAME;
}
function vertexKeyer() {
  const cells = /* @__PURE__ */ new Map();
  const q = (v) => Math.round(v / BOOL_EPS);
  return (p) => {
    const cx = q(p.x);
    const cy = q(p.y);
    for (let i = -1; i <= 1; i++) {
      for (let j = -1; j <= 1; j++) {
        const k = `${cx + i},${cy + j}`;
        const hit = cells.get(k);
        if (hit && Math.hypot(hit.p.x - p.x, hit.p.y - p.y) <= BOOL_EPS) return hit.key;
      }
    }
    const key = `${cx},${cy}`;
    cells.set(key, { key, p });
    return key;
  };
}
function stitch(edges) {
  const keyOf = vertexKeyer();
  const nodes = /* @__PURE__ */ new Map();
  for (const e of edges) {
    const ka = keyOf(e.a);
    const kb = keyOf(e.b);
    if (ka === kb) continue;
    e.ka = ka;
    e.kb = kb;
    e.used = false;
    e.ang = Math.atan2(e.b.y - e.a.y, e.b.x - e.a.x);
    if (!nodes.has(ka)) nodes.set(ka, { p: e.a, out: [] });
    if (!nodes.has(kb)) nodes.set(kb, { p: e.b, out: [] });
    nodes.get(ka).out.push(e);
  }
  for (const n of nodes.values()) n.out.sort((p, q) => p.ang - q.ang);
  const rings = [];
  for (const start of edges) {
    if (start.used || start.ka === start.kb) continue;
    const ring = [];
    let e = start;
    let guard = 0;
    const limit = edges.length + 2;
    while (e && !e.used && guard++ < limit) {
      e.used = true;
      ring.push({ x: e.a.x, y: e.a.y });
      const node = nodes.get(e.kb);
      if (!node) break;
      const back = Math.atan2(e.a.y - e.b.y, e.a.x - e.b.x);
      let next = null;
      let bestGap = Infinity;
      let bestUnused = null;
      let bestUnusedGap = Infinity;
      for (const cand of node.out) {
        let gap = back - cand.ang;
        while (gap <= 0) gap += Math.PI * 2;
        while (gap > Math.PI * 2) gap -= Math.PI * 2;
        if (gap < bestGap) {
          bestGap = gap;
          next = cand;
        }
        if (!cand.used && gap < bestUnusedGap) {
          bestUnusedGap = gap;
          bestUnused = cand;
        }
      }
      if (next && next.used) next = next === start ? null : bestUnused;
      if (!next) break;
      e = next;
    }
    const r = cleanRing(ring);
    if (r.length >= 3 && ringArea(r) > AREA_EPS) rings.push(r);
  }
  return rings;
}
function buildPolygons(rings) {
  const outers = [];
  const holes = [];
  for (const r of rings) (signedArea(r) > 0 ? outers : holes).push(r);
  const polys = outers.map((outer) => ({ outer, holes: [] })).sort((a, b) => ringArea(a.outer) - ringArea(b.outer));
  holes.sort((a, b) => ringArea(b) - ringArea(a));
  for (const h of holes) {
    let owner = null;
    for (const poly of polys) {
      const probe = probeOn(h, poly.outer);
      if (probe && pointInPolygon(probe, poly.outer)) {
        owner = poly;
        break;
      }
    }
    if (owner) {
      owner.holes.push(h);
      continue;
    }
    polys.push({ outer: h.slice().reverse(), holes: [] });
  }
  return polys.filter((p) => ringArea(p.outer) > AREA_EPS);
}
function orientRingSet(rings) {
  const rs = rings.map((r) => cleanRing(r)).filter((r) => r.length >= 3);
  const dead = /* @__PURE__ */ new Set();
  for (let i = 0; i < rs.length; i++) {
    if (dead.has(i)) continue;
    for (let j = i + 1; j < rs.length; j++) {
      if (dead.has(j)) continue;
      if (probeOn(rs[i], rs[j]) === null && probeOn(rs[j], rs[i]) === null) {
        dead.add(i);
        dead.add(j);
        break;
      }
    }
  }
  const live = rs.filter((_, i) => !dead.has(i));
  return live.map((r) => {
    let depth = 0;
    for (const other of live) {
      if (other === r) continue;
      const probe = probeOn(r, other);
      if (probe && pointInPolygon(probe, other)) depth++;
    }
    return orientRing(r, depth % 2 === 0);
  });
}
function probeOn(r, other) {
  const n = r.length;
  for (let i = 0; i < n; i++) {
    const a = r[i];
    const b = r[(i + 1) % n];
    for (const p of [a, { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }]) {
      if (!pointOnRings(p, [other])) return p;
    }
  }
  return null;
}
function operate(aRings, bRings, op) {
  const A = orientRingSet(aRings);
  const B = orientRingSet(bRings);
  if (!A.length && !B.length) return [];
  if (!B.length) return op === "intersection" ? [] : buildPolygons(A);
  if (!A.length) return op === "union" ? buildPolygons(B) : [];
  const aSub = splitRings(A, B);
  const bSub = splitRings(B, A);
  const kept = [];
  for (const e of aSub) {
    const c = classify(e, B);
    if (op === "union" && (c === OUT || c === ON_SAME)) kept.push(e);
    else if (op === "intersection" && (c === IN || c === ON_SAME)) kept.push(e);
    else if (op === "difference" && (c === OUT || c === ON_OPP)) kept.push(e);
  }
  for (const e of bSub) {
    const c = classify(e, A);
    if (op === "union" && c === OUT) kept.push(e);
    else if (op === "intersection" && c === IN) kept.push(e);
    else if (op === "difference" && c === IN) kept.push({ a: e.b, b: e.a });
  }
  return buildPolygons(stitch(kept));
}
var ringUnion = (a, b) => operate(a, b, "union");
var ringDifference = (a, b) => operate(a, b, "difference");
var ringIntersection = (a, b) => operate(a, b, "intersection");

// src/CADModel/image.js
var IMAGE_MIN_EDGE = 1e-9;
var isFiniteNum = (n) => typeof n === "number" && Number.isFinite(n);
var pt = (p, fx = 0, fy = 0) => ({
  x: isFiniteNum(p && p.x) ? p.x : fx,
  y: isFiniteNum(p && p.y) ? p.y : fy
});
var len2 = (v) => Math.hypot(v.x, v.y);
var cross2 = (a, b) => a.x * b.y - a.y * b.x;
var dot2 = (a, b) => a.x * b.x + a.y * b.y;
function normalizeImageDef(def) {
  if (!def || typeof def !== "object") return null;
  const data = typeof def.data === "string" ? def.data : "";
  const mime = def.mime === "image/png" || def.mime === "image/jpeg" ? def.mime : null;
  const width = Math.floor(Number(def.width));
  const height = Math.floor(Number(def.height));
  if (!data || !mime || !Number.isFinite(width) || !Number.isFinite(height)) return null;
  if (!(width > 0) || !(height > 0)) return null;
  const src = def.source && typeof def.source === "object" ? def.source : {};
  return Object.freeze({
    id: typeof def.id === "string" && def.id ? def.id : genId(),
    mime,
    data,
    width,
    height,
    source: Object.freeze({
      name: typeof src.name === "string" ? src.name : "",
      width: Math.floor(Number(src.width)) > 0 ? Math.floor(Number(src.width)) : width,
      height: Math.floor(Number(src.height)) > 0 ? Math.floor(Number(src.height)) : height,
      bytes: Math.floor(Number(src.bytes)) > 0 ? Math.floor(Number(src.bytes)) : 0
    }),
    // Derived, not trusted from the caller: "was this downscaled" is a fact about
    // the two sizes, and a stored boolean could disagree with them.
    downscaled: (Math.floor(Number(src.width)) || width) > width || (Math.floor(Number(src.height)) || height) > height
  });
}
function imageDataUrl(def) {
  return def ? `data:${def.mime};base64,${def.data}` : "";
}
function imageDefFor(doc, entity) {
  if (!doc || !entity || !entity.imageDefId) return null;
  const defs = doc.imageDefs;
  if (!defs || !Object.hasOwn(defs, entity.imageDefId)) return null;
  return defs[entity.imageDefId] || null;
}
function docAttachImageDef(doc, def) {
  const norm3 = normalizeImageDef(def);
  if (!norm3) return { doc, def: null };
  const defs = doc.imageDefs || {};
  if (defs[norm3.id]) return { doc, def: defs[norm3.id] };
  return { doc: { ...doc, imageDefs: { ...defs, [norm3.id]: norm3 } }, def: norm3 };
}
function orphanImageDefIds(doc) {
  const defs = doc && doc.imageDefs ? doc.imageDefs : {};
  const used = /* @__PURE__ */ new Set();
  const mark = (list) => {
    for (const e of Array.isArray(list) ? list : []) {
      if (e && e.type === "image" && e.imageDefId) used.add(e.imageDefId);
    }
  };
  mark(doc && doc.entities);
  for (const def of Object.values(doc && doc.blockDefs || {})) mark(def && def.entities);
  return Object.keys(defs).filter((id) => !used.has(id));
}
function normalizeImageEntity(e) {
  if (!e || e.type !== "image") return e;
  if (!hasImageFrame(e)) return null;
  const origin = pt(e.origin);
  let u = pt(e.u, 1, 0);
  let v = pt(e.v, 0, 1);
  if (Math.abs(dot2(u, v)) > 1e-9 * len2(u) * len2(v)) {
    const side = cross2(u, v) >= 0 ? 1 : -1;
    const n = { x: -u.y / len2(u), y: u.x / len2(u) };
    const h = len2(v);
    v = { x: n.x * h * side, y: n.y * h * side };
  }
  return {
    ...e,
    origin,
    u,
    v,
    imageDefId: typeof e.imageDefId === "string" ? e.imageDefId : null,
    lockAspect: e.lockAspect === void 0 ? true : !!e.lockAspect,
    behind: e.behind === void 0 ? true : !!e.behind,
    opacity: isFiniteNum(e.opacity) ? Math.min(1, Math.max(0, e.opacity)) : 1,
    clip: null
    // reserved for IMAGECLIP; a stored value is dropped, not honoured
  };
}
function hasImageFrame(e) {
  return !!(e && e.origin && isFiniteNum(e.origin.x) && isFiniteNum(e.origin.y) && e.u && isFiniteNum(e.u.x) && isFiniteNum(e.u.y) && e.v && isFiniteNum(e.v.x) && isFiniteNum(e.v.y) && len2(e.u) >= IMAGE_MIN_EDGE && len2(e.v) >= IMAGE_MIN_EDGE);
}
function imageCorners(e) {
  if (!hasImageFrame(e)) return [];
  const o = e.origin;
  const u = e.u;
  const v = e.v;
  return [
    { x: o.x, y: o.y },
    { x: o.x + u.x, y: o.y + u.y },
    { x: o.x + u.x + v.x, y: o.y + u.y + v.y },
    { x: o.x + v.x, y: o.y + v.y }
  ];
}
var imageWidth = (e) => len2(e.u);
var imageHeight = (e) => len2(e.v);
var imageRotationDeg = (e) => Math.atan2(e.u.y, e.u.x) * 180 / Math.PI;
var imageFlipped = (e) => cross2(e.u, e.v) < 0;
function imageFromPlacement({ origin, width, height, rotationDeg = 0, flipped = false }) {
  const w = Math.max(IMAGE_MIN_EDGE, isFiniteNum(Number(width)) ? Number(width) : 0);
  const h = Math.max(IMAGE_MIN_EDGE, isFiniteNum(Number(height)) ? Number(height) : 0);
  const deg = Number(rotationDeg);
  const a = (isFiniteNum(deg) ? deg : 0) * (Math.PI / 180);
  const c = Math.cos(a);
  const s = Math.sin(a);
  const u = { x: c * w, y: s * w };
  const side = flipped ? -1 : 1;
  const v = { x: -s * h * side, y: c * h * side };
  return { origin: pt(origin), u, v };
}
function transformImageFrame(e, mapPoint2, mapVector) {
  return { ...e, origin: mapPoint2(e.origin), u: mapVector(e.u), v: mapVector(e.v) };
}
function resizeImage(e, size, anchor = 0) {
  if (!hasImageFrame(e)) return e;
  const a = ((Math.trunc(Number(anchor)) || 0) % 4 + 4) % 4;
  const w0 = imageWidth(e);
  const h0 = imageHeight(e);
  if (!(w0 > 0) || !(h0 > 0)) return e;
  let w = isFiniteNum(size.width) && size.width > 0 ? size.width : w0;
  let h = isFiniteNum(size.height) && size.height > 0 ? size.height : h0;
  if (e.lockAspect) {
    if (isFiniteNum(size.width) && size.width > 0) h = w / w0 * h0;
    else if (isFiniteNum(size.height) && size.height > 0) w = h / h0 * w0;
  }
  w = Math.max(IMAGE_MIN_EDGE, w);
  h = Math.max(IMAGE_MIN_EDGE, h);
  const u = { x: e.u.x / w0 * w, y: e.u.y / w0 * w };
  const v = { x: e.v.x / h0 * h, y: e.v.y / h0 * h };
  const before = imageCorners(e)[a];
  const after = imageCorners({ ...e, u, v })[a];
  return {
    ...e,
    u,
    v,
    origin: { x: e.origin.x + (before.x - after.x), y: e.origin.y + (before.y - after.y) }
  };
}
function imageMatrix(entity, def) {
  const W = def && def.width > 0 ? def.width : 1;
  const H = def && def.height > 0 ? def.height : 1;
  const { origin, u, v } = entity;
  return [
    u.x / W,
    u.y / W,
    -v.x / H,
    -v.y / H,
    origin.x + v.x,
    origin.y + v.y
  ];
}

// src/cadModelCore/entities.js
function entityVertices(e) {
  switch (e.type) {
    case "line":
      return [
        { x: e.x1, y: e.y1 },
        { x: e.x2, y: e.y2 }
      ];
    case "polyline":
      return e.points.map((p) => ({ x: p.x, y: p.y }));
    case "rectangle":
      return [
        { x: e.x, y: e.y },
        { x: e.x + e.width, y: e.y },
        { x: e.x + e.width, y: e.y + e.height },
        { x: e.x, y: e.y + e.height }
      ];
    case "circle":
      return [{ x: e.cx, y: e.cy }];
    case "arc":
      return [{ x: e.cx, y: e.cy }];
    case "ellipse":
      return [{ x: e.cx, y: e.cy }];
    case "spline":
      return splineDefiningPoints(e).map((p) => ({ x: p.x, y: p.y }));
    case "ray":
    case "xline":
      return [{ x: e.x, y: e.y }];
    // the base/through point (the line is infinite)
    case "revcloud":
      return (e.points || []).map((p) => ({ x: p.x, y: p.y }));
    case "leader":
      return (e.points || []).map((p) => ({ x: p.x, y: p.y }));
    case "text":
      return [{ x: e.x, y: e.y }];
    case "mtext":
      return mtextBoxCorners(e);
    case "table":
      return tableCorners(e);
    case "point":
      return [{ x: e.x, y: e.y }];
    case "hatch":
      return Array.isArray(e.cachedBoundary) ? e.cachedBoundary.map((p) => ({ x: p.x, y: p.y })) : [];
    case "insert":
    case "xref":
      return [{ x: e.insertionPoint && e.insertionPoint.x || 0, y: e.insertionPoint && e.insertionPoint.y || 0 }];
    // [RASTER-IMAGE-1] The four CORNERS, deliberately unlike insert/xref's single
    // anchor. A placed photo is a rectangle a drafter traces against, so its
    // corners are legitimate endpoint-osnap targets — and because the frame lives
    // on the entity, this needs no `imageDefs` lookup the way insert needs
    // `blockDefs`. That is what exempts bounds, hit-test and grips from threading
    // the def map at all.
    case "image":
      return imageCorners(e);
    case "dimension":
      if (e.dimType === "ordinate") {
        return [
          { x: e.feature.x, y: e.feature.y },
          { x: e.leader.x, y: e.leader.y }
        ];
      }
      if (e.dimType === "angular") {
        return [
          { x: e.vertex.x, y: e.vertex.y },
          { x: e.p1.x, y: e.p1.y },
          { x: e.p2.x, y: e.p2.y }
        ];
      }
      if (e.dimType === "radius" || e.dimType === "diameter") {
        return [
          { x: e.center.x - e.radius, y: e.center.y },
          { x: e.center.x + e.radius, y: e.center.y },
          { x: e.center.x, y: e.center.y - e.radius },
          { x: e.center.x, y: e.center.y + e.radius },
          { x: e.leaderPoint.x, y: e.leaderPoint.y }
        ];
      }
      if (e.dimType === "arclength") {
        return [
          { x: e.center.x - e.radius, y: e.center.y },
          { x: e.center.x + e.radius, y: e.center.y },
          { x: e.center.x, y: e.center.y - e.radius },
          { x: e.center.x, y: e.center.y + e.radius },
          { x: e.linePoint.x, y: e.linePoint.y }
        ];
      }
      return [
        { x: e.p1.x, y: e.p1.y },
        { x: e.p2.x, y: e.p2.y },
        { x: e.linePoint.x, y: e.linePoint.y }
      ];
    // C4b. Every vertex of every ring, holes included. This one case is what
    // gives a region its bounds, its fit-zoom, its marquee hit and its endpoint
    // snaps — entityBounds/documentBounds/framingBounds all read through here,
    // so without it a region is invisible to all four.
    case "region":
      return (e.rings || []).flatMap(
        (ring) => (ring || []).map((p) => ({ x: p.x, y: p.y }))
      );
    default:
      return [];
  }
}
var INSERT_BOUNDS_MAX_DEPTH = 8;
function insertBounds(e, blockDefs, xrefDefs, depth = 0) {
  const ip = e.insertionPoint || { x: 0, y: 0 };
  const def = blockDefs && blockDefs[e.blockName];
  if (!def || !Array.isArray(def.entities) || !def.entities.length) {
    return { minX: ip.x, minY: ip.y, maxX: ip.x, maxY: ip.y };
  }
  const base = def.basePoint || { x: 0, y: 0 };
  const sx = e.scaleX != null ? e.scaleX : 1;
  const sy = e.scaleY != null ? e.scaleY : 1;
  const rot = e.rotation || 0;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const se of def.entities) {
    const b = depth < INSERT_BOUNDS_MAX_DEPTH ? entityBoundsDeep(se, blockDefs, xrefDefs, depth + 1) : entityBounds(se);
    if (!b) continue;
    const corners = [
      { x: b.minX, y: b.minY },
      { x: b.maxX, y: b.minY },
      { x: b.maxX, y: b.maxY },
      { x: b.minX, y: b.maxY }
    ];
    for (const cpt of corners) {
      const t = transformPoint(cpt, ip, sx, sy, rot, base);
      if (t.x < minX) minX = t.x;
      if (t.y < minY) minY = t.y;
      if (t.x > maxX) maxX = t.x;
      if (t.y > maxY) maxY = t.y;
    }
  }
  if (!isFinite(minX)) return { minX: ip.x, minY: ip.y, maxX: ip.x, maxY: ip.y };
  return { minX, minY, maxX, maxY };
}
function entityBoundsDeep(e, blockDefs, xrefDefs, depth) {
  if (e && e.type === "insert") return insertBounds(e, blockDefs, xrefDefs, depth);
  if (e && e.type === "xref") return xrefBounds(e, xrefDefs, blockDefs, depth);
  return entityBounds(e, blockDefs, xrefDefs);
}
function xrefBounds(e, xrefDefs, outerBlockDefs, depth = 0) {
  const ip = e.insertionPoint || { x: 0, y: 0 };
  const def = xrefDefs && xrefDefs[e.xrefName];
  if (!def || !Array.isArray(def.entities) || !def.entities.length) {
    return { minX: ip.x, minY: ip.y, maxX: ip.x, maxY: ip.y };
  }
  const sx = e.scaleX != null ? e.scaleX : 1;
  const sy = e.scaleY != null ? e.scaleY : 1;
  const rot = e.rotation || 0;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const se of def.entities) {
    const defs = def.blockDefs || outerBlockDefs;
    const b = depth < INSERT_BOUNDS_MAX_DEPTH ? entityBoundsDeep(se, defs, xrefDefs, depth + 1) : entityBounds(se, defs);
    if (!b) continue;
    const corners = [
      { x: b.minX, y: b.minY },
      { x: b.maxX, y: b.minY },
      { x: b.maxX, y: b.maxY },
      { x: b.minX, y: b.maxY }
    ];
    for (const cpt of corners) {
      const t = transformPoint(cpt, ip, sx, sy, rot, { x: 0, y: 0 });
      if (t.x < minX) minX = t.x;
      if (t.y < minY) minY = t.y;
      if (t.x > maxX) maxX = t.x;
      if (t.y > maxY) maxY = t.y;
    }
  }
  if (!isFinite(minX)) return { minX: ip.x, minY: ip.y, maxX: ip.x, maxY: ip.y };
  return { minX, minY, maxX, maxY };
}
function xrefFrameBounds(e, xrefDefs) {
  return xrefBounds(e, xrefDefs);
}
function entityBounds(e, blockDefs, xrefDefs) {
  if (e.type === "insert") return insertBounds(e, blockDefs, xrefDefs);
  if (e.type === "xref") return xrefBounds(e, xrefDefs);
  if (e.type === "circle") {
    return { minX: e.cx - e.r, minY: e.cy - e.r, maxX: e.cx + e.r, maxY: e.cy + e.r };
  }
  if (e.type === "arc") {
    return { minX: e.cx - e.r, minY: e.cy - e.r, maxX: e.cx + e.r, maxY: e.cy + e.r };
  }
  if (e.type === "ellipse") {
    const rr = Math.max(e.rx, e.ry);
    return { minX: e.cx - rr, minY: e.cy - rr, maxX: e.cx + rr, maxY: e.cy + rr };
  }
  if (e.type === "ray" || e.type === "xline") {
    return { minX: e.x, minY: e.y, maxX: e.x, maxY: e.y };
  }
  const verts = entityVertices(e);
  if (!verts.length) return null;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of verts) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { minX, minY, maxX, maxY };
}
function documentBounds(entities, blockDefs, xrefDefs) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const e of entities) {
    const b = entityBounds(e, blockDefs, xrefDefs);
    if (!b) continue;
    if (b.minX < minX) minX = b.minX;
    if (b.minY < minY) minY = b.minY;
    if (b.maxX > maxX) maxX = b.maxX;
    if (b.maxY > maxY) maxY = b.maxY;
  }
  if (!isFinite(minX)) return null;
  return { minX, minY, maxX, maxY };
}
function framingBounds(entities, blockDefs, xrefDefs) {
  const full = documentBounds(entities, blockDefs, xrefDefs);
  if (!full) return null;
  const span = Math.max(full.maxX - full.minX, full.maxY - full.minY);
  if (span < 1e5) return full;
  const cs = [];
  for (const e of entities) {
    const b = entityBounds(e, blockDefs, xrefDefs);
    if (b) cs.push({ cx: (b.minX + b.maxX) / 2, cy: (b.minY + b.maxY) / 2, b });
  }
  if (cs.length < 3) return full;
  const med = (arr) => {
    const s = [...arr].sort((a, b) => a - b);
    return s[s.length - 1 >> 1];
  };
  const mx = med(cs.map((c) => c.cx));
  const my = med(cs.map((c) => c.cy));
  const madx = med(cs.map((c) => Math.abs(c.cx - mx))) || 1;
  const mady = med(cs.map((c) => Math.abs(c.cy - my))) || 1;
  const K = 20;
  const kept = cs.filter((c) => Math.abs(c.cx - mx) <= K * madx && Math.abs(c.cy - my) <= K * mady);
  if (!kept.length || kept.length === cs.length) return full;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const c of kept) {
    if (c.b.minX < minX) minX = c.b.minX;
    if (c.b.minY < minY) minY = c.b.minY;
    if (c.b.maxX > maxX) maxX = c.b.maxX;
    if (c.b.maxY > maxY) maxY = c.b.maxY;
  }
  return { minX, minY, maxX, maxY };
}
function translateEntity(e, dx, dy) {
  switch (e.type) {
    case "line":
      return { ...e, x1: e.x1 + dx, y1: e.y1 + dy, x2: e.x2 + dx, y2: e.y2 + dy };
    case "polyline":
      return { ...e, points: e.points.map((p) => ({ x: p.x + dx, y: p.y + dy })) };
    case "rectangle":
      return { ...e, x: e.x + dx, y: e.y + dy };
    case "circle":
    case "arc":
    case "ellipse":
      return { ...e, cx: e.cx + dx, cy: e.cy + dy };
    case "spline":
      return {
        ...e,
        controlPoints: Array.isArray(e.controlPoints) ? e.controlPoints.map((p) => ({ x: p.x + dx, y: p.y + dy })) : e.controlPoints,
        fitPoints: Array.isArray(e.fitPoints) ? e.fitPoints.map((p) => ({ x: p.x + dx, y: p.y + dy })) : e.fitPoints
      };
    case "ray":
    case "xline":
      return { ...e, x: e.x + dx, y: e.y + dy };
    // direction unchanged
    case "revcloud":
      return { ...e, points: e.points.map((p) => ({ x: p.x + dx, y: p.y + dy })) };
    case "leader":
      return { ...e, points: e.points.map((p) => ({ x: p.x + dx, y: p.y + dy })) };
    case "text":
    case "mtext":
    // anchor-only: the box (width/rotation/wrap) rides along unchanged
    case "table":
    // anchor-only: the grid (sizes/cells/rotation) rides along unchanged
    case "point":
      return { ...e, x: e.x + dx, y: e.y + dy };
    // C4b. Every point of every ring. Without this MOVE silently no-ops on a
    // region (the switch's `default` returns the entity unchanged), which reads
    // to a user as "the drawing is frozen" rather than as a missing case.
    case "region":
      return { ...e, rings: mapRings(e.rings, (p) => ({ x: p.x + dx, y: p.y + dy })) };
    case "hatch":
      return {
        ...e,
        cachedLines: (e.cachedLines || []).map((s) => ({
          x1: s.x1 + dx,
          y1: s.y1 + dy,
          x2: s.x2 + dx,
          y2: s.y2 + dy
        })),
        cachedBoundary: (e.cachedBoundary || []).map((p) => ({ x: p.x + dx, y: p.y + dy })),
        // Island holes move with the fill so a moved hatch keeps its holes until rebuild.
        cachedHoles: Array.isArray(e.cachedHoles) ? e.cachedHoles.map((ring) => ring.map((p) => ({ x: p.x + dx, y: p.y + dy }))) : e.cachedHoles,
        explicitBoundary: Array.isArray(e.explicitBoundary) ? e.explicitBoundary.map((p) => ({ x: p.x + dx, y: p.y + dy })) : e.explicitBoundary,
        // Declared island rings (imported hatch) move with the boundary they belong to.
        explicitHoles: Array.isArray(e.explicitHoles) ? e.explicitHoles.map((ring) => ring.map((p) => ({ x: p.x + dx, y: p.y + dy }))) : e.explicitHoles,
        // Carry the region seed point along so moving a region hatch together with
        // its boundary re-detects the region at the moved location (rebuildHatch).
        regionSeed: e.regionSeed && e.regionSeed.point ? { point: { x: e.regionSeed.point.x + dx, y: e.regionSeed.point.y + dy }, sourceIds: [...e.regionSeed.sourceIds || []] } : e.regionSeed
      };
    // `insert` and `xref` share this case since [C5]. They shared it already in
    // rotate/scale/mirror and in cadGrips, and the divergence here was the odd one
    // out: an xref that carried placed attributes would have had them stranded by
    // MOVE alone, while every other gesture carried them. Nothing produces such an
    // xref today (no importer sets `attributes` on one), which is exactly why it was
    // worth unifying rather than deleting the other four — a contract four call
    // sites honour and a fifth does not is a bug waiting for its first producer.
    case "image":
      return { ...e, origin: { x: (e.origin?.x || 0) + dx, y: (e.origin?.y || 0) + dy } };
    case "insert":
    case "xref":
      return {
        ...e,
        insertionPoint: { x: (e.insertionPoint?.x || 0) + dx, y: (e.insertionPoint?.y || 0) + dy },
        // Placed attributes carry world positions, so they move with the insert.
        // Spread, not an unconditional key: writing `attributes: undefined` onto
        // every attribute-less INSERT is the bimodal-field trap `normalizeDocument`
        // guards for viewport `locked` — harmless against today's `diffDocuments`
        // (measured in round-1 review) and exactly the shape a future deep-equal
        // that is not undefined-tolerant would report as a modification.
        ...Array.isArray(e.attributes) && e.attributes.length ? { attributes: e.attributes.map((a) => ({ ...a, x: (a.x || 0) + dx, y: (a.y || 0) + dy })) } : {}
      };
    case "dimension":
      if (e.dimType === "ordinate") {
        return {
          ...e,
          feature: { x: e.feature.x + dx, y: e.feature.y + dy },
          leader: { x: e.leader.x + dx, y: e.leader.y + dy },
          datum: { x: e.datum.x + dx, y: e.datum.y + dy }
        };
      }
      if (e.dimType === "angular") {
        return {
          ...e,
          vertex: { x: e.vertex.x + dx, y: e.vertex.y + dy },
          p1: { x: e.p1.x + dx, y: e.p1.y + dy },
          p2: { x: e.p2.x + dx, y: e.p2.y + dy }
        };
      }
      if (e.dimType === "radius" || e.dimType === "diameter") {
        return {
          ...e,
          center: { x: e.center.x + dx, y: e.center.y + dy },
          leaderPoint: { x: e.leaderPoint.x + dx, y: e.leaderPoint.y + dy }
        };
      }
      if (e.dimType === "arclength") {
        return {
          ...e,
          center: { x: e.center.x + dx, y: e.center.y + dy },
          linePoint: { x: e.linePoint.x + dx, y: e.linePoint.y + dy }
        };
      }
      return {
        ...e,
        p1: { x: e.p1.x + dx, y: e.p1.y + dy },
        p2: { x: e.p2.x + dx, y: e.p2.y + dy },
        linePoint: { x: e.linePoint.x + dx, y: e.linePoint.y + dy }
      };
    default:
      return e;
  }
}
function cloneEntity(e) {
  const copy = genId(e.type[0]);
  if (e.type === "polyline") {
    return { ...e, id: copy, points: e.points.map((p) => ({ ...p })) };
  }
  if (e.type === "spline") {
    return {
      ...e,
      id: copy,
      controlPoints: Array.isArray(e.controlPoints) ? e.controlPoints.map((p) => ({ ...p })) : e.controlPoints,
      fitPoints: Array.isArray(e.fitPoints) ? e.fitPoints.map((p) => ({ ...p })) : e.fitPoints
    };
  }
  if (e.type === "revcloud") {
    return { ...e, id: copy, points: (e.points || []).map((p) => ({ ...p })) };
  }
  if (e.type === "leader") {
    return { ...e, id: copy, points: (e.points || []).map((p) => ({ ...p })) };
  }
  if (e.type === "hatch") {
    return {
      ...e,
      id: copy,
      boundaryPolylineIds: Array.isArray(e.boundaryPolylineIds) ? [...e.boundaryPolylineIds] : [],
      cachedLines: (e.cachedLines || []).map((s) => ({ ...s })),
      cachedBoundary: (e.cachedBoundary || []).map((p) => ({ ...p })),
      cachedHoles: Array.isArray(e.cachedHoles) ? e.cachedHoles.map((ring) => ring.map((p) => ({ ...p }))) : e.cachedHoles,
      holeSourceIds: Array.isArray(e.holeSourceIds) ? [...e.holeSourceIds] : e.holeSourceIds,
      explicitBoundary: Array.isArray(e.explicitBoundary) ? e.explicitBoundary.map((p) => ({ ...p })) : e.explicitBoundary,
      explicitHoles: Array.isArray(e.explicitHoles) ? e.explicitHoles.map((ring) => ring.map((p) => ({ ...p }))) : e.explicitHoles,
      regionSeed: e.regionSeed && e.regionSeed.point ? { point: { ...e.regionSeed.point }, sourceIds: [...e.regionSeed.sourceIds || []] } : e.regionSeed,
      derivedError: e.derivedError ? { ...e.derivedError } : e.derivedError
    };
  }
  if (e.type === "region") {
    return {
      ...e,
      id: copy,
      rings: Array.isArray(e.rings) ? e.rings.map((ring) => Array.isArray(ring) ? ring.map((p) => ({ ...p })) : ring) : e.rings
    };
  }
  if (e.type === "image") {
    return {
      ...e,
      id: copy,
      origin: e.origin ? { ...e.origin } : { x: 0, y: 0 },
      u: e.u ? { ...e.u } : { x: 1, y: 0 },
      v: e.v ? { ...e.v } : { x: 0, y: 1 }
    };
  }
  if (e.type === "insert") {
    return {
      ...e,
      id: copy,
      insertionPoint: e.insertionPoint ? { ...e.insertionPoint } : { x: 0, y: 0 },
      attributeValues: e.attributeValues ? { ...e.attributeValues } : {}
    };
  }
  if (e.type === "xref") {
    return {
      ...e,
      id: copy,
      insertionPoint: e.insertionPoint ? { ...e.insertionPoint } : { x: 0, y: 0 },
      layerVisibility: e.layerVisibility ? { ...e.layerVisibility } : {}
    };
  }
  if (e.type === "dimension") {
    if (e.dimType === "ordinate") {
      return { ...e, id: copy, feature: { ...e.feature }, leader: { ...e.leader }, datum: { ...e.datum } };
    }
    if (e.dimType === "angular") {
      return { ...e, id: copy, vertex: { ...e.vertex }, p1: { ...e.p1 }, p2: { ...e.p2 } };
    }
    if (e.dimType === "radius" || e.dimType === "diameter") {
      return { ...e, id: copy, center: { ...e.center }, leaderPoint: { ...e.leaderPoint } };
    }
    return { ...e, id: copy, p1: { ...e.p1 }, p2: { ...e.p2 }, linePoint: { ...e.linePoint } };
  }
  if (e.type === "table") {
    return {
      ...e,
      id: copy,
      colWidths: Array.isArray(e.colWidths) ? [...e.colWidths] : e.colWidths,
      rowHeights: Array.isArray(e.rowHeights) ? [...e.rowHeights] : e.rowHeights,
      cells: e.cells && typeof e.cells === "object" ? Object.fromEntries(Object.entries(e.cells).map(([k, v]) => [k, { ...v }])) : e.cells
    };
  }
  return { ...e, id: copy };
}
function addPolylineVertex(e, afterIndex, point) {
  if (e.type !== "polyline") return null;
  const points = e.points.map((p) => ({ ...p }));
  points.splice(afterIndex + 1, 0, { x: point.x, y: point.y });
  return { points };
}
function removePolylineVertex(e, index) {
  if (e.type !== "polyline" || e.points.length <= 2) return null;
  return { points: e.points.filter((_, i) => i !== index).map((p) => ({ ...p })) };
}

// src/CADModel/linetypes.js
var LINETYPE_DEFS = {
  CONTINUOUS: { name: "CONTINUOUS", description: "Solid", pattern: [] },
  DASHED: { name: "DASHED", description: "Dashed __ __ __", pattern: [0.5, -0.25] },
  DOTTED: { name: "DOTTED", description: "Dotted . . . . .", pattern: [0, -0.25] },
  CENTER: {
    name: "CENTER",
    description: "Center ____ _ ____",
    pattern: [1.25, -0.25, 0.25, -0.25]
  },
  HIDDEN: { name: "HIDDEN", description: "Hidden _ _ _ _", pattern: [0.25, -0.125] },
  DASHDOT: { name: "DASHDOT", description: "Dash dot __ . __ .", pattern: [0.5, -0.25, 0, -0.25] },
  DIVIDE: {
    name: "DIVIDE",
    description: "Divide __ . . __ . .",
    pattern: [0.5, -0.25, 0, -0.25, 0, -0.25]
  }
};
var BUILTIN_LINETYPES = new Set(Object.keys(LINETYPE_DEFS));
var cloneDefs = (o) => o == null ? o : JSON.parse(JSON.stringify(o));
function linetypeToDash(pattern, pixelScale, ltscale = 1) {
  if (!Array.isArray(pattern) || pattern.length === 0) return null;
  const s = pixelScale * (Number.isFinite(ltscale) && ltscale > 0 ? ltscale : 1);
  const dash = pattern.map((v) => {
    const a = Math.abs(v) * s;
    return a > 0 ? a : Math.max(0.01, 0.15 * s);
  });
  if (dash.length % 2 === 1) dash.push(dash[dash.length - 1]);
  return dash;
}
function resolveLinetypeName(entity, layers) {
  const own = entity && entity.linetype;
  if (own) return own;
  const layer = layers && layers.find((l) => l.name === (entity && entity.layer));
  const inherited = layer && layer.linetype;
  return inherited || null;
}
function resolveLinetypePattern(entity, layers, linetypeDefs) {
  const name = resolveLinetypeName(entity, layers);
  if (!name || !linetypeDefs) return null;
  const def = linetypeDefs[name];
  return def && Array.isArray(def.pattern) && def.pattern.length ? def.pattern : null;
}

// src/CADModel/lineweight.js
var LINEWEIGHTS_MM = [
  0,
  0.05,
  0.09,
  0.13,
  0.15,
  0.18,
  0.2,
  0.25,
  0.3,
  0.35,
  0.4,
  0.5,
  0.53,
  0.6,
  0.7,
  0.8,
  0.9,
  1,
  1.06,
  1.2,
  1.4,
  1.58,
  2,
  2.11
];
var DEFAULT_LINEWEIGHT_MM = 0.25;
var LINEWEIGHT_DISPLAY_PX_PER_MM = 96 / 25.4;
function clampLineweight(v) {
  if (v === null || v === void 0 || v === "") return null;
  const n = Number(v);
  if (!Number.isFinite(n) || n < 0) return null;
  let best = LINEWEIGHTS_MM[0];
  let bestD = Infinity;
  for (const w of LINEWEIGHTS_MM) {
    const d = Math.abs(w - n);
    if (d < bestD) {
      bestD = d;
      best = w;
    }
  }
  return best;
}
function resolveLineweightMm(entity, layers) {
  const own = entity && entity.lineweight;
  if (own != null && Number.isFinite(own)) return own;
  const layer = layers && layers.find((l) => l.name === (entity && entity.layer));
  if (layer && layer.lineweight != null && Number.isFinite(layer.lineweight))
    return layer.lineweight;
  return DEFAULT_LINEWEIGHT_MM;
}
function lineweightToScreenPx(entity, layers, lwDisplay) {
  if (!lwDisplay) return 1;
  const mm = resolveLineweightMm(entity, layers);
  if (!(mm > 0)) return 1;
  const capped = Math.min(mm, LINEWEIGHTS_MM[LINEWEIGHTS_MM.length - 1]);
  return Math.max(1, capped * LINEWEIGHT_DISPLAY_PX_PER_MM);
}
function polylineWidthWorld(entity) {
  if (!entity) return 0;
  const field = polylineWidthField(entity && entity.type);
  if (!field) return 0;
  const w = entity[field];
  return Number.isFinite(w) && w > 0 ? w : 0;
}
function polylineWidthField(type) {
  if (type === "rectangle") return "polyWidth";
  if (type === "polyline") return "width";
  return null;
}

// src/CADModel/blocks.js
var GEOTECH_BLOCKS = {
  BH: {
    description: "Borehole",
    basePoint: { x: 0, y: 0 },
    entities: [
      { type: "circle", layer: "0", cx: 0, cy: 0, r: 0.5 },
      { type: "line", layer: "0", x1: -0.5, y1: 0, x2: 0.5, y2: 0 },
      { type: "line", layer: "0", x1: 0, y1: -0.5, x2: 0, y2: 0.5 }
    ]
  },
  SPT: {
    description: "Standard Penetration Test",
    basePoint: { x: 0, y: 0 },
    entities: [
      { type: "polyline", layer: "0", closed: true, points: [{ x: -0.5, y: -0.5 }, { x: 0.5, y: -0.5 }, { x: 0.5, y: 0.5 }, { x: -0.5, y: 0.5 }] },
      { type: "line", layer: "0", x1: -0.5, y1: -0.5, x2: 0.5, y2: 0.5 }
    ]
  },
  CPT: {
    description: "Cone Penetration Test",
    basePoint: { x: 0, y: 0 },
    entities: [
      { type: "polyline", layer: "0", closed: true, points: [{ x: -0.5, y: 0.5 }, { x: 0.5, y: 0.5 }, { x: 0, y: -0.5 }] }
    ]
  },
  PILE: {
    description: "Pile",
    basePoint: { x: 0, y: 0 },
    entities: [
      { type: "polyline", layer: "0", closed: true, points: [{ x: -0.25, y: -1.5 }, { x: 0.25, y: -1.5 }, { x: 0.25, y: 0 }, { x: -0.25, y: 0 }] }
    ]
  },
  ANCHOR: {
    description: "Ground Anchor",
    basePoint: { x: 0, y: 0 },
    entities: [
      { type: "line", layer: "0", x1: 0, y1: 0, x2: 2, y2: -1 },
      { type: "polyline", layer: "0", closed: true, points: [{ x: 2, y: -1 }, { x: 1.55, y: -0.7 }, { x: 1.7, y: -1.25 }] }
    ]
  },
  FOOTING: {
    description: "Footing",
    basePoint: { x: 0, y: 0 },
    entities: [
      { type: "polyline", layer: "0", closed: true, points: [{ x: -1, y: -0.3 }, { x: 1, y: -0.3 }, { x: 1, y: 0.3 }, { x: -1, y: 0.3 }] }
    ]
  },
  // Default sheet title block. Unlike the geotech symbols (model metres), its
  // geometry is in PAPER millimetres — CADPaperSpace renders it via mmToPx at the
  // paper origin (bottom-left). `paperBlock` flags this so it isn't offered as a
  // model-space insert symbol.
  TITLE_BLOCK: {
    description: "Title Block",
    paperBlock: true,
    basePoint: { x: 0, y: 0 },
    entities: [
      { type: "rectangle", layer: "0", x: 0, y: 0, width: 180, height: 56 },
      { type: "line", layer: "0", x1: 0, y1: 14, x2: 120, y2: 14 },
      { type: "line", layer: "0", x1: 0, y1: 28, x2: 120, y2: 28 },
      { type: "line", layer: "0", x1: 0, y1: 42, x2: 120, y2: 42 },
      { type: "line", layer: "0", x1: 120, y1: 0, x2: 120, y2: 56 },
      { type: "text", layer: "0", x: 2, y: 45, text: "PROJECT:", height: 3, rotation: 0 },
      { type: "text", layer: "0", x: 2, y: 31, text: "FIGURE No.:", height: 3, rotation: 0 },
      { type: "text", layer: "0", x: 2, y: 17, text: "SCALE:", height: 3, rotation: 0 },
      { type: "text", layer: "0", x: 2, y: 3, text: "DATE:", height: 3, rotation: 0 },
      { type: "text", layer: "0", x: 126, y: 25, text: "TITLE", height: 6, rotation: 0 }
    ]
  }
};

// src/cadLayerAccess.js
function layerShown(layer) {
  return !layer || layer.visible !== false && layer.frozen !== true;
}
function layerFrozen(layer) {
  return !!(layer && layer.frozen === true);
}
function layerPlotted(layer) {
  return !layer || layer.plot !== false;
}
function layerLocked(layer) {
  return !!(layer && layer.locked === true);
}
function freezeCurrentLayerError(name) {
  return `\u201C${name}\u201D is the current layer. Make another layer current before freezing it.`;
}
function currentLayerFrozenError(name) {
  return `\u201C${name}\u201D is frozen. Thaw it before drawing on it.`;
}
function layerIndex(layers) {
  if (layers instanceof Map) return layers;
  const index = /* @__PURE__ */ new Map();
  for (const l of layers || []) {
    if (!l || typeof l !== "object" || typeof l.name !== "string") continue;
    if (!index.has(l.name)) index.set(l.name, l);
  }
  return index;
}
function recordFor(entity, index) {
  const name = entity && entity.layer;
  return typeof name === "string" ? index.get(name) : void 0;
}
function selectableEntity(entity, layers) {
  const layer = recordFor(entity, layerIndex(layers));
  return layerShown(layer) && !layerLocked(layer);
}
function selectableEntities(entities, layers) {
  const index = layerIndex(layers);
  return (entities || []).filter((e) => {
    const layer = recordFor(e, index);
    return layerShown(layer) && !layerLocked(layer);
  });
}
function shownEntities(entities, layers) {
  const index = layerIndex(layers);
  return (entities || []).filter((e) => layerShown(recordFor(e, index)));
}
function editableEntity(entity, layers) {
  return !layerLocked(recordFor(entity, layerIndex(layers)));
}
function unfrozenEntities(entities, layers) {
  const index = layerIndex(layers);
  let anyFrozen = false;
  for (const l of index.values()) {
    if (layerFrozen(l)) {
      anyFrozen = true;
      break;
    }
  }
  const list = entities || [];
  if (!anyFrozen) return list;
  return list.filter((e) => !layerFrozen(recordFor(e, index)));
}
function lockedEntityIds(doc) {
  const index = layerIndex(doc && doc.layers);
  let anyLocked = false;
  for (const l of index.values()) {
    if (layerLocked(l)) {
      anyLocked = true;
      break;
    }
  }
  if (!anyLocked) return [];
  const out = [];
  for (const e of doc && doc.entities || []) {
    if (e && e.id != null && layerLocked(recordFor(e, index))) out.push(e.id);
  }
  return out;
}
function partitionLockedIds(doc, ids) {
  const list = ids || [];
  const index = layerIndex(doc && doc.layers);
  let anyLocked = false;
  for (const l of index.values()) {
    if (layerLocked(l)) {
      anyLocked = true;
      break;
    }
  }
  if (!anyLocked) return { allowed: [...list], locked: [] };
  const want = new Set(list);
  const byId = /* @__PURE__ */ new Map();
  for (const e of doc && doc.entities || []) {
    if (e && want.has(e.id)) {
      byId.set(e.id, e);
      if (byId.size === want.size) break;
    }
  }
  const allowed = [];
  const locked = [];
  for (const id of list) {
    const e = byId.get(id);
    if (e && layerLocked(recordFor(e, index))) locked.push(id);
    else allowed.push(id);
  }
  return { allowed, locked };
}

// src/cadView.js
var SCALE_MIN = 1e-4;
var SCALE_MAX = 1e6;
function clampScale2(scale, { min = SCALE_MIN, max = SCALE_MAX } = {}) {
  if (!Number.isFinite(scale) || scale <= 0) return min;
  return Math.min(max, Math.max(min, scale));
}
function normalizeCanvasRect(viewport) {
  const v = viewport || {};
  return {
    x: Number.isFinite(v.x) ? v.x : 0,
    y: Number.isFinite(v.y) ? v.y : 0,
    width: Number.isFinite(v.width) ? v.width : 0,
    height: Number.isFinite(v.height) ? v.height : 0
  };
}
function fitRectToCanvasRect(worldRect, rect, padding = 0.9, clampOpts) {
  const r = normalizeCanvasRect(rect);
  if (!worldRect || !r.width || !r.height) {
    return { scale: 1, x: r.x + r.width / 2, y: r.y + r.height / 2 };
  }
  const w = Math.max(worldRect.maxX - worldRect.minX, 1e-6);
  const h = Math.max(worldRect.maxY - worldRect.minY, 1e-6);
  const scale = clampScale2(Math.min(r.width / w, r.height / h) * padding, clampOpts);
  const cx = (worldRect.minX + worldRect.maxX) / 2;
  const cy = -(worldRect.minY + worldRect.maxY) / 2;
  return {
    scale,
    x: r.x + r.width / 2 - cx * scale,
    y: r.y + r.height / 2 - cy * scale
  };
}
function fitRectToViewport(worldRect, vpW, vpH, padding = 0.9) {
  return fitRectToCanvasRect(worldRect, { x: 0, y: 0, width: vpW || 0, height: vpH || 0 }, padding);
}
function sameView(a, b, { eps = 0.5, scaleEps = 1e-6 } = {}) {
  if (!a || !b) return false;
  if (!Number.isFinite(a.scale) || !Number.isFinite(b.scale)) return false;
  const rel = Math.abs(a.scale - b.scale) / Math.max(Math.abs(a.scale), Math.abs(b.scale), 1e-12);
  return rel <= scaleEps && Math.abs(a.x - b.x) <= eps && Math.abs(a.y - b.y) <= eps;
}
function zoomByFactor(factor, screenAnchor, view, clampOpts) {
  const newScale = clampScale2(view.scale * factor, clampOpts);
  const kx = (screenAnchor.x - view.x) / view.scale;
  const ky = (screenAnchor.y - view.y) / view.scale;
  return { scale: newScale, x: screenAnchor.x - kx * newScale, y: screenAnchor.y - ky * newScale };
}
function zoomToScreenBox(box, view, viewport, padding = 0.98, clampOpts) {
  const toWorld = (sx, sy) => ({ x: (sx - view.x) / view.scale, y: -((sy - view.y) / view.scale) });
  const a = toWorld(box.x1, box.y1);
  const b = toWorld(box.x2, box.y2);
  const worldRect = {
    minX: Math.min(a.x, b.x),
    maxX: Math.max(a.x, b.x),
    minY: Math.min(a.y, b.y),
    maxY: Math.max(a.y, b.y)
  };
  return fitRectToCanvasRect(worldRect, viewport, padding, clampOpts);
}
function fitExtents(entities, blockDefs, xrefDefs, viewport, padding = 0.9, clampOpts) {
  const b = documentBounds(entities || [], blockDefs, xrefDefs);
  if (!b) return null;
  return fitRectToCanvasRect(b, viewport, padding, clampOpts);
}
function fitSelection(selectedEntities, blockDefs, xrefDefs, viewport, padding = 0.7) {
  if (!selectedEntities || !selectedEntities.length) return null;
  const b = documentBounds(selectedEntities, blockDefs, xrefDefs);
  if (!b) return null;
  return fitRectToCanvasRect(b, viewport, padding);
}
var easeInOutCubic = (t) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
function lerpView(a, b, t, easing) {
  const tt = Math.max(0, Math.min(1, t));
  const te = easing ? easing(tt) : tt;
  const sa = a.scale > 0 ? a.scale : 1e-6;
  const sb = b.scale > 0 ? b.scale : 1e-6;
  return {
    scale: Math.exp(Math.log(sa) + (Math.log(sb) - Math.log(sa)) * te),
    x: a.x + (b.x - a.x) * te,
    y: a.y + (b.y - a.y) * te
  };
}

// src/cadTemplates.js
var BUILTIN_TEMPLATE_ID = "builtin";
var FIELD_TAG_ALIASES = {
  project: ["project", "projectname", "projecttitle", "job", "jobname"],
  figure: ["figure", "figureno", "fig", "figurenumber", "drawingno", "dwgno", "sheetno", "drawing"],
  scale: ["scale"],
  date: ["date", "dwgdate", "plotdate"],
  drawnBy: ["drawnby", "drawn", "drawer", "author", "by", "designedby"],
  company: ["company", "client", "organisation", "organization", "firm"],
  // [G4b] Drawing-level facts a title block can carry (design F6). NOT the design's
  // 'copyright' or 'grid': title blocks use those tags for their OWN static text, and
  // binding them would overwrite a firm's copyright line with imagery credits.
  crs: ["crs", "datum", "projection", "coordinatesystem"],
  gridNorth: ["gridnorth", "convergence", "gridconvergence"],
  attribution: ["attribution", "imagery", "datasources", "mapdata"]
};
var CONTEXT_KEYS = /* @__PURE__ */ new Set(["crs", "gridNorth", "attribution"]);
function normalizeFieldTag(tag) {
  if (tag == null) return null;
  const k = String(tag).toLowerCase().replace(/[^a-z0-9]/g, "");
  if (!k) return null;
  for (const [key, aliases] of Object.entries(FIELD_TAG_ALIASES)) {
    if (aliases.includes(k)) return key;
  }
  return null;
}
function deriveSheetScale(layout) {
  const vps = layout && layout.viewports || [];
  if (!vps.length) return "";
  const labels = [];
  for (const vp of vps) {
    const lbl = formatScale(vp.scale);
    if (!labels.includes(lbl)) labels.push(lbl);
  }
  if (!labels.length) return "";
  return labels.length === 1 ? labels[0] : "As noted";
}
function fieldValue(key, titleFields, derived) {
  const explicit = titleFields && titleFields[key] != null ? String(titleFields[key]) : "";
  if (explicit) return explicit;
  const d = derived && derived[key];
  return d == null ? "" : String(d);
}
function builtinDrawList(titleFields, sheet, derived) {
  const items = [];
  const f = titleFields || {};
  const margin = 8;
  const tbH = 40;
  const tbW = Math.min(190, sheet.w - 2 * margin);
  const bx2 = sheet.w - margin;
  const bx1 = bx2 - tbW;
  const by1 = margin;
  const by2 = margin + tbH;
  const logoW = Math.min(52, tbW * 0.35);
  const colX = bx1 + logoW;
  const rows = 5;
  const rowH = tbH / rows;
  const rect = (x, y, w, h) => items.push({ kind: "poly", closed: true, color: "#111", pts: [
    { x, y },
    { x: x + w, y },
    { x: x + w, y: y + h },
    { x, y: y + h }
  ] });
  const line = (x1, y1, x2, y2) => items.push({ kind: "poly", color: "#111", pts: [{ x: x1, y: y1 }, { x: x2, y: y2 }] });
  const text = (s, x, y, height, color) => {
    if (s) items.push({ kind: "text", text: String(s), x, y, height, color: color || "#111", align: "left" });
  };
  rect(margin, margin, sheet.w - 2 * margin, sheet.h - 2 * margin);
  rect(bx1, by1, tbW, tbH);
  line(colX, by1, colX, by2);
  for (let i = 1; i < rows; i++) {
    const y = by1 + i * rowH;
    line(colX, y, bx2, y);
  }
  const fields = [
    ["PROJECT", fieldValue("project", f, derived)],
    ["FIGURE No.", fieldValue("figure", f, derived)],
    ["SCALE", fieldValue("scale", f, derived)],
    ["DATE", fieldValue("date", f, derived)],
    ["DRAWN BY", fieldValue("drawnBy", f, derived)]
  ];
  fields.forEach(([label, value], i) => {
    const rowTop = by2 - i * rowH;
    text(label, colX + 2, rowTop - 2.6, 2, "#555");
    text(value, colX + 3, rowTop - rowH + 2.4, 3.2, "#111");
  });
  text(fieldValue("company", f, derived), bx1 + 3, by1 + 3.5, 3.4, "#111");
  const logoBox = { x: bx1 + 2, y: by2 - 2 - 24, width: logoW - 4, height: 24 };
  return { items, logoBox };
}
function accArc(acc, cx, cy, r, startDeg, endDeg) {
  const at = (deg) => {
    const a = deg * Math.PI / 180;
    acc(cx + r * Math.cos(a), cy + r * Math.sin(a));
  };
  at(startDeg);
  at(endDeg);
  let s = (startDeg % 360 + 360) % 360;
  let e = (endDeg % 360 + 360) % 360;
  if (e <= s) e += 360;
  for (const axis of [0, 90, 180, 270, 360, 450, 540, 630]) {
    if (axis >= s && axis <= e) at(axis);
  }
}
function templateBounds(template) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const acc = (x, y) => {
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  };
  for (const e of template.entities || []) {
    switch (e.type) {
      case "line":
        acc(e.x1, e.y1);
        acc(e.x2, e.y2);
        break;
      case "rectangle":
        acc(e.x, e.y);
        acc(e.x + e.width, e.y + e.height);
        break;
      case "circle":
        acc(e.cx - e.r, e.cy - e.r);
        acc(e.cx + e.r, e.cy + e.r);
        break;
      case "arc":
        accArc(acc, e.cx, e.cy, e.r, e.startAngle || 0, e.endAngle == null ? 360 : e.endAngle);
        break;
      case "polyline":
        (e.points || []).forEach((p) => acc(p.x, p.y));
        break;
      case "text":
        acc(e.x, e.y);
        break;
      default:
        break;
    }
  }
  for (const fl of template.fields || []) acc(fl.x, fl.y);
  if (template.logoBox) {
    acc(template.logoBox.x, template.logoBox.y);
    acc(template.logoBox.x + template.logoBox.width, template.logoBox.y + template.logoBox.height);
  }
  if (!Number.isFinite(minX)) return { minX: 0, minY: 0, maxX: 0, maxY: 0, w: 0, h: 0 };
  return { minX, minY, maxX, maxY, w: maxX - minX, h: maxY - minY };
}
function placeTemplate(template, sheet) {
  const w = template.bbox && template.bbox.w || templateBounds(template).w;
  const h = template.bbox && template.bbox.h || templateBounds(template).h;
  if (!(w > 0) || !(h > 0)) return { scale: 1, dx: 0, dy: 0 };
  const scale = Math.min(sheet.w / w, sheet.h / h);
  return { scale, dx: (sheet.w - w * scale) / 2, dy: (sheet.h - h * scale) / 2 };
}
function customDrawList(template, titleFields, sheet, derived) {
  const { scale: s, dx, dy } = placeTemplate(template, sheet);
  const items = [];
  const off = (p) => ({ x: dx + p.x * s, y: dy + p.y * s });
  for (const e of template.entities || []) {
    const color = e.color || "#111";
    switch (e.type) {
      case "line":
        items.push({ kind: "poly", color, pts: [off({ x: e.x1, y: e.y1 }), off({ x: e.x2, y: e.y2 })] });
        break;
      case "rectangle":
        items.push({ kind: "poly", closed: true, color, pts: [
          off({ x: e.x, y: e.y }),
          off({ x: e.x + e.width, y: e.y }),
          off({ x: e.x + e.width, y: e.y + e.height }),
          off({ x: e.x, y: e.y + e.height })
        ] });
        break;
      case "polyline":
        items.push({ kind: "poly", closed: !!e.closed, color, pts: (e.points || []).map(off) });
        break;
      case "circle":
        items.push({ kind: "circle", color, center: off({ x: e.cx, y: e.cy }), r: e.r * s });
        break;
      case "arc":
        items.push({ kind: "arc", color, center: off({ x: e.cx, y: e.cy }), r: e.r * s, startAngle: e.startAngle, endAngle: e.endAngle });
        break;
      case "text": {
        const p = off({ x: e.x, y: e.y });
        items.push({ kind: "text", text: String(e.text || ""), x: p.x, y: p.y, height: (e.height || 3) * s, color, align: e.align || "left", rotation: e.rotation || 0 });
        break;
      }
      default:
        break;
    }
  }
  for (const fl of template.fields || []) {
    const key = normalizeFieldTag(fl.tag);
    let value = key ? fieldValue(key, titleFields, derived) : fl.text || "";
    if (value === "" && key && CONTEXT_KEYS.has(key)) value = fl.text || "";
    if (value === "" && key) continue;
    const p = off({ x: fl.x, y: fl.y });
    items.push({ kind: "text", text: String(value), x: p.x, y: p.y, height: (fl.height || 3) * s, color: fl.color || "#111", align: fl.align || "left", rotation: fl.rotation || 0 });
  }
  const logoBox = template.logoBox ? { x: dx + template.logoBox.x * s, y: dy + template.logoBox.y * s, width: template.logoBox.width * s, height: template.logoBox.height * s } : null;
  return { items, logoBox };
}
function templateToDrawList(template, opts = {}) {
  const titleFields = opts.titleFields || {};
  const sheet = opts.sheet || { w: 420, h: 297 };
  const derived = {
    scale: opts.scaleLabel || "",
    crs: opts.crs || "",
    gridNorth: opts.gridNorth || "",
    attribution: opts.attribution || ""
  };
  if (!template || template.source === "builtin" || template.id === BUILTIN_TEMPLATE_ID) {
    return builtinDrawList(titleFields, sheet, derived);
  }
  return customDrawList(template, titleFields, sheet, derived);
}

// src/cadFields.js
var FIELD_CODES = [
  "<<PROJECT>>",
  "<<FIGURE>>",
  "<<SHEETNAME>>",
  "<<SCALE>>",
  "<<DATE>>",
  "<<DRAWNBY>>",
  "<<COMPANY>>",
  // [G1-GEOREF] The drawing's coordinate system, read from `doc.georef` — the ONE
  // code that is a property of the DRAWING rather than of a sheet. It resolves to
  // '' for a drawing that is not georeferenced, so a title block that carries it
  // before GEOREF is run shows nothing rather than a guess.
  "<<CRS>>",
  // [G4b] Supplied by the caller's FieldContext (see the header): the grid convergence
  // at the sheet's first viewport, and the credits of the map data the sheet draws.
  "<<GRIDNORTH>>",
  "<<ATTRIBUTION>>"
];
var CONTEXT_CODES = /* @__PURE__ */ new Set(["<<GRIDNORTH>>", "<<ATTRIBUTION>>"]);
var FIELD_LABELS = {
  "<<PROJECT>>": "Project name",
  "<<FIGURE>>": "Sheet / figure number",
  "<<SHEETNAME>>": "Sheet name",
  "<<SCALE>>": "Plot scale",
  "<<DATE>>": "Date",
  "<<DRAWNBY>>": "Drawn by",
  "<<COMPANY>>": "Company / client",
  "<<CRS>>": "Coordinate system (GEOREF)",
  "<<GRIDNORTH>>": "Grid convergence (GEOREF)",
  "<<ATTRIBUTION>>": "Map data credits (GEOREF)"
};
function fieldCodeLabel(code) {
  return FIELD_LABELS[String(code)] || String(code);
}
function isFieldCode(s) {
  return FIELD_CODES.includes(String(s));
}
function bareTag(code) {
  const m = /^<<\s*([^<>]+?)\s*>>$/.exec(String(code));
  return m ? m[1] : null;
}
function fieldLayoutFor(doc, layoutRef) {
  const layouts = doc && doc.layouts || [];
  const paper = layouts.filter((l) => l && !l.isModel);
  if (layoutRef) {
    const byId = paper.find((l) => l.id === layoutRef);
    if (byId) return byId;
    const byName = paper.find((l) => l.name === layoutRef);
    if (byName) return byName;
  }
  return paper[0] || null;
}
function initialFieldValues(def, doc, layoutRef, ctxFor) {
  const layout = fieldLayoutFor(doc, layoutRef);
  const wantsCtx = (def && def.attdefs || []).some((a) => a && CONTEXT_CODES.has(a.field));
  const ctx = wantsCtx && typeof ctxFor === "function" && layout ? ctxFor(layout) : void 0;
  return Object.fromEntries(
    (def && def.attdefs || []).filter((a) => a && a.field && a.tag).map((a) => [a.tag, resolveField(a.field, layout, doc, ctx)]).filter(([, v]) => v != null)
  );
}
function healFieldBindings(entities, layouts) {
  const paper = (layouts || []).filter((l) => l && !l.isModel);
  if (!paper.length || !Array.isArray(entities)) return entities;
  const byName = /* @__PURE__ */ new Map();
  for (const l of paper) {
    if (byName.has(l.name)) byName.set(l.name, null);
    else byName.set(l.name, l.id);
  }
  const ids = new Set(paper.map((l) => l.id));
  let touched = false;
  const next = entities.map((e) => {
    if (!e || e.type !== "insert" || !Array.isArray(e.attributes) || !e.attributes.length) return e;
    let hit = false;
    const attributes = e.attributes.map((a) => {
      if (!a || !a.fieldLayout || ids.has(a.fieldLayout)) return a;
      const id = byName.get(a.fieldLayout);
      if (!id) return a;
      hit = true;
      return { ...a, fieldLayout: id };
    });
    if (!hit) return e;
    touched = true;
    return { ...e, attributes };
  });
  return touched ? next : entities;
}
function resolveField(code, layout, doc, ctx) {
  const tag = bareTag(code);
  if (!tag || !isFieldCode(code)) return null;
  const fields = layout && layout.titleFields || {};
  if (tag === "GRIDNORTH" || tag === "ATTRIBUTION") {
    const v = ctx ? ctx[tag === "GRIDNORTH" ? "gridNorth" : "attribution"] : void 0;
    return v === void 0 || v === null ? null : String(v);
  }
  if (tag === "CRS") {
    const g = doc && doc.georef;
    return g && g.crs ? String(g.crsName || g.crs) : "";
  }
  if (tag === "SHEETNAME") return layout ? String(layout.name || "") : "";
  const key = normalizeFieldTag(tag);
  const explicit = key && fields[key] != null ? String(fields[key]) : "";
  if (explicit) return explicit;
  if (key === "scale") return layout ? deriveSheetScale(layout) : "";
  return "";
}
function refreshFields(doc, ctxFor) {
  if (!doc || !Array.isArray(doc.entities)) return doc;
  const ctxs = /* @__PURE__ */ new Map();
  const ctxOf = (layout) => {
    if (typeof ctxFor !== "function" || !layout) return void 0;
    if (!ctxs.has(layout)) ctxs.set(layout, ctxFor(layout));
    return ctxs.get(layout);
  };
  let touched = false;
  const entities = doc.entities.map((e) => {
    if (!e || e.type !== "insert" || !Array.isArray(e.attributes) || !e.attributes.length) return e;
    let attrTouched = false;
    const attributes = e.attributes.map((a) => {
      if (!a || !a.field) return a;
      const layout = fieldLayoutFor(doc, a.fieldLayout);
      const ctx = CONTEXT_CODES.has(a.field) ? ctxOf(layout) : void 0;
      const resolved = resolveField(a.field, layout, doc, ctx);
      if (resolved == null || resolved === a.value) return a;
      attrTouched = true;
      return { ...a, value: resolved };
    });
    if (!attrTouched) return e;
    touched = true;
    const attributeValues = Object.fromEntries([
      ...Object.entries(e.attributeValues || {}),
      ...attributes.filter((a) => a && a.tag).map((a) => [a.tag, a.value == null ? "" : a.value])
    ]);
    return { ...e, attributes, attributeValues };
  });
  return touched ? { ...doc, entities } : doc;
}

// src/cadSheetSymbols.js
var SHEET_SYMBOL_TYPES = Object.freeze(["scalebar", "northarrow"]);
function annotationRotatable(ann) {
  return !!ann && !SHEET_SYMBOL_TYPES.includes(ann.type);
}
var SCALEBAR_MIXED_TEXT = "Scale bar: sheet has mixed scales \u2014 bind it to a viewport";
var SCALEBAR_NONE_TEXT = "Scale bar: this sheet has no viewport to measure";
var LABEL_GAP_MM = 1;
var SHEET_SYMBOL_PEN_MM = 0.18;
var LEGEND_TEXT_MM = 2;
var LEGEND_BAND_MM = 3.2;
var LEGEND_SAMPLE = "Grid convergence -1\xB023\u203245.6\u2033 (TN = GN + \u03B3)";
var MAX_BAR_MM = 1e3;
var MIN_BAR_MM = 2;
var finite = (v) => typeof v === "number" && Number.isFinite(v);
var inRange = (v, lo, hi, d) => finite(v) && v >= lo && v <= hi ? v : d;
function normalizeSheetSymbol(ann) {
  if (!ann || !SHEET_SYMBOL_TYPES.includes(ann.type)) return ann;
  const out = { ...ann };
  delete out.rotation;
  out.x = finite(ann.x) ? ann.x : 0;
  out.y = finite(ann.y) ? ann.y : 0;
  out.viewportId = typeof ann.viewportId === "string" && ann.viewportId ? ann.viewportId : null;
  out.color = typeof ann.color === "string" && ann.color ? ann.color : "#111111";
  if (ann.type === "scalebar") {
    out.targetMm = inRange(ann.targetMm, 10, 400, 50);
    out.groundMetres = finite(ann.groundMetres) && ann.groundMetres > 0 ? ann.groundMetres : null;
    out.divisions = Number.isInteger(ann.divisions) ? Math.min(10, Math.max(1, ann.divisions)) : 4;
    out.barMm = inRange(ann.barMm, 0.5, 20, 2.5);
    out.textMm = inRange(ann.textMm, 1, 20, 2.5);
  } else {
    out.sizeMm = inRange(ann.sizeMm, 5, 100, 15);
    out.showConvergence = ann.showConvergence !== false;
  }
  return out;
}
var denomOf = (s) => finite(s) && s > 0 ? 1 / s : null;
function sheetScaleBinding(layout, viewportId) {
  const vps = (layout && layout.viewports || []).filter(Boolean);
  const bound = viewportId ? vps.find((v) => v.id === viewportId) : null;
  if (bound) return { denom: denomOf(bound.scale), reason: "viewport" };
  if (!vps.length) return { denom: null, reason: "none" };
  const labels = new Set(vps.map((v) => formatScale(v.scale)));
  if (labels.size !== 1) return { denom: null, reason: "mixed" };
  return { denom: denomOf(vps[0].scale), reason: "sheet" };
}
function sheetScaleDenom(layout, viewportId) {
  return sheetScaleBinding(layout, viewportId).denom;
}
function niceGroundLength(targetMm, denom) {
  if (!(targetMm > 0) || !(denom > 0)) return null;
  const maxM = targetMm * denom / 1e3;
  let k = Math.floor(Math.log10(maxM));
  if (10 ** k > maxM) k -= 1;
  if (10 ** (k + 1) <= maxM) k += 1;
  for (const m of [5, 2, 1]) {
    const v = Number((m * 10 ** k).toPrecision(12));
    if (v <= maxM) return v;
  }
  return Number((10 ** k).toPrecision(12));
}
var labelNumber = (v) => String(Number(v.toPrecision(10)));
function scaleBarSpec(ann, denom) {
  if (!(denom > 0)) return null;
  const a = normalizeSheetSymbol({ ...ann, type: "scalebar" });
  let groundMetres = a.groundMetres;
  const explicitMm = groundMetres ? groundMetres * 1e3 / denom : 0;
  if (groundMetres && (explicitMm > MAX_BAR_MM || explicitMm < MIN_BAR_MM)) groundMetres = null;
  if (!groundMetres) groundMetres = niceGroundLength(a.targetMm, denom);
  const lengthMm = groundMetres * 1e3 / denom;
  const km = groundMetres >= 1e3;
  const labels = Array.from(
    { length: a.divisions + 1 },
    (_, i) => labelNumber((km ? groundMetres / 1e3 : groundMetres) * i / a.divisions)
  );
  labels[labels.length - 1] += km ? " km" : " m";
  return { groundMetres, lengthMm, divisions: a.divisions, labels };
}
function scaleBarDrawList(ann, layout) {
  const a = normalizeSheetSymbol({ ...ann, type: "scalebar" });
  const color = a.color;
  const { denom, reason } = sheetScaleBinding(layout, a.viewportId);
  const spec = scaleBarSpec(a, denom);
  const yb = a.y + a.textMm + LABEL_GAP_MM;
  const yt = yb + a.barMm;
  const pen = SHEET_SYMBOL_PEN_MM;
  const half = pen / 2;
  const end = a.x + (spec ? spec.lengthMm : a.targetMm);
  const rect = (x0, x1, fill) => {
    const l = x0 <= a.x + 1e-9 ? x0 + half : x0;
    const r = x1 >= end - 1e-9 ? x1 - half : x1;
    return {
      kind: "poly",
      closed: true,
      color,
      widthMm: pen,
      ...fill ? { fill: color } : {},
      pts: [
        { x: l, y: yb + half },
        { x: r, y: yb + half },
        { x: r, y: yt - half },
        { x: l, y: yt - half }
      ]
    };
  };
  if (!spec) {
    const text = reason === "none" ? SCALEBAR_NONE_TEXT : SCALEBAR_MIXED_TEXT;
    return [
      rect(a.x, a.x + a.targetMm, false),
      { kind: "text", text, x: a.x, y: a.y, height: a.textMm, color, align: "left" }
    ];
  }
  const items = [];
  const seg = spec.lengthMm / spec.divisions;
  for (let i = 0; i < spec.divisions; i += 2)
    items.push(rect(a.x + i * seg, a.x + (i + 1) * seg, true));
  items.push(rect(a.x, a.x + spec.lengthMm, false));
  spec.labels.forEach(
    (text, i) => items.push({
      kind: "text",
      text,
      x: a.x + i * seg,
      y: a.y,
      height: a.textMm,
      color,
      align: i === 0 ? "left" : i === spec.divisions ? "right" : "center"
    })
  );
  return items;
}
function northArrowDrawList(ann, conv = null, { georeferenced = !!conv } = {}) {
  const a = normalizeSheetSymbol({ ...ann, type: "northarrow" });
  const s = a.sizeMm;
  const color = a.color;
  const y0 = a.y + (a.showConvergence ? LEGEND_BAND_MM : 0);
  const cx = a.x + s / 2;
  const P = (fx, fy) => ({ x: cx + fx * s, y: y0 + fy * s });
  const items = [
    {
      kind: "poly",
      widthMm: SHEET_SYMBOL_PEN_MM,
      closed: true,
      color,
      pts: [P(0, 0.8), P(-0.25, 0.05), P(0, 0.25), P(0.25, 0.05)]
    },
    {
      kind: "poly",
      widthMm: SHEET_SYMBOL_PEN_MM,
      closed: true,
      color,
      fill: color,
      pts: [P(0, 0.8), P(0, 0.25), P(0.25, 0.05)]
    },
    {
      kind: "text",
      text: georeferenced ? "GN" : "N",
      x: cx,
      y: y0 + 0.84 * s,
      height: 0.16 * s,
      color,
      align: "center"
    }
  ];
  if (georeferenced && a.showConvergence) {
    if (conv && finite(conv.rad)) {
      const o = P(0, 0.05);
      const len3 = 0.7 * s;
      const tip = { x: o.x + len3 * Math.sin(conv.rad), y: o.y + len3 * Math.cos(conv.rad) };
      items.push({ kind: "poly", widthMm: SHEET_SYMBOL_PEN_MM, color, pts: [o, tip] });
      items.push({
        kind: "text",
        text: "TN",
        x: cx + 0.3 * s,
        // beside the shaft, clear of the head and of GN (γ is ~1°: at the tip it sat on GN)
        y: y0 + 0.45 * s,
        height: 0.12 * s,
        color,
        align: "left"
      });
    }
    items.push({
      kind: "text",
      text: conv && conv.text ? `Grid convergence ${conv.text} (TN = GN + \u03B3)` : "Grid convergence: not yet computed",
      x: a.x,
      y: a.y + 0.5,
      height: LEGEND_TEXT_MM,
      color,
      align: "left"
    });
  }
  return items;
}
function sheetTextBoxMm(text, height) {
  const h = height || 5;
  return { w: Math.max((text || " ").length * h * 0.6, h), h: h * 1.25 };
}
function annotationBoxMm(ann, layout, { georeferenced = false } = {}) {
  if (!ann) return null;
  if (ann.type === "image" || ann.type === "table")
    return { x: ann.x, y: ann.y, w: ann.width, h: ann.height };
  if (ann.type === "scalebar") {
    const a = normalizeSheetSymbol(ann);
    const { denom, reason } = sheetScaleBinding(layout, a.viewportId);
    const spec = scaleBarSpec(a, denom);
    const w = spec ? spec.lengthMm : Math.max(
      a.targetMm,
      sheetTextBoxMm(reason === "none" ? SCALEBAR_NONE_TEXT : SCALEBAR_MIXED_TEXT, a.textMm).w
    );
    return { x: a.x, y: a.y, w, h: a.textMm + LABEL_GAP_MM + a.barMm };
  }
  if (ann.type === "northarrow") {
    const a = normalizeSheetSymbol(ann);
    const legendW = a.showConvergence && georeferenced ? sheetTextBoxMm(LEGEND_SAMPLE, LEGEND_TEXT_MM).w : 0;
    return {
      x: a.x,
      y: a.y,
      w: Math.max(a.sizeMm, legendW),
      h: a.sizeMm + (a.showConvergence ? LEGEND_BAND_MM : 0)
    };
  }
  const b = sheetTextBoxMm(ann.text, ann.height);
  return { x: ann.x, y: ann.y, w: b.w, h: b.h };
}

// src/CADModel/layouts.js
function modelLayout() {
  return { id: "model", name: "Model", isModel: true, paperSize: null, orientation: null, viewports: [] };
}
function getLayout(doc, id) {
  return (doc.layouts || []).find((l) => l.id === id) || null;
}
var DEFAULT_TITLE_FIELDS = {
  project: "",
  figure: "",
  scale: "",
  date: "",
  drawnBy: "",
  company: "",
  notes: ""
};
function addLayout(doc, name, paperSize, orientation = "landscape", extra = {}) {
  const layouts = doc.layouts || [modelLayout()];
  const layout = {
    id: genId("lay"),
    name: name || `Layout${layouts.length}`,
    isModel: false,
    paperSize: paperSize || "A3",
    orientation: orientation === "portrait" ? "portrait" : "landscape",
    viewports: [],
    annotations: [],
    // paper-space text/image objects, in sheet mm (Y-up)
    showTitleBlock: true,
    titleFields: { ...DEFAULT_TITLE_FIELDS },
    logo: null,
    // dataURL of an optional company logo
    ...extra && typeof extra === "object" ? extra : {}
    // e.g. { titleBlock, custom }
  };
  return { ...doc, layouts: [...layouts, layout] };
}
function addAnnotation(doc, layoutId, ann) {
  const a = normalizeSheetSymbol({ id: genId("ann"), ...ann });
  return {
    ...doc,
    layouts: (doc.layouts || []).map((l) => l.id === layoutId && !l.isModel ? { ...l, annotations: [...l.annotations || [], a] } : l)
  };
}
function updateAnnotation(doc, layoutId, annId, patch) {
  return {
    ...doc,
    layouts: (doc.layouts || []).map((l) => {
      if (l.id !== layoutId) return l;
      return {
        ...l,
        annotations: (l.annotations || []).map((a) => a.id === annId ? normalizeSheetSymbol({ ...a, ...patch }) : a)
      };
    })
  };
}
function removeAnnotation(doc, layoutId, annId) {
  return {
    ...doc,
    layouts: (doc.layouts || []).map((l) => l.id === layoutId ? { ...l, annotations: (l.annotations || []).filter((a) => a.id !== annId) } : l)
  };
}
function updateLayout(doc, id, patch) {
  if (id === "model") return doc;
  return {
    ...doc,
    layouts: (doc.layouts || []).map((l) => {
      if (l.id !== id) return l;
      const next = { ...l, ...patch };
      if (patch.titleFields) next.titleFields = { ...l.titleFields || {}, ...patch.titleFields };
      return next;
    })
  };
}
function deleteLayout(doc, id) {
  if (id === "model") return doc;
  const layouts = (doc.layouts || []).filter((l) => l.id !== id);
  if (layouts.length === (doc.layouts || []).length) return doc;
  return { ...doc, layouts };
}
function renameLayout(doc, id, name) {
  if (id === "model" || !name) return doc;
  return { ...doc, layouts: (doc.layouts || []).map((l) => l.id === id ? { ...l, name } : l) };
}
function addViewport(doc, layoutId, worldRect, scale, paperPos) {
  const wr = worldRect || { x: 0, y: 0, width: 1, height: 1 };
  const vp = {
    id: genId("vp"),
    worldRect: { x: wr.x, y: wr.y, width: wr.width, height: wr.height },
    scale: scale || 0.01,
    locked: false,
    // plot-scale lock (AutoCAD's "display locked"), off on creation
    layerOverrides: {},
    ...paperPos ? { paperPos: { x: paperPos.x, y: paperPos.y } } : {}
  };
  return {
    ...doc,
    layouts: (doc.layouts || []).map((l) => l.id === layoutId && !l.isModel ? { ...l, viewports: [...l.viewports || [], vp] } : l)
  };
}
function updateViewport(doc, layoutId, viewportId, patch) {
  return {
    ...doc,
    layouts: (doc.layouts || []).map((l) => {
      if (l.id !== layoutId) return l;
      return {
        ...l,
        viewports: (l.viewports || []).map((v) => {
          if (v.id !== viewportId) return v;
          const next = { ...v, ...patch };
          if (patch.worldRect) next.worldRect = { ...v.worldRect, ...patch.worldRect };
          if (patch.layerOverrides) next.layerOverrides = { ...v.layerOverrides, ...patch.layerOverrides };
          next.locked = patch.locked === void 0 ? !!v.locked : !!patch.locked;
          if (v.locked && "scale" in patch && patch.locked !== false) next.scale = v.scale;
          return next;
        })
      };
    })
  };
}
function removeViewport(doc, layoutId, viewportId) {
  return {
    ...doc,
    layouts: (doc.layouts || []).map((l) => l.id === layoutId ? { ...l, viewports: (l.viewports || []).filter((v) => v.id !== viewportId) } : l)
  };
}
var DEFAULT_MODEL_MARGIN_MM = 10;
var PLOT_SCALE_FIT = "fit";
var EDGES = ["top", "right", "bottom", "left"];
var MAX_MARGIN_MM = 1e4;
var edgeMm = (v) => typeof v === "number" && Number.isFinite(v) && v >= 0 && v <= MAX_MARGIN_MM ? v : null;
function normalizeMargin(m, fallback = DEFAULT_MODEL_MARGIN_MM) {
  const fb = edgeMm(fallback) == null ? DEFAULT_MODEL_MARGIN_MM : Number(fallback);
  if (edgeMm(m) != null) return { top: +m, right: +m, bottom: +m, left: +m };
  const out = {};
  for (const e of EDGES) {
    const v = m && typeof m === "object" ? edgeMm(m[e]) : null;
    out[e] = v == null ? fb : v;
  }
  return out;
}
function storableMargin(m) {
  if (edgeMm(m) != null) return { top: m, right: m, bottom: m, left: m };
  if (!m || typeof m !== "object" || Array.isArray(m)) return null;
  const out = {};
  for (const e of EDGES) {
    if (m[e] === void 0) continue;
    const v = edgeMm(m[e]);
    if (v == null) return null;
    out[e] = v;
  }
  return Object.keys(out).length ? out : null;
}
function usableMargin(m, paper) {
  const out = { ...m };
  if (m.left + m.right >= paper.w) out.left = out.right = 0;
  if (m.top + m.bottom >= paper.h) out.top = out.bottom = 0;
  return out;
}
var MAX_PLOT_SCALE = 1e3;
function normalizePlotScale(s) {
  if (s === PLOT_SCALE_FIT) return PLOT_SCALE_FIT;
  return typeof s === "number" && Number.isFinite(s) && s > 0 && s <= MAX_PLOT_SCALE ? s : null;
}
function normalizePageSetup(raw) {
  if (!raw || typeof raw !== "object") return null;
  const out = {};
  const scale = normalizePlotScale(raw.scale);
  if (scale != null) out.scale = scale;
  const margin = storableMargin(raw.margin);
  if (margin) out.margin = margin;
  if (typeof raw.paperSize === "string" && raw.paperSize) out.paperSize = raw.paperSize;
  if (raw.orientation === "portrait" || raw.orientation === "landscape") {
    out.orientation = raw.orientation;
  }
  if (out.paperSize === "custom") {
    const cw = edgeMm(raw.custom && raw.custom.width);
    const ch = edgeMm(raw.custom && raw.custom.height);
    if (cw && ch) out.custom = { width: cw, height: ch };
    else delete out.paperSize;
  }
  return Object.keys(out).length ? out : null;
}
function setPageSetup(doc, layoutId, patch) {
  let changed = false;
  const layouts = (doc.layouts || []).map((l) => {
    if (l.id !== layoutId) return l;
    const raw = { ...l.pageSetup || {}, ...patch || {} };
    const pm = patch && patch.margin;
    const lm = l.pageSetup && l.pageSetup.margin;
    if (pm && typeof pm === "object" && !Array.isArray(pm) && lm) raw.margin = { ...lm, ...pm };
    const merged = normalizePageSetup(raw);
    if (JSON.stringify(merged) === JSON.stringify(l.pageSetup ?? null)) return l;
    changed = true;
    if (!merged) {
      const { pageSetup: _dropped, ...rest } = l;
      return rest;
    }
    return { ...l, pageSetup: merged };
  });
  return changed ? { ...doc, layouts } : doc;
}

// src/cadTextStyle.js
var TEXTSTYLE_DEFAULTS = {
  fontFamily: DEFAULT_FONT,
  // must equal CAD_FONTS[0] / DEFAULT_FONT (cadModelCore/core.js)
  fixedHeight: 0,
  // 0 = not fixed (AutoCAD convention); >0 forces the height
  widthFactor: 1,
  // horizontal scale (DXF STYLE group 41)
  oblique: 0,
  // slant in degrees (DXF STYLE group 50); positive slants RIGHT
  bold: false,
  // synthetic WEIGHT — Konva fontStyle / SVG font-weight
  italic: false,
  // synthetic SLANT — distinct from `oblique` (see below)
  // ANNOTATIVE (ROADMAP 2.2 criterion 1) — the text twin of DIMSTYLE's flag. When true,
  // the resolved height (the entity's own `height`, else `fixedHeight`) is read as
  // PAPER MILLIMETRES and converted to model units by the annotation scale in force
  // where the text is drawn: effectiveEntityTextHeight below is the ONE place that
  // conversion happens. Default false ⇒ every existing drawing is untouched.
  annotative: false
};
var RESERVED_STYLE_KEYS = ["__proto__", "constructor", "prototype"];
function isSafeStyleName(name) {
  return typeof name === "string" && name !== "" && !RESERVED_STYLE_KEYS.includes(name);
}
function rawStyle2(e, textStyleDefs, currentStyle) {
  if (!textStyleDefs) return {};
  const cur = currentStyle && textStyleDefs[currentStyle] || {};
  const named = e && e.textStyle && textStyleDefs[e.textStyle] || {};
  return { ...cur, ...named };
}
function resolveTextStyle(e, textStyleDefs, currentStyle) {
  const merged = { ...TEXTSTYLE_DEFAULTS, ...rawStyle2(e, textStyleDefs, currentStyle) };
  merged.bold = merged.bold === true;
  merged.italic = merged.italic === true;
  merged.annotative = merged.annotative === true;
  if (e) {
    if (typeof e.fontFamily === "string" && e.fontFamily) merged.fontFamily = e.fontFamily;
    if (Number.isFinite(e.height) && e.height > 0) merged.fixedHeight = e.height;
    if (typeof e.bold === "boolean") merged.bold = e.bold;
    if (typeof e.italic === "boolean") merged.italic = e.italic;
    merged.annotative = isAnnotative(e, merged);
  }
  return merged;
}
function effectiveEntityTextHeight(e, textStyleDefs, currentStyle, sizing) {
  const style = resolveTextStyle(e, textStyleDefs, currentStyle);
  if (style.annotative) {
    if (Number.isFinite(e && e.paperHeight) && e.paperHeight > 0) {
      return paperMmToModel(e.paperHeight, sizingAnnoScale(sizing));
    }
    const styleOnly = resolveTextStyle({ textStyle: e && e.textStyle }, textStyleDefs, currentStyle);
    return paperMmToModel(styleOnly.fixedHeight, sizingAnnoScale(sizing));
  }
  return e && e.height;
}

// src/cadColorIndex.js
var BASIC = {
  1: 16711680,
  2: 16776960,
  3: 65280,
  4: 65535,
  5: 255,
  6: 16711935,
  7: 16777215,
  8: 8421504,
  9: 12632256
};
var GRAYS = [3355443, 5987163, 8684676, 11382189, 14079702, 16777215];
var BRIGHT = [1, 1, 0.65, 0.65, 0.5, 0.5, 0.3, 0.3, 0.15, 0.15];
function hueRgb(deg) {
  const h = (deg % 360 + 360) % 360 / 60;
  const x = 255 * (1 - Math.abs(h % 2 - 1));
  if (h < 1) return [255, x, 0];
  if (h < 2) return [x, 255, 0];
  if (h < 3) return [0, 255, x];
  if (h < 4) return [0, x, 255];
  if (h < 5) return [x, 0, 255];
  return [255, 0, x];
}
var pack = (r, g, b) => Math.round(r) << 16 | Math.round(g) << 8 | Math.round(b);
function aci2int(index) {
  const i = Number(index);
  if (!Number.isInteger(i) || i <= 0 || i >= 256) return null;
  if (i < 10) return BASIC[i];
  if (i >= 250) return GRAYS[i - 250];
  const n = i - 10;
  const hue = Math.floor(n / 10);
  const level = n % 10;
  const peak = 255 * BRIGHT[level];
  const tint = level % 2 === 1;
  const [r0, g0, b0] = hueRgb(hue * 15);
  const ch = (c) => {
    const v = c / 255 * peak;
    return tint ? v + (peak - v) * 0.5 : v;
  };
  return pack(ch(r0), ch(g0), ch(b0));
}
function int2aci(color) {
  let n = null;
  if (typeof color === "number" && Number.isFinite(color)) n = color & 16777215;
  else if (typeof color === "string") {
    const hex = color.trim().replace(/^#/, "");
    const full = hex.length === 3 ? hex.replace(/./g, (c) => c + c) : hex;
    if (/^[0-9a-f]{6}$/i.test(full)) n = parseInt(full, 16);
  }
  if (n == null) return null;
  const r = n >> 16 & 255;
  const g = n >> 8 & 255;
  const b = n & 255;
  let best = null;
  let bestD = Infinity;
  for (let i = 1; i < 256; i++) {
    const c = aci2int(i);
    if (c == null) continue;
    const dr = (c >> 16 & 255) - r;
    const dg = (c >> 8 & 255) - g;
    const db = (c & 255) - b;
    const d = dr * dr + dg * dg + db * db;
    if (d < bestD) {
      bestD = d;
      best = i;
      if (d === 0) break;
    }
  }
  return best;
}

// src/cadPlotStyle.js
var PLOT_STYLE_MODES = ["color", "mono"];
var PLOT_STYLE_MAX_ENTRIES = 255;
function emptyPlotStyleTable() {
  return { mode: "color", entries: [] };
}
function normalizeColorKey(c) {
  if (typeof c === "number") {
    const n = aci2int(c);
    return n == null ? null : `#${n.toString(16).padStart(6, "0")}`;
  }
  if (typeof c !== "string") return null;
  let h = c.trim().replace(/^#/, "").toLowerCase();
  if (!/^[0-9a-f]{3}$|^[0-9a-f]{6}$/.test(h)) return null;
  if (h.length === 3)
    h = h.split("").map((x) => x + x).join("");
  return `#${h}`;
}
function normalizePlotStyleTable(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const mode = raw.mode === "mono" ? "mono" : "color";
  const seen = /* @__PURE__ */ new Map();
  for (const e of Array.isArray(raw.entries) ? raw.entries : []) {
    if (!e || typeof e !== "object") continue;
    const color = normalizeColorKey(e.color);
    if (!color) continue;
    const lw = e.lineweightMm == null || e.lineweightMm === "" ? NaN : Number(e.lineweightMm);
    const lineweightMm = Number.isFinite(lw) && lw >= 0 ? lw : null;
    const plotColor = normalizeColorKey(e.plotColor);
    if (lineweightMm == null && plotColor == null) continue;
    seen.set(color, { color, lineweightMm, plotColor });
    if (seen.size >= PLOT_STYLE_MAX_ENTRIES) break;
  }
  return { mode, entries: [...seen.values()] };
}
function plotPenFor(table, colorHex) {
  if (!table || !Array.isArray(table.entries) || !table.entries.length) return null;
  const key = normalizeColorKey(colorHex);
  if (!key) return null;
  return table.entries.find((e) => e.color === key) || null;
}
function makePlotPenResolver(table) {
  if (!table || !Array.isArray(table.entries) || !table.entries.length) return () => null;
  const byColor = new Map(table.entries.map((e) => [e.color, e]));
  const cache = /* @__PURE__ */ new Map();
  return (colorHex) => {
    if (cache.has(colorHex)) return cache.get(colorHex);
    const key = normalizeColorKey(colorHex);
    const pen = key ? byColor.get(key) || null : null;
    cache.set(colorHex, pen);
    return pen;
  };
}
function isMonoPlot(table, colorMode) {
  return colorMode === "mono" || !!(table && table.mode === "mono");
}

// src/cadLayerRename.js
var LAYER_NAME_MAX_LENGTH = 255;
var ILLEGAL_LAYER_NAME_CHARS = /[<>/\\":;?*|,=`]/;
var CONTROL_CHARS = /[\u0000-\u001f\u007f]/;
function layerNameError(name, existingNames, opts) {
  if (typeof name !== "string") return "Layer name must be text.";
  const trimmed = name.trim();
  if (!trimmed) return "Layer name cannot be empty.";
  if (trimmed.length > LAYER_NAME_MAX_LENGTH) {
    return `Layer name cannot be longer than ${LAYER_NAME_MAX_LENGTH} characters.`;
  }
  if (CONTROL_CHARS.test(trimmed)) return "Layer name cannot contain control characters.";
  if (ILLEGAL_LAYER_NAME_CHARS.test(trimmed)) {
    return 'Layer name cannot contain any of  < > / \\ " : ; ? * | , = `';
  }
  const except = opts && typeof opts.except === "string" ? opts.except : null;
  const folded = layerNameKey(trimmed);
  for (const existing of existingNames || []) {
    if (typeof existing !== "string") continue;
    if (except !== null && existing === except) continue;
    if (layerNameKey(existing) === folded) {
      return `A layer named "${existing}" already exists.`;
    }
  }
  return null;
}
function layerNameKey(name) {
  return typeof name === "string" ? name.toLowerCase() : "";
}
function normalizeLayerName(name) {
  return typeof name === "string" ? name.trim() : "";
}
function renameKey(map, oldName, newName) {
  if (!map || typeof map !== "object") return map;
  if (!Object.prototype.hasOwnProperty.call(map, oldName)) return map;
  const out = /* @__PURE__ */ Object.create(null);
  for (const k of Object.keys(map)) {
    if (k === oldName) out[newName] = map[oldName];
    else if (k !== newName) out[k] = map[k];
  }
  return { ...out };
}
function layerRenameError(doc, oldName, newName) {
  if (!doc) return "There is no drawing to rename in.";
  if (oldName === DEFAULT_LAYER) return `Layer "${DEFAULT_LAYER}" cannot be renamed.`;
  const layers = (doc.layers || []).filter(Boolean);
  const matches = layers.filter((l) => l.name === oldName);
  if (!matches.length) return `Layer "${oldName}" no longer exists.`;
  if (matches.length > 1) {
    return `This drawing has ${matches.length} layers named "${oldName}"; rename cannot tell them apart.`;
  }
  return layerNameError(
    newName,
    layers.map((l) => l.name),
    { except: oldName }
  );
}
function renameLayer(doc, oldName, newName) {
  if (layerRenameError(doc, oldName, newName)) return null;
  const layers = doc.layers || [];
  const to = normalizeLayerName(newName);
  if (to === oldName) return null;
  const isOld = (e) => (e && e.layer != null ? e.layer : DEFAULT_LAYER) === oldName;
  let next = {
    ...doc,
    layers: layers.map((l) => l && l.name === oldName ? { ...l, name: to } : l)
  };
  const ents = doc.entities || [];
  if (ents.some(isOld)) {
    next = { ...next, entities: ents.map((e) => isOld(e) ? { ...e, layer: to } : e) };
  }
  const defs = doc.blockDefs || {};
  let defsTouched = false;
  const nextDefs = /* @__PURE__ */ Object.create(null);
  for (const bn of Object.keys(defs)) {
    const def = defs[bn];
    const bents = def && def.entities || [];
    if (bents.some(isOld)) {
      defsTouched = true;
      nextDefs[bn] = { ...def, entities: bents.map((e) => isOld(e) ? { ...e, layer: to } : e) };
    } else {
      nextDefs[bn] = def;
    }
  }
  if (defsTouched) next = { ...next, blockDefs: { ...nextDefs } };
  if (Array.isArray(doc.layouts)) {
    let touched = false;
    const layouts = doc.layouts.map((lay) => {
      const vps = lay && lay.viewports;
      if (!Array.isArray(vps) || !vps.length) return lay;
      let vpTouched = false;
      const nvps = vps.map((v) => {
        const ov = renameKey(v && v.layerOverrides, oldName, to);
        if (ov === (v && v.layerOverrides)) return v;
        vpTouched = true;
        return { ...v, layerOverrides: ov };
      });
      if (!vpTouched) return lay;
      touched = true;
      return { ...lay, viewports: nvps };
    });
    if (touched) next = { ...next, layouts };
  }
  const settings = doc.settings || {};
  const settingsPatch = {};
  if (settings.currentLayer === oldName) settingsPatch.currentLayer = to;
  const iso = settings.layerIsolation;
  if (iso && iso.prev) {
    const prev = renameKey(iso.prev, oldName, to);
    const prevCurrentMoves = iso.prevCurrent === oldName;
    if (prev !== iso.prev || prevCurrentMoves) {
      settingsPatch.layerIsolation = {
        ...iso,
        prev,
        ...prevCurrentMoves ? { prevCurrent: to } : {}
      };
    }
  }
  return { doc: next, settingsPatch, from: oldName, to };
}

// src/CADModel/georef.js
var GEOREF_CRS_PATTERN = /^EPSG:\d{4,6}$/;
var DEFAULT_GEOREF = null;
var MAX_CRS_NAME = 80;
var MAX_BASEMAPS = 32;
var MAX_SOURCE_ID = 128;
var finite2 = (v) => typeof v === "number" && Number.isFinite(v);
var CONTROL_CHARS2 = /[\p{Cc}\p{Cf}]/gu;
var BASEMAP_ID_PATTERN = /^[\w.:#~-]{1,64}$/;
function isBasemapId(id) {
  return typeof id === "string" && BASEMAP_ID_PATTERN.test(id);
}
function derivedBasemapId(sourceId, index, seen) {
  const base = `${sourceId.replace(/[^\w.:~-]/g, "_").slice(0, 48)}#${index}`;
  let id = base;
  for (let k = 1; seen.has(id); k++) id = `${base}~${k}`;
  return id;
}
function normalizeBasemap(b, index, seen) {
  if (!b || typeof b !== "object") return null;
  if (typeof b.sourceId !== "string" || !b.sourceId.trim()) return null;
  const sourceId = b.sourceId.trim().slice(0, MAX_SOURCE_ID);
  const opacity = Number(b.opacity);
  let id = typeof b.id === "string" ? b.id.trim() : "";
  if (!BASEMAP_ID_PATTERN.test(id) || seen.has(id)) id = derivedBasemapId(sourceId, index, seen);
  seen.add(id);
  return {
    id,
    sourceId,
    opacity: Number.isFinite(opacity) ? Math.min(1, Math.max(0, opacity)) : 1,
    visible: b.visible == null ? true : !!b.visible,
    plot: b.plot == null ? true : !!b.plot
  };
}
function normalizeGeoref(g) {
  if (!g || typeof g !== "object" || Array.isArray(g)) return null;
  const crs = typeof g.crs === "string" ? g.crs.trim().toUpperCase() : "";
  if (!GEOREF_CRS_PATTERN.test(crs) || /^EPSG:0+$/.test(crs)) return null;
  const o = g.origin;
  if (!o || typeof o !== "object" || !finite2(o.e) || !finite2(o.n)) return null;
  const out = { crs, origin: { e: o.e, n: o.n }, basemaps: [] };
  if (typeof g.crsName === "string") {
    const name = g.crsName.replace(CONTROL_CHARS2, "").trim().slice(0, MAX_CRS_NAME);
    if (name) out.crsName = name;
  }
  if (Array.isArray(g.basemaps)) {
    const seen = /* @__PURE__ */ new Set();
    for (let i = 0; i < g.basemaps.length; i++) {
      if (out.basemaps.length >= MAX_BASEMAPS) break;
      const nb = normalizeBasemap(g.basemaps[i], i, seen);
      if (nb) out.basemaps.push(nb);
    }
  }
  return isCanonicalTwin(g, out) ? g : out;
}
function isCanonicalTwin(g, out) {
  try {
    if (JSON.stringify(g) !== JSON.stringify(out)) return false;
  } catch {
    return false;
  }
  if (Object.keys(g).length !== Object.keys(out).length) return false;
  if (Object.keys(g.origin).length !== 2) return false;
  if (!Array.isArray(g.basemaps)) return false;
  return g.basemaps.every((b) => b && Object.keys(b).length === 5);
}
function sameGeoref(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  return JSON.stringify(normalizeGeoref(a)) === JSON.stringify(normalizeGeoref(b));
}
function sameGeorefFrame(a, b) {
  const na = normalizeGeoref(a);
  const nb = normalizeGeoref(b);
  if (!na || !nb) return na === nb;
  return na.crs === nb.crs && (na.crsName ?? null) === (nb.crsName ?? null) && na.origin.e === nb.origin.e && na.origin.n === nb.origin.n;
}
function gisMode(georef) {
  if (!georef || typeof georef !== "object") return false;
  const list = Array.isArray(georef.basemaps) ? georef.basemaps : [];
  return list.length === 0 || list.some((b) => b && b.visible);
}
var GEOREF_MAX_BASEMAPS = MAX_BASEMAPS;
function addBasemap(georef, desc) {
  if (!georef) return { ok: false, reason: "no-georef" };
  const list = georef.basemaps ?? [];
  if (list.length >= MAX_BASEMAPS) return { ok: false, reason: "limit" };
  if (!desc || typeof desc.id !== "string" || !BASEMAP_ID_PATTERN.test(desc.id)) {
    return { ok: false, reason: "invalid" };
  }
  if (list.some((b) => b.id === desc.id)) return { ok: false, reason: "duplicate" };
  const next = normalizeGeoref({ ...georef, basemaps: [...list, desc] });
  if (!next || next.basemaps.length !== list.length + 1) return { ok: false, reason: "invalid" };
  return { ok: true, georef: next };
}
function updateBasemap(georef, id, patch) {
  if (!georef) return { ok: false, reason: "no-georef" };
  const list = georef.basemaps ?? [];
  const i = list.findIndex((b) => b.id === id);
  if (i < 0) return { ok: false, reason: "not-found" };
  const cur = list[i];
  const merged = { ...cur };
  for (const k of ["opacity", "visible", "plot"]) if (patch && k in patch) merged[k] = patch[k];
  const next = normalizeGeoref({ ...georef, basemaps: list.map((b, j) => j === i ? merged : b) });
  const nb = next.basemaps[i];
  if (nb.opacity === cur.opacity && nb.visible === cur.visible && nb.plot === cur.plot) {
    return { ok: true, georef, unchanged: true };
  }
  return { ok: true, georef: next };
}
function removeBasemap(georef, id) {
  if (!georef) return { ok: false, reason: "no-georef" };
  const list = georef.basemaps ?? [];
  if (!list.some((b) => b.id === id)) return { ok: false, reason: "not-found" };
  return { ok: true, georef: normalizeGeoref({ ...georef, basemaps: list.filter((b) => b.id !== id) }) };
}
function clearBasemaps(georef) {
  if (!georef) return { ok: false, reason: "no-georef" };
  const list = georef.basemaps ?? [];
  if (list.length === 0) return { ok: false, reason: "no-basemap" };
  return { ok: true, georef: normalizeGeoref({ ...georef, basemaps: [] }) };
}
function setBasemapsVisible(georef, visible) {
  if (!georef) return { ok: false, reason: "no-georef" };
  const list = georef.basemaps ?? [];
  if (list.length === 0) return { ok: false, reason: "no-basemap" };
  const v = !!visible;
  if (list.every((b) => b.visible === v)) return { ok: true, georef, unchanged: true };
  return {
    ok: true,
    georef: normalizeGeoref({ ...georef, basemaps: list.map((b) => ({ ...b, visible: v })) })
  };
}
function worldToGrid(georef, p) {
  if (!georef || !georef.origin) return { e: p.x, n: p.y };
  return { e: p.x + georef.origin.e, n: p.y + georef.origin.n };
}
function gridToWorld(georef, g) {
  if (!georef || !georef.origin) return { x: g.e, y: g.n };
  return { x: g.e - georef.origin.e, y: g.n - georef.origin.n };
}
function formatGridCoord(georef, p, digits = 3) {
  if (!georef || !georef.origin || !p) return null;
  const g = worldToGrid(georef, p);
  const f = (v) => Number.isFinite(v) ? v.toFixed(digits) : "\u2014";
  return `E ${f(g.e)} N ${f(g.n)}`;
}
function roundOrigin(origin, step = 100) {
  const s = step > 0 ? step : 100;
  return { e: Math.round(origin.e / s) * s, n: Math.round(origin.n / s) * s };
}

// src/cadGroup.js
var GROUP_NAME_MAX = 64;
function normalizeGroupName(name) {
  if (typeof name !== "string") return null;
  const t = name.replace(/\s+/g, " ").trim();
  if (!t) return null;
  return t.length > GROUP_NAME_MAX ? t.slice(0, GROUP_NAME_MAX) : t;
}
var documentGroups = (doc) => doc && Array.isArray(doc.groups) ? doc.groups : [];
function groupOf(doc, entityId) {
  if (!entityId) return null;
  for (const g of documentGroups(doc)) {
    if (g && Array.isArray(g.entityIds) && g.entityIds.includes(entityId)) return g;
  }
  return null;
}
function groupById(doc, groupId) {
  return documentGroups(doc).find((g) => g && g.id === groupId) || null;
}
function uniqueGroupName(doc, base = "Group") {
  const taken = new Set(documentGroups(doc).map((g) => g && g.name || ""));
  const clean = normalizeGroupName(base) || "Group";
  if (!taken.has(clean)) return clean;
  for (let i = 2; i < 1e4; i++) {
    const candidate = `${clean} ${i}`;
    if (!taken.has(candidate)) return candidate;
  }
  return `${clean} ${genId("g")}`;
}
function groupRefusal(doc, op, arg) {
  if (!doc) return "no-document";
  if (op === "create") {
    const ids = [...new Set(arg && arg.entityIds || [])];
    if (ids.length < 2) return "need-two-objects";
    const known = new Set((doc.entities || []).map((e) => e.id));
    if (!ids.every((id) => known.has(id))) return "unknown-object";
    if (ids.some((id) => groupOf(doc, id))) return "already-grouped";
    if (arg && arg.name != null && normalizeGroupName(arg.name) === null) return "name-invalid";
    if (arg && arg.name != null) {
      const wanted = normalizeGroupName(arg.name);
      if (documentGroups(doc).some((g) => g.name === wanted)) return "name-taken";
    }
    return null;
  }
  if (op === "ungroup") {
    return groupById(doc, arg && arg.groupId) ? null : "unknown-group";
  }
  if (op === "rename") {
    if (!groupById(doc, arg && arg.groupId)) return "unknown-group";
    const wanted = normalizeGroupName(arg && arg.name);
    if (wanted === null) return "name-invalid";
    if (documentGroups(doc).some((g) => g.name === wanted && g.id !== arg.groupId))
      return "name-taken";
    return null;
  }
  return "unknown-operation";
}
function createGroup(doc, entityIds, name) {
  const ids = [...new Set(entityIds || [])];
  if (groupRefusal(doc, "create", { entityIds: ids, name })) return null;
  const group2 = {
    id: genId("grp"),
    name: name != null ? normalizeGroupName(name) : uniqueGroupName(doc),
    entityIds: ids,
    // AutoCAD's PICKSTYLE: a group can be made non-selectable so its members can
    // be edited individually without ungrouping. Stored per group because that
    // is where the user sets it (the group list), and read by
    // `expandGroupSelection`, which is the only consumer.
    selectable: true
  };
  return { doc: { ...doc, groups: [...documentGroups(doc), group2] }, group: group2 };
}
function ungroup(doc, groupId) {
  if (groupRefusal(doc, "ungroup", { groupId })) return null;
  return { ...doc, groups: documentGroups(doc).filter((g) => g.id !== groupId) };
}
function renameGroup(doc, groupId, name) {
  if (groupRefusal(doc, "rename", { groupId, name })) return null;
  const wanted = normalizeGroupName(name);
  return {
    ...doc,
    groups: documentGroups(doc).map((g) => g.id === groupId ? { ...g, name: wanted } : g)
  };
}
function setGroupSelectable(doc, groupId, selectable) {
  if (!groupById(doc, groupId)) return null;
  return {
    ...doc,
    groups: documentGroups(doc).map(
      (g) => g.id === groupId ? { ...g, selectable: !!selectable } : g
    )
  };
}
function expandGroupSelection(doc, ids, opts = {}) {
  const picked = ids || [];
  if (opts.enterGroup || !documentGroups(doc).length) return [...picked];
  const out = [];
  const seen = /* @__PURE__ */ new Set();
  const push = (id) => {
    if (!seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  };
  const docOrder = new Map((doc.entities || []).map((e, i) => [e.id, i]));
  for (const id of picked) {
    const g = groupOf(doc, id);
    if (!g || g.selectable === false) {
      push(id);
      continue;
    }
    push(id);
    const members = g.entityIds.filter((m) => m !== id && docOrder.has(m)).sort((a, b) => docOrder.get(a) - docOrder.get(b));
    for (const m of members) push(m);
  }
  return out;
}
function pruneGroups(doc) {
  const groups = documentGroups(doc);
  if (!groups.length) return doc;
  const live = new Set((doc.entities || []).map((e) => e.id));
  let changed = false;
  const next = [];
  for (const g of groups) {
    const kept = (g.entityIds || []).filter((id) => live.has(id));
    if (kept.length < 2) {
      changed = true;
      continue;
    }
    if (kept.length !== (g.entityIds || []).length) {
      changed = true;
      next.push({ ...g, entityIds: kept });
    } else {
      next.push(g);
    }
  }
  return changed ? { ...doc, groups: next } : doc;
}
function normalizeGroups(doc) {
  const raw = Array.isArray(doc.groups) ? doc.groups : [];
  const seenIds = /* @__PURE__ */ new Set();
  const shaped = [];
  for (const g of raw) {
    if (!g || typeof g !== "object") continue;
    const id = typeof g.id === "string" && g.id ? g.id : genId("grp");
    if (seenIds.has(id)) continue;
    const entityIds = Array.isArray(g.entityIds) ? [...new Set(g.entityIds.filter((x) => typeof x === "string" && x))] : [];
    if (entityIds.length < 2) continue;
    seenIds.add(id);
    shaped.push({ id, entityIds, selectable: g.selectable !== false, name: g.name });
  }
  const ownerOf = /* @__PURE__ */ new Map();
  for (const g of [...shaped].sort((x, y) => x.id.localeCompare(y.id))) {
    for (const m of g.entityIds) if (!ownerOf.has(m)) ownerOf.set(m, g.id);
  }
  const seenNames = /* @__PURE__ */ new Set();
  const clean = [];
  for (const g of shaped) {
    const entityIds = g.entityIds.filter((m) => ownerOf.get(m) === g.id);
    if (entityIds.length < 2) continue;
    let name = normalizeGroupName(g.name);
    if (name === null || seenNames.has(name)) {
      let n = clean.length + 1;
      name = `Group ${n}`;
      while (seenNames.has(name)) name = `Group ${++n}`;
    }
    seenNames.add(name);
    clean.push({ id: g.id, name, entityIds, selectable: g.selectable });
  }
  return clean;
}

// src/CADModel/document.js
var DRAWING_SETTINGS_KEYS = ["ltscale", "lwDisplay", "plotStyle"];
function isDrawingSetting(key) {
  return DRAWING_SETTINGS_KEYS.includes(key);
}
function defaultSettings() {
  return {
    ltscale: 1,
    // LTSCALE — global linetype-scale multiplier
    lwDisplay: true,
    // LWDISPLAY — draw assigned lineweights (off → 1px hairline)
    currentLayer: DEFAULT_LAYER,
    // CLAYER
    currentColor: null,
    // CECOLOR (null = ByLayer)
    currentLinetype: "BYLAYER",
    // CELTYPE
    currentLineweight: null,
    // CELWEIGHT (mm; null = ByLayer)
    currentLtScale: 1,
    // CELTSCALE — per-object linetype scale for new geometry
    currentDimStyle: "Standard",
    // CDIMSTYLE
    currentTextStyle: "Standard",
    // CTEXTSTYLE (Spec 06) — style stamped onto new text
    // PLOT-STYLE-1: the drawing's colour-dependent plot style table (cadPlotStyle.js),
    // AutoCAD's CTB. null = plot as drawn. It lives on the settings header rather than
    // in the Plot dialog's React state because a pen assignment is a property OF THE
    // DRAWING — it must survive a reload, and reach a collaborator, like LTSCALE does.
    plotStyle: null,
    // [LAYER-OPS-1]: the LAYISO restore marker (see cadLayerIsolate.js). null = no
    // isolation active. On the header for the same reason as plotStyle — it must
    // survive a reload and reach a collaborator, or LAYUNISO dies with the session.
    layerIsolation: null
  };
}
function createBlankDocument() {
  return {
    version: 1,
    layers: [{ name: DEFAULT_LAYER, color: "#ffffff", visible: true, locked: false, frozen: false, plot: true }],
    entities: [],
    // ── v4 named-definition maps (all persisted to IndexedDB with the doc) ──
    linetypeDefs: cloneDefs(LINETYPE_DEFS),
    // Built-in 'Standard' style is empty: resolveDimStyle (cadDimension.js) fills
    // every field from DIMSTYLE_DEFAULTS, so an empty style == "all defaults".
    dimStyleDefs: { Standard: {} },
    // Spec 06: named text styles (STYLE). Built-in 'Standard' is empty for the same
    // reason — resolveTextStyle (cadTextStyle.js) fills every field from TEXTSTYLE_DEFAULTS.
    textStyleDefs: { Standard: {} },
    blockDefs: cloneDefs(GEOTECH_BLOCKS),
    // built-in geotech symbol library
    // ── V4b paper space: named layouts. The Model layout always exists first and
    // can never be deleted. Paper layouts carry viewports onto model space. ──
    layouts: [modelLayout()],
    // ── V4b P3 XREF underlays: one locked snapshot per referenced external file
    // ({ entities, layers, sourceFile }). XREF entities place instances. ──
    xrefDefs: {},
    // ── [RASTER-IMAGE-1] raster definitions: { id → { mime, data(base64), width,
    // height, source, downscaled } }, keyed by the `imageDefId` an `image` entity
    // carries. WRITE-ONCE and frozen, because the undo snapshot ALIASES this map
    // rather than deep-copying it the way it must for xrefDefs — see
    // CADModel/image.js for why that is the difference between working and
    // putting 60 x N x ~1 MB of base64 in the tab. ──
    imageDefs: {},
    // ── V4c P2 named views: saved camera positions [{ id, name, view:{scale,x,y} }].
    // User navigation data — persisted with the doc, but NOT tracked for undo. ──
    namedViews: [],
    // ── [C4] named GROUPS: [{ id, name, entityIds, selectable }]. A saved
    // selection set, NOT a block — the members stay ordinary model-space
    // entities. Membership lives in the record rather than on the entity; see
    // cadGroup.js for why, and for the referential-integrity enumeration. ──
    groups: [],
    // ── v5 geometric relations (cadConstraints.js): auto-inferred at draw time,
    // CHECKED not solved (violations flag red) — undoable with their geometry. ──
    constraints: [],
    // ── Drawing settings header (LTSCALE / LWDISPLAY / CE* current props). Persisted
    // with the doc; NOT on the undo stack (the namedViews discipline). ──
    settings: defaultSettings(),
    // ── [G1-GEOREF] Georeference: which projected CRS and where local (0,0) sits
    // on it (CADModel/georef.js). null = not georeferenced. Undoable (an explicit
    // GEOREF is an edit, not a preference) and synced as a `props` key. ──
    georef: DEFAULT_GEOREF,
    meta: {}
  };
}
function repairEntityRadii(entities) {
  const out = [];
  for (const e of entities) {
    if (!e || typeof e !== "object") {
      out.push(e);
      continue;
    }
    if (e.type === "circle" || e.type === "arc") {
      const r = Math.abs(e.r);
      if (!Number.isFinite(r) || r <= 0) continue;
      out.push(r === e.r ? e : { ...e, r });
    } else if (e.type === "ellipse") {
      const rx = Math.abs(e.rx);
      const ry = Math.abs(e.ry);
      if (!Number.isFinite(rx) || rx <= 0 || !Number.isFinite(ry) || ry <= 0) continue;
      out.push(rx === e.rx && ry === e.ry ? e : { ...e, rx, ry });
    } else if (e.type === "point" && e.pointSize != null) {
      const ps = Math.abs(e.pointSize);
      if (!Number.isFinite(ps) || ps <= 0) {
        const { pointSize: _pointSize, ...rest } = e;
        out.push(rest);
      } else {
        out.push(ps === e.pointSize ? e : { ...e, pointSize: ps });
      }
    } else {
      out.push(e);
    }
  }
  return out;
}
function sanitizeRegions(entities) {
  return entities.map((e) => {
    if (!e || e.type !== "region") return e;
    const src = Array.isArray(e.rings) ? e.rings : [];
    const rings = src.map(
      (ring) => Array.isArray(ring) ? ring.filter((p) => p && Number.isFinite(p.x) && Number.isFinite(p.y)) : null
    ).filter((ring) => ring && ring.length >= 3);
    if (!rings.length) return null;
    const same = rings.length === src.length && rings.every((r, i) => Array.isArray(src[i]) && r.length === src[i].length);
    return same ? e : { ...e, rings };
  }).filter(Boolean);
}
function sanitizeTableCells(entities) {
  return entities.map((e) => {
    if (!e || e.type !== "table") return e;
    const validSizes = (a) => Array.isArray(a) && a.length > 0 && a.every((v) => Number.isFinite(v));
    if (!validSizes(e.colWidths) || !validSizes(e.rowHeights)) return null;
    const rows = e.rowHeights.length;
    const cols = e.colWidths.length;
    const src = e.cells && typeof e.cells === "object" ? e.cells : {};
    const keys = Object.keys(src);
    const cells = {};
    let kept = 0;
    let dropped = 0;
    let changed = e.cells == null || typeof e.cells !== "object";
    for (const k of keys) {
      const v = src[k];
      if (!TABLE_CELL_KEY_RE.test(k) || !v || typeof v !== "object" || typeof v.text !== "string") {
        dropped += 1;
        continue;
      }
      const [r, c] = k.split(",").map(Number);
      if (r >= rows || c >= cols || kept >= TABLE_MAX_CELLS) {
        dropped += 1;
        continue;
      }
      const canonical = `${r},${c}`;
      if (canonical !== k) changed = true;
      const justify = v.justify === "C" || v.justify === "R" || v.justify === "L" ? { justify: v.justify } : null;
      if (justify == null && v.justify != null) changed = true;
      if (Object.keys(v).length > (justify ? 2 : 1)) changed = true;
      cells[canonical] = { text: v.text, ...justify };
      kept += 1;
    }
    if (dropped > 0) {
      console.warn(
        `CADModel: dropped ${dropped} malformed/out-of-grid/over-cap table cell(s) on load (${e.id})`
      );
    }
    return dropped === 0 && !changed ? e : { ...e, cells };
  }).filter(Boolean);
}
function sanitizeConstraints(list) {
  if (!Array.isArray(list)) return [];
  return list.filter(
    (c) => c && typeof c === "object" && typeof c.id === "string" && typeof c.type === "string" && Array.isArray(c.ids) && c.ids.length > 0 && c.ids.every((id) => typeof id === "string") && (c.refs == null || Array.isArray(c.refs))
  );
}
function sanitizeStyleDefs(defs) {
  const out = { Standard: {} };
  if (defs && typeof defs === "object") {
    for (const name of Object.keys(defs)) {
      if (name === "Standard" || !isSafeStyleName(name)) continue;
      out[name] = defs[name];
    }
    if (defs.Standard && typeof defs.Standard === "object") out.Standard = defs.Standard;
  }
  return out;
}
function adoptClaimedLayers(layers, entities, blockDefs) {
  const known = /* @__PURE__ */ new Set();
  const byFold = /* @__PURE__ */ new Map();
  for (const l of layers) {
    if (!l || typeof l.name !== "string") continue;
    known.add(l.name);
    const k = layerNameKey(l.name);
    const held = byFold.get(k);
    if (held === void 0 || l.name < held) byFold.set(k, l.name);
  }
  const missing = /* @__PURE__ */ new Set();
  const recase = /* @__PURE__ */ new Map();
  const claim = (e) => {
    const name = e && e.layer;
    if (typeof name !== "string" || !name) return;
    if (known.has(name)) return;
    const existing = byFold.get(layerNameKey(name));
    if (existing) recase.set(name, existing);
    else missing.add(name);
  };
  for (const e of entities) claim(e);
  for (const bn of Object.keys(blockDefs || {})) {
    const ents = blockDefs[bn] && blockDefs[bn].entities;
    if (Array.isArray(ents)) for (const e of ents) claim(e);
  }
  if (!missing.size && !recase.size) return { layers, recase: null, byFold };
  for (const name of missing) {
    const k = layerNameKey(name);
    const held = byFold.get(k);
    if (held === void 0 || name < held) byFold.set(k, name);
  }
  const adopted = missing.size ? [
    ...layers,
    // Sorted by CODE UNIT (the default `Array.prototype.sort`), never
    // `localeCompare`: two peers must append the same records in the same
    // order, and locale collation differs between machines.
    ...[...missing].sort().map((name) => ({ name, color: "#ffffff", visible: true, locked: false, frozen: false, plot: true }))
  ] : layers;
  return { layers: adopted, recase: recase.size ? recase : null, byFold };
}
function applyRecase(list, recase) {
  if (!Array.isArray(list) || !recase) return list;
  let touched = false;
  const next = list.map((e) => {
    const to = e && typeof e.layer === "string" ? recase.get(e.layer) : void 0;
    if (!to) return e;
    touched = true;
    return { ...e, layer: to };
  });
  return touched ? next : list;
}
function normalizeDocument(doc) {
  if (!doc) return createBlankDocument();
  const d = { ...doc };
  if (!d.meta) d.meta = {};
  if (!Array.isArray(d.layers) || !d.layers.length) {
    d.layers = [{ name: DEFAULT_LAYER, color: "#ffffff", visible: true, locked: false, frozen: false, plot: true }];
  } else {
    d.layers = d.layers.map((l) => {
      if (!l || typeof l !== "object") return l;
      let out = typeof l.visible === "boolean" ? l : { ...l, visible: l.visible !== false };
      if (typeof out.frozen !== "boolean") out = { ...out, frozen: out.frozen === true };
      if (typeof out.plot !== "boolean") out = { ...out, plot: out.plot !== false };
      if (out.lineweight == null) return out;
      const lw = clampLineweight(out.lineweight);
      if (lw === out.lineweight) return out;
      if (lw == null) {
        const { lineweight: _lw, ...rest } = out;
        return rest;
      }
      return { ...out, lineweight: lw };
    });
  }
  if (!Array.isArray(d.entities)) d.entities = [];
  d.entities = repairEntityRadii(d.entities);
  d.entities = sanitizeTableCells(d.entities);
  d.entities = sanitizeRegions(d.entities);
  d.entities = d.entities.map((e) => e && e.type === "image" ? normalizeImageEntity(e) : e).filter(Boolean);
  d.linetypeDefs = { ...cloneDefs(LINETYPE_DEFS), ...d.linetypeDefs || {} };
  d.dimStyleDefs = sanitizeStyleDefs(d.dimStyleDefs);
  d.textStyleDefs = sanitizeStyleDefs(d.textStyleDefs);
  d.blockDefs = {
    ...cloneDefs(GEOTECH_BLOCKS),
    ...d.blockDefs && typeof d.blockDefs === "object" ? d.blockDefs : {}
  };
  const adoption = adoptClaimedLayers(d.layers, d.entities, d.blockDefs);
  d.layers = adoption.layers;
  if (adoption.recase) {
    d.entities = applyRecase(d.entities, adoption.recase);
    const defs = d.blockDefs;
    let defsTouched = false;
    const nextDefs = /* @__PURE__ */ Object.create(null);
    for (const bn of Object.keys(defs)) {
      const def = defs[bn];
      const ents = applyRecase(def && def.entities, adoption.recase);
      if (def && ents !== def.entities) {
        defsTouched = true;
        nextDefs[bn] = { ...def, entities: ents };
      } else {
        nextDefs[bn] = def;
      }
    }
    if (defsTouched) d.blockDefs = { ...nextDefs };
  }
  if (!Array.isArray(d.layouts) || !d.layouts.length) {
    d.layouts = [modelLayout()];
  } else if (!d.layouts.some((l) => l.id === "model")) {
    d.layouts = [modelLayout(), ...d.layouts];
  }
  d.entities = healFieldBindings(d.entities, d.layouts);
  d.layouts = d.layouts.map(
    (l) => Array.isArray(l.viewports) && l.viewports.some((v) => typeof v.locked !== "boolean") ? { ...l, viewports: l.viewports.map((v) => ({ ...v, locked: !!v.locked })) } : l
  );
  d.layouts = d.layouts.map((l) => {
    if (!l || l.pageSetup === void 0) return l;
    const ps = normalizePageSetup(l.pageSetup);
    if (ps) return { ...l, pageSetup: ps };
    const { pageSetup: _dropped, ...rest } = l;
    return rest;
  });
  d.xrefDefs = d.xrefDefs && typeof d.xrefDefs === "object" ? d.xrefDefs : {};
  d.imageDefs = d.imageDefs && typeof d.imageDefs === "object" ? Object.fromEntries(
    Object.entries(d.imageDefs).map(([k, v]) => [k, normalizeImageDef(v)]).filter(([, v]) => v)
  ) : {};
  d.namedViews = Array.isArray(d.namedViews) ? d.namedViews : [];
  d.groups = normalizeGroups(d);
  if (d.groups.length) {
    const pruned = pruneGroups(d);
    if (pruned !== d) d.groups = pruned.groups;
  }
  d.constraints = sanitizeConstraints(d.constraints);
  d.settings = normalizeSettings(d.settings);
  d.georef = normalizeGeoref(d.georef);
  const foldTo = (name) => typeof name === "string" && name ? adoption.byFold.get(layerNameKey(name)) : void 0;
  const clayerUsable = (name) => (d.layers || []).some((l) => l && l.name === name && l.frozen !== true);
  if (d.settings.currentLayer) {
    const resolved = foldTo(d.settings.currentLayer);
    if (resolved && resolved !== d.settings.currentLayer) {
      d.settings = { ...d.settings, currentLayer: resolved };
    } else if (!resolved) {
      d.settings = { ...d.settings, currentLayer: DEFAULT_LAYER };
    }
    if (!clayerUsable(d.settings.currentLayer)) {
      const layers = d.layers || [];
      const next = layers.find((l) => l && layerShown(l)) || layers.find((l) => l && l.frozen !== true);
      d.settings = { ...d.settings, currentLayer: next && next.name || DEFAULT_LAYER };
    }
  }
  const iso = d.settings.layerIsolation;
  if (iso && iso.prev) {
    const keys = Object.keys(iso.prev);
    const prev = /* @__PURE__ */ Object.create(null);
    let changed = false;
    for (const k of keys) {
      const live = foldTo(k);
      if (!live) {
        changed = true;
        continue;
      }
      if (live !== k) changed = true;
      prev[live] = iso.prev[k];
    }
    const prevCurrent = foldTo(iso.prevCurrent);
    if (iso.prevCurrent && prevCurrent !== iso.prevCurrent) changed = true;
    if (changed) {
      const kept = Object.keys(prev).length;
      const next = kept ? { prev: { ...prev } } : null;
      if (next && prevCurrent) next.prevCurrent = prevCurrent;
      d.settings = { ...d.settings, layerIsolation: next };
    }
  }
  const styleOf = annotativeStyleOf(
    resolveTextStyle,
    d.textStyleDefs,
    d.settings && d.settings.currentTextStyle
  );
  const annoIn = (list) => Array.isArray(list) && list.some((e) => {
    if (!e) return false;
    if (e.annotative === true) return true;
    const st = styleOf(e);
    return !!(st && st.annotative);
  });
  if (annoIn(d.entities)) {
    d.entities = d.entities.map(migrateAnnotativeEntity);
    const synced = syncAnnotativeHeights(d, styleOf);
    if (synced !== d) d.entities = synced.entities;
  }
  if (d.blockDefs && Object.values(d.blockDefs).some((b) => b && annoIn(b.entities))) {
    const scale = documentAnnotationScale(d);
    d.blockDefs = Object.fromEntries(
      Object.entries(d.blockDefs).map(([name, def]) => [
        name,
        annoIn(def && def.entities) ? {
          ...def,
          entities: def.entities.map(
            (e) => syncAnnotativeEntity(migrateAnnotativeEntity(e), scale, styleOf(e))
          )
        } : def
      ])
    );
  }
  return d;
}
function normalizeSettings(s) {
  const d = defaultSettings();
  if (!s || typeof s !== "object") return d;
  const n = Number(s.ltscale);
  return {
    ltscale: Number.isFinite(n) && n > 0 ? n : d.ltscale,
    lwDisplay: s.lwDisplay == null ? d.lwDisplay : !!s.lwDisplay,
    currentLayer: typeof s.currentLayer === "string" && s.currentLayer ? s.currentLayer : d.currentLayer,
    currentColor: typeof s.currentColor === "string" && s.currentColor !== "BYLAYER" ? s.currentColor : null,
    currentLinetype: typeof s.currentLinetype === "string" && s.currentLinetype ? s.currentLinetype : d.currentLinetype,
    currentLineweight: clampLineweight(s.currentLineweight),
    currentLtScale: Number.isFinite(Number(s.currentLtScale)) && Number(s.currentLtScale) > 0 ? Number(s.currentLtScale) : 1,
    currentDimStyle: typeof s.currentDimStyle === "string" && s.currentDimStyle ? s.currentDimStyle : d.currentDimStyle,
    currentTextStyle: typeof s.currentTextStyle === "string" && s.currentTextStyle ? s.currentTextStyle : d.currentTextStyle,
    // A stored plot style is re-validated on every load: a corrupt or stale table can
    // only ever degrade to null ("plot as drawn"), never to a half-applied pen.
    plotStyle: normalizePlotStyleTable(s.plotStyle),
    // 1.2 LAYISO marker ([LAYER-OPS-1]). Whitelisted here or every load/remote
    // update would strip it (the review's finding 1: isolate → reload → LAYUNISO
    // silently dead). A corrupt marker degrades to null ("no isolation"), never
    // to a guessed restore.
    layerIsolation: normalizeLayerIsolation(s.layerIsolation)
  };
}
function normalizeLayerIsolation(m) {
  if (!m || typeof m !== "object" || !m.prev || typeof m.prev !== "object") return null;
  const prev = /* @__PURE__ */ Object.create(null);
  for (const k of Object.keys(m.prev)) {
    if (typeof m.prev[k] === "boolean") prev[k] = m.prev[k];
  }
  const out = { prev: { ...prev } };
  if (typeof m.prevCurrent === "string" && m.prevCurrent) out.prevCurrent = m.prevCurrent;
  return out;
}

// src/cadTransform/shared.js
var mapSplinePoints = (e, fn) => ({
  ...e,
  controlPoints: Array.isArray(e.controlPoints) ? e.controlPoints.map(fn) : e.controlPoints,
  fitPoints: Array.isArray(e.fitPoints) ? e.fitPoints.map(fn) : e.fitPoints
});
var rectCorners = (e) => [
  { x: e.x, y: e.y },
  { x: e.x + e.width, y: e.y },
  { x: e.x + e.width, y: e.y + e.height },
  { x: e.x, y: e.y + e.height }
];
var asPolyline = (e, points) => {
  const w = polylineWidthWorld(e);
  return carryStyle(e, {
    id: e.id,
    type: "polyline",
    points,
    closed: true,
    // Absent rather than 0 for a widthless source, so a drawing that never used
    // PEDIT Width is byte-identical to one made before the feature existed —
    // the same rule `peditSetWidth` follows.
    ...w > 0 ? { width: w } : null
  });
};
function mapDimensionPoints(e, fn, affine) {
  const t = mapTextOffset(e, affine);
  if (e.dimType === "ordinate")
    return { ...e, ...t, feature: fn(e.feature), leader: fn(e.leader), datum: fn(e.datum) };
  if (e.dimType === "angular")
    return { ...e, ...t, vertex: fn(e.vertex), p1: fn(e.p1), p2: fn(e.p2) };
  if (e.dimType === "radius" || e.dimType === "diameter")
    return { ...e, ...t, center: fn(e.center), leaderPoint: fn(e.leaderPoint) };
  if (e.dimType === "arclength")
    return { ...e, ...t, center: fn(e.center), linePoint: fn(e.linePoint) };
  return { ...e, ...t, p1: fn(e.p1), p2: fn(e.p2), linePoint: fn(e.linePoint) };
}
function mapTextOffset(e, affine) {
  if (typeof affine !== "function") return null;
  const o = e.textOffset;
  if (!o || !Number.isFinite(o.x) || !Number.isFinite(o.y)) return null;
  const zero = affine({ x: 0, y: 0 });
  const moved = affine({ x: o.x, y: o.y });
  return { textOffset: { x: moved.x - zero.x, y: moved.y - zero.y } };
}
var carryId = (e, props) => carryStyle(e, props);
function mapHatchGeometry(e, M) {
  const ring = (r) => Array.isArray(r) ? r.map((p) => M(p)) : r;
  const rings = (rs) => Array.isArray(rs) ? rs.map(ring) : rs;
  return {
    ...e,
    cachedLines: (e.cachedLines || []).map((s) => {
      const a = M({ x: s.x1, y: s.y1 });
      const b = M({ x: s.x2, y: s.y2 });
      return { ...s, x1: a.x, y1: a.y, x2: b.x, y2: b.y };
    }),
    cachedBoundary: ring(e.cachedBoundary || []),
    cachedHoles: rings(e.cachedHoles),
    explicitBoundary: ring(e.explicitBoundary),
    explicitHoles: rings(e.explicitHoles),
    regionSeed: e.regionSeed && e.regionSeed.point ? { ...e.regionSeed, point: M(e.regionSeed.point) } : e.regionSeed
  };
}
function mapRegionRings(e, M) {
  return { ...e, rings: orientRingSet(mapRings(e.rings, (p) => M(p))) };
}
function mapAttributeList(attrs, P, angleOf, heightScale = 1) {
  if (!Array.isArray(attrs) || !attrs.length) return null;
  return attrs.map((a) => {
    const p = P({ x: a.x || 0, y: a.y || 0 });
    return {
      ...a,
      x: p.x,
      y: p.y,
      height: (a.height || 0) * heightScale,
      rotation: angleOf(a.rotation || 0)
    };
  });
}
function withAttributes(e, mapped) {
  return mapped && Array.isArray(e.attributes) && e.attributes.length ? { attributes: mapped } : {};
}
function mapImageFrame(e, map) {
  if (!hasImageFrame(e)) return e;
  const o0 = map({ x: 0, y: 0 });
  const L = (w) => {
    const p = map(w);
    return { x: p.x - o0.x, y: p.y - o0.y };
  };
  return transformImageFrame(e, map, L);
}

// src/cadTransform/rotate.js
var DEG2 = Math.PI / 180;
var rotatePt = (p, c, deg) => {
  const a = deg * DEG2;
  const ca = Math.cos(a);
  const sa = Math.sin(a);
  const dx = p.x - c.x;
  const dy = p.y - c.y;
  return { x: c.x + dx * ca - dy * sa, y: c.y + dx * sa + dy * ca };
};
function rotateEntity(e, c, deg) {
  const R = (p) => rotatePt(p, c, deg);
  switch (e.type) {
    case "line": {
      const a = R({ x: e.x1, y: e.y1 });
      const b = R({ x: e.x2, y: e.y2 });
      return { ...e, x1: a.x, y1: a.y, x2: b.x, y2: b.y };
    }
    case "polyline":
      return { ...e, points: e.points.map(R) };
    case "rectangle":
      return asPolyline(e, rectCorners(e).map(R));
    case "circle": {
      const cc = R({ x: e.cx, y: e.cy });
      return { ...e, cx: cc.x, cy: cc.y };
    }
    case "arc": {
      const cc = R({ x: e.cx, y: e.cy });
      return { ...e, cx: cc.x, cy: cc.y, startAngle: e.startAngle + deg, endAngle: e.endAngle + deg };
    }
    case "ellipse": {
      const cc = R({ x: e.cx, y: e.cy });
      return { ...e, cx: cc.x, cy: cc.y, rotation: (e.rotation || 0) + deg };
    }
    case "spline":
      return mapSplinePoints(e, R);
    case "ray":
    case "xline": {
      const pp = R({ x: e.x, y: e.y });
      const a = deg * DEG2;
      const ca = Math.cos(a);
      const sa = Math.sin(a);
      return { ...e, x: pp.x, y: pp.y, dx: e.dx * ca - e.dy * sa, dy: e.dx * sa + e.dy * ca };
    }
    case "revcloud":
    case "leader":
      return { ...e, points: e.points.map(R) };
    case "text":
    case "mtext":
    // the whole block pivots: anchor rotates, box rotation accumulates
    case "table": {
      const pp = R({ x: e.x, y: e.y });
      return { ...e, x: pp.x, y: pp.y, rotation: (e.rotation || 0) + deg };
    }
    case "point": {
      const pp = R({ x: e.x, y: e.y });
      return { ...e, x: pp.x, y: pp.y };
    }
    // [BUG-INSERT-TRANSFORM-1] A block reference and an xref used to fall to
    // `default: return e` — no throw, no warning, the entity simply came back
    // unchanged. MOVE worked (translateEntity has a case), which is what made this
    // read to a user as "rotate is broken for blocks" rather than as a missing type.
    // An INSERT's frame is R(rotation)·S(scaleX,scaleY) about its insertion point,
    // so a world rotation moves the point and ADDS to the angle; the scales are
    // untouched. Same rule the `text` case above uses, and the same one
    // transformEntity composes a nested insert with.
    case "insert":
    case "xref": {
      const ip = R(e.insertionPoint || { x: 0, y: 0 });
      return {
        ...e,
        insertionPoint: ip,
        rotation: (e.rotation || 0) + deg,
        ...withAttributes(e, mapAttributeList(e.attributes, R, (a) => a + deg))
      };
    }
    // [RASTER-IMAGE-1] One line, because the frame is edge vectors: the origin
    // goes through the point map and the edges through its linear part.
    case "image":
      return mapImageFrame(e, R);
    case "region":
      return mapRegionRings(e, R);
    case "hatch":
      return mapHatchGeometry(e, R);
    case "dimension":
      return mapDimensionPoints(e, R, R);
    // R is affine — the label nudge rotates with it
    default:
      return e;
  }
}

// src/cadTransform/scale.js
var scalePt = (p, c, k) => ({ x: c.x + (p.x - c.x) * k, y: c.y + (p.y - c.y) * k });
var scaledWidth = (e, k) => {
  const w = polylineWidthWorld(e);
  if (!(w > 0)) return null;
  const field = polylineWidthField(e.type);
  return field ? { [field]: w * Math.abs(k) } : null;
};
function scaleEntity(e, c, k) {
  const S = (p) => scalePt(p, c, k);
  switch (e.type) {
    case "line": {
      const a = S({ x: e.x1, y: e.y1 });
      const b = S({ x: e.x2, y: e.y2 });
      return { ...e, x1: a.x, y1: a.y, x2: b.x, y2: b.y };
    }
    case "polyline":
      return { ...e, points: e.points.map(S), ...scaledWidth(e, k) };
    case "rectangle": {
      const o = S({ x: e.x, y: e.y });
      return { ...e, x: o.x, y: o.y, width: e.width * k, height: e.height * k, ...scaledWidth(e, k) };
    }
    case "circle": {
      const cc = S({ x: e.cx, y: e.cy });
      return { ...e, cx: cc.x, cy: cc.y, r: Math.abs(e.r * k) };
    }
    case "arc": {
      const cc = S({ x: e.cx, y: e.cy });
      return { ...e, cx: cc.x, cy: cc.y, r: Math.abs(e.r * k) };
    }
    case "ellipse": {
      const cc = S({ x: e.cx, y: e.cy });
      return { ...e, cx: cc.x, cy: cc.y, rx: Math.abs(e.rx * k), ry: Math.abs(e.ry * k) };
    }
    case "spline":
      return mapSplinePoints(e, S);
    case "ray":
    case "xline": {
      const pp = S({ x: e.x, y: e.y });
      return { ...e, x: pp.x, y: pp.y };
    }
    case "revcloud":
      return { ...e, points: e.points.map(S), arcRadius: (e.arcRadius || 0) * k };
    case "leader": {
      const out = { ...e, points: e.points.map(S) };
      if (Number.isFinite(e.textHeight)) out.textHeight = Math.abs(e.textHeight * k);
      return out;
    }
    case "text": {
      const pp = S({ x: e.x, y: e.y });
      return { ...e, x: pp.x, y: pp.y, height: (e.height || 1) * k };
    }
    case "mtext": {
      const pp = S({ x: e.x, y: e.y });
      return { ...e, x: pp.x, y: pp.y, height: Math.abs((e.height || 1) * k), width: Math.abs((e.width || 0) * k) };
    }
    case "table": {
      const pp = S({ x: e.x, y: e.y });
      const ak = Math.abs(k);
      return {
        ...e,
        x: pp.x,
        y: pp.y,
        colWidths: Array.isArray(e.colWidths) ? e.colWidths.map((w) => w * ak) : e.colWidths,
        rowHeights: Array.isArray(e.rowHeights) ? e.rowHeights.map((h) => h * ak) : e.rowHeights,
        textHeight: Number.isFinite(e.textHeight) ? Math.abs(e.textHeight * k) : e.textHeight
      };
    }
    case "point": {
      const pp = S({ x: e.x, y: e.y });
      return { ...e, x: pp.x, y: pp.y };
    }
    // [BUG-INSERT-TRANSFORM-1] see the note in rotate.js. A UNIFORM scale commutes
    // with the rotation, so the instance keeps its angle and both scale factors take
    // the factor: S(k,k)·R(θ)·S(sx,sy) = R(θ)·S(k·sx, k·sy). A NEGATIVE k therefore
    // negates BOTH, which is a 180° turn and not a reflection — exactly the
    // `negX && negY` branch transformEntity's angle rule already treats as a
    // rotation, so the block's arcs keep their sweep ([BUG-BLOCK-MIRROR-ARC-1]).
    // The attribute HEIGHT takes |k|: a magnitude never goes negative (the same
    // reason the circle case above guards its radius).
    case "insert":
    case "xref": {
      const ip = S(e.insertionPoint || { x: 0, y: 0 });
      return {
        ...e,
        insertionPoint: ip,
        scaleX: (e.scaleX != null ? e.scaleX : 1) * k,
        scaleY: (e.scaleY != null ? e.scaleY : 1) * k,
        ...withAttributes(e, mapAttributeList(e.attributes, S, (a) => a, Math.abs(k)))
      };
    }
    // [RASTER-IMAGE-1] One line, because the frame is edge vectors: the origin
    // goes through the point map and the edges through its linear part.
    case "image":
      return mapImageFrame(e, S);
    case "region":
      return mapRegionRings(e, S);
    case "hatch":
      return mapHatchGeometry(e, S);
    case "dimension": {
      const out = mapDimensionPoints(e, S, S);
      if (e.autoText === false) {
        if (Number.isFinite(e.textHeight)) out.textHeight = e.textHeight * k;
        if (Number.isFinite(e.arrowSize)) out.arrowSize = e.arrowSize * k;
      }
      if (Number.isFinite(e.radius)) out.radius = e.radius * k;
      return out;
    }
    default:
      return e;
  }
}

// src/cadTransform/mirror.js
var reflectPt = (p, a, b) => {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const n2 = dx * dx + dy * dy;
  if (n2 === 0) return { x: p.x, y: p.y };
  const nx = -dy;
  const ny = dx;
  const d = ((p.x - a.x) * nx + (p.y - a.y) * ny) / n2;
  return { x: p.x - 2 * d * nx, y: p.y - 2 * d * ny };
};
var axisAngleDeg = (a, b) => Math.atan2(b.y - a.y, b.x - a.x) * 180 / Math.PI;
function mirrorEntity(e, a, b) {
  const M = (p) => reflectPt(p, a, b);
  const phi = axisAngleDeg(a, b);
  switch (e.type) {
    case "line": {
      const p = M({ x: e.x1, y: e.y1 });
      const q = M({ x: e.x2, y: e.y2 });
      return { ...e, x1: p.x, y1: p.y, x2: q.x, y2: q.y };
    }
    case "polyline":
      return { ...e, points: e.points.map(M) };
    case "rectangle":
      return asPolyline(e, rectCorners(e).map(M));
    case "circle": {
      const cc = M({ x: e.cx, y: e.cy });
      return { ...e, cx: cc.x, cy: cc.y };
    }
    case "arc": {
      const cc = M({ x: e.cx, y: e.cy });
      return { ...e, cx: cc.x, cy: cc.y, startAngle: 2 * phi - e.endAngle, endAngle: 2 * phi - e.startAngle };
    }
    case "ellipse": {
      const cc = M({ x: e.cx, y: e.cy });
      const out = { ...e, cx: cc.x, cy: cc.y, rotation: 2 * phi - (e.rotation || 0) };
      if (e.startAngle != null && e.endAngle != null) {
        out.startAngle = -e.endAngle;
        out.endAngle = -e.startAngle;
      }
      return out;
    }
    case "spline":
      return mapSplinePoints(e, M);
    case "ray":
    case "xline": {
      const pp = M({ x: e.x, y: e.y });
      const tip = M({ x: e.x + e.dx, y: e.y + e.dy });
      return { ...e, x: pp.x, y: pp.y, dx: tip.x - pp.x, dy: tip.y - pp.y };
    }
    case "revcloud":
    case "leader":
      return { ...e, points: e.points.map(M) };
    case "text":
    case "mtext":
    case "table": {
      const pp = M({ x: e.x, y: e.y });
      return { ...e, x: pp.x, y: pp.y };
    }
    case "point": {
      const pp = M({ x: e.x, y: e.y });
      return { ...e, x: pp.x, y: pp.y };
    }
    // [BUG-INSERT-TRANSFORM-1] see the note in rotate.js. A block reference REFLECTS
    // — unlike text, which this file deliberately keeps readable (MIRRTEXT = 0).
    // That is not an inconsistency: a mirrored block is how a left-hand and a
    // right-hand door are made from one definition, and 49 of one real file's 111
    // INSERTs rely on it ([BUG-BLOCK-MIRROR-ARC-1]).
    //
    // The algebra. Reflection across a line at angle φ is R(φ)·S(1,−1)·R(−φ), and
    // S(1,−1)·R(α) = R(−α)·S(1,−1), so
    //   M·R(θ)·S(sx,sy) = R(φ)S(1,−1)R(θ−φ)S(sx,sy) = R(2φ−θ)·S(sx,−sy).
    // Hence: rotation ↦ 2φ − θ, scaleY negates, scaleX is untouched.
    //
    // The placed ATTRIBUTES follow the reflected FRAME (2φ − their own angle) rather
    // than MIRRTEXT's keep-the-angle rule, and this is the one deliberate exception
    // to "follow what the file does to a text". A title-block value that kept its
    // angle while its box reflected would sit outside the box. 2φ − a is a plain
    // rotation — it never mirrors the glyphs — so the text stays readable either way,
    // and only the position rule differs.
    case "insert":
    case "xref": {
      const ip = M(e.insertionPoint || { x: 0, y: 0 });
      return {
        ...e,
        insertionPoint: ip,
        scaleY: -(e.scaleY != null ? e.scaleY : 1),
        rotation: 2 * phi - (e.rotation || 0),
        ...withAttributes(e, mapAttributeList(e.attributes, M, (a2) => 2 * phi - a2))
      };
    }
    // [RASTER-IMAGE-1] One line, because the frame is edge vectors: the origin
    // goes through the point map and the edges through its linear part.
    case "image":
      return mapImageFrame(e, M);
    case "region":
      return mapRegionRings(e, M);
    case "hatch":
      return mapHatchGeometry(e, M);
    case "dimension":
      return mapDimensionPoints(e, M, M);
    // M is affine — the label nudge reflects with it
    default:
      return e;
  }
}

// src/cadTransform/lists.js
var rotateEntities = (entities, c, deg) => entities.map((e) => rotateEntity(e, c, deg));
var scaleEntities = (entities, c, k) => entities.map((e) => scaleEntity(e, c, k));
var mirrorEntities = (entities, a, b) => entities.map((e) => mirrorEntity(e, a, b));

// src/cadGeometry/transform.js
var worldToYDown = (p) => ({ x: p.x, y: -p.y });
var yDownToWorld = (p) => ({ x: p.x, y: -p.y });
var RENDER_FN_CACHE = /* @__PURE__ */ new WeakMap();
function worldToYDownFrom(origin) {
  if (!origin) return worldToYDown;
  let f = RENDER_FN_CACHE.get(origin);
  if (!f) {
    const ox = origin.x;
    const oy = origin.y;
    f = (p) => ({ x: p.x - ox, y: oy - p.y });
    RENDER_FN_CACHE.set(origin, f);
  }
  return f;
}
function worldToScreen(view, p) {
  const k = worldToYDown(p);
  return { x: k.x * view.scale + view.x, y: k.y * view.scale + view.y };
}
function screenToWorld(view, p) {
  const k = { x: (p.x - view.x) / view.scale, y: (p.y - view.y) / view.scale };
  return yDownToWorld(k);
}
var worldToYDownAngle = (deg) => -deg;
function snapToGrid(p, gridSize) {
  if (!gridSize || gridSize <= 0) return p;
  return {
    x: Math.round(p.x / gridSize) * gridSize,
    y: Math.round(p.y / gridSize) * gridSize
  };
}
function fitView(bounds, containerW, containerH, padding = 0.9) {
  return fitRectToViewport(bounds, containerW, containerH, padding);
}
function zoomAt(view, screenPt, factor, minScale = 1e-4, maxScale = 1e6) {
  return zoomByFactor(factor, screenPt, view, { min: minScale, max: maxScale });
}
function formatCoord(n, digits = 2) {
  if (!isFinite(n)) return "0";
  return n.toFixed(digits);
}

// src/cadGeometry/entities.js
function entitySegments(e) {
  switch (e.type) {
    case "line":
      return [[{ x: e.x1, y: e.y1 }, { x: e.x2, y: e.y2 }]];
    case "polyline": {
      const segs = [];
      for (let i = 0; i < e.points.length - 1; i++) segs.push([e.points[i], e.points[i + 1]]);
      if (e.closed && e.points.length > 2) segs.push([e.points[e.points.length - 1], e.points[0]]);
      return segs;
    }
    case "rectangle": {
      const v = entityVertices(e);
      return [[v[0], v[1]], [v[1], v[2]], [v[2], v[3]], [v[3], v[0]]];
    }
    // [RASTER-IMAGE-1] An image's pick geometry is its FRAME — the four edges,
    // closed. That keeps the three selection doors agreeing: the pointer
    // (`distanceToEntity`), the marquee and the lasso all derive from here.
    //
    // THE DECISION, because these segments reach further than selection: a photo
    // is SELECTION and OSNAP geometry, and NOT modify-tool geometry. You may snap
    // to its corners (ENDPOINT), its edges (NEAREST, PERPENDICULAR) and where a
    // line crosses one (INTERSECTION), and pick it by them; TRIM and EXTEND do
    // not treat the frame as a cutting or boundary edge. Making the frame real
    // without saying this had TRIM cutting every line at every backdrop edge on a
    // Select-All while EXTEND ignored the same frame (adversarial review round 2).
    //
    // The exemption lives in TRIM and EXTEND — where the decision is — and NOT in
    // `intersectEntities`, which stays a pure geometric primitive. Putting it
    // there instead looked tidier and was wrong: the classic INTERSECTION osnap
    // computes pairwise over `entitySegments` and never calls
    // `intersectEntities`, so the exemption silenced only the INFERENCE
    // intersection rule — two intersection engines disagreeing, under a comment
    // asserting they agreed. Measured, not reasoned: `findSnap` returned
    // `intersection @ (90,30)` on a frame edge while `intersectEntities` returned
    // [] for the same pair (round 3).
    case "image": {
      const v = entityVertices(e);
      if (v.length !== 4) return [];
      return [[v[0], v[1]], [v[1], v[2]], [v[2], v[3]], [v[3], v[0]]];
    }
    case "ellipse": {
      const pts = ellipseToPolyline(e);
      const segs = [];
      for (let i = 0; i < pts.length - 1; i++) segs.push([pts[i], pts[i + 1]]);
      return segs;
    }
    case "spline": {
      const pts = splineToPolyline(e);
      const segs = [];
      for (let i = 0; i < pts.length - 1; i++) segs.push([pts[i], pts[i + 1]]);
      return segs;
    }
    case "ray":
    case "xline":
      return [constructionSegment(e)];
    case "revcloud": {
      const pts = revisionCloudArcs(e.points || [], !!e.closed, e.arcRadius);
      const segs = [];
      for (let i = 0; i < pts.length - 1; i++) segs.push([pts[i], pts[i + 1]]);
      return segs;
    }
    // C4b. Every ring's edges INCLUDING the closing last→first edge — rings are
    // implicitly closed, so omitting it would leave each ring one edge short and
    // silently make a region's perimeter, intersections and perpendicular snaps
    // wrong rather than absent. Holes contribute their edges too: a hole's
    // boundary is real geometry you can snap to and trim against.
    case "region": {
      const segs = [];
      for (const ring of e.rings || []) {
        const n = (ring || []).length;
        if (n < 3) continue;
        for (let i = 0; i < n; i++) segs.push([ring[i], ring[(i + 1) % n]]);
      }
      return segs;
    }
    default:
      return [];
  }
}
function entityOutlinePoints(e) {
  if (e.type === "circle") {
    const out = [];
    for (let a = 0; a < 360; a += 12) out.push(onCirclePoint(e.cx, e.cy, e.r, a));
    return out;
  }
  if (e.type === "arc") {
    const sweep = ((e.endAngle - e.startAngle) % 360 + 360) % 360;
    const steps = Math.max(2, Math.ceil(sweep / 12));
    const out = [];
    for (let i = 0; i <= steps; i++) out.push(onCirclePoint(e.cx, e.cy, e.r, e.startAngle + sweep * i / steps));
    return out;
  }
  if (e.type === "point") return [{ x: e.x, y: e.y }];
  if (e.type === "region") {
    return (e.rings || []).flatMap((ring) => (ring || []).map((p) => ({ x: p.x, y: p.y })));
  }
  const segs = entitySegments(e);
  if (segs.length) {
    const pts = [segs[0][0]];
    for (const s of segs) pts.push(s[1]);
    return pts;
  }
  const bb = entityBounds(e);
  if (!bb) return [];
  return [
    { x: bb.minX, y: bb.minY },
    { x: bb.maxX, y: bb.minY },
    { x: bb.maxX, y: bb.maxY },
    { x: bb.minX, y: bb.maxY }
  ];
}
function entityLength(e) {
  if (e.type === "line") return Math.hypot(e.x2 - e.x1, e.y2 - e.y1);
  if (e.type === "circle") return 2 * Math.PI * e.r;
  if (e.type === "arc") {
    const sweep = ((e.endAngle - e.startAngle) % 360 + 360) % 360;
    return sweep * Math.PI / 180 * e.r;
  }
  const segs = entitySegments(e);
  if (segs.length) return segs.reduce((s, sg) => s + distance(sg[0], sg[1]), 0);
  return null;
}
function textBoxCorners(e) {
  const h = e.height || 2;
  const w = Math.max((e.text ? e.text.length : 1) * h * 0.6, h * 0.6);
  const { dx, dy } = justifyOffset(e.justify, w, h);
  const rot = (e.rotation || 0) * Math.PI / 180;
  const ca = Math.cos(rot);
  const sa = Math.sin(rot);
  return [
    { x: dx, y: dy - h },
    { x: dx + w, y: dy - h },
    { x: dx + w, y: dy },
    { x: dx, y: dy }
  ].map((c) => ({ x: e.x + c.x * ca - c.y * sa, y: e.y + c.x * sa + c.y * ca }));
}
function nearestOnEntity(p, e) {
  switch (e.type) {
    case "line": {
      const pt2 = closestPointOnSegment(p, { x: e.x1, y: e.y1 }, { x: e.x2, y: e.y2 });
      return { point: pt2, dist: distance(p, pt2) };
    }
    // [RASTER-IMAGE-1] `image` joins this group: a photo's FRAME is an osnap
    // target. Without it NEAREST ignored the frame while INTERSECTION and
    // PERPENDICULAR (which read `entitySegments`) used it — three osnaps
    // disagreeing about the same four edges (adversarial review round 2, lens A).
    case "polyline":
    case "rectangle":
    case "region":
    case "image": {
      let best = null;
      let bd = Infinity;
      for (const seg of entitySegments(e)) {
        const pt2 = closestPointOnSegment(p, seg[0], seg[1]);
        const d = distance(p, pt2);
        if (d < bd) {
          bd = d;
          best = pt2;
        }
      }
      return best ? { point: best, dist: bd } : null;
    }
    case "circle":
    case "arc": {
      const d = Math.hypot(p.x - e.cx, p.y - e.cy);
      const pt2 = d > 1e-9 ? { x: e.cx + e.r * (p.x - e.cx) / d, y: e.cy + e.r * (p.y - e.cy) / d } : { x: e.cx + e.r, y: e.cy };
      if (e.type === "arc" && !angleInArcSweep(e, pt2)) return null;
      return { point: pt2, dist: distance(p, pt2) };
    }
    case "ellipse":
    case "spline":
    case "revcloud": {
      const pts = e.type === "spline" ? splineToPolyline(e) : e.type === "ellipse" ? ellipseToPolyline(e) : revisionCloudArcs(e.points || [], !!e.closed, e.arcRadius);
      let best = null;
      let bd = Infinity;
      for (let i = 0; i < pts.length - 1; i++) {
        const pt2 = closestPointOnSegment(p, pts[i], pts[i + 1]);
        const d = distance(p, pt2);
        if (d < bd) {
          bd = d;
          best = pt2;
        }
      }
      return best ? { point: best, dist: bd } : null;
    }
    case "ray":
    case "xline": {
      const { ux, uy } = lineDir(e);
      let t = (p.x - e.x) * ux + (p.y - e.y) * uy;
      if (e.type === "ray" && t < 0) t = 0;
      const pt2 = { x: e.x + ux * t, y: e.y + uy * t };
      return { point: pt2, dist: distance(p, pt2) };
    }
    default:
      return null;
  }
}
function intersectEntities(e1, e2) {
  const isCurve = (e) => e.type === "circle" || e.type === "arc";
  const segs1 = isCurve(e1) ? [] : entitySegments(e1);
  const segs2 = isCurve(e2) ? [] : entitySegments(e2);
  const out = [];
  const push = (pt2) => {
    if (pt2) out.push(pt2);
  };
  for (const s of segs1) for (const t of segs2) push(segSegIntersection(s[0], s[1], t[0], t[1]));
  const segCurve = (segs, curve) => {
    for (const s of segs) {
      for (const pt2 of segCircleIntersections(s[0], s[1], { x: curve.cx, y: curve.cy }, curve.r)) {
        if (angleInArcSweep(curve, pt2)) push(pt2);
      }
    }
  };
  if (isCurve(e2)) segCurve(segs1, e2);
  if (isCurve(e1)) segCurve(segs2, e1);
  if (isCurve(e1) && isCurve(e2)) {
    for (const pt2 of circleCircleIntersections({ x: e1.cx, y: e1.cy }, e1.r, { x: e2.cx, y: e2.cy }, e2.r)) {
      if (angleInArcSweep(e1, pt2) && angleInArcSweep(e2, pt2)) push(pt2);
    }
  }
  const dedup = [];
  for (const pt2 of out) {
    if (!dedup.some((q) => Math.hypot(q.x - pt2.x, q.y - pt2.y) < 1e-7)) dedup.push(pt2);
  }
  return dedup;
}

// src/cadHatch/islandRule.js
function evenOddSpans(sorted) {
  const spans = [];
  for (let i = 0; i + 1 < sorted.length; i += 2) spans.push([sorted[i], sorted[i + 1]]);
  return spans;
}
function intersectSpans(a, b) {
  const out = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    const lo = Math.max(a[i][0], b[j][0]);
    const hi = Math.min(a[i][1], b[j][1]);
    if (hi > lo) out.push([lo, hi]);
    if (a[i][1] < b[j][1]) i += 1;
    else j += 1;
  }
  return out;
}
function paintedSpans(boundaryCrossings, holeCrossings = [], minLen = 0) {
  if (!boundaryCrossings || boundaryCrossings.length < 2) return [];
  const b = boundaryCrossings.slice().sort((p, q) => p - q);
  const inside = evenOddSpans(b);
  if (!inside.length) return [];
  let spans = inside;
  if (holeCrossings && holeCrossings.length) {
    const all = b.concat(holeCrossings).sort((p, q) => p - q);
    spans = intersectSpans(evenOddSpans(all), inside);
  }
  return minLen > 0 ? spans.filter((s) => s[1] - s[0] > minLen) : spans;
}
function hatchFillsPoint(p, boundary, holes) {
  if (!Array.isArray(boundary) || boundary.length < 3) return false;
  if (!pointInPolygon(p, boundary)) return false;
  let inside = 1;
  for (const h of holes || []) {
    if (Array.isArray(h) && h.length >= 3 && pointInPolygon(p, h)) inside += 1;
  }
  return inside % 2 === 1;
}

// src/cadHatch/hittest.js
function distToRingEdges(ring, p) {
  let best = Infinity;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i];
    const b = ring[(i + 1) % ring.length];
    const vx = b.x - a.x;
    const vy = b.y - a.y;
    const l2 = vx * vx + vy * vy;
    let t = l2 ? ((p.x - a.x) * vx + (p.y - a.y) * vy) / l2 : 0;
    t = Math.max(0, Math.min(1, t));
    best = Math.min(best, Math.hypot(p.x - (a.x + t * vx), p.y - (a.y + t * vy)));
  }
  return best;
}
function distanceToHatch(e, p) {
  const poly = e.cachedBoundary;
  if (!poly || poly.length < 3) return Infinity;
  const holes = Array.isArray(e.cachedHoles) ? e.cachedHoles.filter((h) => Array.isArray(h) && h.length >= 3) : [];
  if (hatchFillsPoint(p, poly, holes)) return 0;
  let best = distToRingEdges(poly, p);
  for (const h of holes) best = Math.min(best, distToRingEdges(h, p));
  return best;
}

// src/cadHatch/boundary.js
function tessellateArc(cx, cy, r, startDeg, endDeg, steps = 48) {
  const pts = [];
  const a0 = startDeg;
  let sweep = ((endDeg - startDeg) % 360 + 360) % 360;
  if (sweep < 1e-9) sweep = 360;
  const n = Math.max(8, Math.round(sweep / 360 * steps));
  for (let i = 0; i <= n; i++) {
    const a = (a0 + sweep * i / n) * Math.PI / 180;
    pts.push({ x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) });
  }
  return pts;
}
function polygonFromEntity(e) {
  if (!e) return null;
  if (e.type === "rectangle") {
    return [
      { x: e.x, y: e.y },
      { x: e.x + e.width, y: e.y },
      { x: e.x + e.width, y: e.y + e.height },
      { x: e.x, y: e.y + e.height }
    ];
  }
  if (e.type === "polyline" && e.closed && e.points.length >= 3) {
    return e.points.map((p) => ({ x: p.x, y: p.y }));
  }
  if (e.type === "circle") {
    const c = tessellateArc(e.cx, e.cy, e.r, 0, 360);
    c.pop();
    return c;
  }
  if (e.type === "ellipse" && isFullEllipse(e)) {
    const c = ellipseToPolyline(e);
    c.pop();
    return c;
  }
  if (e.type === "spline" && e.closed) {
    const c = splineToPolyline(e);
    if (c.length >= 4) {
      c.pop();
      return c;
    }
  }
  if (e.type === "revcloud" && e.closed) {
    const c = revisionCloudArcs(e.points || [], true, e.arcRadius);
    if (c.length >= 4) return c;
  }
  return null;
}
function entitySubSegments(e) {
  switch (e.type) {
    case "line":
      return [[{ x: e.x1, y: e.y1 }, { x: e.x2, y: e.y2 }]];
    case "polyline": {
      const segs = [];
      for (let i = 0; i < e.points.length - 1; i++) segs.push([{ ...e.points[i] }, { ...e.points[i + 1] }]);
      if (e.closed && e.points.length > 2) segs.push([{ ...e.points[e.points.length - 1] }, { ...e.points[0] }]);
      return segs;
    }
    case "rectangle": {
      const v = polygonFromEntity(e);
      return [[v[0], v[1]], [v[1], v[2]], [v[2], v[3]], [v[3], v[0]]];
    }
    case "arc": {
      const pts = tessellateArc(e.cx, e.cy, e.r, e.startAngle, e.endAngle);
      const segs = [];
      for (let i = 0; i < pts.length - 1; i++) segs.push([pts[i], pts[i + 1]]);
      return segs;
    }
    case "circle": {
      const pts = tessellateArc(e.cx, e.cy, e.r, 0, 360);
      const segs = [];
      for (let i = 0; i < pts.length - 1; i++) segs.push([pts[i], pts[i + 1]]);
      return segs;
    }
    case "ellipse": {
      const pts = ellipseToPolyline(e);
      const segs = [];
      for (let i = 0; i < pts.length - 1; i++) segs.push([pts[i], pts[i + 1]]);
      return segs;
    }
    case "spline": {
      const pts = splineToPolyline(e);
      const segs = [];
      for (let i = 0; i < pts.length - 1; i++) segs.push([pts[i], pts[i + 1]]);
      return segs;
    }
    case "revcloud": {
      const pts = revisionCloudArcs(e.points || [], !!e.closed, e.arcRadius);
      const segs = [];
      for (let i = 0; i < pts.length - 1; i++) segs.push([pts[i], pts[i + 1]]);
      return segs;
    }
    default:
      return [];
  }
}
var near = (a, b) => Math.hypot(a.x - b.x, a.y - b.y) <= 1e-6;
function chainSegmentsToRing(segs) {
  if (segs.length < 3) return null;
  const used = new Array(segs.length).fill(false);
  used[0] = true;
  const start = segs[0][0];
  const ring = [{ ...start }, { ...segs[0][1] }];
  let end = segs[0][1];
  let guard = 0;
  while (guard++ < segs.length + 2) {
    if (near(end, start)) {
      ring.pop();
      return ring.length >= 3 ? ring : null;
    }
    let found = -1;
    let nextPt = null;
    for (let i = 0; i < segs.length; i++) {
      if (used[i]) continue;
      if (near(segs[i][0], end)) {
        found = i;
        nextPt = segs[i][1];
        break;
      }
      if (near(segs[i][1], end)) {
        found = i;
        nextPt = segs[i][0];
        break;
      }
    }
    if (found === -1) return null;
    used[found] = true;
    end = nextPt;
    if (near(end, start)) {
      return ring.length >= 3 ? ring : null;
    }
    ring.push({ ...end });
  }
  return null;
}
function boundaryFromEntities(entities) {
  if (!entities || !entities.length) return null;
  if (entities.length === 1) {
    const poly = polygonFromEntity(entities[0]);
    if (poly && poly.length >= 3) return poly;
  }
  const segs = [];
  for (const e of entities) for (const s of entitySubSegments(e)) segs.push(s);
  return chainSegmentsToRing(segs);
}
function isClosedBoundary(e) {
  return !!e && (e.type === "rectangle" || e.type === "circle" || e.type === "ellipse" && isFullEllipse(e) || e.type === "spline" && e.closed || e.type === "revcloud" && e.closed || e.type === "polyline" && e.closed && Array.isArray(e.points) && e.points.length >= 3);
}
var polygonArea = (poly) => {
  let a = 0;
  for (let i = 0; i < poly.length; i++) {
    const p = poly[i];
    const q = poly[(i + 1) % poly.length];
    a += p.x * q.y - q.x * p.y;
  }
  return Math.abs(a) / 2;
};

// src/cadBoolean/region.js
function carry(src, extra) {
  const out = { ...carryStyle(src || {}, extra), id: genId("rg"), type: "region", ...extra };
  if (out.layer == null) out.layer = "0";
  if (out.rings) out.rings = cleanRegionRings(out.rings);
  return out;
}
function polyToRegion(poly, src) {
  return carry(src, { rings: [poly.outer, ...poly.holes].map((r) => r.map((p) => ({ x: p.x, y: p.y }))) });
}
function regionRings(e) {
  return Array.isArray(e?.rings) ? e.rings.filter((r) => Array.isArray(r) && r.length >= 3) : [];
}
function cleanRegionRings(rings) {
  return (rings || []).map((r) => cleanRing(r)).filter((r) => r.length >= 3);
}
function isBooleanOperand(e) {
  return !!e && (e.type === "region" || isClosedBoundary(e));
}
function ringsOf(e) {
  if (!e) return null;
  if (e.type === "region") {
    const rs = regionRings(e);
    return rs.length ? rs : null;
  }
  if (!isClosedBoundary(e)) return null;
  const ring = boundaryFromEntities([e]);
  return ring && ring.length >= 3 ? [ring] : null;
}
function makeRegions(entities) {
  const list = (entities || []).filter(Boolean);
  if (!list.length) return { ok: false, regions: [], consumed: [], reason: "no-entities" };
  const regions = [];
  const consumed = [];
  const leftovers = [];
  for (const e of list) {
    if (e.type === "region") continue;
    const rings = ringsOf(e);
    if (rings) {
      const oriented = orientRingSet(rings);
      const outer = oriented.find((r) => signedArea(r) > 0);
      if (outer && ringArea(outer) > AREA_EPS) {
        regions.push(carry(e, { rings: [outer.map((p) => ({ x: p.x, y: p.y }))] }));
        consumed.push(e.id);
        continue;
      }
    }
    leftovers.push(e);
  }
  if (!regions.length && leftovers.length > 1) {
    const ring = boundaryFromEntities(leftovers);
    const clean = ring ? cleanRing(ring) : null;
    if (clean && clean.length >= 3 && ringArea(clean) > AREA_EPS) {
      const oriented = orientRingSet([clean]);
      regions.push(carry(leftovers[0], { rings: [oriented[0].map((p) => ({ x: p.x, y: p.y }))] }));
      consumed.push(...leftovers.map((e) => e.id));
    }
  }
  if (!regions.length) return { ok: false, regions: [], consumed: [], reason: "no-closed-loop" };
  return { ok: true, regions, consumed, reason: null };
}
function explodeRegion(region) {
  const rings = regionRings(region);
  if (!rings.length) return [];
  return rings.map(
    (ring) => carryStyle(region || {}, {
      type: "polyline",
      points: ring.map((p) => ({ x: p.x, y: p.y })),
      closed: true
    })
  );
}
function regionArea(region) {
  const rings = regionRings(region);
  if (!rings.length) return 0;
  const [outer, ...holes] = rings;
  return Math.max(0, ringArea(outer) - holes.reduce((s, h) => s + ringArea(h), 0));
}
function regionFillsPoint(region, p) {
  const rings = regionRings(region);
  if (!rings.length) return false;
  return hatchFillsPoint(p, rings[0], rings.slice(1));
}
var OPS = { union: ringUnion, subtract: ringDifference, intersect: ringIntersection };
function booleanRegions(entities, op) {
  const fn = OPS[op];
  if (!fn) return { ok: false, regions: [], consumed: [], reason: "unknown-op" };
  const operands = [];
  const consumed = [];
  for (const e of entities || []) {
    const rings = ringsOf(e);
    if (!rings) continue;
    operands.push(orientRingSet(rings));
    consumed.push(e.id);
  }
  if (operands.length < 2)
    return { ok: false, regions: [], consumed: [], reason: "needs-two-operands" };
  let acc = operands[0];
  for (let i = 1; i < operands.length; i++) {
    const polys = fn(acc, operands[i]);
    acc = [];
    for (const p of polys) acc.push(p.outer, ...p.holes);
    acc = orientRingSet(acc);
    if (!acc.length) break;
  }
  const src = (entities || []).find((e) => ringsOf(e));
  const regions = buildPolygons(acc).filter((p) => ringArea(p.outer) > AREA_EPS).map((p) => polyToRegion(p, src));
  if (!regions.length)
    return { ok: true, regions: [], consumed, reason: "empty-result" };
  return { ok: true, regions, consumed, reason: null };
}
var unionRegions = (entities) => booleanRegions(entities, "union");
var subtractRegions = (entities) => booleanRegions(entities, "subtract");
var intersectRegions = (entities) => booleanRegions(entities, "intersect");

// src/cadGeometry/hittest.js
var MARQUEE_OUTLINE_TYPES = /* @__PURE__ */ new Set([
  "line",
  "polyline",
  "rectangle",
  "ellipse",
  "spline",
  "ray",
  "xline",
  "revcloud",
  // A leader has REAL vertices (`entityVertices` returns them), so judging it by its
  // bounding box left it with the exact BUG-A1-003 defect this change removes for
  // lines: a crossing box in the empty corner of a diagonal leader's bbox selected it
  // from 46 units away. It is not in `entitySegments`, so its outline comes from
  // `entityVertices` below rather than from `entityOutlinePoints`.
  "leader",
  // These three are RECTANGLES in world space — the glyph box, the wrap box and the
  // table grid — and each has a corner function the click picker already trusts.
  "dimension",
  // [RASTER-IMAGE-1] An image is judged by its FRAME here. A foreground one is
  // promoted back to an area by `imagePicksByArea` at step 3 — the per-entity
  // answer this set cannot express, which is why the predicate exists.
  "image"
]);
var MARQUEE_AREA_TYPES = /* @__PURE__ */ new Set(["text", "mtext", "table", "hatch", "insert", "xref", "point", "region"]);
var imagePicksByArea = (e) => !!e && e.type === "image" && e.behind === false;
function marqueeAreaCorners(e) {
  if (e.type === "text") return textBoxCorners(e);
  if (e.type === "mtext") return mtextBoxCorners(e);
  if (e.type === "table") return tableCorners(e);
  return null;
}
function regionRingChains(e) {
  const chains = [];
  for (const ring of e.rings || []) {
    if ((ring || []).length >= 3) chains.push([...ring, ring[0]]);
  }
  return chains.length ? chains : null;
}
function marqueeStrokes(e, ctx) {
  if (e.type === "leader") {
    const pts = entityVertices(e);
    return pts.length >= 2 ? [pts] : null;
  }
  if (e.type === "region") return regionRingChains(e);
  if (e.type === "dimension") {
    const g = dimensionPrimitives(
      e,
      ctx && ctx.dimStyleDefs || {},
      ctx && ctx.dimStyle,
      ctx && ctx.sizing
    );
    if (!g) return null;
    const pts = [];
    const chains = [];
    for (const [a, b] of g.lines) chains.push([a, b]);
    for (const arc of g.arcs) {
      const sweep = ((arc.endAngle - arc.startAngle) % 360 + 360) % 360;
      const ring = [];
      for (let d = 0; d <= 8; d++)
        ring.push(onCirclePoint(arc.cx, arc.cy, arc.r, arc.startAngle + sweep * (d / 8)));
      chains.push(ring);
    }
    if (g.text) chains.push([g.text.pos, g.text.pos]);
    return chains.length ? chains : null;
  }
  return null;
}
var INFINITE_TYPES = /* @__PURE__ */ new Set(["ray", "xline"]);
var pointInBox = (p, box) => p.x >= box.minX && p.x <= box.maxX && p.y >= box.minY && p.y <= box.maxY;
var boxCorners = (box) => [
  { x: box.minX, y: box.minY },
  { x: box.maxX, y: box.minY },
  { x: box.maxX, y: box.maxY },
  { x: box.minX, y: box.maxY }
];
function outlineTouchesBox(pts, box) {
  for (const p of pts) if (pointInBox(p, box)) return true;
  if (pts.length < 2) return false;
  const c = boxCorners(box);
  for (let i = 0; i < pts.length - 1; i++) {
    for (let j = 0; j < 4; j++) {
      if (segSegIntersection(pts[i], pts[i + 1], c[j], c[(j + 1) % 4])) return true;
    }
  }
  return false;
}
function circularTouchesBox(e, box) {
  const c = { x: e.cx, y: e.cy };
  const corners = boxCorners(box);
  for (let j = 0; j < 4; j++) {
    const hits = segCircleIntersections(corners[j], corners[(j + 1) % 4], c, e.r);
    for (const p of hits) {
      if (e.type === "circle" || angleInArcSweep(e, p)) return true;
    }
  }
  return pointInBox(onCirclePoint(e.cx, e.cy, e.r, e.type === "arc" ? e.startAngle : 0), box);
}
function entityInMarquee(e, box, crossing, ctx) {
  const bb = entityBounds(e, ctx && ctx.blockDefs, ctx && ctx.xrefDefs);
  if (!bb) return false;
  const infinite = INFINITE_TYPES.has(e.type);
  const area = infinite ? null : marqueeAreaCorners(e);
  const strokes = infinite ? null : marqueeStrokes(e, ctx);
  const shape = area && boundsOf(area) || strokes && boundsOf(strokes.flat()) || tightBounds(e) || bb;
  const encloses = (b) => b.minX >= box.minX && b.maxX <= box.maxX && b.minY >= box.minY && b.maxY <= box.maxY;
  if (!crossing) {
    if (infinite) return false;
    return encloses(shape);
  }
  if (!infinite) {
    if (!(shape.minX <= box.maxX && shape.maxX >= box.minX && shape.minY <= box.maxY && shape.maxY >= box.minY))
      return false;
    if (encloses(shape)) return true;
    if (MARQUEE_AREA_TYPES.has(e.type) || imagePicksByArea(e)) return true;
  }
  if (e.type === "circle" || e.type === "arc") return circularTouchesBox(e, box);
  if (strokes) return strokes.some((chain) => outlineTouchesBox(chain, box));
  if (!MARQUEE_OUTLINE_TYPES.has(e.type)) return true;
  const pts = entityOutlinePoints(e);
  if (!pts.length) return true;
  return outlineTouchesBox(pts, box);
}
function tightBounds(e) {
  if (e.type === "arc") {
    if (!(e.r > 0)) return null;
    const sweep = ((e.endAngle - e.startAngle) % 360 + 360) % 360 || 360;
    const pts = [
      onCirclePoint(e.cx, e.cy, e.r, e.startAngle),
      onCirclePoint(e.cx, e.cy, e.r, e.endAngle)
    ];
    for (const q of [0, 90, 180, 270]) {
      const rel = ((q - e.startAngle) % 360 + 360) % 360;
      if (rel <= sweep) pts.push(onCirclePoint(e.cx, e.cy, e.r, q));
    }
    return boundsOf(pts);
  }
  if (e.type === "ellipse") {
    const rot = (e.rotation || 0) * Math.PI / 180;
    const ca = Math.cos(rot);
    const sa = Math.sin(rot);
    const at = (t) => ({
      x: e.cx + e.rx * Math.cos(t) * ca - e.ry * Math.sin(t) * sa,
      y: e.cy + e.rx * Math.cos(t) * sa + e.ry * Math.sin(t) * ca
    });
    const tx = Math.atan2(-e.ry * sa, e.rx * ca);
    const ty = Math.atan2(e.ry * ca, e.rx * sa);
    const full = e.startAngle == null || e.endAngle == null;
    const s0 = (e.startAngle || 0) * Math.PI / 180;
    const sweep = full ? 2 * Math.PI : (((e.endAngle - e.startAngle) % 360 + 360) % 360 || 360) * (Math.PI / 180);
    const inSweep = (t) => {
      if (full) return true;
      const rel = ((t - s0) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI);
      return rel <= sweep;
    };
    const pts = full ? [] : [at(s0), at(s0 + sweep)];
    for (const t of [tx, tx + Math.PI, ty, ty + Math.PI]) if (inSweep(t)) pts.push(at(t));
    if (full) for (const t of [tx, tx + Math.PI, ty, ty + Math.PI]) pts.push(at(t));
    return boundsOf(pts);
  }
  return null;
}
var boundsOf = (pts) => {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of pts) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return Number.isFinite(minX) ? { minX, minY, maxX, maxY } : null;
};
function entitiesInSelectionBox(entities, box, mode, ctx) {
  const crossing = mode === "crossing";
  const out = [];
  for (const e of entities) {
    if (entityInMarquee(e, box, crossing, ctx)) out.push(e.id);
  }
  return out;
}
function entitiesThroughPoints(entities, points, tol, ctx) {
  const out = /* @__PURE__ */ new Set();
  for (const e of entities) {
    if (e.type === "dimension") continue;
    for (const p of points) {
      if (distanceToEntity(e, p, ctx) <= tol) {
        out.add(e.id);
        break;
      }
    }
  }
  return [...out];
}
function closestEntityWithin(entities, p, tol, ctx) {
  let best = null;
  let bestD = Infinity;
  for (const e of entities) {
    const d = distanceToEntity(e, p, ctx);
    if (d <= tol && d <= bestD) {
      bestD = d;
      best = e;
    }
  }
  return best;
}
function distanceToEntity(e, p, ctx) {
  switch (e.type) {
    case "line":
      return pointToSegment(p, { x: e.x1, y: e.y1 }, { x: e.x2, y: e.y2 });
    case "polyline":
    case "rectangle": {
      const verts = e.type === "rectangle" ? entityVertices(e) : e.points.map((v) => ({ x: v.x, y: v.y }));
      const closed = e.type === "rectangle" || e.closed;
      let best = Infinity;
      for (let i = 0; i < verts.length - 1; i++) {
        best = Math.min(best, pointToSegment(p, verts[i], verts[i + 1]));
      }
      if (closed && verts.length > 2) {
        best = Math.min(best, pointToSegment(p, verts[verts.length - 1], verts[0]));
      }
      return best;
    }
    case "circle":
      return Math.abs(distance(p, { x: e.cx, y: e.cy }) - e.r);
    case "arc": {
      const c = { x: e.cx, y: e.cy };
      if (angleInArcSweep(e, p)) return Math.abs(distance(p, c) - e.r);
      const s = onCirclePoint(e.cx, e.cy, e.r, e.startAngle);
      const en = onCirclePoint(e.cx, e.cy, e.r, e.endAngle);
      return Math.min(distance(p, s), distance(p, en));
    }
    case "ellipse": {
      const pts = ellipseToPolyline(e);
      let best = Infinity;
      for (let i = 0; i < pts.length - 1; i++)
        best = Math.min(best, pointToSegment(p, pts[i], pts[i + 1]));
      return best;
    }
    case "spline": {
      const pts = splineToPolyline(e);
      let best = Infinity;
      for (let i = 0; i < pts.length - 1; i++)
        best = Math.min(best, pointToSegment(p, pts[i], pts[i + 1]));
      return best;
    }
    case "ray":
    case "xline": {
      const { ux, uy } = lineDir(e);
      const vx = p.x - e.x;
      const vy = p.y - e.y;
      const t = vx * ux + vy * uy;
      if (e.type === "ray" && t < 0) return Math.hypot(vx, vy);
      return Math.abs(vx * uy - vy * ux);
    }
    case "revcloud": {
      const pts = revisionCloudArcs(e.points || [], !!e.closed, e.arcRadius);
      let best = Infinity;
      for (let i = 0; i < pts.length - 1; i++)
        best = Math.min(best, pointToSegment(p, pts[i], pts[i + 1]));
      return best;
    }
    case "text": {
      const poly = textBoxCorners(e);
      if (pointInPolygon(p, poly)) return 0;
      let best = Infinity;
      for (let i = 0; i < poly.length; i++) {
        best = Math.min(best, pointToSegment(p, poly[i], poly[(i + 1) % poly.length]));
      }
      return best;
    }
    case "mtext": {
      const poly = mtextBoxCorners(e);
      if (pointInPolygon(p, poly)) return 0;
      let best = Infinity;
      for (let i = 0; i < poly.length; i++) {
        best = Math.min(best, pointToSegment(p, poly[i], poly[(i + 1) % poly.length]));
      }
      return best;
    }
    case "table": {
      if (worldToTableCell(e, p)) return 0;
      const poly = tableCorners(e);
      let best = Infinity;
      for (let i = 0; i < poly.length; i++) {
        best = Math.min(best, pointToSegment(p, poly[i], poly[(i + 1) % poly.length]));
      }
      return best;
    }
    case "point":
      return distance(p, { x: e.x, y: e.y });
    case "leader": {
      const g = leaderGeometry(e, ctx && ctx.dimStyleDefs, ctx && ctx.dimStyle, ctx && ctx.sizing);
      if (!g) return Infinity;
      let best = Infinity;
      for (const [a, b] of g.segs) best = Math.min(best, pointToSegment(p, a, b));
      if (g.landing) best = Math.min(best, pointToSegment(p, g.landing[0], g.landing[1]));
      const text = e.text || "";
      if (text) {
        const w = APPROX_CHAR_WIDTH_FACTOR * g.textHeight * text.length;
        const dir = g.textAlignRight ? -1 : 1;
        best = Math.min(
          best,
          pointToSegment(p, g.textPos, { x: g.textPos.x + dir * w, y: g.textPos.y })
        );
      }
      return best;
    }
    case "region":
      if (regionFillsPoint(e, p)) return 0;
      return entitySegments(e).reduce(
        (best, [a, b]) => Math.min(best, pointToSegment(p, a, b)),
        Infinity
      );
    case "hatch":
      return distanceToHatch(e, p);
    // [RASTER-IMAGE-1] A photo is picked ANYWHERE on it, not at an anchor —
    // deliberately unlike insert/xref below, which pick at their insertion point
    // because hit-testing their geometry would need a def this function is not
    // given. An image needs no def: its corners are on the entity.
    case "image": {
      const quad = imageCorners(e);
      if (quad.length !== 4) return Infinity;
      let best = Infinity;
      for (let i = 0; i < 4; i++) {
        const d = pointToSegment(p, quad[i], quad[(i + 1) % 4]);
        if (d < best) best = d;
      }
      if (e.behind === false && pointInPolygon(p, quad)) return 0;
      return best;
    }
    case "insert":
      return distance(p, e.insertionPoint || { x: 0, y: 0 });
    case "xref":
      return distance(p, e.insertionPoint || { x: 0, y: 0 });
    case "dimension":
      return distanceToDimension(e, p, ctx);
    default:
      return Infinity;
  }
}
function distanceToXrefFrame(xref, p, xrefDefs) {
  const b = xrefFrameBounds(xref, xrefDefs);
  if (!b || b.minX === b.maxX && b.minY === b.maxY) return Infinity;
  const bl = { x: b.minX, y: b.minY };
  const br = { x: b.maxX, y: b.minY };
  const tr = { x: b.maxX, y: b.maxY };
  const tl = { x: b.minX, y: b.maxY };
  return Math.min(
    pointToSegment(p, bl, br),
    pointToSegment(p, br, tr),
    pointToSegment(p, tr, tl),
    pointToSegment(p, tl, bl)
  );
}
function outlineCrossesPolygon(pts, path) {
  for (let i = 0; i < pts.length - 1; i++) {
    for (let j = 0; j < path.length; j++) {
      const a = path[j];
      const b = path[(j + 1) % path.length];
      if (segSegIntersection(pts[i], pts[i + 1], a, b)) return true;
    }
  }
  return false;
}
var lassoOverlapsFrame = (framePts, path) => framePts.length >= 3 && path.some((p) => pointInPolygon(p, framePts));
function entitiesInLasso(entities, path, mode) {
  if (!path || path.length < 3) return [];
  const crossing = mode === "crossing";
  const out = [];
  for (const e of entities) {
    const pts = entityOutlinePoints(e);
    if (!pts.length) continue;
    const inside = pts.map((p) => pointInPolygon(p, path));
    const crosses = () => {
      const chains = e.type === "region" ? regionRingChains(e) : null;
      return chains ? chains.some((c) => outlineCrossesPolygon(c, path)) : pts.length > 1 && outlineCrossesPolygon(pts, path);
    };
    if (crossing) {
      const areaHit = imagePicksByArea(e) && lassoOverlapsFrame(pts, path);
      if (inside.some(Boolean) || areaHit || crosses()) out.push(e.id);
    } else if (inside.every(Boolean)) {
      out.push(e.id);
    }
  }
  return out;
}
function quickSelectMatch(entities, filter = {}, opts = {}) {
  const f = filter || {};
  const layers = opts && opts.layers;
  const index = layers ? layerIndex(layers) : null;
  const out = [];
  for (const e of entities) {
    if (index && !selectableEntity(e, index)) continue;
    if (f.type && e.type !== f.type) continue;
    if (f.layer && e.layer !== f.layer) continue;
    if (f.color != null && f.color !== "" && (e.color || "BYLAYER") !== f.color) continue;
    if (f.linetype && (e.linetype || "BYLAYER") !== f.linetype) continue;
    if (f.lengthMin != null || f.lengthMax != null) {
      const len3 = entityLength(e);
      if (len3 == null) continue;
      if (f.lengthMin != null && len3 < f.lengthMin) continue;
      if (f.lengthMax != null && len3 > f.lengthMax) continue;
    }
    out.push(e.id);
  }
  return out;
}

// src/cadGeometry/snap.js
var SNAP_LABELS = {
  endpoint: "Endpoint",
  midpoint: "Midpoint",
  center: "Center",
  quadrant: "Quadrant",
  intersection: "Intersection",
  perpendicular: "Perpendicular",
  tangent: "Tangent",
  nearest: "Nearest"
};
function snapLabel(type) {
  return SNAP_LABELS[type] || "";
}
var PRECISE_SNAP_TYPES = /* @__PURE__ */ new Set([
  "endpoint",
  "midpoint",
  "center",
  "quadrant",
  "intersection",
  "perpendicular",
  "tangent"
]);
function isPrecisePointSnap(hit) {
  return !!(hit && hit.type && PRECISE_SNAP_TYPES.has(hit.type));
}
var UNBOUNDED_TYPES = /* @__PURE__ */ new Set(["ray", "xline"]);
function findSnap(entities, p, tol, opts = {}) {
  const { excludeId, modes = {}, fromPoint, deferredTangent } = opts;
  const m = {
    endpoint: modes.endpoint !== false,
    midpoint: modes.midpoint !== false,
    center: modes.center !== false,
    intersection: !!modes.intersection,
    tangent: !!modes.tangent,
    perpendicular: !!modes.perpendicular,
    nearest: !!modes.nearest
  };
  let best = null;
  let bestD = tol;
  let owner = null;
  const consider = (pt2, type, extra) => {
    const d = Math.hypot(pt2.x - p.x, pt2.y - p.y);
    if (d < bestD) {
      bestD = d;
      best = { point: { x: pt2.x, y: pt2.y }, type, ...owner ? { entityId: owner } : null, ...extra };
    }
  };
  let nearestCand = null;
  let nearestD = tol;
  const considerNearest = (pt2) => {
    const d = Math.hypot(pt2.x - p.x, pt2.y - p.y);
    if (d < nearestD) {
      nearestD = d;
      nearestCand = { point: { x: pt2.x, y: pt2.y }, type: "nearest", ...owner ? { entityId: owner } : null };
    }
  };
  let interCand = null;
  let interD = tol;
  const considerInter = (pt2, extra) => {
    const d = Math.hypot(pt2.x - p.x, pt2.y - p.y);
    if (d < interD) {
      interD = d;
      interCand = { point: { x: pt2.x, y: pt2.y }, type: "intersection", ...extra };
    }
  };
  const crossing = (a, b) => {
    const ids = [...new Set([a, b].filter(Boolean))];
    return ids.length ? { entityId: ids[0], entityIds: ids } : void 0;
  };
  const gather = tol * 1.5;
  const nearSegs = [];
  const nearArcs = [];
  const wantSegs = m.intersection || m.perpendicular;
  const wantCurves = m.intersection || m.tangent || m.perpendicular;
  for (const e of entities) {
    if (excludeId && e.id === excludeId) continue;
    owner = e.id || null;
    if (!UNBOUNDED_TYPES.has(e.type)) {
      const bb = entityBounds(e);
      if (bb && (p.x < bb.minX - tol || p.x > bb.maxX + tol || p.y < bb.minY - tol || p.y > bb.maxY + tol)) {
        continue;
      }
    }
    if (m.nearest) {
      const n = nearestOnEntity(p, e);
      if (n) considerNearest(n.point);
    }
    if (e.type === "circle" || e.type === "arc") {
      if (m.center) consider({ x: e.cx, y: e.cy }, "center");
      if (e.type === "arc") {
        if (m.endpoint) {
          consider(onCirclePoint(e.cx, e.cy, e.r, e.startAngle), "endpoint");
          consider(onCirclePoint(e.cx, e.cy, e.r, e.endAngle), "endpoint");
        }
        if (m.midpoint) consider(onCirclePoint(e.cx, e.cy, e.r, arcMidAngle(e)), "midpoint");
      } else if (m.endpoint) {
        for (const a of [0, 90, 180, 270]) {
          consider(onCirclePoint(e.cx, e.cy, e.r, a), "quadrant");
        }
      }
      if (m.perpendicular && fromPoint) {
        const dd = Math.hypot(fromPoint.x - e.cx, fromPoint.y - e.cy);
        if (dd > 1e-9) {
          const pt2 = { x: e.cx + e.r * (fromPoint.x - e.cx) / dd, y: e.cy + e.r * (fromPoint.y - e.cy) / dd };
          if (angleInArcSweep(e, pt2)) consider(pt2, "perpendicular");
        }
      }
      if (wantCurves && Math.abs(Math.hypot(p.x - e.cx, p.y - e.cy) - e.r) <= gather) {
        nearArcs.push(e);
      }
      continue;
    }
    if (e.type === "ellipse") {
      if (m.center) consider({ x: e.cx, y: e.cy }, "center");
      if (isFullEllipse(e)) {
        if (m.endpoint) for (const q of ellipseQuadrants(e)) consider(q, "quadrant");
      } else {
        if (m.endpoint) {
          consider(ellipsePoint(e, e.startAngle), "endpoint");
          consider(ellipsePoint(e, e.endAngle), "endpoint");
        }
        if (m.midpoint) consider(ellipsePoint(e, e.startAngle + ellipseSweep(e) / 2), "midpoint");
        if (m.endpoint) {
          const start0 = (e.startAngle % 360 + 360) % 360;
          const sweep = ellipseSweep(e);
          for (const t of [0, 90, 180, 270]) {
            if ((t - start0 + 360) % 360 <= sweep + 1e-6) consider(ellipsePoint(e, t), "quadrant");
          }
        }
      }
      if (wantSegs) {
        for (const seg of entitySegments(e)) {
          if (pointToSegment(p, seg[0], seg[1]) <= gather) nearSegs.push({ a: seg[0], b: seg[1], id: e.id });
        }
      }
      continue;
    }
    if (e.type === "spline") {
      const tess = splineToPolyline(e);
      if (m.endpoint && tess.length && !e.closed) {
        consider(tess[0], "endpoint");
        consider(tess[tess.length - 1], "endpoint");
      }
      if (wantSegs) {
        for (let i = 0; i < tess.length - 1; i++) {
          if (pointToSegment(p, tess[i], tess[i + 1]) <= gather) nearSegs.push({ a: tess[i], b: tess[i + 1], id: e.id });
        }
      }
      continue;
    }
    if (e.type === "ray" || e.type === "xline") {
      if (m.endpoint && e.type === "ray") consider({ x: e.x, y: e.y }, "endpoint");
      if (wantSegs) {
        const seg = constructionSegment(e);
        if (pointToSegment(p, seg[0], seg[1]) <= gather) nearSegs.push({ a: seg[0], b: seg[1], id: e.id });
      }
      continue;
    }
    if (e.type === "revcloud") {
      const verts2 = e.points || [];
      if (m.endpoint && !e.closed && verts2.length) {
        consider(verts2[0], "endpoint");
        consider(verts2[verts2.length - 1], "endpoint");
      }
      if (wantSegs) {
        const tess = revisionCloudArcs(verts2, !!e.closed, e.arcRadius);
        for (let i = 0; i < tess.length - 1; i++) {
          if (pointToSegment(p, tess[i], tess[i + 1]) <= gather) nearSegs.push({ a: tess[i], b: tess[i + 1], id: e.id });
        }
      }
      continue;
    }
    const verts = entityVertices(e);
    if (m.endpoint) for (const v of verts) consider(v, "endpoint");
    if (m.midpoint) {
      if (e.type === "line") {
        consider({ x: (e.x1 + e.x2) / 2, y: (e.y1 + e.y2) / 2 }, "midpoint");
      } else if (e.type === "region") {
        for (const [a, b] of entitySegments(e))
          consider({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }, "midpoint");
      } else if (e.type === "polyline" || e.type === "rectangle") {
        const closed = e.type === "rectangle" || e.closed;
        for (let i = 0; i < verts.length - 1; i++) {
          consider({ x: (verts[i].x + verts[i + 1].x) / 2, y: (verts[i].y + verts[i + 1].y) / 2 }, "midpoint");
        }
        if (closed && verts.length > 2) {
          const a = verts[verts.length - 1];
          const b = verts[0];
          consider({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }, "midpoint");
        }
      }
    }
    if (wantSegs) {
      for (const seg of entitySegments(e)) {
        if (pointToSegment(p, seg[0], seg[1]) <= gather) nearSegs.push({ a: seg[0], b: seg[1], id: e.id });
      }
    }
  }
  owner = null;
  if (m.intersection) {
    for (let i = 0; i < nearSegs.length; i++) {
      const si = nearSegs[i];
      for (let j = i + 1; j < nearSegs.length; j++) {
        const sj = nearSegs[j];
        const pt2 = segSegIntersection(si.a, si.b, sj.a, sj.b);
        const own = crossing(si.id, sj.id);
        if (pt2) consider(pt2, "intersection", own), considerInter(pt2, own);
      }
      for (const arc of nearArcs) {
        const own = crossing(si.id, arc.id);
        for (const pt2 of segCircleIntersections(si.a, si.b, { x: arc.cx, y: arc.cy }, arc.r)) {
          if (angleInArcSweep(arc, pt2)) consider(pt2, "intersection", own), considerInter(pt2, own);
        }
      }
    }
    for (let i = 0; i < nearArcs.length; i++) {
      for (let j = i + 1; j < nearArcs.length; j++) {
        const a = nearArcs[i], b = nearArcs[j];
        const own = crossing(a.id, b.id);
        for (const pt2 of circleCircleIntersections({ x: a.cx, y: a.cy }, a.r, { x: b.cx, y: b.cy }, b.r)) {
          if (angleInArcSweep(a, pt2) && angleInArcSweep(b, pt2)) consider(pt2, "intersection", own), considerInter(pt2, own);
        }
      }
    }
  }
  if (m.tangent && fromPoint) {
    for (const arc of nearArcs) {
      for (const pt2 of tangentPointsToCircle(fromPoint, { x: arc.cx, y: arc.cy }, arc.r)) {
        if (angleInArcSweep(arc, pt2)) consider(pt2, "tangent", arc.id ? { entityId: arc.id } : void 0);
      }
    }
  }
  if (m.tangent && !fromPoint && deferredTangent) {
    for (const arc of nearArcs) {
      const d = Math.hypot(p.x - arc.cx, p.y - arc.cy);
      if (d <= 1e-9) continue;
      const pt2 = { x: arc.cx + arc.r * (p.x - arc.cx) / d, y: arc.cy + arc.r * (p.y - arc.cy) / d };
      if (angleInArcSweep(arc, pt2)) consider(pt2, "tangent", { deferred: true, entityId: arc.id });
    }
  }
  if (m.perpendicular && fromPoint) {
    for (const seg of nearSegs) {
      const foot = perpendicularFoot(fromPoint, seg.a, seg.b);
      const vx = seg.b.x - seg.a.x;
      const vy = seg.b.y - seg.a.y;
      const len22 = vx * vx + vy * vy;
      const t = len22 ? ((foot.x - seg.a.x) * vx + (foot.y - seg.a.y) * vy) / len22 : -1;
      if (t >= -1e-9 && t <= 1 + 1e-9) consider(foot, "perpendicular", seg.id ? { entityId: seg.id } : void 0);
    }
  }
  if (best && best.deferred && interCand) best = interCand;
  if (!best && nearestCand) best = nearestCand;
  return best;
}
function resolveDeferredTangent(curve, otherPoint, hint) {
  if (!curve || curve.type !== "circle" && curve.type !== "arc") return null;
  const cands = tangentPointsToCircle(otherPoint, { x: curve.cx, y: curve.cy }, curve.r).filter(
    (pt2) => angleInArcSweep(curve, pt2)
  );
  if (!cands.length) return null;
  if (!hint || cands.length === 1) return cands[0];
  const d2 = (a) => (a.x - hint.x) ** 2 + (a.y - hint.y) ** 2;
  return d2(cands[0]) <= d2(cands[1]) ? cands[0] : cands[1];
}

// src/cadTransform/offset.js
var OFFSETTABLE = /* @__PURE__ */ new Set([
  "line",
  "circle",
  "arc",
  "ellipse",
  "spline",
  "ray",
  "xline",
  "polyline",
  "rectangle"
]);
var isZeroLen = (ax, ay, bx, by) => {
  const d = Math.hypot(bx - ax, by - ay);
  return !Number.isFinite(d) || d <= 1e-12;
};
var finitePts = (pts) => pts.every((p) => p && Number.isFinite(p.x) && Number.isFinite(p.y));
function compactPolylinePoints(points, closed) {
  const pts = [];
  for (const p of points || []) {
    const last = pts[pts.length - 1];
    if (last && isZeroLen(last.x, last.y, p.x, p.y)) continue;
    pts.push(p);
  }
  while (closed && pts.length > 1 && isZeroLen(pts[0].x, pts[0].y, pts[pts.length - 1].x, pts[pts.length - 1].y))
    pts.pop();
  return pts;
}
function offsetRefusal(entity, distance2) {
  if (!entity || !OFFSETTABLE.has(entity.type)) return "type-not-offsettable";
  if (!Number.isFinite(distance2)) return "distance-not-finite";
  if (distance2 <= 0) return "distance-not-positive";
  switch (entity.type) {
    case "line":
      return isZeroLen(entity.x1, entity.y1, entity.x2, entity.y2) ? "degenerate-source" : null;
    case "ray":
    case "xline":
      return isZeroLen(0, 0, entity.dx, entity.dy) || !finitePts([{ x: entity.x, y: entity.y }]) ? "degenerate-source" : null;
    case "circle":
    case "arc":
      return Number.isFinite(entity.r) && entity.r > 1e-12 && finitePts([{ x: entity.cx, y: entity.cy }]) ? null : "degenerate-source";
    case "ellipse":
      return Number.isFinite(entity.rx) && Number.isFinite(entity.ry) && entity.rx > 1e-12 && entity.ry > 1e-12 && finitePts([{ x: entity.cx, y: entity.cy }]) ? null : "degenerate-source";
    case "rectangle":
      return Math.abs(entity.width) > 1e-12 && Math.abs(entity.height) > 1e-12 && finitePts([{ x: entity.x, y: entity.y }]) ? null : "degenerate-source";
    case "polyline": {
      const raw = Array.isArray(entity.points) ? entity.points : [];
      if (raw.length < 2 || !finitePts(raw)) return "degenerate-source";
      const pts = compactPolylinePoints(raw, !!entity.closed);
      return pts.length >= 2 ? null : "degenerate-source";
    }
    case "spline": {
      const defining = splineDefiningPoints(entity);
      if (!Array.isArray(defining) || defining.length < 2 || !finitePts(defining))
        return "degenerate-source";
      const pts = splineToPolyline(entity);
      if (pts.length < 2 || !finitePts(pts)) return "degenerate-source";
      const first = pts[0];
      return pts.some((p) => !isZeroLen(first.x, first.y, p.x, p.y)) ? null : "degenerate-source";
    }
    default:
      return null;
  }
}
function offsetLine(e, dist2, side) {
  const a = { x: e.x1, y: e.y1 };
  const b = { x: e.x2, y: e.y2 };
  const L = Math.hypot(b.x - a.x, b.y - a.y) || 1;
  let nx = -(b.y - a.y) / L;
  let ny = (b.x - a.x) / L;
  const sign = (side.x - a.x) * nx + (side.y - a.y) * ny >= 0 ? 1 : -1;
  nx *= sign * dist2;
  ny *= sign * dist2;
  return carryId(e, { type: "line", x1: a.x + nx, y1: a.y + ny, x2: b.x + nx, y2: b.y + ny });
}
function offsetCircular(e, dist2, side) {
  const outside = Math.hypot(side.x - e.cx, side.y - e.cy) > e.r;
  const r = outside ? e.r + dist2 : e.r - dist2;
  if (r <= 1e-9) return null;
  const base = carryId(e, { type: e.type, cx: e.cx, cy: e.cy, r });
  if (e.type === "arc") {
    base.startAngle = e.startAngle;
    base.endAngle = e.endAngle;
  }
  return base;
}
function offsetEllipse(e, dist2, side) {
  const rot = (e.rotation || 0) * Math.PI / 180;
  const ca = Math.cos(rot);
  const sa = Math.sin(rot);
  const lx = (side.x - e.cx) * ca + (side.y - e.cy) * sa;
  const ly = -(side.x - e.cx) * sa + (side.y - e.cy) * ca;
  const inside = lx * lx / (e.rx * e.rx) + ly * ly / (e.ry * e.ry) < 1;
  const rx = inside ? e.rx - dist2 : e.rx + dist2;
  const ry = inside ? e.ry - dist2 : e.ry + dist2;
  if (rx <= 1e-9 || ry <= 1e-9) return null;
  const base = carryId(e, {
    type: "ellipse",
    cx: e.cx,
    cy: e.cy,
    rx,
    ry,
    rotation: e.rotation || 0
  });
  if (e.startAngle != null && e.endAngle != null) {
    base.startAngle = e.startAngle;
    base.endAngle = e.endAngle;
  }
  return base;
}
function offsetSpline(e, dist2, side) {
  const tess = splineToPolyline(e);
  if (tess.length < 2) return null;
  const pts = offsetPolylinePoints(tess, !!e.closed, dist2, side);
  if (!pts || pts.length < 2) return null;
  const maxN = 32;
  let fit = pts;
  if (pts.length > maxN) {
    fit = [];
    const stride = (pts.length - 1) / (maxN - 1);
    for (let i = 0; i < maxN; i++) fit.push({ ...pts[Math.round(i * stride)] });
  } else {
    fit = pts.map((p) => ({ ...p }));
  }
  return carryId(e, { type: "spline", fitPoints: fit, degree: e.degree || 3, closed: !!e.closed });
}
function offsetConstruction(e, dist2, side) {
  const L = Math.hypot(e.dx, e.dy) || 1;
  const nx = -e.dy / L;
  const ny = e.dx / L;
  const sign = (side.x - e.x) * nx + (side.y - e.y) * ny >= 0 ? 1 : -1;
  return carryId(e, {
    type: e.type,
    x: e.x + nx * sign * dist2,
    y: e.y + ny * sign * dist2,
    dx: e.dx,
    dy: e.dy
  });
}
function offsetPolylinePoints(points, closed, dist2, side) {
  const segPts = [];
  for (let i = 0; i < points.length - 1; i++) segPts.push([points[i], points[i + 1]]);
  if (closed && points.length > 2) segPts.push([points[points.length - 1], points[0]]);
  if (!segPts.length) return null;
  const f = segPts[0];
  const fl = Math.hypot(f[1].x - f[0].x, f[1].y - f[0].y) || 1;
  const fnx = -(f[1].y - f[0].y) / fl;
  const fny = (f[1].x - f[0].x) / fl;
  const sign = (side.x - f[0].x) * fnx + (side.y - f[0].y) * fny >= 0 ? 1 : -1;
  const off = segPts.map(([a, b]) => {
    const l = Math.hypot(b.x - a.x, b.y - a.y) || 1;
    const nx = -(b.y - a.y) / l * sign * dist2;
    const ny = (b.x - a.x) / l * sign * dist2;
    return [
      { x: a.x + nx, y: a.y + ny },
      { x: b.x + nx, y: b.y + ny }
    ];
  });
  const n = off.length;
  const inter = (s1, s2) => lineLineIntersection(s1[0], s1[1], s2[0], s2[1]);
  const out = [];
  if (!closed) {
    out.push(off[0][0]);
    for (let i = 1; i < n; i++) out.push(inter(off[i - 1], off[i]) || off[i][0]);
    out.push(off[n - 1][1]);
  } else {
    for (let i = 0; i < n; i++) out.push(inter(off[(i - 1 + n) % n], off[i]) || off[i][0]);
  }
  return out;
}
function offsetEntity(entity, distance2, sidePoint) {
  if (offsetRefusal(entity, distance2)) return null;
  switch (entity.type) {
    case "line":
      return offsetLine(entity, distance2, sidePoint);
    case "circle":
    case "arc":
      return offsetCircular(entity, distance2, sidePoint);
    case "ellipse":
      return offsetEllipse(entity, distance2, sidePoint);
    case "spline":
      return offsetSpline(entity, distance2, sidePoint);
    case "ray":
    case "xline":
      return offsetConstruction(entity, distance2, sidePoint);
    case "polyline": {
      const src = compactPolylinePoints(entity.points, !!entity.closed);
      const pts = offsetPolylinePoints(src, !!entity.closed, distance2, sidePoint);
      return pts ? carryId(entity, { type: "polyline", points: pts, closed: !!entity.closed }) : null;
    }
    case "rectangle": {
      const corners = [
        { x: entity.x, y: entity.y },
        { x: entity.x + entity.width, y: entity.y },
        { x: entity.x + entity.width, y: entity.y + entity.height },
        { x: entity.x, y: entity.y + entity.height }
      ];
      const pts = offsetPolylinePoints(corners, true, distance2, sidePoint);
      return pts ? carryId(entity, { type: "polyline", points: pts, closed: true }) : null;
    }
    default:
      return null;
  }
}

// src/cadTransform/align.js
var fail = (reason) => ({ ok: false, reason });
var finite3 = (p) => !!p && Number.isFinite(p.x) && Number.isFinite(p.y);
function alignTransform(src1, dst1, src2, dst2, opts = {}) {
  if (![src1, dst1, src2, dst2].every(finite3)) return fail("invalid");
  const sdx = src2.x - src1.x;
  const sdy = src2.y - src1.y;
  const ddx = dst2.x - dst1.x;
  const ddy = dst2.y - dst1.y;
  const sLen = Math.hypot(sdx, sdy);
  const dLen = Math.hypot(ddx, ddy);
  if (!(sLen > 1e-12)) return fail("degenerate-source");
  if (!(dLen > 1e-12)) return fail("degenerate-target");
  const rotateDeg = (Math.atan2(ddy, ddx) - Math.atan2(sdy, sdx)) * 180 / Math.PI;
  return {
    ok: true,
    dx: dst1.x - src1.x,
    dy: dst1.y - src1.y,
    rotateDeg,
    scaleFactor: opts.scale ? dLen / sLen : 1,
    pivot: { x: dst1.x, y: dst1.y }
  };
}
function alignEntities(entities, src1, dst1, src2, dst2, opts = {}) {
  const t = alignTransform(src1, dst1, src2, dst2, opts);
  if (!t.ok) return t;
  const out = (entities || []).map((e) => {
    let r = translateEntity(e, t.dx, t.dy);
    if (Math.abs(t.rotateDeg) > 1e-12) r = rotateEntity(r, t.pivot, t.rotateDeg);
    if (Math.abs(t.scaleFactor - 1) > 1e-12) r = scaleEntity(r, t.pivot, t.scaleFactor);
    return r;
  });
  return { ok: true, entities: out, transform: t };
}

// src/cadTransform/insert.js
function transformEntity(e, insert, basePoint = { x: 0, y: 0 }) {
  const ip = insert.insertionPoint || { x: 0, y: 0 };
  const sx = insert.scaleX != null ? insert.scaleX : 1;
  const sy = insert.scaleY != null ? insert.scaleY : 1;
  const rot = insert.rotation || 0;
  const T = (p) => transformPoint(p, ip, sx, sy, rot, basePoint);
  const negX = sx < 0;
  const negY = sy < 0;
  const mirrored = negX !== negY;
  const ang = (a) => {
    const v = a || 0;
    const w = mirrored ? (negX ? 180 - v : -v) + rot : v + (negX && negY ? 180 : 0) + rot;
    return (w % 360 + 360) % 360;
  };
  switch (e.type) {
    case "line": {
      const a = T({ x: e.x1, y: e.y1 });
      const b = T({ x: e.x2, y: e.y2 });
      return { ...e, x1: a.x, y1: a.y, x2: b.x, y2: b.y };
    }
    case "polyline":
      return { ...e, points: e.points.map((p) => T(p)) };
    case "rectangle":
      return asPolyline(e, rectCorners(e).map((p) => T(p)));
    case "circle": {
      const c = T({ x: e.cx, y: e.cy });
      return { ...e, cx: c.x, cy: c.y, r: e.r * Math.abs(sx) };
    }
    case "arc": {
      const c = T({ x: e.cx, y: e.cy });
      return {
        ...e,
        cx: c.x,
        cy: c.y,
        r: e.r * Math.abs(sx),
        startAngle: ang(mirrored ? e.endAngle : e.startAngle),
        endAngle: ang(mirrored ? e.startAngle : e.endAngle)
      };
    }
    case "ellipse": {
      const c = T({ x: e.cx, y: e.cy });
      const out = {
        ...e,
        cx: c.x,
        cy: c.y,
        rx: e.rx * Math.abs(sx),
        ry: e.ry * Math.abs(sy),
        rotation: ang(e.rotation)
      };
      if (mirrored && Number.isFinite(e.startAngle) && Number.isFinite(e.endAngle)) {
        const sweep = Math.abs(e.endAngle - e.startAngle);
        const start = (-e.endAngle % 360 + 360) % 360;
        out.startAngle = start;
        out.endAngle = start + sweep;
      }
      return out;
    }
    case "text": {
      const a = T({ x: e.x, y: e.y });
      return {
        ...e,
        ...e.annotative === true ? { annotative: false } : {},
        x: a.x,
        y: a.y,
        height: (e.height || 1) * Math.abs(sy),
        rotation: (e.rotation || 0) + rot
      };
    }
    case "mtext": {
      const a = T({ x: e.x, y: e.y });
      return {
        ...e,
        x: a.x,
        y: a.y,
        ...e.annotative === true ? { annotative: false } : {},
        // see the text branch
        height: (e.height || 1) * Math.abs(sy),
        width: (e.width || 0) * Math.abs(sx),
        rotation: (e.rotation || 0) + rot
      };
    }
    case "table": {
      const a = T({ x: e.x, y: e.y });
      return {
        ...e,
        x: a.x,
        y: a.y,
        colWidths: Array.isArray(e.colWidths) ? e.colWidths.map((w) => w * Math.abs(sx)) : e.colWidths,
        rowHeights: Array.isArray(e.rowHeights) ? e.rowHeights.map((h) => h * Math.abs(sy)) : e.rowHeights,
        textHeight: Number.isFinite(e.textHeight) ? e.textHeight * Math.abs(sy) : e.textHeight,
        rotation: (e.rotation || 0) + rot
      };
    }
    case "point": {
      const a = T({ x: e.x, y: e.y });
      return { ...e, x: a.x, y: a.y };
    }
    // ── [BUG-BLOCK-NESTED-1] a block inside a block ────────────────────────────
    // A nested INSERT fell to `default` and came back UNTRANSFORMED, so its
    // insertionPoint stayed in the parent block's LOCAL frame and the parent's
    // rotation and scale never reached it. The nested block was still drawn — just
    // somewhere else entirely, which reads as "it is missing".
    //
    // Measured on `fast-food-Restaurant.dwg`: the McCafé kitchen (layer
    // MOB_COCIN161, the whole equipment run in the owner's ShareCAD screenshot) is
    // ONE model-space INSERT of `A$C238B4631` at (339.25, 115.61) whose definition
    // holds exactly two entities — both INSERTs, `MC_MUEB_05` and `MCF_1`. Those two
    // carry the equipment, and both were placed at their block-local coordinates
    // instead of inside the room.
    //
    // The composition: the child's own placement runs THROUGH the parent's, so the
    // insertion point maps like any other point and the scales multiply. Rotation
    // composes by ADDING — except under a parent that mirrors (exactly one negative
    // scale), where the sense reverses and it is the parent's rotation MINUS the
    // child's, the same algebra `mapInsert` uses on import.
    case "insert":
    case "xref": {
      const a = T(e.insertionPoint || { x: 0, y: 0 });
      const mirrored2 = sx < 0 !== sy < 0;
      const own = e.rotation || 0;
      return {
        ...e,
        insertionPoint: a,
        scaleX: (e.scaleX != null ? e.scaleX : 1) * sx,
        scaleY: (e.scaleY != null ? e.scaleY : 1) * sy,
        rotation: mirrored2 ? rot - own : own + rot,
        // Placed ATTRIBUTES are world-anchored text on the child insert; they move
        // with it or they are left behind in the parent's frame. `placeAttribute`
        // is the shared rule — [C5] gave it a second caller (seeding a new INSERT's
        // attributes from its definition's attdefs), and two spellings of one
        // mapping is how a placed attribute and a promoted one come to disagree
        // about where a mirrored block puts its text.
        ...Array.isArray(e.attributes) && e.attributes.length ? { attributes: e.attributes.map((at) => placeAttribute(at, insert, basePoint)) } : {}
      };
    }
    // [RASTER-IMAGE-1] An image inside a block composes through the SAME linear
    // map as everything else — one line, because the frame is edge vectors. A
    // block containing a mirrored insert therefore mirrors its images too, which
    // is correct and is what a `rotation` scalar could not have expressed.
    case "image":
      return mapImageFrame(e, T);
    // The other kinds that used to fall through, each mapped by the SAME helper
    // rotate/scale/mirror use — so a hatch, a spline or a dimension inside a block
    // lands where its neighbours do instead of staying in block-local coordinates.
    case "hatch":
      return mapHatchGeometry(e, T);
    case "spline":
      return mapSplinePoints(e, T);
    case "dimension":
      return mapDimensionPoints(e, T, T);
    // T is affine — the label nudge rides the block transform
    case "revcloud":
      return { ...e, points: (e.points || []).map((p) => T(p)) };
    case "leader":
      return {
        ...e,
        points: (e.points || []).map((p) => T(p)),
        ...e.textPos ? { textPos: T(e.textPos) } : {}
      };
    case "ray":
    case "xline": {
      const a = T({ x: e.x, y: e.y });
      const d = transformPoint(
        { x: (e.dx || 0) + basePoint.x, y: (e.dy || 0) + basePoint.y },
        { x: 0, y: 0 },
        sx,
        sy,
        rot,
        basePoint
      );
      return { ...e, x: a.x, y: a.y, dx: d.x, dy: d.y };
    }
    // C4b: a region inside a block definition. Rings map like any other point
    // list; the winding invariant is restored because a MIRRORED insert
    // (negative scale) reverses every ring — see mapRegionRings' header.
    case "region":
      return mapRegionRings(e, T);
    // ── The enumeration (PROCESS §E.1) ────────────────────────────────────────
    // Every member of ENTITY_TYPES has a case above: line, polyline, rectangle,
    // circle, arc, ellipse, spline, ray, xline, revcloud, text, mtext, point,
    // dimension, leader, table, hatch, insert, xref, region. `default` is therefore
    // UNREACHABLE for a well-formed document, and reaching it means a new entity
    // type was added without deciding how it places inside a block — which is the
    // exact mistake that left nested inserts and hatches sitting in block-local
    // coordinates. It returns the entity untouched (the historical behaviour, so a
    // foreign or future type degrades to "drawn in the wrong place" rather than
    // "throws"), and says so once in development.
    default:
      if (typeof process !== "undefined" && process.env && process.env.NODE_ENV !== "production") {
        console.warn(
          `[cad-engine] transformEntity: no placement rule for '${e && e.type}' \u2014 it will be drawn in BLOCK-LOCAL coordinates. Add a case in cadTransform/insert.js.`
        );
      }
      return { ...e };
  }
}
function explodeAttributeLoss(insert) {
  return (insert && insert.attributes || []).filter((a) => a && a.visible === false && a.value).map((a) => a.tag).filter(Boolean);
}
function reframeAttributes(attrs, from, to) {
  if (!Array.isArray(attrs) || !attrs.length) return null;
  const B = { x: 0, y: 0 };
  return attrs.map((a) => placeAttribute(unplaceAttribute(a, from, B), to, B));
}
function unplaceAttribute(a, insert, basePoint = { x: 0, y: 0 }) {
  const ip = insert.insertionPoint || { x: 0, y: 0 };
  const sx = insert.scaleX != null ? insert.scaleX : 1;
  const sy = insert.scaleY != null ? insert.scaleY : 1;
  const rot = insert.rotation || 0;
  const mirrored = sx < 0 !== sy < 0;
  const dx = (a.x || 0) - (ip.x || 0);
  const dy = (a.y || 0) - (ip.y || 0);
  const rad = -rot * Math.PI / 180;
  const ux = dx * Math.cos(rad) - dy * Math.sin(rad);
  const uy = dx * Math.sin(rad) + dy * Math.cos(rad);
  return {
    ...a,
    x: (sx === 0 ? 0 : ux / sx) + (basePoint.x || 0),
    y: (sy === 0 ? 0 : uy / sy) + (basePoint.y || 0),
    height: sy === 0 ? a.height || 0 : (a.height || 0) / Math.abs(sy),
    rotation: mirrored ? rot - (a.rotation || 0) : (a.rotation || 0) - rot
  };
}
function placeAttribute(a, insert, basePoint = { x: 0, y: 0 }) {
  const ip = insert.insertionPoint || { x: 0, y: 0 };
  const sx = insert.scaleX != null ? insert.scaleX : 1;
  const sy = insert.scaleY != null ? insert.scaleY : 1;
  const rot = insert.rotation || 0;
  const mirrored = sx < 0 !== sy < 0;
  const p = transformPoint({ x: a.x || 0, y: a.y || 0 }, ip, sx, sy, rot, basePoint);
  return {
    ...a,
    x: p.x,
    y: p.y,
    height: (a.height || 0) * Math.abs(sy),
    rotation: mirrored ? rot - (a.rotation || 0) : (a.rotation || 0) + rot
  };
}
function placeBlockChild(se, insert, basePoint = { x: 0, y: 0 }) {
  const t = transformEntity(se, insert, basePoint);
  const layer = se.layer && se.layer !== "0" ? se.layer : insert.layer || "0";
  return { ...t, layer, color: se.color || null, linetype: se.linetype || null };
}
function drawnBlockChildren(insert, blockDef, isShown) {
  if (!blockDef || !Array.isArray(blockDef.entities)) return [];
  const base = blockDef.basePoint || { x: 0, y: 0 };
  const out = [];
  for (const se of blockDef.entities) {
    const placed = placeBlockChild(se, insert, base);
    if (isShown && !isShown(placed.layer)) continue;
    out.push(placed);
  }
  return out;
}
function explodeInsert(insert, blockDef) {
  const pieces = drawnBlockChildren(insert, blockDef, null).map((t) => ({
    ...t,
    id: genId(t.type && t.type[0] || "e")
  }));
  for (const a of insert && insert.attributes || []) {
    if (!a || a.visible === false || !a.value) continue;
    pieces.push({
      id: genId("t"),
      type: "text",
      layer: insert.layer,
      color: insert.color != null ? insert.color : null,
      linetype: null,
      x: a.x || 0,
      y: a.y || 0,
      text: String(a.value),
      height: a.height || 1,
      rotation: a.rotation || 0,
      ...a.bold ? { bold: true } : {},
      ...a.italic ? { italic: true } : {}
    });
  }
  return pieces;
}

// src/CADModel/xref.js
function uniqueXrefName(xrefDefs, name) {
  const base = name || "xref";
  if (!xrefDefs[base]) return base;
  let n = 2;
  while (xrefDefs[`${base}(${n})`]) n += 1;
  return `${base}(${n})`;
}
function attachXref(doc, name, entities, layers, sourceFile, extra = {}) {
  const xrefDefs = { ...doc.xrefDefs || {} };
  const finalName = uniqueXrefName(xrefDefs, name);
  xrefDefs[finalName] = {
    entities: Array.isArray(entities) ? entities : [],
    layers: Array.isArray(layers) ? layers : [],
    sourceFile: sourceFile || "",
    ...extra && extra.blockDefs ? { blockDefs: cloneDefs(extra.blockDefs) } : {},
    ...extra && extra.linetypeDefs ? { linetypeDefs: cloneDefs(extra.linetypeDefs) } : {}
  };
  const entity = {
    id: genId("xr"),
    type: "xref",
    layer: extra && extra.layer || DEFAULT_LAYER,
    color: null,
    linetype: null,
    xrefName: finalName,
    insertionPoint: { x: 0, y: 0 },
    scaleX: 1,
    scaleY: 1,
    rotation: 0,
    layerVisibility: {}
  };
  return { ...doc, xrefDefs, entities: [...doc.entities, entity] };
}
function detachXref(doc, name) {
  if (!doc.xrefDefs || !doc.xrefDefs[name]) return doc;
  const xrefDefs = { ...doc.xrefDefs };
  delete xrefDefs[name];
  return {
    ...doc,
    xrefDefs,
    entities: doc.entities.filter((e) => !(e.type === "xref" && e.xrefName === name))
  };
}
function reloadXref(doc, name, newEntities, newLayers) {
  if (!doc.xrefDefs || !doc.xrefDefs[name]) return doc;
  const xrefDefs = { ...doc.xrefDefs };
  xrefDefs[name] = {
    ...xrefDefs[name],
    entities: Array.isArray(newEntities) ? newEntities : [],
    ...newLayers != null ? { layers: Array.isArray(newLayers) ? newLayers : [] } : {}
  };
  return { ...doc, xrefDefs };
}
function bindXref(doc, xrefName) {
  const def = doc.xrefDefs && doc.xrefDefs[xrefName];
  if (!def) return doc;
  const instances = doc.entities.filter((e) => e.type === "xref" && e.xrefName === xrefName);
  const haveLayer = new Set(doc.layers.map((l) => l.name));
  const newLayers = [];
  const bound = [];
  for (const inst of instances) {
    for (const se of def.entities || []) {
      const t = transformEntity(se, inst, { x: 0, y: 0 });
      const subLayer = se.layer || DEFAULT_LAYER;
      const boundLayer = `${xrefName}$${subLayer}`;
      if (!haveLayer.has(boundLayer)) {
        haveLayer.add(boundLayer);
        const src = (def.layers || []).find((l) => l.name === subLayer);
        newLayers.push({ name: boundLayer, color: src && src.color || "#ffffff", visible: true, locked: false, frozen: false, plot: true });
      }
      bound.push({
        ...t,
        id: genId(t.type && t.type[0] || "e"),
        layer: boundLayer,
        color: se.color || null,
        linetype: se.linetype || null
      });
    }
  }
  const xrefDefs = { ...doc.xrefDefs };
  delete xrefDefs[xrefName];
  return {
    ...doc,
    xrefDefs,
    layers: [...doc.layers, ...newLayers],
    entities: [
      ...doc.entities.filter((e) => !(e.type === "xref" && e.xrefName === xrefName)),
      ...bound
    ]
  };
}
function xrefLayerVisible(xrefEntity, layerName) {
  const v = xrefEntity.layerVisibility;
  return !v || v[layerName] !== false;
}

// src/cadPaperSpace.js
var PX_PER_MM = 96 / 25.4;
var mmToPx = (mm) => mm * PX_PER_MM;
var pxToMm = (px) => px / PX_PER_MM;
var MODEL_UNIT_MM = 1e3;
function modelUnitMm(doc) {
  const u = doc && doc.meta && doc.meta.units;
  if (u === "mm" || u === "millimeters") return 1;
  if (u === "cm" || u === "centimeters") return 10;
  return MODEL_UNIT_MM;
}
var PAPER_SIZES = {
  A4: [297, 210],
  A3: [420, 297],
  A2: [594, 420],
  A1: [841, 594],
  A0: [1189, 841]
};
var MAX_PAPER_MM = 1e4;
var sheetMm = (v) => {
  const n = typeof v === "string" ? Number(v.trim()) : Number(v);
  return Number.isFinite(n) && n > 0 && n <= MAX_PAPER_MM ? n : 0;
};
function paperDims(paperSize, orientation = "landscape", custom = null) {
  let w;
  let h;
  const cw = sheetMm(custom && custom.width);
  const ch = sheetMm(custom && custom.height);
  if (paperSize === "custom" && cw && ch) {
    w = cw;
    h = ch;
  } else {
    const dims = PAPER_SIZES[paperSize] || PAPER_SIZES.A3;
    w = dims[0];
    h = dims[1];
  }
  if (orientation === "portrait") return { w: Math.min(w, h), h: Math.max(w, h) };
  return { w: Math.max(w, h), h: Math.min(w, h) };
}
function layoutPaperDims(layout) {
  if (!layout) return paperDims("A3", "landscape");
  return paperDims(layout.paperSize, layout.orientation, layout.custom);
}
function viewportFrameMm(vp, unitMm = MODEL_UNIT_MM) {
  const wr = vp.worldRect || { width: 1, height: 1 };
  return {
    width: Math.max(wr.width * unitMm * vp.scale, 1e-6),
    height: Math.max(wr.height * unitMm * vp.scale, 1e-6)
  };
}
function scaleViewportKeepingFrame(vp, unitMm = MODEL_UNIT_MM, nextScale) {
  if (!vp || vp.locked) return null;
  if (!Number.isFinite(nextScale) || nextScale <= 0) return null;
  const frame = viewportFrameMm(vp, unitMm);
  const wr = vp.worldRect || { x: 0, y: 0, width: 1, height: 1 };
  const cx = wr.x + wr.width / 2;
  const cy = wr.y + wr.height / 2;
  const width = frame.width / (unitMm * nextScale);
  const height = frame.height / (unitMm * nextScale);
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return null;
  return {
    scale: nextScale,
    worldRect: { x: cx - width / 2, y: cy - height / 2, width, height }
  };
}
function viewportPaperRectMm(vp, layout, unitMm = MODEL_UNIT_MM) {
  const frame = viewportFrameMm(vp, unitMm);
  const sheet = layoutPaperDims(layout);
  const pos = vp.paperPos || {
    x: (sheet.w - frame.width) / 2,
    y: (sheet.h - frame.height) / 2
  };
  return { x: pos.x, y: pos.y, width: frame.width, height: frame.height };
}
function viewportRectPx(vp, layout, unitMm = MODEL_UNIT_MM) {
  const sheet = layoutPaperDims(layout);
  const sheetHpx = mmToPx(sheet.h);
  const rMm = viewportPaperRectMm(vp, layout, unitMm);
  return {
    x: mmToPx(rMm.x),
    y: sheetHpx - mmToPx(rMm.y + rMm.height),
    // flip mm-Y-up → px-Y-down
    width: mmToPx(rMm.width),
    height: mmToPx(rMm.height)
  };
}
function viewportContentAffine(vp, layout, unitMm = MODEL_UNIT_MM) {
  const wr = vp.worldRect;
  const rect = viewportRectPx(vp, layout, unitMm);
  const ax = rect.width / Math.max(wr.width, 1e-9);
  const ay = -rect.height / Math.max(wr.height, 1e-9);
  const bx = rect.x - ax * wr.x;
  const by = rect.y + rect.height - ay * wr.y;
  return { ax, bx, ay, by, rect };
}
function paperPxToModel(px, py, affine) {
  return { x: (px - affine.bx) / affine.ax, y: (py - affine.by) / affine.ay };
}
function fitSheetView(sheet, containerW, containerH, padding = 0.92) {
  const Wpx = mmToPx(sheet.w);
  const Hpx = mmToPx(sheet.h);
  if (!containerW || !containerH) return { scale: 1, x: 0, y: 0 };
  const scale = Math.min(containerW / Wpx, containerH / Hpx) * padding;
  return {
    scale,
    x: (containerW - Wpx * scale) / 2,
    y: (containerH - Hpx * scale) / 2
  };
}
function screenToPaperPx(sheetView, sx, sy) {
  return { x: (sx - sheetView.x) / sheetView.scale, y: (sy - sheetView.y) / sheetView.scale };
}
var SHEET_ZOOM_MIN_FIT = 0.25;
var SHEET_ZOOM_MAX_FIT = 24;
var SHEET_KEEP_VISIBLE_PX = 48;
function clampSheetView(view, sheet, containerW, containerH, fit, keepPx = SHEET_KEEP_VISIBLE_PX) {
  const base = fit || view.scale;
  const scale = clampScale2(view.scale, { min: base * SHEET_ZOOM_MIN_FIT, max: base * SHEET_ZOOM_MAX_FIT });
  const Wpx = mmToPx(sheet.w) * scale;
  const Hpx = mmToPx(sheet.h) * scale;
  const x = Math.min(containerW - keepPx, Math.max(keepPx - Wpx, view.x));
  const y = Math.min(containerH - keepPx, Math.max(keepPx - Hpx, view.y));
  return { scale, x, y };
}
function cullToWorldRect(entities, worldRect, blockDefs) {
  if (!worldRect) return entities;
  const x0 = worldRect.x;
  const y0 = worldRect.y;
  const x1 = worldRect.x + worldRect.width;
  const y1 = worldRect.y + worldRect.height;
  return entities.filter((e) => {
    const b = entityBounds(e, blockDefs);
    if (!b) return true;
    return b.maxX >= x0 && b.minX <= x1 && b.maxY >= y0 && b.minY <= y1;
  });
}
function layerOverridesHash(lo) {
  if (!lo) return "";
  return Object.keys(lo).sort().map((k) => `${k}:${lo[k] ? 1 : 0}`).join(",");
}
function defaultViewportForModel(doc, layout, marginFraction = 0.8) {
  const unitMm = modelUnitMm(doc);
  const sheet = layoutPaperDims(layout);
  const printW = sheet.w * marginFraction;
  const printH = sheet.h * marginFraction;
  let b = documentBounds(unfrozenEntities(doc.entities || [], doc.layers), doc.blockDefs);
  if (!b) {
    b = { minX: -50, minY: -50, maxX: 50, maxY: 50 };
  }
  const padX = (b.maxX - b.minX) * 0.05 || 1;
  const padY = (b.maxY - b.minY) * 0.05 || 1;
  const worldRect = {
    x: b.minX - padX,
    y: b.minY - padY,
    width: b.maxX - b.minX + 2 * padX,
    height: b.maxY - b.minY + 2 * padY
  };
  const scaleW = printW / (worldRect.width * unitMm);
  const scaleH = printH / (worldRect.height * unitMm);
  const scale = Math.min(scaleW, scaleH) || 0.01;
  const n = (layout.viewports || []).length;
  let paperPos;
  if (n > 0) {
    const frameW = worldRect.width * unitMm * scale;
    const frameH = worldRect.height * unitMm * scale;
    const step = Math.max(Math.min(sheet.w, sheet.h) * 0.04, 8);
    paperPos = {
      x: Math.max((sheet.w - frameW) / 2 + n * step, 5),
      y: Math.max((sheet.h - frameH) / 2 - n * step, 5)
    };
  }
  return { worldRect, scale, paperPos };
}
function parseScale(text) {
  if (text == null) return null;
  const s = String(text).trim();
  if (!s) return null;
  const ratio = s.match(/^1\s*[:/]\s*([\d.]+)$/);
  if (ratio) {
    const denom = parseFloat(ratio[1]);
    return denom > 0 ? 1 / denom : null;
  }
  const num2 = Number(s);
  return Number.isFinite(num2) && num2 > 0 ? num2 : null;
}
function rotateVec(dx, dy, deg) {
  const a = deg * Math.PI / 180;
  const c = Math.cos(a);
  const s = Math.sin(a);
  return { x: dx * c - dy * s, y: dx * s + dy * c };
}
function rectCenter(r) {
  return { x: r.x + r.w / 2, y: r.y + r.h / 2 };
}
function rectCornersRot(r, deg) {
  const c = rectCenter(r);
  const hw = r.w / 2;
  const hh = r.h / 2;
  const mk = (sx, sy) => {
    const p = rotateVec(sx * hw, sy * hh, deg);
    return { x: c.x + p.x, y: c.y + p.y };
  };
  return { bl: mk(-1, -1), br: mk(1, -1), tl: mk(-1, 1), tr: mk(1, 1) };
}
function pointInRotRect(p, r, deg) {
  const c = rectCenter(r);
  const l = rotateVec(p.x - c.x, p.y - c.y, -deg);
  return Math.abs(l.x) <= r.w / 2 && Math.abs(l.y) <= r.h / 2;
}
var SIGN = { bl: [-1, -1], br: [1, -1], tl: [-1, 1], tr: [1, 1] };
var OPP_CORNER = { bl: "tr", tr: "bl", tl: "br", br: "tl" };
function resizeRect(start, deg, corner, pt2, minMm = 5) {
  const c0 = rectCenter(start);
  const oppName = OPP_CORNER[corner];
  const [ox, oy] = SIGN[oppName];
  const fixedLocal = { x: ox * start.w / 2, y: oy * start.h / 2 };
  const pl = rotateVec(pt2.x - c0.x, pt2.y - c0.y, -deg);
  const w = Math.max(Math.abs(pl.x - fixedLocal.x), minMm);
  const h = Math.max(Math.abs(pl.y - fixedLocal.y), minMm);
  const localCenter = { x: (pl.x + fixedLocal.x) / 2, y: (pl.y + fixedLocal.y) / 2 };
  const cw = rotateVec(localCenter.x, localCenter.y, deg);
  const cx = c0.x + cw.x;
  const cy = c0.y + cw.y;
  return { x: cx - w / 2, y: cy - h / 2, w, h };
}
function snapRect(rect, others, sheet, tolMm) {
  const movX = { left: rect.x, center: rect.x + rect.w / 2, right: rect.x + rect.w };
  const movY = { bottom: rect.y, middle: rect.y + rect.h / 2, top: rect.y + rect.h };
  const vTargets = [0, sheet.w / 2, sheet.w];
  const hTargets = [0, sheet.h / 2, sheet.h];
  for (const o of others || []) {
    vTargets.push(o.x, o.x + o.w / 2, o.x + o.w);
    hTargets.push(o.y, o.y + o.h / 2, o.y + o.h);
  }
  let bestX = null;
  for (const k of Object.keys(movX)) {
    for (const t of vTargets) {
      const d = t - movX[k];
      if (Math.abs(d) <= tolMm && (!bestX || Math.abs(d) < Math.abs(bestX.delta))) bestX = { delta: d, at: t };
    }
  }
  let bestY = null;
  for (const k of Object.keys(movY)) {
    for (const t of hTargets) {
      const d = t - movY[k];
      if (Math.abs(d) <= tolMm && (!bestY || Math.abs(d) < Math.abs(bestY.delta))) bestY = { delta: d, at: t };
    }
  }
  const guides = [];
  let x = rect.x;
  let y = rect.y;
  if (bestX) {
    x += bestX.delta;
    guides.push({ type: "v", at: bestX.at });
  }
  if (bestY) {
    y += bestY.delta;
    guides.push({ type: "h", at: bestY.at });
  }
  return { x, y, guides };
}
function formatScale(scale) {
  if (!Number.isFinite(scale) || scale <= 0) return "1:1";
  const denom = 1 / scale;
  const rounded = Math.abs(denom - Math.round(denom)) < 1e-6 ? Math.round(denom) : denom.toFixed(2);
  return `1:${rounded}`;
}
var VIEWPORT_SCALE_PRESETS = [1, 2, 5, 10, 20, 50, 100, 200, 250, 500, 1e3, 2e3, 5e3].map(
  (denom) => ({
    label: `1:${denom}`,
    scale: 1 / denom
  })
);
var CAPTURE_DPI = 300;
var MAX_PAGE_MEGAPIXELS = 40;
function captureDpiFor(sheet, { dpi = CAPTURE_DPI, maxMegapixels = MAX_PAGE_MEGAPIXELS } = {}) {
  const w = Math.max(0, Number(sheet && sheet.w) || 0);
  const h = Math.max(0, Number(sheet && sheet.h) || 0);
  const px = w / 25.4 * dpi * (h / 25.4 * dpi);
  return px > maxMegapixels * 1e6 ? dpi * Math.sqrt(maxMegapixels * 1e6 / px) : dpi;
}
function viewportPrintMetresPerPixel(vp, layout, unitMm = MODEL_UNIT_MM, dpi = CAPTURE_DPI) {
  const { ax } = viewportContentAffine(vp, layout, unitMm);
  return unitMm / 1e3 / (Math.abs(ax) * dpi / 96);
}

// src/cadHatch/patterns.js
var HATCH_PATTERNS = {
  // SOLID is not a line family at all — it FILLS the region (honouring island holes)
  // instead of stroking parallel lines. It carries no lines and a `solid: true` flag
  // that the tessellator (skip line generation) and every renderer (even-odd fill of
  // cachedBoundary − cachedHoles) key on. Listed FIRST so it heads the panel dropdown,
  // exactly as SOLID leads AutoCAD's pattern list; patternAngle/patternScale are inert.
  SOLID: { lines: [], solid: true },
  ANSI31: { lines: [{ angle: 45, offset: 1 }] },
  ANSI32: { lines: [{ angle: 45, offset: 1 }, { angle: 45, offset: 1, shift: 0.4 }] },
  STEEL: { lines: [{ angle: 45, offset: 1 }, { angle: 135, offset: 1 }] },
  CONCRETE: { lines: [{ angle: 45, offset: 1.2 }, { angle: 135, offset: 1.2 }] },
  EARTH: { lines: [{ angle: 45, offset: 0.8 }] },
  GRAVEL: { lines: [{ angle: 30, offset: 1 }, { angle: 150, offset: 1 }] },
  SAND: { lines: [{ angle: 0, offset: 1 }, { angle: 90, offset: 1 }] },
  ROCK: { lines: [{ angle: 60, offset: 1 }, { angle: 120, offset: 1 }] },
  GRASS: { lines: [{ angle: 75, offset: 1.5 }] },
  // Spec 07: expanded line-pattern set. These are line-family APPROXIMATIONS of the
  // ANSI/masonry intents within the tessellator's contract (line-only) — the bar is
  // usable visual variety, not exact ANSI geometry.
  ANSI33: { lines: [{ angle: 45, offset: 0.6 }] },
  ANSI34: { lines: [{ angle: 45, offset: 1.5 }] },
  ANSI35: { lines: [{ angle: 45, offset: 1 }, { angle: 45, offset: 1, shift: 0.25 }] },
  ANSI36: { lines: [{ angle: 45, offset: 1.2 }, { angle: 135, offset: 2.4 }] },
  ANSI37: { lines: [{ angle: 45, offset: 0.7 }, { angle: 135, offset: 0.7 }] },
  ANSI38: { lines: [{ angle: 45, offset: 0.9 }, { angle: 0, offset: 1.8 }] },
  BRICK: { lines: [{ angle: 0, offset: 1 }, { angle: 90, offset: 2 }] },
  INSUL: { lines: [{ angle: 0, offset: 0.5 }, { angle: 0, offset: 0.5, shift: 0.5 }] }
};
var DEFAULT_HATCH_PATTERN = "EARTH";
var HATCH_PATTERN_NAMES = Object.keys(HATCH_PATTERNS);
var HATCH_LINEWEIGHT_MIN = 0.25;
var HATCH_LINEWEIGHT_MAX = 20;
var DEFAULT_HATCH_LINEWEIGHT = 1;
function clampHatchLineWeight(w) {
  if (w == null || !Number.isFinite(Number(w))) return null;
  return Math.min(HATCH_LINEWEIGHT_MAX, Math.max(HATCH_LINEWEIGHT_MIN, Number(w)));
}
function hatchLineWeight(hatchEntity) {
  const w = clampHatchLineWeight(hatchEntity && hatchEntity.lineWeight);
  return w == null ? DEFAULT_HATCH_LINEWEIGHT : w;
}

// src/cadHatch/tessellate.js
function ringCrossings(nrm, d, dir, ring) {
  const ts = [];
  const n = ring.length;
  let a = ring[n - 1];
  let da = a.x * nrm.x + a.y * nrm.y - d;
  for (let i = 0; i < n; i++) {
    const b = ring[i];
    const db = b.x * nrm.x + b.y * nrm.y - d;
    if (da <= 0 !== db <= 0) {
      const u = da / (da - db);
      const px = a.x + u * (b.x - a.x);
      const py = a.y + u * (b.y - a.y);
      ts.push(px * dir.x + py * dir.y);
    }
    a = b;
    da = db;
  }
  return ts;
}
function tessellateHatch(boundary, pattern, angleDeg = 0, scale = 1, holes = []) {
  if (!boundary || boundary.length < 3) return [];
  const families = pattern && pattern.lines || HATCH_PATTERNS[DEFAULT_HATCH_PATTERN].lines;
  const rings = (holes || []).filter((h) => Array.isArray(h) && h.length >= 3);
  const out = [];
  const sc = scale > 0 ? scale : 1;
  for (const fam of families) {
    const ang = ((fam.angle || 0) + angleDeg) * Math.PI / 180;
    const dir = { x: Math.cos(ang), y: Math.sin(ang) };
    const nrm = { x: -dir.y, y: dir.x };
    const spacing = Math.max((fam.offset || 1) * sc, 1e-4);
    let minN = Infinity;
    let maxN = -Infinity;
    for (const p of boundary) {
      const d = p.x * nrm.x + p.y * nrm.y;
      if (d < minN) minN = d;
      if (d > maxN) maxN = d;
    }
    const phase = (fam.shift || 0) * spacing;
    const kStart = Math.ceil((minN - phase) / spacing);
    const kEnd = Math.floor((maxN - phase) / spacing);
    for (let k = kStart; k <= kEnd; k++) {
      const d = k * spacing + phase;
      const base = { x: nrm.x * d, y: nrm.y * d };
      const bs = ringCrossings(nrm, d, dir, boundary);
      if (bs.length < 2) continue;
      const hs = [];
      for (const h of rings) for (const t of ringCrossings(nrm, d, dir, h)) hs.push(t);
      for (const [t0, t1] of paintedSpans(bs, hs, 1e-6)) {
        out.push({
          x1: base.x + dir.x * t0,
          y1: base.y + dir.y * t0,
          x2: base.x + dir.x * t1,
          y2: base.y + dir.y * t1
        });
      }
    }
  }
  return out;
}

// src/cadHatch/region.js
function ringBounds(ring) {
  let minx = Infinity, miny = Infinity, maxx = -Infinity, maxy = -Infinity;
  for (const p of ring) {
    if (p.x < minx) minx = p.x;
    if (p.x > maxx) maxx = p.x;
    if (p.y < miny) miny = p.y;
    if (p.y > maxy) maxy = p.y;
  }
  return { minx, miny, maxx, maxy };
}
function findHatchIslands(entities, outerRing, opts = {}) {
  if (!Array.isArray(outerRing) || outerRing.length < 3) return [];
  const exclude = new Set(opts.excludeIds || []);
  const point = opts.point || null;
  const outerArea = polygonArea(outerRing);
  if (!(outerArea > 0)) return [];
  const ob = ringBounds(outerRing);
  const islands = [];
  for (const e of entities || []) {
    if (!e || e.type === "hatch") continue;
    if (e.id != null && exclude.has(e.id)) continue;
    if (!isClosedBoundary(e)) continue;
    const ring = boundaryFromEntities([e]);
    if (!ring || ring.length < 3) continue;
    const b = ringBounds(ring);
    if (b.minx < ob.minx - 1e-9 || b.maxx > ob.maxx + 1e-9 || b.miny < ob.miny - 1e-9 || b.maxy > ob.maxy + 1e-9) continue;
    const a = polygonArea(ring);
    if (!(a > 0) || a >= outerArea * (1 - 1e-9)) continue;
    if (!ring.every((p) => pointInPolygon(p, outerRing))) continue;
    if (point && pointInPolygon(point, ring)) continue;
    islands.push({ id: e.id, ring });
  }
  return islands;
}
function islandsFor(boundary, entities, excludeIds, point) {
  if (!boundary || boundary.length < 3) return { holes: [], holeSourceIds: [] };
  const found = findHatchIslands(entities || [], boundary, { excludeIds, point });
  return {
    holes: found.map((f) => f.ring),
    holeSourceIds: found.map((f) => f.id).filter((id) => id != null)
  };
}
var VTOL = 1e-6;
var AREA_EPS2 = 1e-9;
function paramOn(a, b, p) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len22 = dx * dx + dy * dy;
  if (len22 < 1e-18) return 0;
  return ((p.x - a.x) * dx + (p.y - a.y) * dy) / len22;
}
function entityArrangementSegments(e) {
  if (!e || e.type === "hatch" || e.type === "text" || e.type === "point" || e.type === "dimension" || e.type === "insert" || // [RASTER-IMAGE-1] A photo is a backdrop, not a boundary: its frame edges
  // would enclose cells the user never drew, so BOUNDARY-picking inside a
  // placed image would find the image's own rectangle. (`xref` is missing
  // from this list and that is a pre-existing gap, logged rather than copied.)
  e.type === "image" || // Infinite construction lines would inject a 1e7-long edge into the planar
  // arrangement and swamp the real cells — never a region boundary.
  e.type === "ray" || e.type === "xline") return [];
  return entitySubSegments(e);
}
function makeVertexPool() {
  const buckets = /* @__PURE__ */ new Map();
  const pts = [];
  const key = (ix, iy) => ix + ":" + iy;
  return {
    pts,
    id(p) {
      const ix = Math.round(p.x / VTOL);
      const iy = Math.round(p.y / VTOL);
      for (let dx = -1; dx <= 1; dx++) {
        for (let dy = -1; dy <= 1; dy++) {
          const arr2 = buckets.get(key(ix + dx, iy + dy));
          if (!arr2) continue;
          for (const id2 of arr2) {
            if (Math.hypot(pts[id2].x - p.x, pts[id2].y - p.y) <= VTOL) return id2;
          }
        }
      }
      const id = pts.length;
      pts.push({ x: p.x, y: p.y });
      const k = key(ix, iy);
      const arr = buckets.get(k);
      if (arr) arr.push(id);
      else buckets.set(k, [id]);
      return id;
    }
  };
}
function findRegionBoundaryAt(entities, point, opts = {}) {
  if (!point) return null;
  const wantSources = !!opts.withSources;
  const segs = [];
  const segOwner = [];
  for (const e of entities || []) {
    for (const [a, b] of entityArrangementSegments(e)) {
      if (Math.hypot(a.x - b.x, a.y - b.y) > VTOL) {
        segs.push([{ x: a.x, y: a.y }, { x: b.x, y: b.y }]);
        segOwner.push(e.id);
      }
    }
  }
  if (segs.length < 3) return null;
  const cuts = segs.map(([a, b]) => [{ t: 0, p: a }, { t: 1, p: b }]);
  const box = segs.map(([a, b]) => ({
    minx: Math.min(a.x, b.x),
    maxx: Math.max(a.x, b.x),
    miny: Math.min(a.y, b.y),
    maxy: Math.max(a.y, b.y)
  }));
  for (let i = 0; i < segs.length; i++) {
    for (let j = i + 1; j < segs.length; j++) {
      const bi = box[i];
      const bj = box[j];
      if (bi.maxx < bj.minx - VTOL || bj.maxx < bi.minx - VTOL || bi.maxy < bj.miny - VTOL || bj.maxy < bi.miny - VTOL) continue;
      const p = segSegIntersection(segs[i][0], segs[i][1], segs[j][0], segs[j][1]);
      if (!p) continue;
      cuts[i].push({ t: paramOn(segs[i][0], segs[i][1], p), p });
      cuts[j].push({ t: paramOn(segs[j][0], segs[j][1], p), p });
    }
  }
  const pool = makeVertexPool();
  const adj = [];
  const edgeOwners = /* @__PURE__ */ new Map();
  const ekey = (a, b) => a < b ? a + ":" + b : b + ":" + a;
  const ensure = (id) => {
    while (adj.length <= id) adj.push(/* @__PURE__ */ new Set());
  };
  for (let i = 0; i < segs.length; i++) {
    cuts[i].sort((m, n) => m.t - n.t);
    const ids = cuts[i].map((c) => pool.id(c.p));
    let prev = ids[0];
    for (let k = 1; k < ids.length; k++) {
      const v = ids[k];
      if (v === prev) continue;
      ensure(prev);
      ensure(v);
      adj[prev].add(v);
      adj[v].add(prev);
      if (segOwner[i] != null) {
        const kk = ekey(prev, v);
        let owners = edgeOwners.get(kk);
        if (!owners) {
          owners = /* @__PURE__ */ new Set();
          edgeOwners.set(kk, owners);
        }
        owners.add(segOwner[i]);
      }
      prev = v;
    }
  }
  const V = pool.pts;
  ensure(V.length - 1);
  const half = [];
  const twin = [];
  const outAt = V.map(() => []);
  for (let u = 0; u < adj.length; u++) {
    for (const v of adj[u]) {
      if (u >= v) continue;
      const a = half.length;
      half.push({ from: u, to: v, angle: Math.atan2(V[v].y - V[u].y, V[v].x - V[u].x) });
      const b = half.length;
      half.push({ from: v, to: u, angle: Math.atan2(V[u].y - V[v].y, V[u].x - V[v].x) });
      twin[a] = b;
      twin[b] = a;
      outAt[u].push(a);
      outAt[v].push(b);
    }
  }
  for (const list of outAt) list.sort((p, q) => half[p].angle - half[q].angle);
  const pos = new Array(half.length);
  for (const list of outAt) list.forEach((h, idx) => {
    pos[h] = idx;
  });
  const next = (h) => {
    const t = twin[h];
    const list = outAt[half[t].from];
    return list[(pos[t] - 1 + list.length) % list.length];
  };
  const seen = new Array(half.length).fill(false);
  const faces = [];
  for (let h0 = 0; h0 < half.length; h0++) {
    if (seen[h0]) continue;
    const cycle = [];
    let h = h0;
    let guard = 0;
    while (!seen[h] && guard++ <= half.length) {
      seen[h] = true;
      cycle.push(half[h].from);
      h = next(h);
    }
    if (cycle.length >= 3) faces.push(cycle);
  }
  let best = null;
  let bestCycle = null;
  let bestArea = Infinity;
  for (const cyc of faces) {
    const poly = cyc.map((id) => ({ x: V[id].x, y: V[id].y }));
    const area = polygonArea(poly);
    if (area < AREA_EPS2) continue;
    if (!pointInPolygon(point, poly)) continue;
    if (area < bestArea) {
      bestArea = area;
      best = poly;
      bestCycle = cyc;
    }
  }
  if (!best) return null;
  if (!wantSources) return best;
  const sourceIds = [];
  const seenSrc = /* @__PURE__ */ new Set();
  for (let k = 0; k < bestCycle.length; k++) {
    const owners = edgeOwners.get(ekey(bestCycle[k], bestCycle[(k + 1) % bestCycle.length]));
    if (!owners) continue;
    for (const id of owners) {
      if (!seenSrc.has(id)) {
        seenSrc.add(id);
        sourceIds.push(id);
      }
    }
  }
  return { polygon: best, sourceIds };
}
function findClosedBoundaryAt(entities, point) {
  let best = null;
  let bestArea = Infinity;
  for (const e of entities || []) {
    if (!isClosedBoundary(e)) continue;
    const poly = boundaryFromEntities([e]);
    if (!poly || !pointInPolygon(point, poly)) continue;
    const a = polygonArea(poly);
    if (a < bestArea) {
      best = e;
      bestArea = a;
    }
  }
  return best;
}
function chooseHatchBoundary(entities, point) {
  const region = findRegionBoundaryAt(entities, point, { withSources: true });
  const regionPoly = region && region.polygon;
  const single = findClosedBoundaryAt(entities, point);
  if (single) {
    const singlePoly = boundaryFromEntities([single]) || [];
    const singleArea = polygonArea(singlePoly);
    if (regionPoly && regionPoly.length >= 3 && polygonArea(regionPoly) < singleArea * (1 - 1e-6)) {
      return { polygon: regionPoly, sourceIds: region.sourceIds };
    }
    return { ids: [single.id] };
  }
  if (regionPoly && regionPoly.length >= 3) return { polygon: regionPoly, sourceIds: region.sourceIds };
  return null;
}

// src/cadHatch/cache.js
function withDeclaredHoles(hatchEntity, detected) {
  if (hatchEntity && hatchEntity.islandsFromFile) return declaredRings(hatchEntity);
  if (Array.isArray(detected) && detected.length) return detected;
  const declared = hatchEntity && hatchEntity.explicitHoles;
  if (!Array.isArray(declared)) return detected;
  return declaredRings(hatchEntity);
}
function declaredRings(hatchEntity) {
  const declared = hatchEntity && hatchEntity.explicitHoles;
  if (!Array.isArray(declared)) return [];
  return declared.filter((h) => Array.isArray(h) && h.length >= 3).map((h) => h.map((p) => ({ x: p.x, y: p.y })));
}
function hatchCacheFromBoundary(hatchEntity, boundary, holes) {
  const pattern = HATCH_PATTERNS[hatchEntity.patternName] || HATCH_PATTERNS[DEFAULT_HATCH_PATTERN];
  const cachedHoles = (holes || []).filter((h) => Array.isArray(h) && h.length >= 3).map((h) => h.map((p) => ({ x: p.x, y: p.y })));
  const cachedLines = boundary && !(pattern && pattern.solid) ? tessellateHatch(boundary, pattern, hatchEntity.patternAngle || 0, hatchEntity.patternScale || 1, cachedHoles) : [];
  return { cachedBoundary: boundary || [], cachedLines, cachedHoles };
}
function updateHatchCache(hatchEntity, allEntities) {
  let boundary;
  let excludeIds;
  let point = null;
  if (Array.isArray(hatchEntity.explicitBoundary) && hatchEntity.explicitBoundary.length >= 3) {
    boundary = hatchEntity.explicitBoundary.map((p) => ({ x: p.x, y: p.y }));
    excludeIds = hatchEntity.regionSeed && hatchEntity.regionSeed.sourceIds || [];
    point = hatchEntity.regionSeed && hatchEntity.regionSeed.point ? hatchEntity.regionSeed.point : null;
  } else {
    const byId = new Map((allEntities || []).map((e) => [e.id, e]));
    const boundaryEntities = (hatchEntity.boundaryPolylineIds || []).map((id) => byId.get(id)).filter(Boolean);
    boundary = boundaryFromEntities(boundaryEntities);
    excludeIds = hatchEntity.boundaryPolylineIds || [];
  }
  const { holes, holeSourceIds } = islandsFor(boundary, allEntities, excludeIds, point);
  const rings = withDeclaredHoles(hatchEntity, holes);
  return {
    ...hatchEntity,
    ...hatchCacheFromBoundary(hatchEntity, boundary, rings),
    holeSourceIds: rings === holes ? holeSourceIds : []
  };
}
function rebuildHatch(hatchEntity, byId) {
  const entities = Array.from(byId.values());
  if (hatchEntity.regionSeed && hatchEntity.regionSeed.point) {
    const seed = hatchEntity.regionSeed;
    const sources = (seed.sourceIds || []).map((id) => byId.get(id)).filter(Boolean);
    if (!sources.length) {
      return { ...hatchEntity, derivedError: { reason: "region sources deleted" } };
    }
    const found = findRegionBoundaryAt(sources, seed.point);
    if (!found || found.length < 3) {
      return { ...hatchEntity, derivedError: { reason: "region no longer enclosed" } };
    }
    const { holes: holes2, holeSourceIds: holeSourceIds2 } = islandsFor(found, entities, seed.sourceIds || [], seed.point);
    return {
      ...hatchEntity,
      ...hatchCacheFromBoundary(hatchEntity, found, holes2),
      explicitBoundary: found.map((p) => ({ x: p.x, y: p.y })),
      holeSourceIds: holeSourceIds2,
      derivedError: null
    };
  }
  if (Array.isArray(hatchEntity.explicitBoundary) && hatchEntity.explicitBoundary.length >= 3) {
    const boundary2 = hatchEntity.explicitBoundary.map((p) => ({ x: p.x, y: p.y }));
    if (hatchEntity.islandsFromFile) {
      return {
        ...hatchEntity,
        ...hatchCacheFromBoundary(hatchEntity, boundary2, declaredRings(hatchEntity)),
        holeSourceIds: [],
        derivedError: null
      };
    }
    const { holes: holes2, holeSourceIds: holeSourceIds2 } = islandsFor(boundary2, entities, [], null);
    const rings = withDeclaredHoles(hatchEntity, holes2);
    return {
      ...hatchEntity,
      ...hatchCacheFromBoundary(hatchEntity, boundary2, rings),
      holeSourceIds: rings === holes2 ? holeSourceIds2 : [],
      derivedError: null
    };
  }
  const ids = hatchEntity.boundaryPolylineIds || [];
  const resolved = ids.map((id) => byId.get(id));
  if (!ids.length || resolved.some((e) => !e)) {
    return { ...hatchEntity, derivedError: { reason: ids.length ? "boundary deleted" : "no boundary" } };
  }
  const boundary = boundaryFromEntities(resolved);
  if (!boundary || boundary.length < 3) {
    return { ...hatchEntity, derivedError: { reason: "boundary not closed" } };
  }
  const { holes, holeSourceIds } = islandsFor(boundary, entities, ids, null);
  return { ...hatchEntity, ...hatchCacheFromBoundary(hatchEntity, boundary, holes), holeSourceIds, derivedError: null };
}
function hatchSourceIds(hatchEntity) {
  const base = hatchEntity.regionSeed && Array.isArray(hatchEntity.regionSeed.sourceIds) ? hatchEntity.regionSeed.sourceIds : hatchEntity.boundaryPolylineIds || [];
  const holes = Array.isArray(hatchEntity.holeSourceIds) ? hatchEntity.holeSourceIds : [];
  return holes.length ? [...base, ...holes] : base;
}
function makeHatch({ boundaryPolylineIds, explicitBoundary, explicitHoles, islandsFromFile = false, regionSeed, patternName, patternAngle = 0, patternScale = 1, lineWeight = null, layer = "0" }, allEntities) {
  const base = {
    type: "hatch",
    layer,
    color: null,
    linetype: null,
    // Fill-line thickness (screen px); null = hairline default. Pure RENDER property —
    // it never affects the tessellation, so rebuildHatch carries it through untouched.
    lineWeight: clampHatchLineWeight(lineWeight),
    boundaryPolylineIds: [...boundaryPolylineIds || []],
    // null for an entity-backed hatch; a point ring for a synthetic region hatch.
    explicitBoundary: Array.isArray(explicitBoundary) ? explicitBoundary.map((p) => ({ x: p.x, y: p.y })) : null,
    // Hole rings declared by an imported file (see withDeclaredHoles). Absent for
    // every hatch this app creates — those derive their islands from the drawing.
    ...Array.isArray(explicitHoles) && explicitHoles.length ? { explicitHoles: explicitHoles.filter((h) => Array.isArray(h) && h.length >= 3).map((h) => h.map((p) => ({ x: p.x, y: p.y }))) } : {},
    // [BUG-IMPORT-ISLAND-1] Set by the IMPORTERS only: the file has stated this
    // hatch's island set, so `explicitHoles` above is the whole truth even when it
    // is empty, and no island detection runs on rebuild. Omitted (falsy) for every
    // hatch this app creates — those stay associative to the drawing.
    ...islandsFromFile ? { islandsFromFile: true } : {},
    // Seed for a reactive region hatch: the interior pick point + the ids of the
    // entities whose edges bound the picked face (so it can re-detect on rebuild).
    regionSeed: regionSeed && regionSeed.point ? { point: { x: regionSeed.point.x, y: regionSeed.point.y }, sourceIds: [...regionSeed.sourceIds || []] } : null,
    patternName: patternName || DEFAULT_HATCH_PATTERN,
    patternAngle,
    patternScale,
    cachedLines: [],
    cachedBoundary: [],
    // Island holes carved out of the fill + the entity ids that formed them. Both are
    // derived caches (filled by updateHatchCache); holeSourceIds also drives rebuilds.
    cachedHoles: [],
    holeSourceIds: [],
    derivedError: null
  };
  return updateHatchCache(base, allEntities);
}

// src/cadHatch/area.js
var usableRing = (r) => Array.isArray(r) && r.length >= 3 && r.every((p) => p && Number.isFinite(p.x) && Number.isFinite(p.y));
function pushRingEdges(ring, outer, out) {
  const n = ring.length;
  for (let i = 0; i < n; i += 1) {
    const p = ring[i];
    const q = ring[(i + 1) % n];
    if (p.x === q.x) continue;
    out.push(
      p.x < q.x ? { xa: p.x, ya: p.y, xb: q.x, yb: q.y, outer } : { xa: q.x, ya: q.y, xb: p.x, yb: p.y, outer }
    );
  }
}
function eventXs(byStart) {
  const xs = [];
  for (const e of byStart) xs.push(e.xa, e.xb);
  for (let i = 0; i < byStart.length; i += 1) {
    const e = byStart[i];
    const me = (e.yb - e.ya) / (e.xb - e.xa);
    for (let j = i + 1; j < byStart.length; j += 1) {
      const f = byStart[j];
      if (f.xa >= e.xb) break;
      if (Math.min(e.ya, e.yb) > Math.max(f.ya, f.yb)) continue;
      if (Math.min(f.ya, f.yb) > Math.max(e.ya, e.yb)) continue;
      const mf = (f.yb - f.ya) / (f.xb - f.xa);
      if (me === mf) continue;
      const x = (f.ya - e.ya + me * e.xa - mf * f.xa) / (me - mf);
      if (x > e.xa && x < e.xb && x > f.xa && x < f.xb) xs.push(x);
    }
  }
  xs.sort((a, b) => a - b);
  return xs;
}
function paintedArea(boundary, holes) {
  if (!usableRing(boundary)) return 0;
  const edges = [];
  pushRingEdges(boundary, true, edges);
  for (const h of holes || []) if (usableRing(h)) pushRingEdges(h, false, edges);
  if (edges.length < 2) return 0;
  const byStart = edges.slice().sort((p, q) => p.xa - q.xa);
  const xs = eventXs(byStart);
  let next = 0;
  let active = [];
  let area = 0;
  for (let i = 0; i + 1 < xs.length; i += 1) {
    const w = xs[i + 1] - xs[i];
    if (w <= 0) continue;
    const xm = (xs[i] + xs[i + 1]) / 2;
    while (next < byStart.length && byStart[next].xa < xm) {
      active.push(byStart[next]);
      next += 1;
    }
    if (active.length) active = active.filter((e) => e.xb > xm);
    if (active.length < 2) continue;
    const outerYs = [];
    const holeYs = [];
    for (const e of active) {
      const y = e.ya + (e.yb - e.ya) * (xm - e.xa) / (e.xb - e.xa);
      if (e.outer) outerYs.push(y);
      else holeYs.push(y);
    }
    for (const [y0, y1] of paintedSpans(outerYs, holeYs)) area += w * (y1 - y0);
  }
  return area;
}
function hatchArea(hatchEntity) {
  if (!hatchEntity) return 0;
  return paintedArea(hatchEntity.cachedBoundary, hatchEntity.cachedHoles);
}

// src/cadImportMappers.js
var RAD2DEG = 180 / Math.PI;
var LARGE = 1e5;
var hexFromInt = (n) => typeof n === "number" && isFinite(n) ? "#" + (n & 16777215).toString(16).padStart(6, "0") : null;
var lineweightFromDxf = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n) || n < 0) return null;
  return clampLineweight(n / 100);
};
var normalizeLinetypeName = (name) => {
  if (!name || typeof name !== "string") return null;
  const u = name.trim().toUpperCase();
  if (u === "" || u === "BYLAYER" || u === "BYBLOCK" || u === "CONTINUOUS") return null;
  return name;
};
var stripMText = (s) => s.replace(/\\[A-Za-z][^;]*;/g, "").replace(/[{}]/g, "").replace(/\\P/g, " ").replace(/\\~/g, " ").trim();
var IDENT = { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };
var apply = (m, x, y) => ({ x: m.a * x + m.c * y + m.e, y: m.b * x + m.d * y + m.f });
var matMul = (m, n) => ({
  a: m.a * n.a + m.c * n.b,
  b: m.b * n.a + m.d * n.b,
  c: m.a * n.c + m.c * n.d,
  d: m.b * n.c + m.d * n.d,
  e: m.a * n.e + m.c * n.f + m.e,
  f: m.b * n.e + m.d * n.f + m.f
});
function insertMatrix(ent, block) {
  const pos = ent.position || { x: 0, y: 0 };
  const sx = ent.xScale || 1;
  const sy = ent.yScale || 1;
  const th = (ent.rotation || 0) * Math.PI / 180;
  const B = block && block.position || { x: 0, y: 0 };
  const cos = Math.cos(th);
  const sin = Math.sin(th);
  const a = sx * cos;
  const b = sx * sin;
  const c = -sy * sin;
  const d = sy * cos;
  return { a, b, c, d, e: pos.x - (a * B.x + c * B.y), f: pos.y - (b * B.x + d * B.y) };
}
var identityCtx = () => ({ m: IDENT, scale: 1, rot: 0, mirror: false, ocsVec: OCS_WORLD });
var MIRROR_X = { a: -1, b: 0, c: 0, d: 1, e: 0, f: 0 };
function extrusionVec(ent) {
  if (!ent) return { x: 0, y: 0, z: 1 };
  const d = ent.extrusionDirection;
  const x = Number.isFinite(ent.extrusionDirectionX) ? ent.extrusionDirectionX : d && Number.isFinite(d.x) ? d.x : 0;
  const y = Number.isFinite(ent.extrusionDirectionY) ? ent.extrusionDirectionY : d && Number.isFinite(d.y) ? d.y : 0;
  const z = Number.isFinite(ent.extrusionDirectionZ) ? ent.extrusionDirectionZ : d && Number.isFinite(d.z) ? d.z : 1;
  if (x === 0 && y === 0 && z === 0) return { x: 0, y: 0, z: 1 };
  return { x, y, z };
}
var OCS_WORLD = { x: 0, y: 0, z: 1 };
var EXTRUSION_EPS = 1e-9;
var sameExtrusion = (a, b) => !!a && !!b && Math.abs(a.x - b.x) < EXTRUSION_EPS && Math.abs(a.y - b.y) < EXTRUSION_EPS && Math.abs(a.z - b.z) < EXTRUSION_EPS;
function statedExtrusion(ent) {
  const d = ent && ent.extrusionDirection;
  const has = Number.isFinite(ent && ent.extrusionDirectionX) || Number.isFinite(ent && ent.extrusionDirectionY) || Number.isFinite(ent && ent.extrusionDirectionZ) || d && (Number.isFinite(d.x) || Number.isFinite(d.y) || Number.isFinite(d.z));
  if (!has) return null;
  const v = extrusionVec(ent);
  if (d && d.x === 0 && d.y === 0 && d.z === 0) return null;
  return v;
}
var cross3 = (a, b) => ({
  x: a.y * b.z - a.z * b.y,
  y: a.z * b.x - a.x * b.z,
  z: a.x * b.y - a.y * b.x
});
var norm2 = (v) => {
  const L = Math.hypot(v.x, v.y, v.z);
  return L > 0 ? { x: v.x / L, y: v.y / L, z: v.z / L } : { x: 0, y: 0, z: 1 };
};
function ocsTransform(ent) {
  const n = norm2(extrusionVec(ent));
  if (n.x === 0 && n.y === 0) {
    return n.z >= 0 ? { m: IDENT, mirror: false, tilted: false, identity: true } : { m: MIRROR_X, mirror: true, tilted: false, identity: false };
  }
  const ax = norm2(
    cross3(
      Math.abs(n.x) < 1 / 64 && Math.abs(n.y) < 1 / 64 ? { x: 0, y: 1, z: 0 } : { x: 0, y: 0, z: 1 },
      n
    )
  );
  const ay = norm2(cross3(n, ax));
  const m = { a: ax.x, b: ax.y, c: ay.x, d: ay.y, e: 0, f: 0 };
  return { m, mirror: m.a * m.d - m.b * m.c < 0, tilted: true, identity: false };
}
var ocsCtx = (ctx, ocs, ocsVec) => ({
  m: matMul(ctx.m, ocs.m),
  scale: ctx.scale,
  rot: ctx.rot,
  mirror: ocs.mirror ? !ctx.mirror : !!ctx.mirror,
  // The plane now in force, so a nested entity can tell "I am in this plane" from
  // "put me into a different one" ([BUG-DWG-OCS-2]).
  ocsVec: ocsVec || ctx.ocsVec || OCS_WORLD
});
var INSUNITS_TO_METRES = {
  0: null,
  // unitless — no statement to honour, so no scaling
  1: 0.0254,
  // inches
  2: 0.3048,
  // feet
  3: 1609.344,
  // miles
  4: 1e-3,
  // millimetres
  5: 0.01,
  // centimetres
  6: 1,
  // metres
  7: 1e3,
  // kilometres
  8: 254e-10,
  // microinches
  9: 254e-7,
  // mils
  10: 0.9144,
  // yards
  11: 1e-10,
  // angstroms
  12: 1e-9,
  // nanometres
  13: 1e-6,
  // microns
  14: 0.1,
  // decimetres
  15: 10,
  // decametres
  16: 100,
  // hectometres
  17: 1e9,
  // gigametres
  21: 0.30480061,
  // US survey feet
  22: 0.30480061 / 12,
  // US survey inch
  23: 0.9144018,
  // US survey yard
  24: 1609.3472
  // US survey mile
};
function unitScaleFromHeader(header, warnings) {
  const raw = header && (header.$INSUNITS ?? header.INSUNITS);
  const code = Number(raw && typeof raw === "object" ? raw.value : raw);
  if (!Number.isInteger(code) || code === 6 || code === 0) return 1;
  const f = INSUNITS_TO_METRES[code];
  if (!f || !Number.isFinite(f) || f <= 0) {
    if (warnings)
      warnings.push(`drawing units code ${code} is not one this reader knows \u2014 imported at 1:1`);
    return 1;
  }
  if (warnings)
    warnings.push(
      `drawing units are not metres ($INSUNITS ${code}) \u2014 scaled by ${f} so the drawing measures true`
    );
  return f;
}
var scaledCtx = (f) => f === 1 ? identityCtx() : { m: { a: f, b: 0, c: 0, d: f, e: 0, f: 0 }, scale: f, rot: 0, mirror: false, ocsVec: OCS_WORLD };
function entityContext(ent, ctx = identityCtx(), ensureLayer = (n) => n || DEFAULT_LAYER) {
  const layer = ensureLayer(ent.layer);
  const color = hexFromInt(ent.color);
  const linetype = normalizeLinetypeName(ent.lineType);
  const lineweight = lineweightFromDxf(ent.lineweight);
  const ltScale = Number.isFinite(ent.lineTypeScale) && ent.lineTypeScale > 0 ? ent.lineTypeScale : 1;
  const P = (p) => apply(ctx.m, p && p.x || 0, p && p.y || 0);
  const mk = (type, props) => ({
    id: genId(type[0]),
    type,
    layer,
    color,
    linetype,
    lineweight,
    ltScale,
    ...props
  });
  const ang = (a) => {
    const w = ctx.mirror ? 180 + ctx.rot - a : a + ctx.rot;
    return (w % 360 + 360) % 360;
  };
  return {
    P,
    mk,
    ang,
    mirror: !!ctx.mirror,
    scale: ctx.scale,
    rot: ctx.rot,
    layer,
    color,
    linetype,
    lineweight,
    ltScale
  };
}
var HATCH_PATH_EXTERNAL = 1;
var HATCH_PATH_OUTERMOST = 16;
var HATCH_EDGE_LINE = 1;
var HATCH_EDGE_ARC = 2;
var HATCH_EDGE_ELLIPSE = 3;
var HATCH_EDGE_SPLINE = 4;
function tessellateArcEdge(edge, steps = 24) {
  const out = [];
  const cx = edge.center && edge.center.x || 0;
  const cy = edge.center && edge.center.y || 0;
  const r = edge.radius || 0;
  let a0 = edge.startAngle || 0;
  let a1 = edge.endAngle || 0;
  const ccw = edge.isCCW !== false;
  if (ccw && a1 < a0) a1 += Math.PI * 2;
  if (!ccw && a1 > a0) a1 -= Math.PI * 2;
  for (let i = 0; i <= steps; i++) {
    const t = a0 + (a1 - a0) * (i / steps);
    out.push({ x: cx + r * Math.cos(t), y: cy + r * Math.sin(t) });
  }
  return out;
}
function tessellateEllipseEdge(edge, steps = 32) {
  const cx = edge.center && edge.center.x || 0;
  const cy = edge.center && edge.center.y || 0;
  const mx = edge.majorAxis && edge.majorAxis.x || 0;
  const my = edge.majorAxis && edge.majorAxis.y || 0;
  const ratio = Number.isFinite(edge.axisRatio) && edge.axisRatio > 0 ? edge.axisRatio : 1;
  const nx = -my * ratio;
  const ny = mx * ratio;
  let a0 = edge.startAngle || 0;
  let a1 = Number.isFinite(edge.endAngle) ? edge.endAngle : Math.PI * 2;
  const ccw = edge.isCCW !== false;
  if (ccw && a1 <= a0) a1 += Math.PI * 2;
  if (!ccw && a1 >= a0) a1 -= Math.PI * 2;
  const out = [];
  for (let i = 0; i <= steps; i++) {
    const t = a0 + (a1 - a0) * (i / steps);
    out.push({
      x: cx + mx * Math.cos(t) + nx * Math.sin(t),
      y: cy + my * Math.cos(t) + ny * Math.sin(t)
    });
  }
  return out;
}
var ringPt = (p) => ({ x: p && p.x || 0, y: p && p.y || 0 });
var near2 = (a, b, tol) => Math.abs(a.x - b.x) <= tol && Math.abs(a.y - b.y) <= tol;
function edgeJoinTolerance(edges) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const see = (p) => {
    if (!p || !Number.isFinite(p.x) || !Number.isFinite(p.y)) return;
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  };
  for (const e of edges) {
    see(e.start);
    see(e.end);
    see(e.center);
  }
  if (!Number.isFinite(minX)) return 1e-9;
  return Math.max(1e-9, Math.hypot(maxX - minX, maxY - minY) * 1e-4);
}
function hatchPathToRing(path) {
  if (!path) return [];
  if (Array.isArray(path.vertices)) return path.vertices.map(ringPt);
  if (!Array.isArray(path.edges)) return [];
  const tol = edgeJoinTolerance(path.edges);
  const ring = [];
  const edges = orientFirstEdge(path.edges, tol);
  for (const edge of edges) {
    let pts = edgePoints(edge);
    if (!pts.length) return [];
    if (ring.length) {
      const last = ring[ring.length - 1];
      if (!near2(last, pts[0], tol)) {
        const rev = pts.slice().reverse();
        if (!near2(last, rev[0], tol)) return [];
        pts = rev;
      }
      pts = pts.slice(1);
    }
    for (const p of pts) ring.push(p);
  }
  return ring;
}
function orientFirstEdge(edges, tol) {
  if (!Array.isArray(edges) || edges.length < 2) return edges;
  const a = edges[0];
  if (!a || a.type !== HATCH_EDGE_LINE || !finitePt(a.start) || !finitePt(a.end)) return edges;
  const next = edgePoints(edges[1]);
  if (!next.length) return edges;
  const ends = [next[0], next[next.length - 1]];
  const meets = (p) => ends.some((q) => near2(p, q, tol));
  if (meets(a.end)) return edges;
  if (!meets(a.start)) return edges;
  return [{ ...a, start: a.end, end: a.start }, ...edges.slice(1)];
}
function edgePoints(edge) {
  if (!edge) return [];
  if (edge.type === HATCH_EDGE_LINE) {
    if (!finitePt(edge.start) || !finitePt(edge.end)) return [];
    return [ringPt(edge.start), ringPt(edge.end)];
  }
  if (edge.type === HATCH_EDGE_ARC) {
    if (!finitePt(edge.center) || !(edge.radius > 0)) return [];
    return tessellateArcEdge(edge);
  }
  if (edge.type === HATCH_EDGE_ELLIPSE) {
    if (!finitePt(edge.center) || !finitePt(edge.majorAxis)) return [];
    return tessellateEllipseEdge(edge);
  }
  if (edge.type === HATCH_EDGE_SPLINE && (edge.controlPoints || []).length >= 2) {
    return splineToPolyline({
      controlPoints: edge.controlPoints,
      degree: edge.degree || 3,
      closed: false
    }).map(ringPt);
  }
  return [];
}
var finitePt = (p) => !!p && Number.isFinite(p.x) && Number.isFinite(p.y);
function ringContains(outer, inner) {
  if (!outer || outer.length < 3 || !inner || !inner.length) return false;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of outer) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  }
  let cx = 0, cy = 0;
  for (const p of inner) {
    cx += p.x;
    cy += p.y;
  }
  cx /= inner.length;
  cy /= inner.length;
  const candidates = [inner[0], { x: cx, y: cy }];
  for (const q of candidates) {
    if (q.x < minX || q.x > maxX || q.y < minY || q.y > maxY) continue;
    let hit = false;
    for (let i = 0, j = outer.length - 1; i < outer.length; j = i++) {
      const a = outer[i], b = outer[j];
      if (a.y > q.y !== b.y > q.y && q.x < (b.x - a.x) * (q.y - a.y) / (b.y - a.y) + a.x)
        hit = !hit;
    }
    if (hit) return true;
  }
  return false;
}
function hatchRegionsDetailed(paths) {
  const rings = [];
  let droppedPaths = 0;
  for (const p of paths || []) {
    const ring = hatchPathToRing(p);
    if (ring.length >= 3) rings.push({ flag: p && p.boundaryPathTypeFlag || 0, ring });
    else droppedPaths += 1;
  }
  if (!rings.length) return { regions: [], droppedPaths };
  let outers = rings.filter((r) => r.flag & HATCH_PATH_EXTERNAL);
  let inners = rings.filter((r) => !(r.flag & HATCH_PATH_EXTERNAL));
  if (!outers.length) {
    const i = rings.findIndex((r) => r.flag & HATCH_PATH_OUTERMOST);
    const pick = rings[i < 0 ? 0 : i];
    outers = [pick];
    inners = rings.filter((r) => r !== pick);
  }
  const regions = outers.map((r) => ({ boundary: r.ring, islands: [] }));
  for (const inner of inners) {
    const host = regions.find((rg) => ringContains(rg.boundary, inner.ring));
    if (host) host.islands.push(inner.ring);
    else regions.push({ boundary: inner.ring, islands: [] });
  }
  return { regions, droppedPaths };
}
function importedPatternScale(ent, patternName, unitScale = 1) {
  const spacing = Number(ent && ent.patternSpacing);
  const fam = HATCH_PATTERNS[patternName] && HATCH_PATTERNS[patternName].lines;
  const ours = fam && fam.length ? Number(fam[0].offset) : NaN;
  if (Number.isFinite(spacing) && spacing > 0 && Number.isFinite(ours) && ours > 0) {
    return spacing * Math.abs(unitScale || 1) / ours;
  }
  return ent && ent.patternScale || 1;
}
var mapPatternName = (name) => {
  if (!name) return DEFAULT_HATCH_PATTERN;
  const u = String(name).toUpperCase();
  return HATCH_PATTERNS[u] ? u : DEFAULT_HATCH_PATTERN;
};
function mapLine(ent, ec = entityContext(ent)) {
  if (!ent.vertices || ent.vertices.length < 2) return null;
  const a = ec.P(ent.vertices[0]);
  const b = ec.P(ent.vertices[1]);
  return ec.mk("line", { x1: a.x, y1: a.y, x2: b.x, y2: b.y });
}
var BULGE_MIN_STEPS = 8;
var BULGE_MAX_STEPS = 64;
function bulgeArcPoints(p1, p2, b) {
  const arc = bulgeToArc(p1, p2, b);
  if (!arc) return [];
  const theta = arc.sweepDeg * Math.PI / 180;
  const { cx, cy, r } = arc;
  const a0 = Math.atan2(p1.y - cy, p1.x - cx);
  const steps = Math.max(
    BULGE_MIN_STEPS,
    Math.min(BULGE_MAX_STEPS, Math.ceil(Math.abs(theta) / (Math.PI / 2) * 12))
  );
  const out = [];
  for (let i = 1; i < steps; i += 1) {
    const t = a0 + theta * (i / steps);
    out.push({ x: cx + r * Math.cos(t), y: cy + r * Math.sin(t) });
  }
  return out;
}
function mapPolyline(ent, ec = entityContext(ent)) {
  const vs = ent.vertices || [];
  if (!Array.isArray(vs)) throw new TypeError("polyline vertices must be an array");
  const pts = [];
  let curved = false;
  for (let i = 0; i < vs.length; i += 1) {
    const v = vs[i];
    pts.push(ec.P(v));
    const b = v && v.bulge;
    if (!Number.isFinite(b) || b === 0) continue;
    const nextRaw = i + 1 < vs.length ? vs[i + 1] : ent.shape ? vs[0] : null;
    if (!nextRaw) continue;
    const arc = bulgeArcPoints(
      { x: v.x || 0, y: v.y || 0 },
      { x: nextRaw.x || 0, y: nextRaw.y || 0 },
      b
    );
    if (!arc.length) continue;
    curved = true;
    for (const p of arc) pts.push(ec.P(p));
  }
  if (pts.length < 2) return null;
  const out = { points: pts, closed: !!ent.shape };
  if (curved) out.approx = true;
  const rawW = Number.isFinite(ent.constantWidth) ? ent.constantWidth : ent.width;
  if (Number.isFinite(rawW) && rawW > 0) out.width = rawW * ec.scale;
  return ec.mk("polyline", out);
}
function mapCircle(ent, ec = entityContext(ent)) {
  const c = ec.P(ent.center);
  return ec.mk("circle", { cx: c.x, cy: c.y, r: (ent.radius || 0) * ec.scale });
}
function mapArc(ent, ec = entityContext(ent)) {
  const c = ec.P(ent.center);
  return ec.mk("arc", {
    cx: c.x,
    cy: c.y,
    r: (ent.radius || 0) * ec.scale,
    // Mirrored: the CCW arc runs from the mapped END to the mapped START.
    startAngle: ec.ang((ec.mirror ? ent.endAngle : ent.startAngle) * RAD2DEG || 0),
    endAngle: ec.ang((ec.mirror ? ent.startAngle : ent.endAngle) * RAD2DEG || 0)
  });
}
function mapEllipse(ent, ec = entityContext(ent), opts = null) {
  const c = ec.P(ent.center);
  const mj = ent.majorAxisEndPoint || { x: 1, y: 0 };
  const rx = Math.hypot(mj.x, mj.y) * ec.scale;
  const hasParams = ent.startAngle !== void 0 && ent.endAngle !== void 0;
  const sweep = hasParams ? ent.endAngle - ent.startAngle : 2 * Math.PI;
  const partial = hasParams && Math.abs(sweep) < 6.2831;
  const out = {
    cx: c.x,
    cy: c.y,
    rx,
    ry: rx * (ent.axisRatio || 1),
    rotation: ec.ang(Math.atan2(mj.y, mj.x) * RAD2DEG)
  };
  if (partial) {
    const flip = ec.mirror !== !!(opts && opts.selfMirror);
    const s0 = flip ? -ent.endAngle : ent.startAngle;
    const startDeg = (s0 * RAD2DEG % 360 + 360) % 360;
    out.startAngle = startDeg;
    out.endAngle = startDeg + Math.abs(sweep) * RAD2DEG * (flip ? 1 : Math.sign(sweep) || 1);
  }
  return ec.mk("ellipse", out);
}
function textJustifyFromDXF(halign, valign) {
  const h = Number(halign) || 0;
  const v = Number(valign) || 0;
  if (h === 4) return { justify: "MC" };
  if (h === 3 || h === 5) return { justify: "BL", approx: true };
  const hc = h === 1 ? "C" : h === 2 ? "R" : "L";
  const vc = v === 3 ? "T" : v === 2 ? "M" : "B";
  return { justify: vc + hc };
}
var MTEXT_ATTACHMENTS = ["TL", "TC", "TR", "ML", "MC", "MR", "BL", "BC", "BR"];
var MTEXT_IMPORT_MAX_CHARS = 8192;
function mapMText(ent, ec = entityContext(ent)) {
  let raw = String(ent.text ?? "");
  const truncated = raw.length > MTEXT_IMPORT_MAX_CHARS;
  if (truncated) raw = raw.slice(0, MTEXT_IMPORT_MAX_CHARS);
  const protectedS = raw.replace(/\\\\/g, "\0").replace(/\\\{/g, "").replace(/\\\}/g, "");
  const restore = (s) => s.replaceAll("\0", "\\").replaceAll("", "{").replaceAll("", "}");
  const paras = protectedS.split(/\\P/).map(
    (para) => restore(
      para.replace(/\\[A-Za-z][^;]*;/g, "").replace(/\\~/g, " ").replace(/[{}]/g, "")
    )
  );
  const text = paras.join("\n");
  const approx = truncated || text !== restore(protectedS.split(/\\P/).join("\n"));
  const height = (ent.height || 1) * ec.scale;
  const width = ent.width > 0 ? ent.width * ec.scale : Math.max(height, ...paras.map((t) => approxMeasure(t, height)));
  const justify = MTEXT_ATTACHMENTS[(Number(ent.attachmentPoint) || 1) - 1] || "TL";
  const rotation = ec.ang(ent.rotation || 0);
  const p = ec.P(ent.position || { x: 0, y: 0 });
  const { fx } = justifyFactors(justify);
  const a = fx ? mtextToWorld({ x: p.x, y: p.y, rotation }, -fx * width, 0) : p;
  const out = { x: a.x, y: a.y, width, text, height, rotation };
  if (justify !== "TL") out.justify = justify;
  if (Number.isFinite(ent.lineSpacing) && ent.lineSpacing > 0 && ent.lineSpacing !== MTEXT_DEFAULT_LINE_SPACING) {
    out.lineSpacing = ent.lineSpacing;
  }
  const mStyle = ent.styleName || ent.textStyle;
  if (typeof mStyle === "string" && mStyle && mStyle.toUpperCase() !== "STANDARD" && isSafeStyleName(mStyle)) {
    out.textStyle = mStyle;
  }
  if (approx) out.approx = true;
  return ec.mk("mtext", out);
}
function mapText(ent, ec = entityContext(ent), isMtext = false) {
  if (isMtext) return mapMText(ent, ec);
  const justified = (Number(ent.halign) || 0) !== 0 || (Number(ent.valign) || 0) !== 0;
  const anchor = justified && ent.endPoint ? ent.endPoint : ent.startPoint || ent.endPoint || { x: 0, y: 0 };
  const s = ec.P(anchor);
  const out = {
    x: s.x,
    y: s.y,
    text: ent.text || "",
    height: (ent.textHeight || 1) * ec.scale,
    rotation: ec.ang(ent.rotation || 0)
  };
  const jr = textJustifyFromDXF(ent.halign, ent.valign);
  if (jr.justify !== "BL") out.justify = jr.justify;
  if (jr.approx) out.approx = true;
  const styleName = ent.styleName || ent.textStyle;
  if (typeof styleName === "string" && styleName && styleName.toUpperCase() !== "STANDARD" && isSafeStyleName(styleName)) {
    out.textStyle = styleName;
  }
  return ec.mk("text", out);
}
function mapPoint(ent, ec = entityContext(ent)) {
  const s = ec.P(ent.position || { x: 0, y: 0 });
  return ec.mk("point", { x: s.x, y: s.y });
}
function mapSolid(ent, ec = entityContext(ent)) {
  const pts = (ent.points || ent.vertices || []).map((p) => ec.P(p));
  if (pts.length < 3) return null;
  return ec.mk("polyline", { points: pts, closed: true, approx: true });
}
function mapSpline(ent, ec = entityContext(ent)) {
  const fit = (ent.fitPoints || []).map((p) => ec.P(p));
  const ctrl = (ent.controlPoints || []).map((p) => ec.P(p));
  const degree = ent.degreeOfSplineCurve || 3;
  const closed = !!ent.closed;
  if (fit.length >= 2) return ec.mk("spline", { fitPoints: fit, degree, closed });
  if (ctrl.length >= 2) return ec.mk("spline", { controlPoints: ctrl, degree, closed });
  const pts = fit.length ? fit : ctrl;
  if (pts.length < 2) return null;
  return ec.mk("polyline", { points: pts, closed, approx: true });
}
function mapInsert(ent, ec = entityContext(ent)) {
  const pos = ec.P(ent.position || { x: 0, y: 0 });
  const rotation = ec.mirror ? ec.rot - (ent.rotation || 0) : ec.ang(ent.rotation || 0);
  const props = {
    blockName: ent.name,
    insertionPoint: { x: pos.x, y: pos.y },
    scaleX: (ent.xScale || 1) * (ec.mirror ? -1 : 1),
    scaleY: ent.yScale || 1,
    rotation,
    attributeValues: {}
  };
  if (Array.isArray(ent.attributes) && ent.attributes.length) {
    props.attributes = ent.attributes.map((a) => {
      const p = ec.P({ x: a.x, y: a.y });
      return {
        tag: a.tag,
        value: a.value,
        x: p.x,
        y: p.y,
        height: (a.height || 1) * ec.scale,
        rotation: ec.ang(a.rotation || 0),
        visible: a.visible !== false,
        ...a.prompt != null ? { prompt: a.prompt } : {},
        ...a.field != null ? { field: a.field } : {},
        // The sheet a field-bound attribute reads from, recovered from the ATTRIB's
        // XDATA. Round-1 review: this whitelist dropping a key that the supplement
        // had already carried all the way to it is now the SECOND time — hence the
        // note above. Anything added to an attribute upstream must be added here.
        ...a.fieldLayout != null ? { fieldLayout: a.fieldLayout } : {}
      };
    });
    props.attributeValues = Object.fromEntries(props.attributes.map((a) => [a.tag, a.value]));
  }
  return ec.mk("insert", props);
}
function mapHatchRegions(ent, ec = entityContext(ent)) {
  const regions = Array.isArray(ent.regions) && ent.regions.length ? ent.regions : [{ boundary: ent.boundary || [], islands: ent.islands || [] }];
  const out = [];
  for (const region of regions) {
    const one = mapHatch({ ...ent, boundary: region.boundary, islands: region.islands }, ec);
    if (one) out.push(one);
  }
  return out;
}
function mapHatch(ent, ec = entityContext(ent)) {
  const ring = (ent.boundary || []).map((p) => ec.P(p));
  if (ring.length < 3) return null;
  const solid = !!ent.solidFill || String(ent.patternName || "").toUpperCase() === "SOLID";
  const islands = (ent.islands || []).map((h) => Array.isArray(h) ? h.map((p) => ec.P(p)) : []).filter((h) => h.length >= 3);
  const name = solid ? "SOLID" : mapPatternName(ent.patternName);
  const hatch = makeHatch(
    {
      explicitBoundary: ring,
      explicitHoles: islands,
      // [BUG-IMPORT-ISLAND-1] The file has STATED the island set — `islands` above,
      // which is legitimately empty when the file records one boundary loop. Say so,
      // so the store does not later go looking for islands among the furniture
      // standing in the room. See withDeclaredHoles in cadHatch/cache.js.
      islandsFromFile: true,
      patternName: name,
      patternAngle: ent.patternAngle || 0,
      patternScale: importedPatternScale(ent, name, ec.scale),
      layer: ec.layer
    },
    []
  );
  if (solid) {
    if (!hatch.cachedBoundary || hatch.cachedBoundary.length < 3) return null;
  } else if (!hatch.cachedLines || hatch.cachedLines.length === 0) {
    return null;
  }
  return { ...hatch, id: genId("h"), color: ec.color, linetype: ec.linetype, approx: true };
}
function mapDimension(ent, ec = entityContext(ent), style = null) {
  const t = ent.dimType;
  let dim = null;
  if (t === "linear" || t === "aligned") {
    if (!ent.p1 || !ent.p2 || !ent.linePoint) return null;
    dim = makeDimension({
      dimType: t,
      p1: ec.P(ent.p1),
      p2: ec.P(ent.p2),
      linePoint: ec.P(ent.linePoint)
    });
  } else if (t === "angular") {
    if (!ent.vertex || !ent.p1 || !ent.p2) return null;
    dim = makeDimension({
      dimType: "angular",
      vertex: ec.P(ent.vertex),
      p1: ec.P(ent.p1),
      p2: ec.P(ent.p2)
    });
  } else if (t === "radius" || t === "diameter") {
    if (!ent.center || !ent.leaderPoint) return null;
    const c = ec.P(ent.center);
    const lp = ec.P(ent.leaderPoint);
    const radius = Math.hypot(lp.x - c.x, lp.y - c.y);
    if (!(radius > 0)) return null;
    dim = makeDimension({ dimType: t, center: c, radius, leaderPoint: lp });
  } else if (t === "ordinate") {
    if (!ent.feature || !ent.leader) return null;
    const f = ec.P(ent.feature);
    const lp = ec.P(ent.leader);
    const axis = ent.axis === "x" ? "x" : "y";
    const m = typeof ent.measurement === "number" ? ent.measurement : axis === "x" ? f.x : f.y;
    const datum = axis === "x" ? { x: f.x - m, y: 0 } : { x: 0, y: f.y - m };
    dim = makeDimension({ dimType: "ordinate", feature: f, leader: lp, datum, axis });
  }
  if (!dim) return null;
  const sized = style && (style.textHeight > 0 || style.arrowSize > 0) ? {
    autoText: false,
    textHeight: style.textHeight > 0 ? style.textHeight * ec.scale : dim.textHeight,
    arrowSize: (style.arrowSize > 0 ? style.arrowSize : style.textHeight) * ec.scale || dim.arrowSize
  } : null;
  return {
    ...dim,
    ...style && style.name ? { dimStyle: style.name } : {},
    ...sized || {},
    id: genId("dim"),
    layer: ec.layer,
    color: ec.color,
    linetype: ec.linetype
  };
}
function computeImportOffset(entities) {
  const b = documentBounds(entities);
  if (!b) return { x: 0, y: 0 };
  const cx = (b.minX + b.maxX) / 2;
  const cy = (b.minY + b.maxY) / 2;
  if (Math.abs(cx) > LARGE || Math.abs(cy) > LARGE) {
    return { x: Math.round(b.minX), y: Math.round(b.minY) };
  }
  return { x: 0, y: 0 };
}
function applyImportOffset(entities, offset) {
  if (!offset || !offset.x && !offset.y) return entities;
  return entities.map((e) => translateEntity(e, -offset.x, -offset.y));
}
function normalizeCoords(entities) {
  const offset = computeImportOffset(entities);
  return { entities: applyImportOffset(entities, offset), offset };
}

// src/cadDxfSupplement.js
var DETAILED = /* @__PURE__ */ new Set([
  "SECTION",
  "TABLE",
  "LAYER",
  "STYLE",
  "DIMSTYLE",
  "BLOCK",
  "HATCH",
  "ATTRIB",
  "ATTDEF",
  // [C4] The OBJECTS section's group records. dxf-parser skips OBJECTS entirely,
  // so this walk is the ONLY reader of a DXF GROUP — see the OBJECTS branch below.
  "GROUP",
  "DICTIONARY",
  // [G5-INTEROP] The georeference, and the BLOCK_RECORD that names its extension
  // dictionary. A record type absent from this set is parsed with `groups: null` — it
  // keeps its type and handle and DROPS every tag, which reads exactly like "the file
  // does not contain one". Measured the hard way: the GEODATA branch below was correct
  // and still returned an empty pair list until both names were added here.
  "GEODATA",
  "BLOCK_RECORD"
]);
var DEG2RAD = Math.PI / 180;
function toRecords(text) {
  const src = String(text == null ? "" : text);
  const n = src.length;
  const recs = [];
  let cur = null;
  let pos = 0;
  const codeAt = (from, to) => {
    let v = 0;
    let seen = false;
    for (let i = from; i < to; i++) {
      const c = src.charCodeAt(i);
      if (c === 32 || c === 9 || c === 13) {
        if (seen) break;
        continue;
      }
      if (c < 48 || c > 57) return NaN;
      v = v * 10 + (c - 48);
      seen = true;
    }
    return seen ? v : NaN;
  };
  const lineEnd = (from) => {
    const i = src.indexOf("\n", from);
    return i < 0 ? n : i;
  };
  const value = (from, to) => src.slice(from, to > from && src.charCodeAt(to - 1) === 13 ? to - 1 : to);
  while (pos < n) {
    const codeEnd = lineEnd(pos);
    const code = codeAt(pos, codeEnd);
    if (!Number.isInteger(code)) break;
    if (codeEnd >= n) break;
    const valStart = codeEnd + 1;
    const valEnd = lineEnd(valStart);
    if (code === 0) {
      if (cur) recs.push(cur);
      const type = value(valStart, valEnd);
      cur = {
        type,
        groups: DETAILED.has(type) ? [] : null,
        handle: void 0,
        z: void 0,
        ls: void 0
      };
    } else if (cur && cur.groups) {
      const v = value(valStart, valEnd);
      cur.groups.push([code, v]);
      if (code === 5) cur.handle = v;
    } else if (cur) {
      if (code === 5) cur.handle = value(valStart, valEnd);
      else if (code === 210) cur.nx = Number(value(valStart, valEnd));
      else if (code === 220) cur.ny = Number(value(valStart, valEnd));
      else if (code === 230) cur.z = Number(value(valStart, valEnd));
      else if (code === 44) cur.ls = Number(value(valStart, valEnd));
    }
    pos = valEnd + 1;
  }
  if (cur) recs.push(cur);
  return recs;
}
var num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
function group(rec, code) {
  if (!rec.groups) return void 0;
  for (const [c, v] of rec.groups) if (c === code) return v;
  return void 0;
}
function namedHandlePairs(rec) {
  const out = [];
  let pending = null;
  for (const [code, v] of rec.groups || []) {
    if (code === 3) pending = v;
    else if ((code === 350 || code === 360) && pending != null) {
      out.push([pending, v]);
      pending = null;
    }
  }
  return out;
}
function readLayerRecord(rec) {
  const name = group(rec, 2);
  if (name == null) return null;
  const out = { name };
  for (const [code, value] of rec.groups) {
    if (code === 6) out.lineType = value;
    else if (code === 70) {
      const flags = num(value) || 0;
      out.locked = (flags & 4) !== 0;
      out.frozen = (flags & 1) !== 0;
    } else if (code === 62) {
      const n = num(value);
      if (n != null) {
        out.aci = Math.abs(n);
        out.visible = n >= 0;
      }
    } else if (code === 290) {
      const n = num(value);
      if (n != null) out.plot = n !== 0;
    } else if (code === 370) {
      const n = num(value);
      if (n != null) out.lineWeight = n;
    } else if (code === 420) {
      const n = num(value);
      if (n != null) out.trueColor = n & 16777215;
    }
  }
  return out;
}
function readStyleRecord(rec) {
  const name = group(rec, 2);
  if (name == null) return null;
  const out = {};
  const font = group(rec, 3);
  if (typeof font === "string" && font) out.font = font;
  const fixed = num(group(rec, 40));
  if (fixed != null) out.fixedHeight = fixed;
  const width = num(group(rec, 41));
  if (width != null) out.widthFactor = width;
  const oblique = num(group(rec, 50));
  if (oblique != null) out.oblique = oblique;
  return { name, fields: out };
}
function readDimStyleRecord(rec) {
  const name = group(rec, 2);
  if (name == null) return null;
  const out = {};
  const txt = num(group(rec, 140));
  if (txt != null) out.DIMTXT = txt;
  const asz = num(group(rec, 41));
  if (asz != null) out.DIMASZ = asz;
  const dec = num(group(rec, 271));
  if (dec != null) out.DIMDEC = dec;
  const adec = num(group(rec, 179));
  if (adec != null) out.DIMADEC = adec;
  return { name, fields: out };
}
var XDATA_APP = "BRANCHCAD";
function readAttribRecord(rec) {
  const out = { tag: "", value: "", x: 0, y: 0, height: 1, rotation: 0, visible: true };
  let sawTag = false;
  for (const [code, value] of rec.groups) {
    switch (code) {
      case 1:
        out.value = value;
        break;
      case 2:
        out.tag = value;
        sawTag = true;
        break;
      case 3:
        out.prompt = value;
        break;
      case 10:
        out.x = num(value) || 0;
        break;
      case 20:
        out.y = num(value) || 0;
        break;
      case 40:
        out.height = num(value) || 1;
        break;
      case 50:
        out.rotation = num(value) || 0;
        break;
      case 70:
        out.visible = ((num(value) || 0) & 1) === 0;
        break;
      // [C5] XDATA: 1001 names the application, 1000 is the string that follows it.
      // We write exactly one pair — the NAME of the sheet a field-bound attribute
      // reads from — because the binding has no home in ATTRIB's own vocabulary and
      // dropping it silently re-pointed every field in a multi-sheet drawing at
      // sheet 1 (round-1 review, measured). Any other application's XDATA is
      // ignored: `xdataApp` only latches ours.
      case 1001:
        out.__xapp = value === XDATA_APP ? value : null;
        break;
      case 1e3:
        if (out.__xapp === XDATA_APP && out.fieldLayout == null) out.fieldLayout = value;
        break;
      default:
        break;
    }
  }
  delete out.__xapp;
  return sawTag ? out : null;
}
function readHatchRecord(rec) {
  const ent = {
    type: "HATCH",
    layer: "0",
    patternName: "",
    patternAngle: 0,
    patternScale: 1,
    solidFill: false
  };
  let aci = null;
  const paths = [];
  let path = null;
  let edge = null;
  let pathsLeft = 0;
  let inBoundary = false;
  let vertsLeft = 0;
  let pendingX = null;
  let pendingX2 = null;
  const closeEdge = () => {
    if (!edge) return;
    if (path && path.edges) path.edges.push(edge);
    edge = null;
  };
  const closePath = () => {
    closeEdge();
    if (!path) return;
    paths.push(path);
    path = null;
    pendingX = null;
    pendingX2 = null;
    vertsLeft = 0;
    if (--pathsLeft <= 0) inBoundary = false;
  };
  for (const [code, value] of rec.groups) {
    if (inBoundary) {
      const n = num(value);
      switch (code) {
        case 92: {
          closePath();
          const flags = n || 0;
          path = flags & 2 ? { boundaryPathTypeFlag: flags, vertices: [] } : { boundaryPathTypeFlag: flags, edges: [] };
          break;
        }
        case 93:
          vertsLeft = path && path.vertices ? n || 0 : 0;
          break;
        case 72:
          if (path && path.edges) {
            closeEdge();
            edge = { type: n || 0 };
          }
          break;
        case 73:
          if (edge) edge.isCCW = n !== 0;
          break;
        case 10:
          pendingX = n;
          break;
        case 20:
          if (path && path.vertices) {
            if (pendingX != null && n != null && vertsLeft > 0) {
              path.vertices.push({ x: pendingX, y: n });
              vertsLeft--;
            }
          } else if (edge) {
            const p = { x: pendingX || 0, y: n || 0 };
            if (edge.type === 1) edge.start = p;
            else if (edge.type === 4) (edge.controlPoints || (edge.controlPoints = [])).push(p);
            else edge.center = p;
          }
          pendingX = null;
          break;
        case 11:
          pendingX2 = n;
          break;
        case 21:
          if (edge) {
            const p = { x: pendingX2 || 0, y: n || 0 };
            if (edge.type === 1) edge.end = p;
            else edge.majorAxis = p;
          }
          pendingX2 = null;
          break;
        case 40:
          if (edge) {
            if (edge.type === 2) edge.radius = n || 0;
            else if (edge.type === 3) edge.axisRatio = n || 0;
          }
          break;
        case 94:
          if (edge && edge.type === 4) edge.degree = n || 3;
          break;
        case 50:
          if (edge) edge.startAngle = (n || 0) * DEG2RAD;
          break;
        case 51:
          if (edge) edge.endAngle = (n || 0) * DEG2RAD;
          break;
        case 97:
          closePath();
          break;
        default:
          break;
      }
      continue;
    }
    switch (code) {
      case 5:
        ent.handle = value;
        break;
      case 8:
        ent.layer = value;
        break;
      case 6:
        ent.lineType = value;
        break;
      case 48: {
        const n = num(value);
        if (n != null) ent.lineTypeScale = n;
        break;
      }
      case 62:
        aci = num(value);
        break;
      case 210: {
        const n = num(value);
        if (n != null) ent.extrusionDirectionX = n;
        break;
      }
      case 220: {
        const n = num(value);
        if (n != null) ent.extrusionDirectionY = n;
        break;
      }
      case 230: {
        const n = num(value);
        if (n != null) ent.extrusionDirectionZ = n;
        break;
      }
      case 370: {
        const n = num(value);
        if (n != null) ent.lineweight = n;
        break;
      }
      case 420: {
        const n = num(value);
        if (n != null) ent.color = n & 16777215;
        break;
      }
      case 2:
        ent.patternName = value;
        break;
      case 70:
        ent.solidFill = num(value) === 1;
        break;
      case 91:
        pathsLeft = num(value) || 0;
        inBoundary = pathsLeft > 0;
        break;
      case 52: {
        const n = num(value);
        if (n != null) ent.patternAngle = n;
        break;
      }
      case 41: {
        const n = num(value);
        if (n != null) ent.patternScale = n;
        break;
      }
      default:
        break;
    }
  }
  closePath();
  const { regions, droppedPaths } = hatchRegionsDetailed(paths);
  const { boundary, islands } = regions.length ? regions[0] : { boundary: [], islands: [] };
  ent.boundary = boundary;
  ent.islands = islands;
  ent.regions = regions;
  ent.droppedPaths = droppedPaths;
  if (ent.color == null && aci != null) {
    const rgb = aci2int(Math.abs(aci));
    if (rgb != null) ent.color = rgb;
  }
  return ent;
}
function mergeEntities(entities, order, drop) {
  const list = Array.isArray(entities) ? entities : [];
  const hatches = order.filter((o) => o.ent);
  const dropping = drop && drop.size ? list.some((e) => e && drop.has(String(e.handle))) : false;
  if (!hatches.length && !dropping) return list;
  const kept = dropping ? list.filter((e) => !(e && drop.has(String(e.handle)))) : list;
  const byHandle = /* @__PURE__ */ new Map();
  for (const e of kept) {
    const h = e && e.handle != null ? String(e.handle) : null;
    if (h != null && !byHandle.has(h)) byHandle.set(h, e);
  }
  const scanned = new Set(order.map((o) => o.handle != null ? String(o.handle) : null));
  const orderable = kept.every((e) => e && e.handle != null && scanned.has(String(e.handle)));
  if (!orderable) return [...kept, ...hatches.map((o) => o.ent)];
  const out = [];
  const used = /* @__PURE__ */ new Set();
  for (const o of order) {
    if (o.ent) {
      out.push(o.ent);
      continue;
    }
    const e = o.handle != null ? byHandle.get(String(o.handle)) : null;
    if (e && !used.has(e)) {
      out.push(e);
      used.add(e);
    }
  }
  for (const e of kept) if (!used.has(e)) out.push(e);
  return out;
}
function supplementParsedDxf(text, parsed) {
  if (!parsed || typeof parsed !== "object") return parsed;
  const recs = toRecords(text);
  if (!recs.length) return parsed;
  const layers = /* @__PURE__ */ Object.create(null);
  const styles = /* @__PURE__ */ Object.create(null);
  const dimStyles = /* @__PURE__ */ Object.create(null);
  const perHandle = /* @__PURE__ */ Object.create(null);
  const attdefsByBlock = /* @__PURE__ */ Object.create(null);
  const droppedHandles = /* @__PURE__ */ new Set();
  const entityOrder = [];
  const blockOrder = /* @__PURE__ */ Object.create(null);
  const extrasFor = (handle) => {
    const k = String(handle);
    if (!perHandle[k]) perHandle[k] = {};
    return perHandle[k];
  };
  const groupRecords = [];
  const groupNameByHandle = /* @__PURE__ */ Object.create(null);
  let groupDictHandle = null;
  const pendingDicts = [];
  const geodataRecords = [];
  let modelSpaceXdict = null;
  let section = null;
  let table = null;
  let block = null;
  let lastInsert = null;
  for (const rec of recs) {
    if (rec.type === "SECTION") {
      section = group(rec, 2) || null;
      table = null;
      block = null;
      lastInsert = null;
      continue;
    }
    if (rec.type === "ENDSEC") {
      section = null;
      table = null;
      block = null;
      lastInsert = null;
      continue;
    }
    if (section === "OBJECTS") {
      if (rec.type === "DICTIONARY") {
        const pairs = namedHandlePairs(rec);
        if (groupDictHandle === null) {
          for (const [name, handle] of pairs) {
            if (name === "ACAD_GROUP") {
              groupDictHandle = String(handle);
              break;
            }
          }
        }
        if (rec.handle != null && groupDictHandle === String(rec.handle)) {
          for (const [name, handle] of pairs) groupNameByHandle[handle] = name;
        } else {
          pendingDicts.push({ handle: rec.handle != null ? String(rec.handle) : null, pairs });
        }
        continue;
      }
      if (rec.type === "GROUP") {
        const members = [];
        let selectable = true;
        for (const [code, v] of rec.groups || []) {
          if (code === 340) members.push(v);
          else if (code === 71) selectable = v.trim() !== "0";
        }
        if (members.length >= 2) {
          groupRecords.push({
            handle: rec.handle != null ? String(rec.handle) : null,
            // Group 300 is the description and is written as a fallback; the
            // dictionary entry is the authority, resolved after the walk.
            fallbackName: group(rec, 300) || null,
            selectable,
            memberHandles: members
          });
        }
        continue;
      }
      if (rec.type === "GEODATA") {
        geodataRecords.push({
          handle: rec.handle != null ? String(rec.handle) : null,
          pairs: (rec.groups || []).map(([code, value]) => [code, value])
        });
        continue;
      }
      continue;
    }
    if (section === "TABLES") {
      if (rec.type === "TABLE") {
        table = group(rec, 2) || null;
        continue;
      }
      if (rec.type === "ENDTAB") {
        table = null;
        continue;
      }
      if (table === "BLOCK_RECORD" && rec.type === "BLOCK_RECORD") {
        if (group(rec, 2) === "*Model_Space") {
          const x = group(rec, 360);
          if (x) modelSpaceXdict = String(x);
        }
        continue;
      }
      if (table === "LAYER" && rec.type === "LAYER") {
        const l = readLayerRecord(rec);
        if (l) layers[l.name] = l;
      } else if (table === "STYLE" && rec.type === "STYLE") {
        const s = readStyleRecord(rec);
        if (s) styles[s.name] = s.fields;
      } else if (table === "DIMSTYLE" && rec.type === "DIMSTYLE") {
        const s = readDimStyleRecord(rec);
        if (s) dimStyles[s.name] = s.fields;
      }
      continue;
    }
    if (section !== "BLOCKS" && section !== "ENTITIES") continue;
    if (section === "BLOCKS") {
      if (rec.type === "BLOCK") {
        block = group(rec, 2) || null;
        if (block && !blockOrder[block]) blockOrder[block] = [];
        lastInsert = null;
        continue;
      }
      if (rec.type === "ENDBLK") {
        block = null;
        lastInsert = null;
        continue;
      }
      if (!block || !blockOrder[block]) continue;
    }
    if (rec.type === "ATTDEF" && block) {
      const a = readAttribRecord(rec);
      if (a) {
        const { visible, value, ...rest } = a;
        if (!attdefsByBlock[block]) attdefsByBlock[block] = [];
        attdefsByBlock[block].push({
          ...rest,
          default: value,
          visible,
          ...isFieldCode(value) ? { field: value } : {}
        });
        if (rec.handle != null) droppedHandles.add(String(rec.handle));
      }
      continue;
    }
    if (rec.type === "ATTRIB") {
      const a = readAttribRecord(rec);
      if (a && lastInsert != null) {
        const ex = extrasFor(lastInsert);
        (ex.attributes || (ex.attributes = [])).push(a);
      }
      continue;
    }
    if (rec.type === "SEQEND") {
      lastInsert = null;
      continue;
    }
    if (rec.type === "INSERT") lastInsert = rec.handle;
    else if (rec.type !== "VERTEX") lastInsert = null;
    if (rec.handle != null && (Number.isFinite(rec.z) || Number.isFinite(rec.nx) || Number.isFinite(rec.ny) || Number.isFinite(rec.ls))) {
      const ex = extrasFor(rec.handle);
      if (Number.isFinite(rec.z)) ex.z = rec.z;
      if (Number.isFinite(rec.nx)) ex.nx = rec.nx;
      if (Number.isFinite(rec.ny)) ex.ny = rec.ny;
      if (Number.isFinite(rec.ls)) ex.lineSpacing = rec.ls;
    }
    const entry = { handle: rec.handle, ent: rec.type === "HATCH" ? readHatchRecord(rec) : null };
    if (section === "BLOCKS") blockOrder[block].push(entry);
    else entityOrder.push(entry);
  }
  const out = { ...parsed };
  if (groupDictHandle) {
    for (const d of pendingDicts) {
      if (d.handle === groupDictHandle) {
        for (const [name, handle] of d.pairs) groupNameByHandle[handle] = name;
      }
    }
  }
  if (groupRecords.length) {
    const groups = [];
    for (const g of groupRecords) {
      const memberHandles = [...new Set(g.memberHandles.map((h) => String(h)))];
      if (memberHandles.length < 2) continue;
      groups.push({
        name: g.handle && groupNameByHandle[g.handle] || g.fallbackName || null,
        selectable: g.selectable,
        memberHandles
      });
    }
    if (groups.length) out.branchcadGroups = groups;
  }
  if (geodataRecords.length) {
    out.branchcadGeodata = { records: geodataRecords, hostXdict: modelSpaceXdict };
  }
  const parsedLayers = out.tables && out.tables.layer && out.tables.layer.layers;
  if (parsedLayers && Object.keys(layers).length) {
    const merged = /* @__PURE__ */ Object.create(null);
    for (const name of Object.keys(parsedLayers)) {
      const extra = layers[name];
      const L = { ...parsedLayers[name] };
      if (extra) {
        if (extra.trueColor != null) L.color = extra.trueColor;
        if (extra.lineWeight != null) L.lineWeight = extra.lineWeight;
        if (extra.lineType != null) L.lineType = extra.lineType;
        if (extra.locked != null) L.locked = extra.locked;
        if (extra.frozen != null) L.frozen = extra.frozen;
        if (extra.plot != null) L.plot = extra.plot;
      }
      merged[name] = L;
    }
    for (const name of Object.keys(layers)) {
      if (Object.prototype.hasOwnProperty.call(merged, name)) continue;
      if (!(name in Object.prototype)) continue;
      const extra = layers[name];
      merged[name] = {
        name,
        color: extra.trueColor != null ? extra.trueColor : extra.aci != null ? aci2int(extra.aci) : void 0,
        // Two states, kept apart [LAYER-FREEZE-1] — the same split cadImportBuild
        // now makes for every other record.
        visible: extra.visible !== false,
        frozen: extra.frozen === true,
        locked: extra.locked === true,
        plot: extra.plot !== false,
        ...extra.lineWeight != null ? { lineWeight: extra.lineWeight } : {},
        ...extra.lineType != null ? { lineType: extra.lineType } : {}
      };
    }
    out.tables = { ...out.tables, layer: { ...out.tables.layer, layers: merged } };
  }
  if (Object.keys(styles).length || Object.keys(dimStyles).length) {
    out.tables = { ...out.tables || {} };
    if (Object.keys(styles).length) out.tables.style = { ...out.tables.style || {}, styles };
    if (Object.keys(dimStyles).length) {
      out.tables.dimStyle = { ...out.tables.dimStyle || {}, dimStyles };
    }
  }
  const applyExtras = (list) => {
    if (!Array.isArray(list)) return list;
    let touched = false;
    const next = list.map((e) => {
      const ex = e && e.handle != null ? perHandle[String(e.handle)] : null;
      if (!ex) return e;
      const patch = {};
      const parserHasIt = e.extrusionDirectionZ != null || e.extrusionDirection && e.extrusionDirection.z != null;
      if (!parserHasIt && (ex.z != null || ex.nx != null || ex.ny != null)) {
        patch.extrusionDirectionX = ex.nx || 0;
        patch.extrusionDirectionY = ex.ny || 0;
        patch.extrusionDirectionZ = ex.z != null ? ex.z : 1;
      }
      if (ex.lineSpacing != null && e.lineSpacing == null) patch.lineSpacing = ex.lineSpacing;
      const defs = e && e.name && attdefsByBlock[e.name] || null;
      const mineList = ex.attributes || null;
      const ambiguous = (list2) => {
        const seen = /* @__PURE__ */ new Set();
        const dup = /* @__PURE__ */ new Set();
        for (const x of list2 || []) {
          if (x && x.tag != null) (seen.has(x.tag) ? dup : seen).add(x.tag);
        }
        return dup;
      };
      const dupMine = ambiguous(mineList);
      const dupDefs = ambiguous(defs);
      const pair = (src, dups, list2, a, i) => {
        if (!src) return null;
        if (src.length === list2.length && src[i] && src[i].tag === a.tag) return src[i];
        if (dups.has(a.tag)) return null;
        return src.find((x) => x.tag === a.tag) || null;
      };
      const enrich = (list2) => list2.map((a, i) => {
        const mine = pair(mineList, dupMine, list2, a, i);
        const d = pair(defs, dupDefs, list2, a, i);
        const add2 = {};
        if (a.prompt == null && mine && mine.prompt != null) add2.prompt = mine.prompt;
        if (a.field == null && d && d.field) add2.field = d.field;
        if (a.fieldLayout == null && mine && mine.fieldLayout != null) add2.fieldLayout = mine.fieldLayout;
        return Object.keys(add2).length ? { ...a, ...add2 } : a;
      });
      const baseAttrs = Array.isArray(e.attributes) && e.attributes.length ? e.attributes : mineList;
      if (baseAttrs && (mineList || defs)) {
        const enriched = enrich(baseAttrs);
        if (!Array.isArray(e.attributes) || enriched.some((a, i) => a !== baseAttrs[i])) {
          patch.attributes = enriched;
        }
      }
      if (!Object.keys(patch).length) return e;
      touched = true;
      return { ...e, ...patch };
    });
    return touched ? next : list;
  };
  if (entityOrder.some((o) => o.ent) || Object.keys(perHandle).length) {
    out.entities = applyExtras(mergeEntities(out.entities, entityOrder, droppedHandles));
  }
  const blockNames = Object.keys(blockOrder).filter(
    (n) => blockOrder[n].some((o) => o.ent) || attdefsByBlock[n] || Object.keys(perHandle).length
  );
  if (blockNames.length && out.blocks) {
    out.blocks = { ...out.blocks };
    for (const name of blockNames) {
      const blk = out.blocks[name];
      if (!blk) continue;
      const entities = applyExtras(mergeEntities(blk.entities, blockOrder[name], droppedHandles));
      const attdefs = attdefsByBlock[name];
      if (entities === blk.entities && !attdefs) continue;
      out.blocks[name] = { ...blk, entities, ...attdefs ? { attdefs } : {} };
    }
  }
  return out;
}

// src/dxf/dxfValue.js
var DxfValueError = class extends Error {
  constructor(field, raw, reason = "is not a finite number") {
    super(`${field} ${reason} (${String(raw)})`);
    this.name = "DxfValueError";
    this.field = field;
    this.raw = raw;
  }
};
var MAX_ABS = 1e15;
var MIN_ABS = 1e-12;
function fmtReal(x, field = "value") {
  if (typeof x !== "number" || !Number.isFinite(x)) throw new DxfValueError(field, x);
  const a = Math.abs(x);
  if (a >= MAX_ABS) throw new DxfValueError(field, x, "is out of range");
  if (a < MIN_ABS) return "0";
  const s = String(x);
  if (s.includes("e") || s.includes("E")) {
    const fixed = x.toFixed(20).replace(/0+$/, "");
    return fixed.endsWith(".") ? fixed.slice(0, -1) : fixed;
  }
  return s;
}
function fmtInt(x, field = "value") {
  if (typeof x !== "number" || !Number.isFinite(x)) throw new DxfValueError(field, x);
  if (Math.abs(x) >= MAX_ABS) throw new DxfValueError(field, x, "is out of range");
  return String(Math.round(x));
}
var FORBIDDEN = /[<>/\\":;?*|,=`]/g;
var CONTROLS = /[\u0000-\u001f\u007f-\u009f\u200e\u200f\u202a-\u202e\u2066-\u2069]/g;
var MAX_NAME = 255;
function dxfSymbolName(name) {
  let s = String(name == null ? "" : name);
  s = s.replace(/[\r\n\t]+/g, " ");
  s = s.replace(CONTROLS, "");
  s = s.replace(FORBIDDEN, "_");
  s = s.trim();
  if (s.length > MAX_NAME) s = s.slice(0, MAX_NAME);
  return s === "" ? "_" : s;
}
function bandOf(code) {
  const c = Number(code);
  if (!Number.isFinite(c)) return "text";
  if (c >= 10 && c <= 59 || c >= 140 && c <= 149 || c >= 210 && c <= 239) return "real";
  if (c >= 1010 && c <= 1059) return "real";
  if (c >= 60 && c <= 99 || c >= 170 && c <= 179) return "int";
  if (c >= 270 && c <= 299 || c >= 370 && c <= 389 || c >= 400 && c <= 409) return "int";
  if (c >= 1060 && c <= 1071) return "int";
  return "text";
}
var DxfTagSink = class extends Array {
  // `Array.prototype.map`/`filter`/`slice` construct via the species constructor and
  // would otherwise build a DxfTagSink with an element count as its first argument.
  static get [Symbol.species]() {
    return Array;
  }
  push(...args) {
    for (const arg of args) {
      if (this._code === void 0) {
        this._code = String(arg);
        super.push(this._code);
        continue;
      }
      const code = this._code;
      this._code = void 0;
      super.push(this._checked(code, arg));
    }
    return this.length;
  }
  _checked(code, value) {
    const band = bandOf(code);
    if (band === "text") return String(value);
    const raw = String(value);
    const n = Number(raw);
    if (raw.trim() === "" || !Number.isFinite(n)) throw new DxfValueError(`group ${code}`, raw);
    return band === "real" ? fmtReal(n, `group ${code}`) : fmtInt(n, `group ${code}`);
  }
  // Push without the numeric check, for the handful of values that are legal DXF
  // but deliberately outside the range this sink enforces — today only AutoCAD's
  // `±1e+20` "no extents recorded" sentinels in $EXTMIN/$EXTMAX. Parity is still
  // tracked, so a raw push cannot desynchronise the checked ones that follow.
  //
  // Kept deliberately narrow and named: an escape hatch that is easy to reach for
  // is one that gets used to silence a real fault.
  pushRaw(...args) {
    for (const arg of args) {
      if (this._code === void 0) {
        this._code = String(arg);
        super.push(this._code);
      } else {
        this._code = void 0;
        super.push(String(arg));
      }
    }
    return this.length;
  }
  // Rewind to a previous length, for the transactional entity write. Also clears any
  // half-consumed code so the next push starts on a code again — without this a
  // rollback mid-pair would shift every subsequent tag by one and corrupt the file.
  rewind(mark) {
    this.length = mark;
    this._code = void 0;
  }
};
var SymbolNameMap = class {
  constructor() {
    this._byOriginal = /* @__PURE__ */ new Map();
    this._taken = /* @__PURE__ */ new Set();
    this.warnings = [];
  }
  // Reserve a name this exporter writes ITSELF, so user data can never be handed it
  // (`Continuous`, `ByLayer`, `*Model_Space`, `*D1`, …).
  //
  // Reserved names are written literally by their own writers and are deliberately
  // NOT routed through `map()` — `map()` is for user data, and its job is to move
  // user data out of the way of these.
  reserve(name) {
    this._taken.add(String(name).toLowerCase());
    return name;
  }
  // Is this name already reserved or mapped? Lets a caller skip an alias of a name
  // the exporter writes itself (a doc's `CONTINUOUS` beside the built-in `Continuous`).
  has(name) {
    return this._taken.has(String(name).toLowerCase());
  }
  // Allocate a DISTINCT name for something that is not identified by its name.
  //
  // `map()` memoises by the original string, which is right for a symbol table — two
  // references to layer `WALLS` mean the same layer. It is wrong for LAYOUTS: two
  // different sheets can both be called "Sheet", and they are still two sheets. Using
  // `map()` there returned the same name twice and the ACAD_LAYOUT dictionary silently
  // lost one (a dictionary cannot hold a key twice).
  mapFresh(original) {
    const base = dxfSymbolName(original == null ? "" : original);
    let out = base;
    if (this._taken.has(out.toLowerCase())) {
      for (let n = 1; ; n += 1) {
        const suffix = `_${n}`;
        const head = base.slice(0, Math.max(1, MAX_NAME - suffix.length));
        const candidate = `${head}${suffix}`;
        if (!this._taken.has(candidate.toLowerCase())) {
          out = candidate;
          break;
        }
      }
    }
    this._taken.add(out.toLowerCase());
    if (out !== String(original)) {
      this.warnings.push(
        `name "${original}" exported as "${out}" (illegal or duplicate DXF symbol name)`
      );
    }
    return out;
  }
  // The written name for `original`, stable across calls within one export.
  map(original) {
    const key = String(original == null ? "" : original);
    const hit = this._byOriginal.get(key);
    if (hit !== void 0) return hit;
    const base = dxfSymbolName(key);
    let out = base;
    if (this._taken.has(out.toLowerCase())) {
      for (let n = 1; ; n += 1) {
        const suffix = `_${n}`;
        const head = base.slice(0, Math.max(1, MAX_NAME - suffix.length));
        const candidate = `${head}${suffix}`;
        if (!this._taken.has(candidate.toLowerCase())) {
          out = candidate;
          break;
        }
      }
    }
    this._taken.add(out.toLowerCase());
    this._byOriginal.set(key, out);
    if (out !== key) {
      this.warnings.push(
        `name "${key}" exported as "${out}" (illegal or duplicate DXF symbol name)`
      );
    }
    return out;
  }
};

// src/dxf/dxfGeodata.js
var GEODATA_VERSION = 2;
var GEODATA_COORD_LOCAL_GRID = 1;
var GEODATA_COORD_PROJECTED_GRID = 2;
var GEODATA_COORD_GEOGRAPHIC = 3;
var GEODATA_DICT_ENTRY = "ACAD_GEOGRAPHICDATA";
var UNITS_METRES = 6;
var DEFINITION_CHUNK = 255;
var MAX_DEFINITION_CHARS = 64 * 1024;
function chunkDefinition(text) {
  const escaped = String(text == null ? "" : text).replace(/\r\n?/g, "\n").replace(/\t/g, " ").replace(/[\u0000-\u0008\u000b-\u001f\u007f]/g, "").replace(/\n/g, "^J");
  if (escaped === "") return [""];
  const chunks = [];
  let pos = 0;
  while (pos < escaped.length) {
    let chunk = escaped.slice(pos, pos + DEFINITION_CHUNK);
    pos += chunk.length;
    if (chunk.length === DEFINITION_CHUNK && chunk.endsWith("^")) {
      chunk = chunk.slice(0, -1);
      pos -= 1;
    }
    chunks.push(chunk);
  }
  return chunks;
}
function joinDefinition(chunks) {
  return chunks.join("").replace(/\^J/g, "\n");
}
function epsgCode(digits) {
  const code = `EPSG:${digits}`;
  return GEOREF_CRS_PATTERN.test(code) && !/^EPSG:0+$/.test(code) ? code : null;
}
function minimalCrsXml(code, name) {
  const epsg = /^EPSG:(\d{4,6})$/.exec(String(code || ""));
  if (!epsg) return null;
  const esc = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  const label = esc(name || code);
  return [
    '<?xml version="1.0" encoding="UTF-16" standalone="no" ?>',
    '<Dictionary version="1.0" xmlns="http://www.osgeo.org/mapguide/coordinatesystem">',
    '<Axis uom="Meter">',
    "<CoordinateSystemAxis><AxisOrder>1</AxisOrder><AxisName>Easting</AxisName><AxisAbbreviation>E</AxisAbbreviation><AxisDirection>East</AxisDirection></CoordinateSystemAxis>",
    "<CoordinateSystemAxis><AxisOrder>2</AxisOrder><AxisName>Northing</AxisName><AxisAbbreviation>N</AxisAbbreviation><AxisDirection>North</AxisDirection></CoordinateSystemAxis>",
    "</Axis>",
    `<Alias id="${epsg[1]}" type="CoordinateSystem">`,
    `<ObjectId>${label}</ObjectId>`,
    "<Namespace>EPSG Code</Namespace>",
    "</Alias>",
    "</Dictionary>"
  ].join("\n");
}
function crsCodeFromDefinition(definition, rssTag) {
  const def = String(definition == null ? "" : definition).slice(0, MAX_DEFINITION_CHARS);
  const plain = def.replace(/<\s*\/?\s*[A-Za-z_][\w.-]*:/g, (m2) => m2.replace(/[A-Za-z_][\w.-]*:/, ""));
  const aliasRe = /<Alias\b([^>]*)>([\s\S]*?)<\/Alias>/gi;
  let m;
  while ((m = aliasRe.exec(plain)) !== null) {
    const attrs = m[1];
    const body = m[2];
    if (!/\btype\s*=\s*"CoordinateSystem"/i.test(attrs)) continue;
    if (!/<Namespace>\s*EPSG\s*Code\s*<\/Namespace>/i.test(body)) continue;
    const id = /\bid\s*=\s*"(\d{4,6})"/i.exec(attrs);
    const code = id && epsgCode(id[1]);
    if (code) return code;
  }
  const authRe = /AUTHORITY\s*\[\s*"EPSG"\s*,\s*"(\d{4,6})"\s*\]/gi;
  let last = null;
  while ((m = authRe.exec(def)) !== null) last = m[1];
  if (last && epsgCode(last)) return epsgCode(last);
  const rss = /(?:^|:)(?:EPSG)[:\s]*:?\s*(\d{4,6})\s*$/i.exec(
    String(rssTag == null ? "" : rssTag).trim().slice(0, 256)
  );
  if (rss && epsgCode(rss[1])) return epsgCode(rss[1]);
  return null;
}
function pushGeodataBody(sink, body, handles) {
  const { geodataHandle, xdictHandle, hostHandle } = handles;
  sink.push("0", "GEODATA");
  sink.push("5", geodataHandle);
  sink.push("102", "{ACAD_REACTORS");
  sink.push("330", xdictHandle);
  sink.push("102", "}");
  sink.push("330", xdictHandle);
  sink.push("100", "AcDbGeoData");
  sink.push("90", GEODATA_VERSION);
  sink.push("330", hostHandle);
  sink.push("70", GEODATA_COORD_LOCAL_GRID);
  sink.push("10", body.design.x, "20", body.design.y, "30", body.design.z);
  sink.push("11", body.reference.e, "21", body.reference.n, "31", body.reference.z);
  sink.push("40", 1);
  sink.push("91", UNITS_METRES);
  sink.push("41", 1);
  sink.push("92", UNITS_METRES);
  sink.push("210", 0, "220", 0, "230", 1);
  sink.push("12", 0, "22", 1);
  sink.push("95", 1);
  sink.push("141", 1);
  sink.push("294", 0);
  sink.push("142", 0);
  sink.push("143", 0);
  const chunks = chunkDefinition(body.definition);
  for (let i = 0; i < chunks.length - 1; i += 1) sink.push("303", chunks[i]);
  sink.push("301", chunks[chunks.length - 1]);
  if (body.geoRssTag) sink.push("302", body.geoRssTag);
  sink.push("93", 0);
  sink.push("96", 0);
}
function planGeodata(doc, opts = {}) {
  if (opts.frame === "grid") return { plan: null, warning: null };
  const georef = normalizeGeoref(doc && doc.georef);
  if (!georef) return { plan: null, warning: null };
  const supplied = opts.crsDefinition == null ? null : String(opts.crsDefinition);
  if (supplied != null && supplied.length > MAX_DEFINITION_CHARS) {
    return {
      plan: null,
      warning: `georef not exported: the CRS definition is ${supplied.length} characters (max ${MAX_DEFINITION_CHARS})`
    };
  }
  const definition = supplied || minimalCrsXml(georef.crs, georef.crsName);
  if (!definition) {
    return { plan: null, warning: `georef not exported: ${georef.crs} is not an EPSG code` };
  }
  const body = {
    design: { x: 0, y: 0, z: 0 },
    // The local origin (0,0) IS the design point, so the reference point is the georef
    // origin unchanged. Nothing is computed, which is why criterion 1's round trip is
    // bit-exact: `(x + origin) − origin` is not, and this never does it.
    reference: { e: georef.origin.e, n: georef.origin.n, z: 0 },
    definition,
    geoRssTag: georef.crs,
    crs: georef.crs
  };
  try {
    pushGeodataBody(new DxfTagSink(), body, {
      geodataHandle: "0",
      xdictHandle: "0",
      hostHandle: "0"
    });
  } catch (err) {
    return { plan: null, warning: `georef not exported: ${err && err.message ? err.message : String(err)}` };
  }
  return { plan: { body }, warning: null };
}
function writeGeodataObjects(sink, plan, handles) {
  if (!plan) return;
  const { xdictHandle, geodataHandle, hostHandle } = handles;
  sink.push("0", "DICTIONARY");
  sink.push("5", xdictHandle);
  sink.push("330", hostHandle);
  sink.push("100", "AcDbDictionary");
  sink.push("280", "1");
  sink.push("281", "1");
  sink.push("3", GEODATA_DICT_ENTRY);
  sink.push("350", geodataHandle);
  pushGeodataBody(sink, plan.body, handles);
}
var REFUSE = (reason, detail, owner = null) => ({ ok: false, reason, detail, owner });
function readGeodata(pairs) {
  const get = (code) => {
    for (const [c, v] of pairs) if (Number(c) === code) return v;
    return void 0;
  };
  const owner = (() => {
    const end = pairs.findIndex(([c, v]) => Number(c) === 100 && String(v) === "AcDbGeoData");
    const limit = end < 0 ? pairs.length : end;
    let last = null;
    for (let i = 0; i < limit; i += 1) if (Number(pairs[i][0]) === 330) last = String(pairs[i][1]);
    return last;
  })();
  const hostHandle = (() => {
    const i = pairs.findIndex(([c]) => Number(c) === 90);
    if (i < 0) return null;
    for (let j = i + 1; j < pairs.length; j += 1) {
      if (Number(pairs[j][0]) === 330) return String(pairs[j][1]);
    }
    return null;
  })();
  const num2 = (code, fallback) => {
    const raw = get(code);
    if (raw === void 0) return fallback;
    const n = Number(raw);
    return Number.isFinite(n) ? n : fallback;
  };
  const version = num2(90, null);
  if (version !== GEODATA_VERSION) {
    return REFUSE("version", `GEODATA version ${version == null ? "missing" : version}, expected 2`, owner);
  }
  const coordinateType = num2(70, 0);
  if (coordinateType === 0) return REFUSE("coord-type", "coordinate type 0 (unknown)", owner);
  if (![GEODATA_COORD_LOCAL_GRID, GEODATA_COORD_PROJECTED_GRID, GEODATA_COORD_GEOGRAPHIC].includes(coordinateType)) {
    return REFUSE("coord-type", `coordinate type ${coordinateType}`, owner);
  }
  const up = { x: num2(210, 0), y: num2(220, 0), z: num2(230, 1) };
  if (Math.abs(up.x) > 1e-9 || Math.abs(up.y) > 1e-9 || Math.abs(up.z - 1) > 1e-9) {
    return REFUSE("tilted", `up direction (${up.x}, ${up.y}, ${up.z})`, owner);
  }
  const north = { x: num2(12, 0), y: num2(22, 1) };
  if (Math.abs(north.x) > 1e-9 || Math.abs(north.y - 1) > 1e-9) {
    return REFUSE("rotated", `north direction (${north.x}, ${north.y})`, owner);
  }
  const userScale = num2(141, 1);
  if (num2(95, 1) === 2 && Math.abs(userScale - 1) > 1e-9) {
    return REFUSE("scaled", `user scale factor ${userScale}`, owner);
  }
  for (const code of [11, 21]) {
    const raw = get(code);
    if (raw === void 0 || !Number.isFinite(Number(raw))) {
      return REFUSE("reference", `the reference point's group ${code} is ${raw === void 0 ? "missing" : `not a number (${raw})`}`, owner);
    }
  }
  const chunks = [];
  let chunkChars = 0;
  for (const [c, v] of pairs) {
    const code = Number(c);
    if (code !== 301 && code !== 303) continue;
    const s = String(v == null ? "" : v);
    if (chunkChars >= MAX_DEFINITION_CHARS) break;
    chunks.push(s.slice(0, MAX_DEFINITION_CHARS - chunkChars));
    chunkChars += s.length;
  }
  const crsDefinition = joinDefinition(chunks);
  const geoRssTag = get(302) == null ? null : String(get(302));
  return {
    ok: true,
    version,
    coordinateType,
    design: { x: num2(10, 0), y: num2(20, 0), z: num2(30, 0) },
    reference: { e: num2(11, 0), n: num2(21, 0), z: num2(31, 0) },
    north,
    unitScale: num2(40, 1),
    crs: crsCodeFromDefinition(crsDefinition, geoRssTag),
    crsDefinition,
    geoRssTag,
    // > 0 means the file refines the transform per-region. We apply a pure translation,
    // so those refinements are LOST — the caller says so rather than dropping them
    // silently, because the error they hide is a metre-scale misplacement.
    meshPoints: Math.max(0, num2(93, 0)),
    hostHandle,
    owner,
    handle: get(5) == null ? null : String(get(5))
  };
}

// src/DXFExporter.js
var ENTITY_SUBCLASS = {
  LINE: ["AcDbLine"],
  LWPOLYLINE: ["AcDbPolyline"],
  CIRCLE: ["AcDbCircle"],
  // ARC's second marker sits BETWEEN group 40 and group 50, not at the head —
  // the arc's angles belong to AcDbArc while its centre/radius belong to
  // AcDbCircle. Written by the ARC case itself, so the head emits only the first.
  ARC: ["AcDbCircle"],
  ELLIPSE: ["AcDbEllipse"],
  SPLINE: ["AcDbSpline"],
  POINT: ["AcDbPoint"],
  // TEXT carries `AcDbText` TWICE — ALWAYS, not only when justified. Only the FIRST
  // is listed here, because `_cadHead` pushes this chain at the record HEAD; the
  // closing marker belongs at the END of the record and is pushed by the writer
  // (`case 'text'`, and the three exploded writers). Do not add a second entry here:
  // it would emit both markers up front and put the text body inside the wrong
  // subclass. [DXF-TEXT-SUBCLASS-1] — the missing closing marker on an UNJUSTIFIED
  // text is what made AutoCAD refuse every file this app produced.
  // ATTRIB and ATTDEF are different: their own subclass marker closes the record in
  // place of the second `AcDbText`, which is why they were never affected.
  TEXT: ["AcDbText"],
  MTEXT: ["AcDbMText"],
  ATTRIB: ["AcDbText"],
  ATTDEF: ["AcDbText"],
  INSERT: ["AcDbBlockReference"],
  SOLID: ["AcDbTrace"],
  HATCH: ["AcDbHatch"],
  // SEQEND is an entity with no class of its own beyond AcDbEntity.
  SEQEND: [],
  // DIMENSION's chain is family-dependent (AcDbDimension, then AcDbAlignedDimension
  // / AcDbRadialDimension / …) and interleaved with its definition points, so
  // _writeNativeDimension pushes it rather than the head.
  DIMENSION: []
};
function textJustifyGroups(justify) {
  const { fx, fy } = justifyFactors(justify);
  const h = fx === 0.5 ? 1 : fx === 1 ? 2 : 0;
  const v = fy === 0 ? 3 : fy === 0.5 ? 2 : 0;
  if (h === 0 && v === 0) return null;
  return { h: String(h), v: String(v) };
}
function mtextAttachment(justify) {
  const { fx, fy } = justifyFactors(justify || "TL");
  const col = fx === 0.5 ? 1 : fx === 1 ? 2 : 0;
  const row = fy === 0 ? 0 : fy === 0.5 ? 1 : 2;
  return row * 3 + col + 1;
}
function mtextEncode(text) {
  return String(text ?? "").replace(/\\/g, "\\\\").replace(/\{/g, "\\{").replace(/\}/g, "\\}").replace(/\n/g, "\\P");
}
var SOIL_ACI_COLORS = {
  TOPSOIL: 80,
  // dark green
  FILL: 30,
  // orange-brown
  CLAY: 40,
  // tan
  SILT: 50,
  // yellowish
  SAND: 2,
  // yellow
  GRAVEL: 8,
  // grey
  COBBLES: 9,
  // light grey
  PEAT: 46,
  // dark brown
  ROCK: 251,
  // dark grey
  SANDSTONE: 42,
  MUDSTONE: 252,
  BASALT: 250,
  TUFF: 32,
  ASH: 9
};
var DXFExporter = class _DXFExporter {
  constructor() {
    this.lines = new DxfTagSink();
    this.handleCounter = 100;
    this.layers = [];
    this.entityHandles = [];
    this.warnings = [];
  }
  _handle() {
    return (this.handleCounter++).toString(16).toUpperCase();
  }
  // A numeric field of a TABLE or BLOCK record, guaranteed finite.
  //
  // [review] The transactional write covers ENTITIES only, so one `null` in a text
  // style's width factor threw out of `exportCADModel` and the user got no file —
  // the same "no file at all" failure the dimension bodies had. Measured: a null
  // `widthFactor`, a null `textHeight`, an undefined block base point and a
  // `[1, null]` dash pattern each aborted the whole export.
  //
  // Unlike an entity, a table record CANNOT simply be dropped: a missing LAYER
  // orphans every entity that names it, and a missing BLOCK breaks its INSERTs. So
  // the answer to PROCESS §E.2 q1 is different here — substituting a sane default
  // and SAYING SO leaves the document in a better state than either alternative.
  //
  // [round 2] No silent path. A `null`-and-fallback-0 case used to pass without a
  // warning on the theory that it meant "absent optional field" — but every caller
  // already spreads its defaults first, so a null that reaches here IS a fault, and
  // the rule's only effect was to turn a null LTYPE dash into a silent zero-length
  // dash (a DOT). And a number is shown as a number: `JSON.stringify(NaN)` is
  // "null", which named the value it was reporting wrongly.
  _tableNum(value, fallback, what) {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    const shown = typeof value === "number" ? String(value) : JSON.stringify(value);
    this.warnings.push(`${what}: ${shown} is not a usable number \u2014 exported as ${fallback}`);
    return fallback;
  }
  // Report an entity whose bold/italic cannot ride the DXF record it is written as.
  // No-op for the overwhelmingly common unstyled case, so a plain drawing exports with
  // an empty `warnings` list. Called from every path that flattens app text to a DXF
  // TEXT/MTEXT record — TEXT, MTEXT and the exploded TABLE cells.
  _warnTextWeightLoss(e) {
    if (!e || e.bold !== true && e.italic !== true) return;
    const what = e.bold === true && e.italic === true ? "bold + italic" : e.bold === true ? "bold" : "italic";
    this.warnings.push(
      `${String(e.type || "text").toUpperCase()} ${e.id}: ${what} is not representable in DXF (weight/slant come from the STYLE table font file, not the entity) \u2014 exported unstyled`
    );
  }
  /**
   * Generate a full DXF document for a cross-section
   */
  exportCrossSection(crossSectionData, options = {}) {
    const {
      projectName = "Geotechnical Cross Section",
      sectionName = "Section A-B",
      author = "GeoTech AI Platform",
      units = "METERS",
      includeGrid = true,
      includeAnnotations = true,
      coordinateSystem = "LOCAL",
      // LOCAL or WGS84
      verticalExaggeration = 1
    } = options;
    this.lines = new DxfTagSink();
    this.handleCounter = 100;
    this._writeHeader(projectName, units);
    this._writeTables(crossSectionData);
    this._writeBlocks();
    this._writeEntitiesStart();
    if (coordinateSystem === "LOCAL") {
      this._writeCrossSectionLocal(crossSectionData, {
        includeGrid,
        includeAnnotations,
        verticalExaggeration,
        sectionName
      });
    } else {
      this._writeCrossSectionWGS84(crossSectionData, { verticalExaggeration });
    }
    this._writeEntitiesEnd();
    this.lines.push("0", "EOF");
    return this.lines.join("\r\n");
  }
  /**
   * Export 3D soil model as DXF
   */
  export3DModel(model3D, boreholes, parcelBoundary, options = {}) {
    const { projectName = "3D Subsurface Model", author = "GeoTech AI Platform" } = options;
    this.lines = new DxfTagSink();
    this.handleCounter = 100;
    this._writeHeader(projectName, "METERS");
    this._write3DModelTables(model3D);
    this._writeBlocks();
    this._writeEntitiesStart();
    this._write3DModelEntities(model3D, boreholes, parcelBoundary);
    this._writeEntitiesEnd();
    this.lines.push("0", "EOF");
    return this.lines.join("\r\n");
  }
  // ═════════════════════════════════════════════
  // HEADER SECTION
  // ═════════════════════════════════════════════
  _writeHeader(projectName, units) {
    const l = this.lines;
    l.push("999", `DXF generated by GeoTech AI Platform`);
    l.push("999", `Project: ${projectName}`);
    l.push("999", `Date: ${(/* @__PURE__ */ new Date()).toISOString()}`);
    l.push("0", "SECTION");
    l.push("2", "HEADER");
    l.push("9", "$ACADVER");
    l.push("1", "AC1027");
    l.push("9", "$INSUNITS");
    l.push("70", "6");
    l.push("9", "$LUNITS");
    l.push("70", "2");
    l.push("9", "$LUPREC");
    l.push("70", "3");
    l.push("9", "$TEXTSIZE");
    l.push("40", "0.5");
    l.push("0", "ENDSEC");
  }
  // ═════════════════════════════════════════════
  // TABLES SECTION (LAYERS)
  // ═════════════════════════════════════════════
  _writeTables(crossSectionData) {
    const l = this.lines;
    l.push("0", "SECTION");
    l.push("2", "TABLES");
    l.push("0", "TABLE");
    l.push("2", "LTYPE");
    l.push("5", this._handle());
    l.push("70", "4");
    this._writeLinetype("Continuous", "", 0, []);
    this._writeLinetype("DASHED", "__ __ __ __", 2, [1, -0.5]);
    this._writeLinetype("HIDDEN", "_ _ _ _ _ _", 2, [0.5, -0.25]);
    this._writeLinetype("DASHDOT", "____ . ____ .", 4, [1.5, -0.25, 0, -0.25]);
    l.push("0", "ENDTAB");
    l.push("0", "TABLE");
    l.push("2", "LAYER");
    l.push("5", this._handle());
    l.push("70", "50");
    this._writeLayer("0", 7, "Continuous");
    this._writeLayer("GROUND_SURFACE", 3, "Continuous");
    this._writeLayer("WATER_TABLE", 5, "DASHED");
    this._writeLayer("BOREHOLES", 1, "Continuous");
    this._writeLayer("BH_LABELS", 7, "Continuous");
    this._writeLayer("GRID", 8, "Continuous");
    this._writeLayer("GRID_LABELS", 8, "Continuous");
    this._writeLayer("ANNOTATIONS", 7, "Continuous");
    this._writeLayer("TITLE_BLOCK", 7, "Continuous");
    this._writeLayer("PARCEL_BOUNDARY", 3, "Continuous");
    this._writeLayer("SECTION_LINE", 1, "DASHDOT");
    this._writeLayer("CROSS_SECTION_FRAME", 7, "Continuous");
    const soilTypes = this._extractSoilTypes(crossSectionData);
    soilTypes.forEach((type) => {
      const color = SOIL_ACI_COLORS[type] || 7;
      this._writeLayer(`SOIL_${type}`, color, "Continuous");
      this._writeLayer(`SOIL_${type}_BOUNDARY`, color, "Continuous");
      this._writeLayer(`SOIL_${type}_HATCH`, color, "Continuous");
      this._writeLayer(`SOIL_${type}_LABEL`, color, "Continuous");
    });
    this._writeLayer("WATER_TABLE_FILL", 141, "Continuous");
    this._writeLayer("WATER_LEVEL_LABELS", 5, "Continuous");
    l.push("0", "ENDTAB");
    l.push("0", "TABLE");
    l.push("2", "STYLE");
    l.push("5", this._handle());
    l.push("70", "3");
    this._writeTextStyle("Standard", "txt");
    this._writeTextStyle("Title", "txt");
    this._writeTextStyle("Label", "txt");
    l.push("0", "ENDTAB");
    l.push("0", "ENDSEC");
  }
  _write3DModelTables(model3D) {
    const l = this.lines;
    l.push("0", "SECTION");
    l.push("2", "TABLES");
    l.push("0", "TABLE");
    l.push("2", "LTYPE");
    l.push("5", this._handle());
    l.push("70", "2");
    this._writeLinetype("Continuous", "", 0, []);
    this._writeLinetype("DASHED", "__ __ __ __", 2, [1, -0.5]);
    l.push("0", "ENDTAB");
    l.push("0", "TABLE");
    l.push("2", "LAYER");
    l.push("5", this._handle());
    l.push("70", "30");
    this._writeLayer("0", 7, "Continuous");
    this._writeLayer("GROUND_SURFACE", 3, "Continuous");
    this._writeLayer("WATER_TABLE", 5, "DASHED");
    this._writeLayer("BOREHOLES", 1, "Continuous");
    this._writeLayer("BH_LABELS", 7, "Continuous");
    this._writeLayer("PARCEL_BOUNDARY", 3, "Continuous");
    if (model3D?.soilTypes) {
      model3D.soilTypes.forEach((type) => {
        const color = SOIL_ACI_COLORS[type] || 7;
        this._writeLayer(`SOIL_${type}`, color, "Continuous");
      });
    }
    l.push("0", "ENDTAB");
    l.push("0", "TABLE");
    l.push("2", "STYLE");
    l.push("5", this._handle());
    l.push("70", "1");
    this._writeTextStyle("Standard", "txt");
    l.push("0", "ENDTAB");
    l.push("0", "ENDSEC");
  }
  // `tableHandle` is optional so the legacy geotech exporters (which write no
  // TABLE header of their own) keep working unchanged; the CAD path always passes
  // it, and the conformance test asserts every record on that path has an owner.
  _writeLinetype(name, desc, numDashes, pattern, tableHandle) {
    const l = this.lines;
    if (tableHandle) {
      this._tableRecord("LTYPE", "AcDbLinetypeTableRecord", tableHandle);
    } else {
      l.push("0", "LTYPE");
      l.push("5", this._handle());
    }
    l.push("2", this._ltypeName(name));
    l.push("70", "0");
    l.push("3", desc);
    l.push("72", "65");
    l.push("73", String(numDashes));
    const dashes = pattern.map((v, i) => this._tableNum(v, 0, `LTYPE "${name}" dash ${i + 1}`));
    l.push("40", String(dashes.reduce((a, b) => a + Math.abs(b), 0)));
    dashes.forEach((v) => {
      l.push("49", String(v));
      l.push("74", "0");
    });
  }
  _writeLayer(name, color, linetype) {
    const l = this.lines;
    l.push("0", "LAYER");
    l.push("5", this._handle());
    l.push("2", name);
    l.push("70", "0");
    l.push("62", String(color));
    l.push("6", linetype);
  }
  _writeTextStyle(name, font) {
    const l = this.lines;
    l.push("0", "STYLE");
    l.push("5", this._handle());
    l.push("2", name);
    l.push("70", "0");
    l.push("40", "0");
    l.push("41", "1");
    l.push("50", "0");
    l.push("71", "0");
    l.push("42", "0.5");
    l.push("3", font);
  }
  // ═════════════════════════════════════════════
  // BLOCKS SECTION
  // ═════════════════════════════════════════════
  _writeBlocks() {
    const l = this.lines;
    l.push("0", "SECTION");
    l.push("2", "BLOCKS");
    l.push("0", "BLOCK");
    l.push("5", this._handle());
    l.push("8", "0");
    l.push("2", "BH_SYMBOL");
    l.push("70", "0");
    l.push("10", "0");
    l.push("20", "0");
    l.push("30", "0");
    l.push("0", "CIRCLE");
    l.push("5", this._handle());
    l.push("8", "0");
    l.push("10", "0");
    l.push("20", "0");
    l.push("30", "0");
    l.push("40", "0.3");
    l.push("0", "ENDBLK");
    l.push("5", this._handle());
    l.push("8", "0");
    l.push("0", "BLOCK");
    l.push("5", this._handle());
    l.push("8", "0");
    l.push("2", "WL_SYMBOL");
    l.push("70", "0");
    l.push("10", "0");
    l.push("20", "0");
    l.push("30", "0");
    l.push("0", "LWPOLYLINE");
    l.push("5", this._handle());
    l.push("8", "0");
    l.push("90", "4");
    l.push("70", "1");
    l.push("10", "-0.4");
    l.push("20", "0");
    l.push("10", "0.4");
    l.push("20", "0");
    l.push("10", "0");
    l.push("20", "-0.6");
    l.push("10", "-0.4");
    l.push("20", "0");
    l.push("0", "ENDBLK");
    l.push("5", this._handle());
    l.push("8", "0");
    l.push("0", "ENDSEC");
  }
  // ═════════════════════════════════════════════
  // ENTITIES SECTION
  // ═════════════════════════════════════════════
  _writeEntitiesStart() {
    this.lines.push("0", "SECTION");
    this.lines.push("2", "ENTITIES");
  }
  _writeEntitiesEnd() {
    this.lines.push("0", "ENDSEC");
  }
  /**
   * Write cross-section in LOCAL coordinate system (distance vs elevation)
   * This is the standard way geotech cross sections appear in CAD
   */
  _writeCrossSectionLocal(data, opts) {
    const { terrainProfile, soilProfile, start, end } = data;
    if (!terrainProfile?.points) return;
    const { includeGrid, includeAnnotations, verticalExaggeration, sectionName } = opts;
    const points = terrainProfile.points;
    const stats = terrainProfile.statistics;
    const distance2 = terrainProfile.distance || data.distance;
    let minElev = stats.minElevation;
    let maxElev = stats.maxElevation;
    if (soilProfile?.profile) {
      soilProfile.profile.forEach((pt2) => {
        if (pt2.groundElevation !== null) {
          pt2.layers?.forEach((layer) => {
            if (layer.elevationBottom !== null) {
              minElev = Math.min(minElev, layer.elevationBottom);
            }
          });
        }
      });
    }
    const elevPad = (maxElev - minElev) * 0.15;
    minElev = Math.floor(minElev - elevPad);
    maxElev = Math.ceil(maxElev + elevPad);
    const VE = verticalExaggeration;
    this._writeLine("CROSS_SECTION_FRAME", 0, minElev * VE, distance2, minElev * VE);
    this._writeLine("CROSS_SECTION_FRAME", 0, maxElev * VE, distance2, maxElev * VE);
    this._writeLine("CROSS_SECTION_FRAME", 0, minElev * VE, 0, maxElev * VE);
    this._writeLine("CROSS_SECTION_FRAME", distance2, minElev * VE, distance2, maxElev * VE);
    if (includeGrid) {
      const elevStep = this._niceInterval(maxElev - minElev, 10);
      const distStep = this._niceInterval(distance2, 10);
      for (let e = Math.ceil(minElev / elevStep) * elevStep; e <= maxElev; e += elevStep) {
        this._writeLine("GRID", 0, e * VE, distance2, e * VE);
        this._writeText("GRID_LABELS", -2, e * VE, `${e.toFixed(1)}m`, 0.4, 0, "RIGHT");
      }
      for (let d = 0; d <= distance2; d += distStep) {
        this._writeLine("GRID", d, minElev * VE, d, maxElev * VE);
        this._writeText(
          "GRID_LABELS",
          d,
          (minElev - 0.5) * VE,
          `${d.toFixed(0)}m`,
          0.4,
          0,
          "CENTER"
        );
      }
    }
    if (soilProfile?.profile) {
      const sp = soilProfile.profile;
      const soilTypes = [...new Set(sp.flatMap((pt2) => (pt2.layers || []).map((l) => l.soilType)))];
      soilTypes.forEach((soilType) => {
        const topPts = [];
        const botPts = [];
        sp.forEach((pt2) => {
          const layer = pt2.layers?.find((l) => l.soilType === soilType);
          if (layer && layer.elevationTop !== null && layer.elevationBottom !== null) {
            topPts.push([pt2.distance, layer.elevationTop * VE]);
            botPts.push([pt2.distance, layer.elevationBottom * VE]);
          }
        });
        if (topPts.length < 2) return;
        this._writePolyline(`SOIL_${soilType}_BOUNDARY`, topPts);
        this._writePolyline(`SOIL_${soilType}_BOUNDARY`, botPts);
        const outline = [...topPts, ...botPts.reverse()];
        this._writeClosedPolyline(`SOIL_${soilType}`, outline);
        if (topPts.length > 2) {
          const mid = Math.floor(topPts.length / 2);
          const labelX = topPts[mid][0];
          const labelY = (topPts[mid][1] + botPts[botPts.length - 1 - mid]?.[1] || topPts[mid][1]) / 2;
          this._writeText(`SOIL_${soilType}_LABEL`, labelX, labelY, soilType, 0.5, 0, "CENTER");
        }
      });
    }
    if (soilProfile?.profile) {
      const waterPts = soilProfile.profile.filter((pt2) => pt2.waterTableRL !== null && pt2.waterTableRL !== void 0).map((pt2) => [pt2.distance, pt2.waterTableRL * VE]);
      if (waterPts.length > 1) {
        this._writePolyline("WATER_TABLE", waterPts);
        const midW = waterPts[Math.floor(waterPts.length / 2)];
        this._writeText(
          "WATER_LEVEL_LABELS",
          midW[0] + 1,
          midW[1] + 0.3 * VE,
          "WATER TABLE",
          0.4,
          0,
          "LEFT"
        );
      }
    }
    const groundPts = points.filter((p) => p.elevation !== null && p.elevation !== void 0).map((p) => [p.distance, p.elevation * VE]);
    if (groundPts.length > 1) {
      this._writePolyline("GROUND_SURFACE", groundPts);
    }
    if (soilProfile?.boreholePositions) {
      soilProfile.boreholePositions.forEach((bh) => {
        if (!bh.elevation) return;
        if (bh.projectedDistance < 0 || bh.projectedDistance > distance2) return;
        const x = bh.projectedDistance;
        const yTop = bh.elevation * VE;
        const maxLayerDepth = bh.layers?.reduce((max, l) => Math.max(max, l.depthTo || 0), 0) || 0;
        const yBot = (bh.elevation - maxLayerDepth) * VE;
        this._writeLine("BOREHOLES", x, yTop, x, yBot);
        (bh.layers || []).forEach((layer) => {
          const lt = (bh.elevation - layer.depthFrom) * VE;
          const lb = (bh.elevation - layer.depthTo) * VE;
          this._writeLine(`SOIL_${layer.soilType}`, x - 0.3, lt, x - 0.3, lb);
          this._writeLine(`SOIL_${layer.soilType}`, x + 0.3, lt, x + 0.3, lb);
        });
        this._writeText("BH_LABELS", x, yTop + 1.5 * VE, bh.name, 0.6, 0, "CENTER");
        if (bh.perpendicularDist > 1) {
          this._writeText(
            "BH_LABELS",
            x,
            yTop + 0.8 * VE,
            `(${bh.perpendicularDist.toFixed(1)}m off)`,
            0.3,
            0,
            "CENTER"
          );
        }
        if (bh.waterTableRL) {
          const yw = bh.waterTableRL * VE;
          this._writeInsert("WL_SYMBOL", "WATER_TABLE", x, yw);
        }
      });
    }
    if (includeAnnotations) {
      this._writeText(
        "TITLE_BLOCK",
        distance2 / 2,
        (maxElev + 2) * VE,
        sectionName || "GEOLOGICAL CROSS SECTION",
        1.2,
        0,
        "CENTER"
      );
      this._writeText(
        "ANNOTATIONS",
        distance2 / 2,
        (minElev - 2.5) * VE,
        "DISTANCE (m)",
        0.6,
        0,
        "CENTER"
      );
      this._writeText(
        "ANNOTATIONS",
        -5,
        (maxElev + minElev) / 2 * VE,
        "ELEVATION (m RL)",
        0.6,
        90,
        "CENTER"
      );
      if (VE !== 1) {
        this._writeText(
          "ANNOTATIONS",
          distance2,
          (minElev - 2) * VE,
          `V.E. = ${VE}x`,
          0.4,
          0,
          "RIGHT"
        );
      }
      this._writeText("ANNOTATIONS", 0, (maxElev + 0.5) * VE, "A", 0.8, 0, "CENTER");
      this._writeText("ANNOTATIONS", distance2, (maxElev + 0.5) * VE, "B", 0.8, 0, "CENTER");
      const scaleLen = this._niceInterval(distance2, 5);
      const scaleY = (minElev - 3.5) * VE;
      this._writeLine("ANNOTATIONS", 0, scaleY, scaleLen, scaleY);
      this._writeLine("ANNOTATIONS", 0, scaleY - 0.2, 0, scaleY + 0.2);
      this._writeLine("ANNOTATIONS", scaleLen, scaleY - 0.2, scaleLen, scaleY + 0.2);
      this._writeText(
        "ANNOTATIONS",
        scaleLen / 2,
        scaleY - 0.5,
        `${scaleLen.toFixed(0)}m`,
        0.3,
        0,
        "CENTER"
      );
    }
  }
  /**
   * Write cross-section in WGS84 3D coordinates
   */
  _writeCrossSectionWGS84(data, opts) {
    const { terrainProfile, soilProfile } = data;
    if (!terrainProfile?.points) return;
    const VE = opts.verticalExaggeration || 1;
    const groundCoords = terrainProfile.points.filter((p) => p.elevation !== null).map((p) => [p.lon, p.lat, p.elevation * VE]);
    this._write3DPolyline("GROUND_SURFACE", groundCoords);
    if (soilProfile?.profile) {
      const waterCoords = soilProfile.profile.filter((pt2) => pt2.waterTableRL !== null).map((pt2) => [pt2.lon, pt2.lat, pt2.waterTableRL * VE]);
      if (waterCoords.length > 1) {
        this._write3DPolyline("WATER_TABLE", waterCoords);
      }
      const soilTypes = [
        ...new Set(soilProfile.profile.flatMap((pt2) => (pt2.layers || []).map((l) => l.soilType)))
      ];
      soilTypes.forEach((soilType) => {
        const topCoords = soilProfile.profile.map((pt2) => {
          const layer = pt2.layers?.find((l) => l.soilType === soilType);
          return layer?.elevationTop != null ? [pt2.lon, pt2.lat, layer.elevationTop * VE] : null;
        }).filter(Boolean);
        const botCoords = soilProfile.profile.map((pt2) => {
          const layer = pt2.layers?.find((l) => l.soilType === soilType);
          return layer?.elevationBottom != null ? [pt2.lon, pt2.lat, layer.elevationBottom * VE] : null;
        }).filter(Boolean);
        if (topCoords.length > 1) this._write3DPolyline(`SOIL_${soilType}`, topCoords);
        if (botCoords.length > 1) this._write3DPolyline(`SOIL_${soilType}`, botCoords);
      });
    }
  }
  /**
   * Write 3D model entities
   */
  _write3DModelEntities(model3D, boreholes, parcelBoundary) {
    if (!model3D?.grid) return;
    (boreholes || []).forEach((bh) => {
      if (!bh.coordinates || !bh.elevation) return;
      const [lon, lat] = bh.coordinates;
      this._write3DText("BH_LABELS", lon, lat, bh.elevation + 2, bh.name, 3e-5);
      (bh.layers || []).forEach((layer) => {
        const top = bh.elevation - layer.depthFrom;
        const bot = bh.elevation - layer.depthTo;
        this._write3DLine(`SOIL_${layer.soilType}`, lon, lat, top, lon, lat, bot);
      });
    });
    const grid = model3D.grid;
    const lonStep = (model3D.bounds.east - model3D.bounds.west) / Math.max(model3D.cols, 1);
    const latStep = (model3D.bounds.north - model3D.bounds.south) / Math.max(model3D.rows, 1);
    const lookup = {};
    grid.forEach((col) => {
      const key = `${col.lon.toFixed(7)}_${col.lat.toFixed(7)}`;
      lookup[key] = col;
    });
    grid.forEach((col) => {
      if (col.groundElevation === null) return;
      const findNeighbor = (dLon, dLat) => {
        const key = `${(col.lon + dLon).toFixed(7)}_${(col.lat + dLat).toFixed(7)}`;
        return lookup[key];
      };
      const east = findNeighbor(lonStep, 0);
      const north = findNeighbor(0, latStep);
      const ne = findNeighbor(lonStep, latStep);
      if (east?.groundElevation != null && north?.groundElevation != null && ne?.groundElevation != null) {
        this._write3DFace(
          "GROUND_SURFACE",
          [col.lon, col.lat, col.groundElevation],
          [east.lon, east.lat, east.groundElevation],
          [ne.lon, ne.lat, ne.groundElevation],
          [north.lon, north.lat, north.groundElevation]
        );
        if (col.waterTableRL != null && east.waterTableRL != null && north.waterTableRL != null && ne.waterTableRL != null) {
          this._write3DFace(
            "WATER_TABLE",
            [col.lon, col.lat, col.waterTableRL],
            [east.lon, east.lat, east.waterTableRL],
            [ne.lon, ne.lat, ne.waterTableRL],
            [north.lon, north.lat, north.waterTableRL]
          );
        }
      }
    });
    if (parcelBoundary?.geometry?.coordinates) {
      const coords = parcelBoundary.geometry.type === "MultiPolygon" ? parcelBoundary.geometry.coordinates[0][0] : parcelBoundary.geometry.coordinates[0];
      const coords3D = coords.map((c) => {
        const col = grid.find(
          (g) => Math.abs(g.lon - c[0]) < lonStep && Math.abs(g.lat - c[1]) < latStep
        );
        return [c[0], c[1], col?.groundElevation || 0];
      });
      this._write3DPolyline("PARCEL_BOUNDARY", coords3D);
    }
  }
  // ═════════════════════════════════════════════
  // ENTITY PRIMITIVES
  // ═════════════════════════════════════════════
  _writeLine(layer, x1, y1, x2, y2) {
    const l = this.lines;
    l.push("0", "LINE");
    l.push("5", this._handle());
    l.push("8", layer);
    l.push("10", String(x1));
    l.push("20", String(y1));
    l.push("30", "0");
    l.push("11", String(x2));
    l.push("21", String(y2));
    l.push("31", "0");
  }
  _write3DLine(layer, x1, y1, z1, x2, y2, z2) {
    const l = this.lines;
    l.push("0", "LINE");
    l.push("5", this._handle());
    l.push("8", layer);
    l.push("10", String(x1));
    l.push("20", String(y1));
    l.push("30", String(z1));
    l.push("11", String(x2));
    l.push("21", String(y2));
    l.push("31", String(z2));
  }
  _writePolyline(layer, points) {
    const l = this.lines;
    l.push("0", "LWPOLYLINE");
    l.push("5", this._handle());
    l.push("8", layer);
    l.push("90", String(points.length));
    l.push("70", "0");
    points.forEach(([x, y]) => {
      l.push("10", String(x));
      l.push("20", String(y));
    });
  }
  _writeClosedPolyline(layer, points) {
    const l = this.lines;
    l.push("0", "LWPOLYLINE");
    l.push("5", this._handle());
    l.push("8", layer);
    l.push("90", String(points.length));
    l.push("70", "1");
    points.forEach(([x, y]) => {
      l.push("10", String(x));
      l.push("20", String(y));
    });
  }
  _write3DPolyline(layer, points) {
    const l = this.lines;
    l.push("0", "POLYLINE");
    l.push("5", this._handle());
    l.push("8", layer);
    l.push("70", "8");
    points.forEach(([x, y, z]) => {
      l.push("0", "VERTEX");
      l.push("5", this._handle());
      l.push("8", layer);
      l.push("10", String(x));
      l.push("20", String(y));
      l.push("30", String(z));
      l.push("70", "32");
    });
    l.push("0", "SEQEND");
    l.push("5", this._handle());
    l.push("8", layer);
  }
  _write3DFace(layer, p1, p2, p3, p4) {
    const l = this.lines;
    l.push("0", "3DFACE");
    l.push("5", this._handle());
    l.push("8", layer);
    l.push("10", String(p1[0]));
    l.push("20", String(p1[1]));
    l.push("30", String(p1[2]));
    l.push("11", String(p2[0]));
    l.push("21", String(p2[1]));
    l.push("31", String(p2[2]));
    l.push("12", String(p3[0]));
    l.push("22", String(p3[1]));
    l.push("32", String(p3[2]));
    l.push("13", String(p4[0]));
    l.push("23", String(p4[1]));
    l.push("33", String(p4[2]));
  }
  _writeText(layer, x, y, text, height, rotation, align) {
    const l = this.lines;
    l.push("0", "TEXT");
    l.push("5", this._handle());
    l.push("8", layer);
    l.push("10", String(x));
    l.push("20", String(y));
    l.push("30", "0");
    l.push("40", String(height));
    l.push("1", text);
    l.push("50", String(rotation));
    const hAlign = align === "CENTER" ? "1" : align === "RIGHT" ? "2" : "0";
    l.push("72", hAlign);
    if (hAlign !== "0") {
      l.push("11", String(x));
      l.push("21", String(y));
      l.push("31", "0");
    }
  }
  _write3DText(layer, x, y, z, text, height) {
    const l = this.lines;
    l.push("0", "TEXT");
    l.push("5", this._handle());
    l.push("8", layer);
    l.push("10", String(x));
    l.push("20", String(y));
    l.push("30", String(z));
    l.push("40", String(height));
    l.push("1", text);
  }
  _writeInsert(blockName, layer, x, y) {
    const l = this.lines;
    l.push("0", "INSERT");
    l.push("5", this._handle());
    l.push("8", layer);
    l.push("2", blockName);
    l.push("10", String(x));
    l.push("20", String(y));
    l.push("30", "0");
  }
  // ═════════════════════════════════════════════
  // GENERIC CAD MODEL EXPORT (used by the 2D CAD editor)
  // ═════════════════════════════════════════════
  /**
   * Export a CADModel document (src/modules/cad/CADModel.js) to DXF (AC1027).
   * World coordinates are CAD convention (Y up), so no flip is needed. The
   * import offset (doc.meta.importOffset) is added back so coordinates round-trip.
   */
  /**
   * [C4] The OBJECTS section — today, the ACAD_GROUP dictionary and nothing else.
   *
   * A DXF GROUP is a real, standard object: a named entry in the named-object
   * dictionary's ACAD_GROUP child, holding group 340 references to its members'
   * handles. Writing the REAL structure rather than one of this exporter's
   * private carrier blocks (the *Paper_Space and XREFDEF$ tricks above) is
   * possible here for one reason: a group's payload is handles and a name, and
   * both survive a standards-compliant reader. A layout's payload is a viewport
   * transform that dxf-parser has no handler for at all, which is why THAT one
   * had to be smuggled through a block.
   *
   * The reader half is `cadDxfSupplement.js`, not dxf-parser — dxf-parser skips
   * the OBJECTS section entirely. The supplement already walks the raw group
   * stream and already records every record's handle, so reading these back is
   * a matter of matching 340s against that walk.
   *
   * Written ONLY when the drawing has groups, so a drawing that has none is
   * byte-identical to what this exporter produced before groups existed
   * (`cadExportGolden` pins that).
   */
  _writeObjects(doc) {
    const resolved = (this._plannedGroups || []).map((r) => ({
      g: r.g,
      handle: r.handle,
      handles: r.ids.flatMap((id) => this._handleById.get(id) || [])
    })).filter((r) => r.handles.length > 0);
    const groupNames = new SymbolNameMap();
    const groupKey = resolved.map((r, i) => groupNames.mapFresh(r.g.name || `Group${i + 1}`));
    this.warnings.push(...groupNames.warnings);
    resolved.forEach((r, i) => {
      if (new Set(r.handles).size < 2) {
        this.warnings.push(
          `GROUP "${groupKey[i]}": only one member could be exported, so it will not re-import as a group`
        );
      }
    });
    const l = this.lines;
    l.push("0", "SECTION");
    l.push("2", "OBJECTS");
    const rootHandle = this._rootDictHandle;
    const groupDictHandle = this._groupDictHandle;
    l.push("0", "DICTIONARY");
    l.push("5", rootHandle);
    l.push("330", "0");
    l.push("100", "AcDbDictionary");
    l.push("281", "1");
    l.push("3", "ACAD_COLOR");
    l.push("350", this._colorDictHandle);
    l.push("3", "ACAD_GROUP");
    l.push("350", groupDictHandle);
    l.push("3", "ACAD_LAYOUT");
    l.push("350", this._layoutDictHandle);
    l.push("3", "ACAD_MATERIAL");
    l.push("350", this._materialDictHandle);
    l.push("3", "ACAD_MLEADERSTYLE");
    l.push("350", this._mleaderDictHandle);
    l.push("3", "ACAD_MLINESTYLE");
    l.push("350", this._mlineDictHandle);
    l.push("3", "ACAD_PLOTSETTINGS");
    l.push("350", this._plotSettingsDictHandle);
    l.push("3", "ACAD_PLOTSTYLENAME");
    l.push("350", this._plotStyleDictHandle);
    l.push("3", "ACAD_SCALELIST");
    l.push("350", this._scaleListDictHandle);
    l.push("3", "ACAD_TABLESTYLE");
    l.push("350", this._tableStyleDictHandle);
    l.push("3", "ACAD_VISUALSTYLE");
    l.push("350", this._visualStyleDictHandle);
    this._writeNamedDictionaries();
    const groupHandles = resolved.map((r) => r.handle);
    l.push("0", "DICTIONARY");
    l.push("5", groupDictHandle);
    l.push("330", rootHandle);
    l.push("100", "AcDbDictionary");
    l.push("281", "1");
    resolved.forEach((r, i) => {
      l.push("3", groupKey[i]);
      l.push("350", groupHandles[i]);
    });
    resolved.forEach((r, i) => {
      l.push("0", "GROUP");
      l.push("5", groupHandles[i]);
      l.push("330", groupDictHandle);
      l.push("100", "AcDbGroup");
      l.push("300", groupKey[i]);
      l.push("70", "0");
      l.push("71", r.g.selectable === false ? "0" : "1");
      for (const h of r.handles) l.push("340", h);
    });
    this._writeLayoutObjects(doc);
    this._writeMlineStyle();
    if (this._geodataPlan) {
      writeGeodataObjects(l, this._geodataPlan, {
        xdictHandle: this._geodataXdictHandle,
        geodataHandle: this._geodataHandle,
        hostHandle: this._blockRecordHandles["*Model_Space"]
      });
    }
    l.push("0", "ENDSEC");
  }
  // The ACAD_LAYOUT dictionary and one LAYOUT object per space.
  //
  // This is what gives a viewer its Model and Layout tabs. A file with entities but
  // no LAYOUT has nothing to show them in — which is what "Design is empty" means
  // in Autodesk's translator, and why ezdxf had to SYNTHESISE a Model layout to
  // open the user's file at all.
  //
  // Each LAYOUT names its BLOCK_RECORD (the trailing 330) and each of those records
  // names its LAYOUT back (group 340) — a bijection the conformance test checks,
  // because half of it is a dangling reference.
  _writeLayoutObjects(doc) {
    const l = this.lines;
    const sheets = (doc.layouts || []).filter((ly) => ly && !ly.isModel);
    const spaces = ["*Model_Space", ...this._paperBlockNames];
    l.push("0", "DICTIONARY");
    l.push("5", this._layoutDictHandle);
    l.push("330", this._rootDictHandle);
    l.push("100", "AcDbDictionary");
    l.push("281", "1");
    spaces.forEach((blockName, i) => {
      l.push("3", this._layoutName(i, sheets[i - 1]));
      l.push("350", this._layoutHandleByBlock[blockName]);
    });
    spaces.forEach((blockName, i) => {
      const sheet = i === 0 ? null : sheets[i - 1];
      const name = this._layoutName(i, sheet);
      const size = this._paperSize(sheet);
      l.push("0", "LAYOUT");
      l.push("5", this._layoutHandleByBlock[blockName]);
      l.push("330", this._layoutDictHandle);
      l.push("100", "AcDbPlotSettings");
      l.push("1", "");
      l.push("2", "");
      l.push("4", size.name);
      l.push("6", "");
      l.push("40", "7.5", "41", "20", "42", "7.5", "43", "20");
      l.push("44", String(size.w), "45", String(size.h));
      l.push("46", "0", "47", "0", "48", "0", "49", "0");
      l.push("140", "0", "141", "0", "142", "1", "143", "1");
      l.push("70", i === 0 ? "1024" : "0");
      l.push("72", "1");
      l.push("73", "0", "74", "5", "7", "", "75", "16", "76", "0", "77", "2", "78", "300");
      l.push("147", "1", "148", "0", "149", "0");
      l.push("100", "AcDbLayout");
      l.push("1", name);
      l.push("70", "1");
      l.push("71", String(i));
      l.push("10", "0", "20", "0");
      l.push("11", String(size.w), "21", String(size.h));
      l.push("12", "0", "22", "0", "32", "0");
      this.lines.pushRaw("14", "1e+20", "24", "1e+20", "34", "1e+20");
      this.lines.pushRaw("15", "-1e+20", "25", "-1e+20", "35", "-1e+20");
      l.push("146", "0");
      l.push("13", "0", "23", "0", "33", "0");
      l.push("16", "1", "26", "0", "36", "0");
      l.push("17", "0", "27", "1", "37", "0");
      l.push("76", "1");
      l.push("330", this._blockRecordHandles[blockName] || "0");
    });
  }
  // The written tab name for a layout — index 0 is always the model tab. Resolved
  // through the layout name map so the dictionary key and the LAYOUT's own group 1
  // can never disagree, and so two sheets never share a key.
  _layoutName(i, sheet) {
    if (i === 0) return "Model";
    const hit = sheet && this._layoutWritten && this._layoutWritten.get(sheet);
    return hit !== void 0 ? hit : _DXFExporter.dxfSafeName(sheet?.name || `Layout${i}`);
  }
  // Paper dimensions for a layout, in millimetres. A4 landscape is the fallback for
  // Model and for a sheet whose size we do not recognise.
  _paperSize(sheet) {
    const DEFAULT = { name: "ISO A3", w: 420, h: 297 };
    if (!sheet) return DEFAULT;
    const p = PAPER_SIZES && PAPER_SIZES[sheet.paperSize];
    if (!Array.isArray(p) || p.length !== 2) return DEFAULT;
    const landscape = sheet.orientation !== "portrait";
    return {
      name: `ISO ${String(sheet.paperSize).toUpperCase()}`,
      w: landscape ? p[0] : p[1],
      h: landscape ? p[1] : p[0]
    };
  }
  // AutoCAD's own minimal files always carry an ACAD_MLINESTYLE dictionary with a
  // Standard entry. It needs no CLASSES record, so it is cheap insurance against a
  // reader that expects the full set of root dictionary children.
  _writeMlineStyle() {
    const l = this.lines;
    l.push("0", "DICTIONARY");
    l.push("5", this._mlineDictHandle);
    l.push("330", this._rootDictHandle);
    l.push("100", "AcDbDictionary");
    l.push("281", "1");
    l.push("3", "Standard");
    l.push("350", this._mlineStyleHandle);
    l.push("0", "MLINESTYLE");
    l.push("5", this._mlineStyleHandle);
    l.push("330", this._mlineDictHandle);
    l.push("100", "AcDbMlineStyle");
    l.push("2", "Standard");
    l.push("70", "0");
    l.push("3", "");
    l.push("62", "256");
    l.push("51", "90", "52", "90");
    l.push("71", "2");
    l.push("49", "0.5", "62", "256", "6", "BYLAYER");
    l.push("49", "-0.5", "62", "256", "6", "BYLAYER");
  }
  exportCADModel(doc, opts = {}) {
    this.lines = new DxfTagSink();
    this._opts = opts || {};
    this._offset = null;
    this.handleCounter = 100;
    this.warnings = [];
    this._regionSeq = 0;
    this._extentsWarned = false;
    this._regionTaken = null;
    this._doc = doc;
    const off = this._writeOffset(doc);
    this._dimStyleDefs = doc.dimStyleDefs || {};
    this._dimCurrentStyle = doc.settings && doc.settings.currentDimStyle || void 0;
    this._dimSizing = makeDimSizing(doc, this._safeDocumentBounds(doc));
    this._handleById = /* @__PURE__ */ new Map();
    this._planNames(doc);
    this._planCarrierLayers(doc);
    this._planDimensionBlocks(doc);
    this._planSymbolNames(doc);
    const geodata = planGeodata(doc, this._opts);
    this._geodataPlan = geodata.plan;
    if (geodata.warning) this.warnings.push(geodata.warning);
    this._planHandles(doc);
    this._writeCADHeader(doc);
    this._writeClasses();
    this._writeGenericTables(doc);
    this._writeCADBlocks(doc);
    this._writeEntitiesStart();
    this._owner = this._blockRecordHandles["*Model_Space"];
    this._paper = false;
    (doc.entities || []).forEach((e) => this._writeCADEntity(e, off.x, off.y));
    this._owner = void 0;
    this._writeEntitiesEnd();
    this._writeObjects(doc);
    this.lines.push("0", "EOF");
    if (this._handseedIdx != null) this.lines[this._handseedIdx] = this._handle();
    this.warnings.push(...this._nameWarnings());
    return this.lines.join("\r\n");
  }
  // The HEADER for the CAD path. The old one wrote five variables; a reader needs
  // rather more than that to open a drawing rather than merely parse it —
  // $EXTMIN/$EXTMAX in particular are what a viewer frames the view with, and
  // their absence is part of why the user's file reported "Design is empty".
  _writeCADHeader(doc) {
    const l = this.lines;
    l.push("999", "DXF generated by BranchCAD");
    l.push("999", `Date: ${(/* @__PURE__ */ new Date()).toISOString()}`);
    l.push("0", "SECTION");
    l.push("2", "HEADER");
    const hv = (name, ...pairs) => {
      l.push("9", name);
      for (let i = 0; i < pairs.length; i += 2) l.push(String(pairs[i]), pairs[i + 1]);
    };
    hv("$ACADVER", "1", "AC1027");
    hv("$ACADMAINTVER", "70", "105");
    hv("$DWGCODEPAGE", "3", "ANSI_1252");
    hv("$INSBASE", "10", "0", "20", "0", "30", "0");
    const b = this._extents(doc);
    if (b.empty) {
      this.lines.pushRaw("9", "$EXTMIN");
      this.lines.pushRaw("10", "1e+20", "20", "1e+20", "30", "1e+20");
      this.lines.pushRaw("9", "$EXTMAX");
      this.lines.pushRaw("10", "-1e+20", "20", "-1e+20", "30", "-1e+20");
    } else {
      hv("$EXTMIN", "10", String(b.minX), "20", String(b.minY), "30", "0");
      hv("$EXTMAX", "10", String(b.maxX), "20", String(b.maxY), "30", "0");
    }
    const settings = doc.settings || {};
    hv("$LTSCALE", "40", String(Number.isFinite(settings.ltscale) ? settings.ltscale : 1));
    hv("$TEXTSIZE", "40", "2.5");
    hv("$TEXTSTYLE", "7", "Standard");
    const layerNames = new Set(
      (doc.layers || []).filter(Boolean).map((ly) => this._layerName(ly.name))
    );
    const clayer = this._layerName(settings.currentLayer || "0");
    hv("$CLAYER", "8", layerNames.has(clayer) ? clayer : "0");
    hv("$CELTYPE", "6", "ByLayer");
    hv("$CECOLOR", "62", "256");
    hv("$CELTSCALE", "40", "1");
    const dimNames = Object.keys(doc.dimStyleDefs || {});
    const curDim = settings.currentDimStyle;
    hv(
      "$DIMSTYLE",
      "2",
      this._dimStyleName(dimNames.includes(curDim) ? curDim : dimNames[0] || "Standard")
    );
    hv("$DIMASSOC", "280", "1");
    hv("$LUNITS", "70", "2");
    hv("$LUPREC", "70", "3");
    hv("$AUNITS", "70", "0");
    hv("$AUPREC", "70", "1");
    hv("$INSUNITS", "70", "6");
    hv("$MEASUREMENT", "70", "1");
    hv("$CEPSNTYPE", "380", "0");
    hv("$PSTYLEMODE", "290", "1");
    hv("$PDMODE", "70", "3");
    hv("$PDSIZE", "40", "0");
    hv("$TILEMODE", "70", "1");
    hv("$EXTNAMES", "290", "1");
    hv("$PSVPSCALE", "40", "0");
    l.push("9", "$HANDSEED");
    l.push("5", "FFFF");
    this._handseedIdx = this.lines.length - 1;
    l.push("0", "ENDSEC");
  }
  // Document bounds that one malformed entity cannot take down.
  //
  // [round 3] `documentBounds` runs at the top of exportCADModel — for dimension
  // sizing and for the header/VPORT extents — which is BEFORE any per-entity guard.
  // One entity with a missing point (`p2: undefined`) threw out of `entityVertices`
  // and the whole export aborted: neither the transaction nor `_degenerateReason`
  // could help, because bounds reached the entity first. Found while pinning the
  // last unpinned round-1 fix, whose test was red WITH the fix in place.
  //
  // An entity whose bounds throw is left out of the extents; the entity walk then
  // usually refuses it and names it. Not always — an INSERT whose block BODY is
  // malformed is still written (the body's entity is what gets refused), and is
  // silently left out of the extents (Lens D, round 3).
  _safeDocumentBounds(doc) {
    const blockDefs = doc.blockDefs;
    const xrefDefs = doc.xrefDefs;
    const inRange2 = (b) => !b || [b.minX, b.minY, b.maxX, b.maxY].every((v) => Number.isFinite(v) && Math.abs(v) < MAX_ABS);
    const boundable = (doc.entities || []).filter((e) => {
      try {
        return inRange2(entityBounds(e, blockDefs, xrefDefs));
      } catch {
        return false;
      }
    });
    try {
      return documentBounds(boundable, blockDefs, xrefDefs);
    } catch {
      return null;
    }
  }
  // The drawing's extents in WRITTEN coordinates (i.e. with importOffset put back),
  // used by $EXTMIN/$EXTMAX and by the VPORT's framing. `documentBounds` returns
  // null for a drawing with nothing in it; callers get a benign unit box and the
  // header writes AutoCAD's "no extents" sentinels instead.
  //
  // [round 2] The SUMS are validated, not just the bounds. The header and the VPORT
  // run outside every transaction, so a NaN `importOffset` (it is import-derived), or
  // an offset that carries a coordinate past MAX_ABS, threw out of exportCADModel —
  // measured: no file and no warnings, the worst available outcome, and exactly the
  // one `_tableNum` was added to close for the tables. An unusable extent now falls
  // back to the "no extents" sentinels and is reported; the entities themselves are
  // still refused one at a time by the transaction.
  // [G5-INTEROP] THE ONE PLACE that decides what is added to every written coordinate.
  //
  // WHY IT IS ONE PLACE. Six sites used to read `doc.meta.importOffset` independently —
  // the entity walk, `$EXTMIN/$EXTMAX`, the dimension-block DRY RUN, the dimension-block
  // bodies, and the xref instance transforms. They agreed only because they all spelled
  // the same expression. The moment a second frame exists, one of them not being updated
  // writes the extents (or the dimension blocks) in a DIFFERENT frame from the entities:
  // a file whose geometry is 1.75 million metres away from the view it declares. So the
  // rule lives here and every site asks (PROCESS §E.1 — the enumeration, written down):
  //
  //   exportCADModel       — the model-space entity walk
  //   _extents             — $EXTMIN/$EXTMAX and the VPORT's view centre
  //   _planDimensionBlocks — the dry run (MUST match the real write, or a dimension the
  //                          offset pushes past MAX_ABS passes the dry run and throws
  //                          for real, outside any transaction)
  //   _writeCADBlocks      — the `*D` dimension block bodies
  //   _writeXrefBlocks     — xref instance transforms, inside the XREFINST carrier
  //
  // METHOD NAMES, not line numbers: the first version of this table cited five lines
  // that were already stale in the commit that wrote them, which sends the reviewer
  // §E.1 invites to falsify the list to the wrong place.
  //
  //   _writePaperSpaceBlocks — the `PSVP|v=2|x=…|y=…` viewport carrier
  //
  // That last one was EXEMPT for one review round, on the argument that writer and
  // reader "agree by construction" because neither applies the offset. Round 2 refuted
  // it by measurement: that holds for the pre-G5 shift frame, where both offsets come
  // from the same bounding box, and FAILS for the grid frame, where the writer's is the
  // ORIGIN and the reader's is bbox-derived. A grid export reopened here came back with
  // its viewport 10 m from the geometry it framed. The carrier is now VERSIONED, so new
  // files are correct and files exported before the change still decode as they did.
  //
  // THE RULE, and the reason for each row:
  //
  //   | georef | importOffset | frame | written | why                                  |
  //   |--------|--------------|-------|---------|--------------------------------------|
  //   | none   | off          | local | off     | today's behaviour, unchanged         |
  //   | none   | off          | grid  | off     | + warning: there is no grid to go to |
  //   | set    | 0            | local | 0       | the normal state; GEODATA carries it |
  //   | set    | off ≠ 0      | local | 0       | + warning (see below)                |
  //   | set    | 0            | grid  | origin  | criterion 3: grid = local + origin   |
  //   | set    | off ≠ 0      | grid  | origin  | + the same warning                   |
  //
  // A georeferenced drawing IGNORES `importOffset`. That state is reachable only by
  // calling `setGeoref` on a drawing that still carries an import shift — accepting a
  // georef proposal folds the shift into the origin and zeroes it in the same commit —
  // and neither alternative is defensible: adding both double-counts the shift once the
  // origin already includes it, and writing `origin + off` as the reference point
  // invents a frame the user has never seen. So it is ignored and SAID, rather than
  // silently chosen between.
  //
  // Memoised per export: the warnings must be said once, not six times.
  _writeOffset(doc) {
    if (this._offset) return this._offset;
    const off = doc && doc.meta && doc.meta.importOffset || { x: 0, y: 0 };
    const georef = normalizeGeoref(doc && doc.georef);
    const frame = this._opts && this._opts.frame || "local";
    const grid = frame === "grid";
    if (frame !== "local" && frame !== "grid") {
      this.warnings.push(`unknown export frame "${frame}": the drawing's own coordinates were written`);
    }
    if (!georef) {
      if (grid) {
        this.warnings.push(
          "grid coordinates were requested but this drawing has no georeference: the file is written exactly as a normal export"
        );
      }
      this._offset = off;
      return this._offset;
    }
    if (off.x !== 0 || off.y !== 0) {
      this.warnings.push(
        `import shift E ${off.x}, N ${off.y} was ignored: this drawing is georeferenced, so its origin already states where local (0,0) sits`
      );
    }
    this._offset = grid ? { x: georef.origin.e, y: georef.origin.n } : { x: 0, y: 0 };
    return this._offset;
  }
  _extents(doc) {
    const off = this._writeOffset(doc);
    const b = this._safeDocumentBounds(doc);
    const EMPTY = { minX: 0, minY: 0, maxX: 0, maxY: 0, empty: true };
    if (!b || !Number.isFinite(b.minX) || !Number.isFinite(b.maxX)) return EMPTY;
    const out = {
      minX: b.minX + off.x,
      minY: b.minY + off.y,
      maxX: b.maxX + off.x,
      maxY: b.maxY + off.y,
      empty: false
    };
    const usable = [out.minX, out.minY, out.maxX, out.maxY].every(
      (v) => Number.isFinite(v) && Math.abs(v) < MAX_ABS
    );
    if (!usable) {
      if (!this._extentsWarned) {
        this._extentsWarned = true;
        this.warnings.push(
          "drawing extents are not representable (the import offset places the drawing outside the range DXF can hold) \u2014 the file records no extents"
        );
      }
      return EMPTY;
    }
    return out;
  }
  // The paper-space BLOCK_RECORD names. There is ALWAYS at least one, because a
  // valid file has a Layout1 even when the drawing has no sheets — AutoCAD's own
  // naming: the first is `*Paper_Space`, the rest `*Paper_Space0`, `*Paper_Space1`…
  _paperSpaceNames(doc) {
    const sheets = (doc.layouts || []).filter((ly) => ly && !ly.isModel).length;
    const n = Math.max(sheets, 1);
    return Array.from({ length: n }, (_, i) => i === 0 ? "*Paper_Space" : `*Paper_Space${i - 1}`);
  }
  // Every block name that will appear in the BLOCKS section, in the order it will
  // appear. BLOCK_RECORD is written from this same list so the two cannot drift —
  // a record naming a block that is not there is exactly the dangling reference
  // that makes a reader dereference nothing.
  _blockOrder(doc) {
    const sheets = (doc.layouts || []).filter((ly) => ly && !ly.isModel);
    const xrefs = Object.keys(doc.xrefDefs || {});
    return [
      "*Model_Space",
      ...this._paperSpaceNames(doc),
      ...Object.keys(doc.blockDefs || {}).map((n) => this._blockName(n)),
      ...sheets.map((_, i) => `LAYOUTDEF$${i}`),
      ...xrefs.map((_, i) => `XREFDEF$${i}`),
      // [DXF-DIM-NATIVE-1] one anonymous block per native dimension
      ...(this._dimBlocks || []).map((d) => d.name)
    ];
  }
  // The LAYOUT object a space block belongs to, or undefined for an ordinary block.
  _layoutHandleForBlock(name) {
    return (this._layoutHandleByBlock || {})[name];
  }
  // Work out the CARRIER LAYER names before the LAYER table is written.
  //
  // The V4c carriers (ray/xline/revcloud) and the region carrier put their records
  // on a synthetic `V4C$…` layer. Those names used to be minted during the entity
  // walk — i.e. AFTER the LAYER table had already been written — so every one of
  // them was a group 8 naming a layer with no record behind it. That is the same
  // dangling reference this package exists to remove, and it was invisible until
  // the conformance test asked whether every referenced layer resolves.
  //
  // The region layer is assigned HERE and looked up by object identity during the
  // walk, rather than allocated twice from a counter: two passes that must agree on
  // a sequence number are two passes that can disagree.
  // [DXF-VALID-2] Per-TABLE name maps.
  //
  // DXF symbol namespaces are per table — a layer and a block may share a name —
  // so there is one map each, not one shared map. Each is pre-loaded with the
  // names this exporter writes verbatim, so user data can never be handed one of
  // them (a layer called `0` that is not THE layer 0, a block called
  // `*Model_Space`).
  _planNames(doc) {
    this._names = {
      layer: new SymbolNameMap(),
      block: new SymbolNameMap(),
      style: new SymbolNameMap(),
      ltype: new SymbolNameMap(),
      dimstyle: new SymbolNameMap(),
      layout: new SymbolNameMap()
    };
    for (const n of ["ByBlock", "ByLayer", "Continuous"]) this._names.ltype.reserve(n);
    this._names.block.reserve("*Model_Space");
    for (const n of this._paperSpaceNames(doc)) this._names.block.reserve(n);
    for (const ly of doc.layers || []) if (ly && ly.name != null) this._names.layer.map(ly.name);
  }
  // Reserve every name this exporter GENERATES, then map the document's own names
  // around them. Runs after _planCarrierLayers and _planDimensionBlocks.
  //
  // [review] Four of these five maps were dead code: only layers were ever mapped,
  // while BLOCK, STYLE, LTYPE and DIMSTYLE names went out verbatim — and the comment
  // above them claimed the opposite, which is precisely the kind of promise PROCESS
  // §E.1 warns will be tested. Measured: a document carrying a blockDef named
  // `*Model_Space` (which an import can produce) wrote TWO `*Model_Space`
  // BLOCK_RECORDs, and every model-space entity's owner handle pointed at the USER's
  // record — ezdxf: "Invalid block reference cycle detected".
  _planSymbolNames(doc) {
    const N = this._names;
    for (const n of ["*Model_Space", ...this._paperSpaceNames(doc)]) N.block.reserve(n);
    (doc.layouts || []).filter((ly) => ly && !ly.isModel).forEach((_, i) => N.block.reserve(`LAYOUTDEF$${i}`));
    this._xrefBlockOf = /* @__PURE__ */ new Map();
    Object.keys(doc.xrefDefs || {}).forEach((name, i) => {
      N.block.reserve(`XREFDEF$${i}`);
      this._xrefBlockOf.set(name, `XREFDEF$${i}`);
    });
    for (const d of this._dimBlocks || []) N.block.reserve(d.name);
    this._blockWritten = /* @__PURE__ */ new Map();
    for (const name of Object.keys(doc.blockDefs || {})) {
      this._blockWritten.set(name, N.block.map(name));
    }
    this._styleWritten = /* @__PURE__ */ new Map();
    for (const name of Object.keys(doc.textStyleDefs || { Standard: {} })) {
      this._styleWritten.set(name, N.style.map(name));
    }
    this._dimStyleWritten = /* @__PURE__ */ new Map();
    for (const name of Object.keys(doc.dimStyleDefs || { Standard: {} })) {
      this._dimStyleWritten.set(name, N.dimstyle.map(name));
    }
    const BUILTIN_LT_LOWER = /* @__PURE__ */ new Set(["byblock", "bylayer", "continuous"]);
    this._ltypeWritten = /* @__PURE__ */ new Map();
    const mapLtype = (name) => {
      if (BUILTIN_LT_LOWER.has(String(name).toLowerCase())) return;
      if (this._ltypeWritten.has(name)) return;
      this._ltypeWritten.set(name, N.ltype.map(name));
    };
    for (const name of Object.keys(doc.linetypeDefs || {})) mapLtype(name);
    for (const layer of doc.layers || []) {
      if (layer && layer.linetype) mapLtype(layer.linetype);
    }
    N.layout.reserve("Model");
    this._layoutWritten = /* @__PURE__ */ new Map();
    (doc.layouts || []).filter((ly) => ly && !ly.isModel).forEach((ly, i) => {
      this._layoutWritten.set(ly, N.layout.mapFresh(ly.name || `Layout${i + 1}`));
    });
  }
  // The written name for a reference into one of the symbol tables. Unknown names map
  // on first use, so a reference and its record can never disagree.
  _blockName(name) {
    const key = name == null ? "" : name;
    const hit = this._blockWritten && this._blockWritten.get(key);
    return hit !== void 0 ? hit : String(key);
  }
  _styleName(name) {
    const key = name == null ? "" : name;
    const hit = this._styleWritten && this._styleWritten.get(key);
    return hit !== void 0 ? hit : String(key);
  }
  _ltypeName(name) {
    const key = name == null ? "" : name;
    const hit = this._ltypeWritten && this._ltypeWritten.get(key);
    return hit !== void 0 ? hit : String(key);
  }
  _dimStyleName(name) {
    const key = name == null ? "" : name;
    const hit = this._dimStyleWritten && this._dimStyleWritten.get(key);
    return hit !== void 0 ? hit : String(key);
  }
  // Every warning the name maps produced, for the export's `warnings` list.
  _nameWarnings() {
    return Object.values(this._names || {}).flatMap((m) => m.warnings);
  }
  // The written LAYER name for a reference. Unknown names (a carrier layer, or an
  // entity on a layer the document never declared) map on first use.
  _layerName(name) {
    return this._names ? this._names.layer.map(name == null ? "0" : name) : String(name ?? "0");
  }
  _planCarrierLayers(doc) {
    const taken = new Set((doc.layers || []).map((ly) => ly && ly.name));
    const extra = [];
    const add2 = (n) => {
      if (taken.has(n)) return;
      taken.add(n);
      extra.push(n);
    };
    this._regionLayerOf = /* @__PURE__ */ new Map();
    let seq = 0;
    const visit = (e) => {
      if (!e) return;
      if (e.layer != null && e.layer !== "" && !String(e.layer).startsWith("V4C$")) {
        add2(String(e.layer));
      }
      if (e.type === "ray" || e.type === "xline" || e.type === "revcloud") {
        add2(`V4C$${e.type}`);
      } else if (e.type === "region") {
        const rings = (Array.isArray(e.rings) ? e.rings : []).filter(
          (r) => Array.isArray(r) && r.length >= 3
        );
        if (!rings.length) return;
        let ly = `V4C$region$${seq++}`;
        while (taken.has(ly)) ly = `V4C$region$${seq++}`;
        taken.add(ly);
        extra.push(ly);
        this._regionLayerOf.set(e, ly);
      }
    };
    for (const name of Object.keys(doc.blockDefs || {})) {
      ((doc.blockDefs[name] || {}).entities || []).forEach(visit);
    }
    this._extraLayerColor = /* @__PURE__ */ new Map();
    for (const name of Object.keys(doc.xrefDefs || {})) {
      const def = doc.xrefDefs[name] || {};
      for (const ly of def.layers || []) {
        if (ly && ly.name != null && ly.color) this._extraLayerColor.set(String(ly.name), ly.color);
      }
      (def.entities || []).forEach(visit);
    }
    (doc.entities || []).forEach(visit);
    this._carrierLayers = extra;
  }
  // [DXF-DIM-NATIVE-1] Which dimensions get a REAL DIMENSION record, and the name
  // of the anonymous block that will hold their drawn geometry.
  //
  // AutoCAD stores a dimension as an entity plus a `*D<n>` block containing the
  // lines, arrowheads and label it currently draws. The entity carries the
  // DEFINITION POINTS, so when the user edits or restyles it AutoCAD regenerates
  // the block from those — which is the whole difference between a dimension and
  // the loose geometry we used to export.
  //
  // Only TOP-LEVEL dimensions qualify. One inside a block definition would need a
  // block inside a block, which AutoCAD does not do for dimensions, so those stay
  // exploded (as does arclength — see _dimNativeKind).
  _planDimensionBlocks(doc) {
    const dimOff = this._writeOffset(doc);
    this._dimBlockOf = /* @__PURE__ */ new Map();
    this._dimBlocks = [];
    let n = 1;
    for (const e of doc.entities || []) {
      if (!e || e.type !== "dimension") continue;
      if (!this._dimNativeKind(e)) continue;
      if (!this._dimBodyIsWritable(e, dimOff)) continue;
      const name = `*D${n++}`;
      this._dimBlockOf.set(e, name);
      this._dimBlocks.push({ name, entity: e });
    }
  }
  // Can this dimension's drawn body actually be WRITTEN?
  //
  // [review] The `*D` block bodies are emitted by `_writeCADBlocks`, which runs
  // OUTSIDE `_writeCADEntity`'s transaction — so one non-finite coordinate in one
  // dimension threw out of `exportCADModel` and the user got NO FILE AT ALL.
  // Measured: a linear dimension with `p1.x = NaN` aborted the whole export, where
  // before this package it merely wrote a corrupt token. Turning "corrupt file" into
  // "no file" is a worse trade than the one it replaced.
  //
  // It cannot be fixed at write time: BLOCK_RECORD is written BEFORE the BLOCKS
  // section, so abandoning a block half way would leave a record naming a block that
  // is not there. So the body is dry-run HERE, into a throwaway sink, before the
  // record is planned. A dimension that fails simply gets no native block and falls
  // back to the exploded writer in the entity walk — which IS inside the
  // transaction, so it is refused and named there like any other entity.
  _dimBodyIsWritable(e, off = { x: 0, y: 0 }) {
    const realLines = this.lines;
    const realOwner = this._owner;
    const realWarnings = this.warnings.length;
    this.lines = new DxfTagSink();
    this._owner = "0";
    try {
      const g = dimensionPrimitives(e, this._dimStyleDefs, this._dimCurrentStyle, this._dimSizing);
      if (!g) return false;
      this._writeDimensionExploded(e, off.x, off.y);
      return true;
    } catch {
      return false;
    } finally {
      this.lines = realLines;
      this._owner = realOwner;
      this.warnings.length = realWarnings;
    }
  }
  // The DXF dimension FAMILY for one of our dimensions, or null when it has to stay
  // exploded. Returns the group-70 base type and the subclass chain that follows
  // AcDbDimension.
  //
  // `arclength` has no standard subtype: ARC_DIMENSION is an R2004+ CLASS which
  // would need a CLASSES section entry to be legal, so it keeps the exploded path
  // and says so in the warnings rather than being written as something it is not.
  _dimNativeKind(e) {
    switch (e && e.dimType) {
      case "linear":
      case "vertical":
        return { type: 0, chain: ["AcDbAlignedDimension", "AcDbRotatedDimension"] };
      case "aligned":
        return { type: 1, chain: ["AcDbAlignedDimension"] };
      case "diameter":
        return { type: 3, chain: ["AcDbDiametricDimension"] };
      case "radius":
        return { type: 4, chain: ["AcDbRadialDimension"] };
      case "angular":
        return { type: 5, chain: ["AcDb3PointAngularDimension"] };
      case "ordinate":
        return { type: 6, chain: ["AcDbOrdinateDimension"] };
      default:
        return null;
    }
  }
  // Reserve the handles that must be known BEFORE the records that reference them
  // are written. The file is written in one forward pass, but the reference graph
  // is not a tree: a BLOCK_RECORD in the TABLES section points forward at a LAYOUT
  // object in the OBJECTS section, which points back at the record. Minting both
  // up front is the only way to write either.
  _planHandles(doc) {
    this._rootDictHandle = this._handle();
    this._groupDictHandle = this._handle();
    this._layoutDictHandle = this._handle();
    this._mlineDictHandle = this._handle();
    this._mlineStyleHandle = this._handle();
    this._colorDictHandle = this._handle();
    this._materialDictHandle = this._handle();
    this._mleaderDictHandle = this._handle();
    this._plotSettingsDictHandle = this._handle();
    this._plotStyleDictHandle = this._handle();
    this._plotStyleHandle = this._handle();
    this._scaleListDictHandle = this._handle();
    this._tableStyleDictHandle = this._handle();
    this._visualStyleDictHandle = this._handle();
    const byId = new Set((doc.entities || []).map((e) => e && e.id));
    this._plannedGroups = (doc.groups || []).filter((g) => g && Array.isArray(g.entityIds)).map((g) => ({ g, ids: [...new Set(g.entityIds.filter((id) => byId.has(id)))] })).filter((r) => r.ids.length >= 2).map((r) => ({ ...r, handle: this._handle() }));
    this._groupHandlesByEntityId = /* @__PURE__ */ new Map();
    for (const r of this._plannedGroups) {
      for (const id of r.ids) {
        const list = this._groupHandlesByEntityId.get(id) || [];
        list.push(r.handle);
        this._groupHandlesByEntityId.set(id, list);
      }
    }
    if (this._geodataPlan) {
      this._geodataXdictHandle = this._handle();
      this._geodataHandle = this._handle();
    } else {
      this._geodataXdictHandle = null;
      this._geodataHandle = null;
    }
    const paperNames = this._paperSpaceNames(doc);
    this._layoutHandleByBlock = { "*Model_Space": this._handle() };
    for (const n of paperNames) this._layoutHandleByBlock[n] = this._handle();
    this._paperBlockNames = paperNames;
  }
  // Open a TABLE, returning ITS handle — every record inside must name that handle
  // as its owner (330), so the helper hands it back rather than leaving the caller
  // to remember. `count` is group 70, the reader's capacity hint.
  _tableStart(name, count, extraMarker) {
    const l = this.lines;
    const h = this._handle();
    l.push("0", "TABLE");
    l.push("2", name);
    l.push("5", h);
    l.push("330", "0");
    l.push("100", "AcDbSymbolTable");
    l.push("70", String(Math.max(count, 0)));
    if (extraMarker) l.push("100", extraMarker);
    return h;
  }
  // Open a table RECORD under `tableHandle`, returning its own handle. Pairing this
  // with _tableStart is what makes an ownerless record impossible to write by
  // accident — the previous code had no owner anywhere in the TABLES section.
  // `xdictHandle` attaches an EXTENSION DICTIONARY to the record. [G5-INTEROP] needs it
  // on *Model_Space, which is the only thing a GEODATA can hang off. Its position is not
  // free: the `102 {ACAD_XDICTIONARY` group goes between the record's handle and its
  // owner, which is where AutoCAD writes it and where a reader walking the record
  // expects it — measured against ezdxf, not inferred.
  _tableRecord(type, subclass, tableHandle, handleCode = "5", xdictHandle = null) {
    const l = this.lines;
    const h = this._handle();
    l.push("0", type);
    l.push(handleCode, h);
    if (xdictHandle) {
      l.push("102", "{ACAD_XDICTIONARY");
      l.push("360", xdictHandle);
      l.push("102", "}");
    }
    l.push("330", tableHandle);
    l.push("100", "AcDbSymbolTableRecord");
    l.push("100", subclass);
    return h;
  }
  // The nine tables AC1015+ requires, in AutoCAD's own order. VPORT, VIEW, UCS,
  // APPID and BLOCK_RECORD were all absent before [DXF-VALID-1]; BLOCK_RECORD is
  // the one that matters most, because it is what an entity's owner handle points
  // at and therefore what makes the drawing belong to model space at all.
  _writeGenericTables(doc) {
    const l = this.lines;
    const layers = (doc.layers || []).filter(Boolean);
    l.push("0", "SECTION");
    l.push("2", "TABLES");
    this._writeVPortTable(doc);
    const ltDefs = doc.linetypeDefs && Object.keys(doc.linetypeDefs).length ? doc.linetypeDefs : { CONTINUOUS: { name: "CONTINUOUS", pattern: [] } };
    this._ltypeNames = /* @__PURE__ */ new Set();
    const ltNames = Object.keys(ltDefs);
    const layerOnly = [];
    for (const layer of doc.layers || []) {
      if (layer && layer.linetype && !(layer.linetype in ltDefs) && !layerOnly.includes(layer.linetype)) {
        layerOnly.push(layer.linetype);
      }
    }
    const BUILTIN_LT = ["ByBlock", "ByLayer", "Continuous"];
    const builtinLower = new Set(BUILTIN_LT.map((n) => n.toLowerCase()));
    const ownNames = ltNames.filter((n) => !builtinLower.has(String(n).toLowerCase()));
    const ownLayerOnly = layerOnly.filter((n) => !builtinLower.has(String(n).toLowerCase()));
    const ltHandle = this._tableStart(
      "LTYPE",
      BUILTIN_LT.length + ownNames.length + ownLayerOnly.length
    );
    for (const name of BUILTIN_LT) {
      this._writeLinetype(name, "", 0, [], ltHandle);
      this._ltypeNames.add(name);
    }
    for (const name of ltNames)
      if (builtinLower.has(String(name).toLowerCase())) this._ltypeNames.add(name);
    for (const name of ownNames) {
      const def = ltDefs[name] || { pattern: [] };
      const pattern = Array.isArray(def.pattern) ? def.pattern : [];
      this._writeLinetype(name, def.description || "", pattern.length, pattern, ltHandle);
      this._ltypeNames.add(name);
    }
    for (const name of ownLayerOnly) {
      this._writeLinetype(name, "Imported (definition unknown)", 0, [], ltHandle);
      this._ltypeNames.add(name);
    }
    l.push("0", "ENDTAB");
    const hasZero = layers.some((ly) => ly.name === "0");
    const carriers = this._carrierLayers || [];
    const layerHandle = this._tableStart(
      "LAYER",
      layers.length + (hasZero ? 0 : 1) + carriers.length
    );
    if (!hasZero) this._writeGenericLayer({ name: "0", color: "#ffffff" }, layerHandle);
    layers.forEach((ly) => this._writeGenericLayer(ly, layerHandle));
    for (const name of carriers) {
      const color = this._extraLayerColor && this._extraLayerColor.get(name) || "#ffffff";
      this._writeGenericLayer({ name, color }, layerHandle);
    }
    l.push("0", "ENDTAB");
    this._writeGenericStyleTable(doc.textStyleDefs || { Standard: {} });
    this._tableStart("VIEW", 0);
    l.push("0", "ENDTAB");
    this._tableStart("UCS", 0);
    l.push("0", "ENDTAB");
    const appidHandle = this._tableStart("APPID", 2);
    for (const app of ["ACAD", XDATA_APP]) {
      this._tableRecord("APPID", "AcDbRegAppTableRecord", appidHandle);
      l.push("2", app);
      l.push("70", "0");
    }
    l.push("0", "ENDTAB");
    this._writeDimStyleTable(doc.dimStyleDefs || { Standard: {} });
    this._writeBlockRecordTable(doc);
    l.push("0", "ENDSEC");
  }
  // The VPORT table's one record, `*Active`, is the view AutoCAD restores when the
  // drawing opens. It is written for a reason beyond mere validity: with no VPORT
  // and no $EXTMIN/$EXTMAX, a viewer has nothing to frame the drawing with and
  // opens looking at empty space near the origin — which reads to the user as
  // "Design is empty" even once the file parses.
  _writeVPortTable(doc) {
    const l = this.lines;
    const h = this._tableStart("VPORT", 1);
    this._tableRecord("VPORT", "AcDbViewportTableRecord", h);
    const b = this._extents(doc);
    const cx = (b.minX + b.maxX) / 2;
    const cy = (b.minY + b.maxY) / 2;
    const w = Math.max(b.maxX - b.minX, 0);
    const hgt = Math.max(b.maxY - b.minY, 0);
    const view = Math.max(hgt, w / 1.5, 1) * 1.1;
    l.push("2", "*Active");
    l.push("70", "0");
    l.push("10", "0", "20", "0");
    l.push("11", "1", "21", "1");
    l.push("12", String(cx), "22", String(cy));
    l.push("13", "0", "23", "0");
    l.push("14", "10", "24", "10");
    l.push("15", "10", "25", "10");
    l.push("16", "0", "26", "0", "36", "1");
    l.push("17", "0", "27", "0", "37", "0");
    l.push("40", String(view));
    l.push("41", "1.5");
    l.push("42", "50");
    l.push("43", "0", "44", "0");
    l.push("50", "0", "51", "0");
    l.push("71", "0", "72", "1000", "73", "1", "74", "3");
    l.push("75", "0", "76", "0", "77", "0", "78", "0");
    l.push("281", "0", "65", "0", "146", "0");
    l.push("0", "ENDTAB");
  }
  // One BLOCK_RECORD per BLOCK, in the same order — the pairing is what the
  // conformance test checks, because a record without its block (or the reverse)
  // is a dangling reference rather than a missing nicety.
  //
  // `*Model_Space` and `*Paper_Space` come first and always exist, even for a
  // drawing with no blocks and no sheets: they are what every entity's owner
  // handle points at, and what the LAYOUT objects in the OBJECTS section name.
  _writeBlockRecordTable(doc) {
    const l = this.lines;
    const names = this._blockOrder(doc);
    const h = this._tableStart("BLOCK_RECORD", names.length);
    this._blockRecordHandles = {};
    for (const name of names) {
      const xdict = name === "*Model_Space" ? this._geodataXdictHandle : null;
      const rec = this._tableRecord("BLOCK_RECORD", "AcDbBlockTableRecord", h, "5", xdict);
      this._blockRecordHandles[name] = rec;
      l.push("2", name);
      l.push("340", this._layoutHandleForBlock(name) || "0");
      l.push("70", "0");
      l.push("280", "1");
      l.push("281", "0");
    }
    l.push("0", "ENDTAB");
  }
  // Write a STYLE table (Spec 06) from doc.textStyleDefs — one record per named text
  // style, each field resolved against the same TEXTSTYLE_DEFAULTS the app renders with.
  // Groups: 2 name, 40 fixed height (0 = not fixed), 41 width factor, 50 oblique angle,
  // 3 the font FILE (best-effort `family.toLowerCase()+'.ttf'`). TEXT entities carry the
  // style NAME in group 7 (cadImportBuild reverses group 3 → family on re-import).
  _writeGenericStyleTable(textStyleDefs) {
    const l = this.lines;
    const defs = textStyleDefs && Object.keys(textStyleDefs).length ? textStyleDefs : { Standard: {} };
    const names = Object.keys(defs);
    const styleHandle = this._tableStart("STYLE", names.length);
    this._styleHandles = {};
    for (const name of names) {
      const s = { ...TEXTSTYLE_DEFAULTS, ...defs[name] || {} };
      if (s.bold === true || s.italic === true) {
        const what = s.bold === true && s.italic === true ? "bold + italic" : s.bold === true ? "bold" : "italic";
        this.warnings.push(
          `STYLE "${name}": ${what} is not representable in the DXF STYLE table (it would need a guessed font-file name) \u2014 exported with the regular font file`
        );
      }
      const fontFile = `${String(s.fontFamily || "arial").toLowerCase()}.ttf`;
      this._styleHandles[name] = this._tableRecord(
        "STYLE",
        "AcDbTextStyleTableRecord",
        styleHandle
      );
      l.push("2", this._styleName(name));
      l.push("70", "0");
      const sf = (v, d, f) => this._tableNum(v, d, `STYLE "${name}" ${f}`);
      l.push("40", String(sf(s.fixedHeight || 0, 0, "height")));
      l.push("41", String(sf(s.widthFactor, 1, "width factor")));
      l.push("50", String(sf(s.oblique, 0, "oblique angle")));
      l.push("71", "0");
      l.push("42", "0.5");
      l.push("3", fontFile);
      l.push("4", "");
    }
    l.push("0", "ENDTAB");
  }
  // Write a DIMSTYLE table. Each named style resolves its fields against the same
  // defaults cadDimension.resolveDimStyle uses, so the exported vars match what the
  // app renders. The in-app dimension entities still export as exploded primitives.
  _writeDimStyleTable(dimStyleDefs) {
    const l = this.lines;
    const DEFAULTS = {
      textHeight: 2.5,
      arrowSize: 2.5,
      textGap: 0.25,
      unitsPrecision: 2,
      anglePrecision: 1
    };
    const names = Object.keys(dimStyleDefs).length ? Object.keys(dimStyleDefs) : ["Standard"];
    const dimHandle = this._tableStart("DIMSTYLE", names.length, "AcDbDimStyleTable");
    for (const name of names) {
      const s = { ...DEFAULTS, ...dimStyleDefs[name] || {} };
      this._tableRecord("DIMSTYLE", "AcDbDimStyleTableRecord", dimHandle, "105");
      l.push("2", this._dimStyleName(name));
      l.push("70", "0");
      const df = (v, d, f) => this._tableNum(v, d, `DIMSTYLE "${name}" ${f}`);
      l.push("140", String(df(s.textHeight, 2.5, "text height")));
      l.push("41", String(df(s.arrowSize, 2.5, "arrow size")));
      l.push("147", String(df(s.textGap, 0.25, "text gap")));
      l.push("271", String(df(s.unitsPrecision, 2, "precision")));
      l.push("179", String(df(s.anglePrecision, 1, "angle precision")));
    }
    l.push("0", "ENDTAB");
  }
  // BLOCKS section from doc.blockDefs. Each definition's sub-entities are written
  // in block-local coordinates (no offset); INSERT entities in the ENTITIES section
  // reference them by name.
  // Open a BLOCK, set `_owner` so every sub-entity is owned by this block's record,
  // and emit the two class markers a BLOCK/ENDBLK pair needs. `flags` is group 70:
  // 0 plain, 1 anonymous, 2 has attribute definitions.
  _blockStart(name, basePoint, flags = 0, label) {
    const l = this.lines;
    const rec = this._blockRecordHandles[name];
    l.push("0", "BLOCK");
    l.push("5", this._handle());
    l.push("330", rec || "0");
    l.push("100", "AcDbEntity");
    l.push("8", "0");
    l.push("100", "AcDbBlockBegin");
    l.push("2", name);
    l.push("70", String(flags));
    l.push(
      "10",
      String(this._tableNum(basePoint.x, 0, `BLOCK "${name}" base x`)),
      "20",
      String(this._tableNum(basePoint.y, 0, `BLOCK "${name}" base y`)),
      "30",
      "0"
    );
    l.push("3", name);
    l.push("1", "");
    this._owner = rec;
    this._currentBlock = label || `block "${name}"`;
  }
  _blockEnd(name) {
    const l = this.lines;
    l.push("0", "ENDBLK");
    l.push("5", this._handle());
    l.push("330", this._blockRecordHandles[name] || "0");
    l.push("100", "AcDbEntity");
    l.push("8", "0");
    l.push("100", "AcDbBlockEnd");
    this._owner = void 0;
    this._currentBlock = void 0;
  }
  _writeCADBlocks(doc) {
    const l = this.lines;
    const defs = doc.blockDefs || {};
    l.push("0", "SECTION");
    l.push("2", "BLOCKS");
    this._blockStart("*Model_Space", { x: 0, y: 0 });
    this._blockEnd("*Model_Space");
    for (const pn of this._paperBlockNames) {
      this._blockStart(pn, { x: 0, y: 0 });
      this._blockEnd(pn);
    }
    for (const name of Object.keys(defs)) {
      const def = defs[name] || {};
      const bp = def.basePoint || { x: 0, y: 0 };
      const attdefs = def.attdefs || [];
      const written = this._blockName(name);
      this._blockStart(written, bp, attdefs.length ? 2 : 0, `block "${name}"`);
      (def.entities || []).forEach((se) => this._writeCADEntity(se, 0, 0));
      attdefs.forEach((a) => this._writeAttdef(a));
      this._blockEnd(written);
    }
    this._writeLayoutBlocks(doc);
    this._writeXrefBlocks(doc);
    const dimOff = this._writeOffset(doc);
    for (const { name, entity } of this._dimBlocks || []) {
      this._blockStart(name, { x: 0, y: 0 }, 1);
      this._writeDimensionExploded(entity, dimOff.x, dimOff.y);
      this._blockEnd(name);
    }
    l.push("0", "ENDSEC");
  }
  // Encode paper-space layouts as *Paper_Space* blocks carrying marker TEXT, the
  // only structure dxf-parser preserves (it skips the OBJECTS/LAYOUT section and
  // has no VIEWPORT handler). Each paper layout becomes one block (group 67=1 sets
  // the paper-space flag the parser reads) holding a PSMETA text + one PSVP text
  // per viewport. Omitted entirely when the doc has only the Model layout, so a
  // simple drawing's DXF is byte-identical to before (and contains no "LAYOUT").
  // NOTE: this carrier is private to our own importer — these layouts will NOT
  // open as paper space in third-party CAD. The client deliverable is PDF and
  // layouts also persist via IndexedDB JSON, so that is acceptable for this phase.
  _writeLayoutBlocks(doc) {
    const l = this.lines;
    const paper = (doc.layouts || []).filter((ly) => ly && !ly.isModel);
    if (!paper.length) return;
    const clean = (s) => String(s == null ? "" : s).replace(/[|=\r\n]/g, " ");
    paper.forEach((ly, i) => {
      const bn = `LAYOUTDEF$${i}`;
      this._blockStart(bn, { x: 0, y: 0 });
      this._markerHead("0");
      l.push("10", "0", "20", "0", "30", "0");
      l.push("40", "1");
      l.push(
        "1",
        `PSMETA|name=${clean(ly.name)}|paper=${clean(ly.paperSize)}|orient=${clean(ly.orientation)}`
      );
      this._markerTail();
      const tf = ly.titleFields || {};
      const fieldPairs = Object.keys(tf).filter((k) => tf[k] != null && String(tf[k]) !== "").map((k) => `${clean(k)}=${encodeURIComponent(String(tf[k]))}`);
      if (fieldPairs.length) {
        this._markerHead("0");
        l.push("10", "0", "20", "0", "30", "0");
        l.push("40", "1");
        l.push("1", `PSFIELDS|${fieldPairs.join("|")}`);
        this._markerTail();
      }
      const off = this._writeOffset(doc);
      (ly.viewports || []).filter(Boolean).forEach((vp) => {
        const wr = vp.worldRect || { x: 0, y: 0, width: 1, height: 1 };
        this._markerHead("0");
        l.push("10", "0", "20", "0", "30", "0");
        l.push("40", "1");
        l.push(
          "1",
          `PSVP|v=2|x=${wr.x + off.x}|y=${wr.y + off.y}|w=${wr.width}|h=${wr.height}|s=${vp.scale}`
        );
        this._markerTail();
      });
      this._blockEnd(bn);
    });
  }
  // V4b P3: encode XREF underlays as carrier blocks. dxf-parser cannot read the
  // OBJECTS section nor the external-reference block flag (the same limitation
  // Phase 1 found for LAYOUT), so an XREF rides as a NORMAL block named XREFDEF$<i>
  // holding: an XREFMETA marker TEXT (name + source file), one XREFINST marker TEXT
  // per placed instance (its transform + hidden-layer list), and the locked
  // geometry as ordinary block sub-entities. On reimport, dxfImport.liftXrefCarriers
  // lifts these back into xrefDefs + XREF entities and drops the helper block.
  // Omitted entirely when the doc has no XREFs, so a simple drawing's DXF is
  // byte-identical to before. NOTE: the markers are private to our own importer. Other CAD
  // tools see the geometry only because `_writeXrefInsert` places an INSERT of this
  // block for each instance — before [round 2] there was none, and the claim that
  // third-party CAD "sees flattened reference geometry" was false: an unreferenced
  // block draws nothing. They see an ordinary block, never an XREF. XREFs persist losslessly via IndexedDB JSON; the client
  // deliverable is PDF, so that is acceptable for this phase.
  _writeXrefBlocks(doc) {
    const xrefDefs = doc.xrefDefs || {};
    const names = Object.keys(xrefDefs);
    if (!names.length) return;
    const off = this._writeOffset(doc);
    const clean = (s) => String(s == null ? "" : s).replace(/[|=\r\n]/g, " ");
    names.forEach((name, i) => {
      const def = xrefDefs[name] || {};
      const bn = `XREFDEF$${i}`;
      this._blockStart(bn, { x: 0, y: 0 }, 0, `xref "${name}"`);
      this._writeXrefCarrier(`XREFMETA|name=${clean(name)}|src=${clean(def.sourceFile)}`);
      (doc.entities || []).filter((e) => e && e.type === "xref" && e.xrefName === name).forEach((inst) => {
        const ip = inst.insertionPoint || { x: 0, y: 0 };
        const vis = inst.layerVisibility || {};
        const hidden = Object.keys(vis).filter((k) => vis[k] === false).map((k) => clean(k).replace(/,/g, " ")).join(",");
        this._writeXrefCarrier(
          `XREFINST|x=${ip.x + off.x}|y=${ip.y + off.y}|sx=${inst.scaleX != null ? inst.scaleX : 1}|sy=${inst.scaleY != null ? inst.scaleY : 1}|rot=${inst.rotation || 0}|layer=${clean(inst.layer)}|vis=${hidden}`
        );
      });
      (def.entities || []).forEach((se) => this._writeCADEntity(se, 0, 0));
      this._blockEnd(bn);
    });
  }
  // One XREF carrier marker as a zero-height TEXT (group 1 holds the payload).
  _writeXrefCarrier(text) {
    const l = this.lines;
    this._markerHead("0");
    l.push("10", "0", "20", "0", "30", "0");
    l.push("40", "1");
    l.push("1", text);
    this._markerTail();
  }
  // A layer NAME is user text headed into a line-oriented group stream: a CR/LF
  // inside it ends the group early and corrupts the whole file (measured: our own
  // export then HANGS dxf-parser on re-import — LAYER-DXF-NAME-1). Collapse line
  // breaks to spaces, at the record AND at every entity reference, so the two
  // always agree. Other characters are left alone — they round-trip byte-for-byte.
  static dxfSafeName(name) {
    return String(name).replace(/[\r\n]+/g, " ");
  }
  _writeGenericLayer(layer, tableHandle) {
    const l = this.lines;
    if (tableHandle) {
      this._tableRecord("LAYER", "AcDbLayerTableRecord", tableHandle);
    } else {
      l.push("0", "LAYER");
      l.push("5", this._handle());
    }
    l.push("2", this._layerName(layer.name));
    l.push("70", String((layer.frozen === true ? 1 : 0) | (layer.locked === true ? 4 : 0)));
    const aci = int2aci(layer.color) || 7;
    l.push("62", String(layer.visible === false ? -aci : aci));
    this._pushTrueColor(layer.color);
    const lt = layer.linetype && this._ltypeNames && this._ltypeNames.has(layer.linetype) ? this._ltypeName(layer.linetype) : "Continuous";
    l.push("6", lt);
    if (layer.plot === false) l.push("290", "0");
    if (layer.lineweight != null && Number.isFinite(layer.lineweight)) {
      l.push("370", String(Math.round(layer.lineweight * 100)));
    }
    if (this._plotStyleHandle) l.push("390", this._plotStyleHandle);
  }
  // [DXF-CONTAINER-1] The CLASSES section.
  //
  // A CLASS record tells a reader how to handle an object type that is not built into
  // the DXF spec itself. We write one ONLY for the types this exporter actually emits —
  // writing classes for objects that never appear would be inventing bookkeeping rather
  // than describing the file. LAYOUT is the notable one: we have emitted LAYOUT objects
  // since [DXF-VALID-1] with no class record for them.
  //
  // Shapes measured from ezdxf's AC1027 output, not copied from a reference page.
  _writeClasses() {
    const l = this.lines;
    l.push("0", "SECTION");
    l.push("2", "CLASSES");
    const cls = (record, cpp, flags = "0") => {
      l.push("0", "CLASS");
      l.push("1", record);
      l.push("2", cpp);
      l.push("3", "ObjectDBX Classes");
      l.push("90", flags);
      l.push("91", "0");
      l.push("280", "0");
      l.push("281", "0");
    };
    cls("ACDBDICTIONARYWDFLT", "AcDbDictionaryWithDefault");
    cls("ACDBPLACEHOLDER", "AcDbPlaceHolder");
    cls("LAYOUT", "AcDbLayout");
    if (this._geodataPlan) cls("GEODATA", "AcDbGeoData", "4095");
    l.push("0", "ENDSEC");
  }
  // [DXF-CONTAINER-1] The named-object dictionaries beyond the three this exporter
  // used to write. Most are legitimately EMPTY — an empty dictionary is what ezdxf
  // writes for ACAD_COLOR, ACAD_PLOTSETTINGS, ACAD_SCALELIST and ACAD_TABLESTYLE, and
  // its presence is the point: a reader that looks one up finds a dictionary rather
  // than nothing.
  //
  // We deliberately do NOT copy ezdxf's LAYER group 347 material pointer. ACAD_MATERIAL
  // is written empty here, so a 347 would name a MATERIAL that does not exist — a
  // dangling reference, which is the class of defect this whole line of work removes.
  _writeNamedDictionaries() {
    const l = this.lines;
    const emptyDict = (handle) => {
      l.push("0", "DICTIONARY");
      l.push("5", handle);
      l.push("330", this._rootDictHandle);
      l.push("100", "AcDbDictionary");
      l.push("281", "1");
    };
    emptyDict(this._colorDictHandle);
    emptyDict(this._materialDictHandle);
    emptyDict(this._mleaderDictHandle);
    emptyDict(this._plotSettingsDictHandle);
    emptyDict(this._scaleListDictHandle);
    emptyDict(this._tableStyleDictHandle);
    emptyDict(this._visualStyleDictHandle);
    l.push("0", "ACDBDICTIONARYWDFLT");
    l.push("5", this._plotStyleDictHandle);
    l.push("330", this._rootDictHandle);
    l.push("100", "AcDbDictionary");
    l.push("281", "1");
    l.push("3", "Normal");
    l.push("350", this._plotStyleHandle);
    l.push("100", "AcDbDictionaryWithDefault");
    l.push("340", this._plotStyleHandle);
    l.push("0", "ACDBPLACEHOLDER");
    l.push("5", this._plotStyleHandle);
    l.push("330", this._plotStyleDictHandle);
  }
  // [BUG-DXF-WHITE-INVISIBLE-1] Group 420 is an ABSOLUTE RGB and it OVERRIDES the ACI
  // in group 62. That matters enormously for one colour: ACI 7 is the special
  // "white/black" index that ADAPTS to the background — black on white paper, white on a
  // dark canvas — which is exactly what every CAD program expects a default-coloured
  // drawing to do.
  //
  // Our canvas is dark, so a default entity is `#ffffff` in the document, and writing
  // that verbatim as `420 16777215` pinned every such drawing to literal white. Measured
  // on the owner's own export: it displays perfectly in a viewer with a BLACK canvas and
  // is completely invisible in AutoCAD, whose paper is white — the file opens, reports no
  // error, and shows nothing. ezdxf writes no 420 on its layers at all.
  //
  // So pure white is written as ACI 7 ALONE and never pinned with a true colour. Every
  // other colour still gets its exact RGB, because there the user chose that specific
  // colour and an ACI approximation would be wrong.
  _pushTrueColor(hex) {
    if (typeof hex !== "string" || hex[0] !== "#") return;
    const n = parseInt(hex.slice(1), 16);
    if (!isFinite(n)) return;
    if ((n & 16777215) === 16777215) return;
    this.lines.push("420", String(n & 16777215));
  }
  _cadHead(type, e, opts = {}) {
    const l = this.lines;
    l.push("0", type);
    const h = this._handle();
    l.push("5", h);
    const groupable = this._owner === (this._blockRecordHandles || {})["*Model_Space"] && type !== "ATTRIB" && type !== "SEQEND";
    const reactors = groupable && e && e.id != null && this._groupHandlesByEntityId ? this._groupHandlesByEntityId.get(e.id) : null;
    if (reactors && reactors.length) {
      l.push("102", "{ACAD_REACTORS");
      for (const rh of reactors) l.push("330", rh);
      l.push("102", "}");
    }
    l.push("330", this._owner || "0");
    l.push("100", "AcDbEntity");
    if (this._paper) l.push("67", "1");
    if (groupable && e && e.id != null && this._handleById) {
      const prev = this._handleById.get(e.id);
      if (prev) prev.push(h);
      else this._handleById.set(e.id, [h]);
    }
    l.push("8", this._layerName(e.layer || "0"));
    if (e.color) {
      l.push("62", String(int2aci(e.color) || 7));
      this._pushTrueColor(e.color);
    }
    if (e.linetype && this._ltypeNames && this._ltypeNames.has(e.linetype)) {
      l.push("6", this._ltypeName(e.linetype));
    }
    if (e.lineweight != null && Number.isFinite(e.lineweight)) {
      l.push("370", String(Math.round(e.lineweight * 100)));
    }
    if (Number.isFinite(e.ltScale) && e.ltScale > 0 && e.ltScale !== 1) {
      l.push("48", String(e.ltScale));
    }
    if (opts.invisible) l.push("60", "1");
    const chain = ENTITY_SUBCLASS[type];
    if (chain === void 0) {
      throw new Error(`DXFExporter: no subclass chain registered for record type ${type}`);
    }
    for (const marker of chain) l.push("100", marker);
    return h;
  }
  // [DXF-VALID-2] The TRANSACTIONAL entity write.
  //
  // Every value goes through DxfTagSink, which THROWS on a non-finite number rather
  // than writing the token `NaN`. That is only half a fix: a throw part-way through
  // a record would leave a half-written entity in the file, which is worse than the
  // bad number. So each entity is written against a mark and rolled back whole.
  //
  // PROCESS §E.2, answered in writing:
  //  1. Does refusing leave a WORSE state? No — and this is the unusual case where
  //     refusing is strictly better. Allowing it wrote `NaN` into a group and a
  //     strict reader then failed at the TAG level, losing EVERY entity. Refusing
  //     the whole export would also give the user nothing. Dropping the one bad
  //     entity and naming it is better than both.
  //  2. Does the refusal REACH the user? Yes: `warnings` is surfaced by
  //     downloadDXF → useFileIO, and by EditorPage via cadModelToDXFDetailed.
  //  3. Does it OVER-REACH? The `UNCHANGED:` conformance test asserts a well-formed
  //     drawing produces no warnings at all and the same record count as before.
  //
  // The rollback covers three things, not one: the lines, the handle map (a GROUP
  // references its members BY HANDLE, so a stale entry would produce a dangling 340
  // — the exact defect _writeObjects guards against), and `warnings` (a fidelity
  // note pushed before the throw would otherwise describe an entity that is not in
  // the file).
  _writeCADEntity(e, ox, oy) {
    const degenerate = this._degenerateReason(e);
    if (degenerate) {
      this.warnings.push(
        `${String(e.type).toUpperCase()} ${this._refName(e)}: ${degenerate} \u2014 not exported`
      );
      return;
    }
    const mark = this.lines.length;
    const handlesBefore = e && e.id != null ? (this._handleById.get(e.id) || []).slice() : null;
    const warningsBefore = this.warnings.length;
    const ownerBefore = this._owner;
    try {
      this._writeCADEntityRecords(e, ox, oy);
    } catch (err) {
      if (err instanceof Error && String(err.message).startsWith("DXFExporter:")) throw err;
      this.lines.rewind(mark);
      this._owner = ownerBefore;
      this.warnings.length = warningsBefore;
      if (e && e.id != null) {
        if (handlesBefore && handlesBefore.length) this._handleById.set(e.id, handlesBefore);
        else this._handleById.delete(e.id);
      }
      const why = err instanceof DxfValueError ? err.message : `could not be written (${err && err.name}: ${err && err.message})`;
      this.warnings.push(
        `${String(e && e.type).toUpperCase()} ${this._refName(e)}: ${why} \u2014 entity not exported`
      );
    }
  }
  // Degenerate geometry a reader has no defined behaviour for. These do not throw —
  // the numbers are finite — but they produce records AutoCAD rejects or draws as
  // nothing, so they are refused here with the same reporting contract.
  _degenerateReason(e) {
    if (!e || typeof e !== "object") return null;
    switch (e.type) {
      case "circle":
        return Number.isFinite(e.r) && e.r > 0 ? null : "radius must be greater than zero";
      case "arc":
        return Number.isFinite(e.r) && e.r > 0 ? null : "radius must be greater than zero";
      case "ellipse":
        if (!(e.rx > 0) || !(e.ry > 0)) return "both semi-axes must be greater than zero";
        return Math.min(e.rx, e.ry) / Math.max(e.rx, e.ry) >= 1e-6 ? null : "axis ratio is too small to represent";
      case "polyline":
        return (e.points || []).length >= 2 ? null : "a polyline needs at least two points";
      case "insert":
        return this._blockRecordHandles && !this._blockRecordHandles[this._blockName(e.blockName)] ? `block "${e.blockName}" is not defined in this drawing` : null;
      // [round 3] The next four each had an early `return` in their writer that
      // wrote nothing and said nothing. Each condition mirrors that return exactly.
      case "spline": {
        const fit = Array.isArray(e.fitPoints) && e.fitPoints.length >= 2;
        const ctrl = Array.isArray(e.controlPoints) && e.controlPoints.length >= 2;
        return fit || ctrl ? null : "a spline needs at least two points";
      }
      case "leader":
        try {
          return leaderGeometry(e, this._dimStyleDefs, this._dimCurrentStyle, this._dimSizing) ? null : "a leader needs at least two points";
        } catch {
          return "its geometry is incomplete";
        }
      case "hatch": {
        const boundary = Array.isArray(e.cachedBoundary) && e.cachedBoundary.length >= 3;
        const lines = Array.isArray(e.cachedLines) && e.cachedLines.length > 0;
        return boundary || lines ? null : "a hatch needs a boundary or cached lines to draw";
      }
      case "region": {
        const rings = (Array.isArray(e.rings) ? e.rings : []).filter(
          (r) => Array.isArray(r) && r.length >= 3
        );
        return rings.length ? null : "a region needs at least one ring of three points";
      }
      // [RASTER-IMAGE-1] A raster is refused BY NAME, never dropped in silence.
      //
      // Two independent reasons this is a refusal rather than a writer, and both
      // are owner decisions recorded in ROADMAP §WAVE D: DXF cannot embed pixels
      // (IMAGEDEF stores an external FILE PATH, so a .dxf alone would open with a
      // missing-image frame and the raster would have to ship beside it), and
      // `AcDbRasterImage` requires a CLASSES section, which this very suite's
      // section-order test currently requires to be ABSENT — deferred to
      // `[DXF-PLOTSTYLE-1]`. The geometry still exports; the user is told exactly
      // what did not, which is the contract every other refusal here follows.
      // SVG embeds the raster and the PDF plot draws it, so DXF is the only loss.
      case "image":
        return "raster images are not exported to DXF (no IMAGEDEF/CLASSES support yet)";
      case "xref":
        return this._xrefBlockOf && this._xrefBlockOf.has(e.xrefName) ? null : `xref definition "${e.xrefName}" is not in this drawing`;
      case "dimension":
        try {
          return dimensionPrimitives(e, this._dimStyleDefs, this._dimCurrentStyle, this._dimSizing) ? null : "its geometry is degenerate, so nothing could be drawn for it";
        } catch {
          return "its geometry is incomplete";
        }
      default:
        return null;
    }
  }
  _writeCADEntityRecords(e, ox, oy) {
    const l = this.lines;
    switch (e.type) {
      case "line":
        this._cadHead("LINE", e);
        l.push("10", String(e.x1 + ox), "20", String(e.y1 + oy), "30", "0");
        l.push("11", String(e.x2 + ox), "21", String(e.y2 + oy), "31", "0");
        break;
      case "polyline":
      case "rectangle": {
        const pts = e.type === "rectangle" ? [
          [e.x, e.y],
          [e.x + e.width, e.y],
          [e.x + e.width, e.y + e.height],
          [e.x, e.y + e.height]
        ] : e.points.map((p) => [p.x, p.y]);
        const closed = e.type === "rectangle" || e.closed;
        this._cadHead("LWPOLYLINE", e);
        l.push("90", String(pts.length));
        l.push("70", closed ? "1" : "0");
        const wWorld = polylineWidthWorld(e);
        if (wWorld > 0) l.push("43", String(wWorld));
        pts.forEach(([x, y]) => l.push("10", String(x + ox), "20", String(y + oy)));
        break;
      }
      case "circle":
        this._cadHead("CIRCLE", e);
        l.push("10", String(e.cx + ox), "20", String(e.cy + oy), "30", "0", "40", String(e.r));
        break;
      case "arc":
        this._cadHead("ARC", e);
        l.push("10", String(e.cx + ox), "20", String(e.cy + oy), "30", "0", "40", String(e.r));
        l.push("100", "AcDbArc");
        l.push("50", String(e.startAngle), "51", String(e.endAngle));
        break;
      case "ellipse": {
        const swap = (e.ry || 0) > (e.rx || 0);
        const major = swap ? e.ry : e.rx;
        const minor = swap ? e.rx : e.ry;
        const rotDeg = (e.rotation || 0) + (swap ? 90 : 0);
        const rot = rotDeg * Math.PI / 180;
        this._cadHead("ELLIPSE", e);
        l.push("10", String(e.cx + ox), "20", String(e.cy + oy), "30", "0");
        l.push("11", String(major * Math.cos(rot)), "21", String(major * Math.sin(rot)), "31", "0");
        l.push("40", String(major ? minor / major : 1));
        const full = e.startAngle == null || e.endAngle == null;
        const shift = swap ? -90 : 0;
        const startRad = full ? 0 : (e.startAngle + shift) * Math.PI / 180;
        const sweepDeg = full ? 360 : ((e.endAngle - e.startAngle) % 360 + 360) % 360 || 360;
        l.push("41", String(startRad), "42", String(startRad + sweepDeg * Math.PI / 180));
        break;
      }
      case "spline":
        this._writeSpline(e, ox, oy);
        break;
      case "region":
        this._writeRegion(e, ox, oy);
        break;
      case "ray":
      case "xline":
      case "revcloud":
        this._writeV4cEntity(e, ox, oy);
        break;
      case "text": {
        this._warnTextWeightLoss(e);
        this._cadHead("TEXT", e);
        const tx = e.x + ox;
        const ty = e.y + oy;
        l.push("10", String(tx), "20", String(ty), "30", "0");
        const body = this._textValue(e.text || "", e);
        l.push("40", String(e.height || 1), "1", body, "50", String(e.rotation || 0));
        l.push("7", this._styleName(e.textStyle || "Standard"));
        const jg = textJustifyGroups(e.justify);
        if (jg) {
          l.push("72", jg.h);
          l.push("11", String(tx), "21", String(ty), "31", "0");
          l.push("100", "AcDbText");
          l.push("73", jg.v);
        } else {
          l.push("100", "AcDbText");
        }
        break;
      }
      case "mtext":
        this._writeMText(e, ox, oy);
        break;
      case "table":
        this._writeTableExploded(e, ox, oy);
        break;
      case "point":
        this._cadHead("POINT", e);
        l.push("10", String(e.x + ox), "20", String(e.y + oy), "30", "0");
        break;
      case "hatch":
        this._writeHatch(e, ox, oy);
        break;
      case "insert":
        this._writeInsertEntity(e, ox, oy);
        break;
      case "dimension": {
        const blockName = this._dimBlockOf && this._dimBlockOf.get(e);
        const kind = blockName ? this._dimNativeKind(e) : null;
        if (kind && blockName) this._writeNativeDimension(e, ox, oy, kind, blockName);
        else this._writeDimensionExploded(e, ox, oy);
        break;
      }
      case "leader":
        this._writeLeaderExploded(e, ox, oy);
        break;
      case "xref":
        this._writeXrefInsert(e, ox, oy);
        break;
      default:
        break;
    }
  }
  // [round 2] An xref instance, written as an INSERT of its carrier block.
  //
  // There was NO case for 'xref' in the entity writer at all. The xref's geometry
  // went into an `XREFDEF$<i>` block that nothing inserted, so in AutoCAD — or any
  // reader other than our own — an xref underlay was simply INVISIBLE. The comment
  // on _writeXrefBlocks claimed "third-party CAD sees flattened reference geometry";
  // an unreferenced block draws nothing. Found by review round 2 (Lens D).
  //
  // The XREFINST marker still rides inside the block for our own reader, which
  // rebuilds the xref entity from it and DROPS this INSERT (dxfImport
  // liftXrefCarriers), so a round trip gains nothing. What a DXF INSERT cannot say
  // is per-layer visibility inside the reference: layers the instance hides will
  // show elsewhere, and that is reported when it actually applies.
  _writeXrefInsert(e, ox, oy) {
    const l = this.lines;
    const ip = e.insertionPoint || { x: 0, y: 0 };
    this._cadHead("INSERT", e);
    l.push("2", this._xrefBlockOf.get(e.xrefName));
    l.push("10", String(ip.x + ox), "20", String(ip.y + oy), "30", "0");
    l.push("41", String(e.scaleX != null ? e.scaleX : 1));
    l.push("42", String(e.scaleY != null ? e.scaleY : 1));
    l.push("43", "1");
    l.push("50", String(e.rotation || 0));
    const vis = e.layerVisibility || {};
    const hidden = Object.keys(vis).filter((k) => vis[k] === false);
    if (hidden.length) {
      const def = this._doc && this._doc.xrefDefs && this._doc.xrefDefs[e.xrefName] || {};
      const bodyLayers = new Set((def.entities || []).map((se) => se && (se.layer || "0")));
      const shown = hidden.filter((n) => bodyLayers.has(n));
      if (shown.length) {
        this.warnings.push(
          `XREF ${this._refName(e)}: hidden layer(s) ${shown.join(", ")} will show in other CAD tools \u2014 a DXF INSERT has no per-layer visibility`
        );
      }
    }
  }
  // The head of a BOOKKEEPING marker — the TEXT records our own reader decodes
  // (PSMETA/PSVP/PSFIELDS, XREFMETA/XREFINST, V4CENT) and no one else should see.
  //
  // [round 2] Every one is INVISIBLE (group 60 = 1). The xref markers force the
  // issue: once `_writeXrefInsert` began drawing the xref's carrier block, the
  // `XREFMETA|name=…` and `XREFINST|x=…` strings inside it would have been drawn over
  // the survey in AutoCAD — the same defect as the PSMETA text that showed on the
  // owner's sheet, reintroduced by the fix one step earlier. The V4c and region
  // markers sit in MODEL space at height 0.0001 and were always drawn as specks.
  // dxf-parser keeps an invisible entity (`visible: false`) and our importer has no
  // entity-visibility filter, so every marker is still read back.
  _markerHead(layer) {
    this._cadHead("TEXT", { layer }, { invisible: true });
  }
  // [DXF-TEXT-SUBCLASS-1] …and the tail every marker TEXT must close with.
  //
  // A conformant TEXT carries `AcDbText` TWICE (measured against ezdxf, for plain text
  // as well as justified). `_markerHead` opens the first via `_cadHead`; without this
  // the record simply stops after its group 1 and a reader walking subclass markers to
  // find the end of the text data has nothing to stop at — which is what made AutoCAD
  // and every other CAD program refuse our files.
  //
  // These nine records were missed by the first pass at this bug, because they do not
  // go through the `case 'text'` writer at all: PSMETA/PSFIELDS/PSVP, XREFMETA/XREFINST
  // and the V4CENT/region markers are written inline by six call sites. Kept as a named
  // pair with `_markerHead` so a seventh call site cannot open a marker without closing
  // it — `dxfConformance` asserts the chain on EVERY text, including these.
  _markerTail() {
    this.lines.push("100", "AcDbText");
  }
  // A TEXT record's group 1, cut to AutoCAD's 255-character single-line limit.
  //
  // [round 3] Cut by CHARACTER (code point), never by UTF-16 unit: slicing at 255
  // units could strand the high half of a surrogate pair, which UTF-8 then writes as
  // U+FFFD. And applied at EVERY TEXT-writing path — round 2 cut only the TEXT
  // entity, so a leader note, a table cell and a dimension override still went out
  // whole. Whether AutoCAD counts the limit in characters or bytes is not verifiable
  // here; characters is the conservative reading that never corrupts one.
  _textValue(s, e) {
    const body = String(s == null ? "" : s);
    const chars = Array.from(body);
    if (chars.length <= 255) return body;
    this.warnings.push(
      `${String(e && e.type).toUpperCase()} ${this._refName(e)}: ${chars.length} characters exceeds the DXF TEXT limit of 255 \u2014 the rest was cut (use MTEXT for long text)`
    );
    return chars.slice(0, 255).join("");
  }
  // How a refusal names the entity. Sub-entities of a block definition carry no id,
  // so the warning used to read "INSERT undefined: …" — unactionable, and a breach
  // of the "refused BY ID, so the user can find it" contract.
  _refName(e) {
    if (e && e.id != null) return e.id;
    return this._currentBlock ? `(in ${this._currentBlock})` : "(unnamed)";
  }
  // Write a SPLINE. A fit-point spline writes its fit points (group 11) AND uses them
  // as the control points (group 10) so foreign viewers render a curve; on re-import
  // the presence of fit points re-selects the fit-point representation. A clamped
  // uniform knot vector (length = #ctrl + degree + 1) is synthesised so the entity is
  // a valid DXF SPLINE regardless of how it was drawn.
  _writeSpline(e, ox, oy) {
    const l = this.lines;
    const fitPts = e.fitPoints && e.fitPoints.length >= 2 ? e.fitPoints : null;
    const ctrl = fitPts || e.controlPoints || [];
    const nCtrl = ctrl.length;
    if (nCtrl < 2) return;
    const p = Math.max(1, Math.min(e.degree || 3, nCtrl - 1));
    const knots = [];
    const m = nCtrl + p + 1;
    for (let i = 0; i < m; i++) {
      if (i <= p) knots.push(0);
      else if (i >= nCtrl) knots.push(1);
      else knots.push((i - p) / (nCtrl - p));
    }
    this._cadHead("SPLINE", e);
    let flag = 8;
    if (e.closed) flag |= 1;
    l.push("70", String(flag));
    l.push("71", String(p));
    l.push("72", String(knots.length));
    l.push("73", String(nCtrl));
    l.push("74", String(fitPts ? fitPts.length : 0));
    for (const k of knots) l.push("40", String(k));
    for (const cp of ctrl) l.push("10", String(cp.x + ox), "20", String(cp.y + oy), "30", "0");
    if (fitPts)
      for (const fp of fitPts) l.push("11", String(fp.x + ox), "21", String(fp.y + oy), "31", "0");
  }
  // V4c: ray / xline / revcloud have no faithful DXF entity (and dxf-parser
  // emits none of them), so each rides as a carrier — a marker TEXT (the round-trip
  // SOURCE OF TRUTH, `V4CENT|...`) plus fallback geometry (LINE / CIRCLE / LWPOLYLINE)
  // so foreign viewers see something sane. Both go on a `V4C$<type>` layer; on reimport
  // cadImportBuild lifts the marker back to the semantic entity and drops everything
  // else on the carrier layer. Mirrors the V4b PSMETA / XREFDEF$ carrier pattern.
  // NOTE: private to our importer — third-party CAD shows only the flattened fallback.
  _writeV4cEntity(e, ox, oy) {
    const l = this.lines;
    const ly = "V4C$" + e.type;
    const clean = (s) => String(s == null ? "" : s).replace(/[|=\r\n]/g, " ");
    const anchor = e.type === "revcloud" ? e.points && e.points[0] || { x: 0, y: 0 } : { x: e.x, y: e.y };
    let payload;
    if (e.type === "ray" || e.type === "xline") {
      payload = `V4CENT|t=${e.type}|ly=${clean(e.layer)}|x=${e.x + ox}|y=${e.y + oy}|dx=${e.dx}|dy=${e.dy}`;
    } else {
      const pts = (e.points || []).map((p) => `${p.x + ox},${p.y + oy}`).join(";");
      payload = `V4CENT|t=revcloud|ly=${clean(e.layer)}|cl=${e.closed ? 1 : 0}|ar=${e.arcRadius || 0}|pts=${pts}`;
    }
    this._markerHead(ly);
    l.push("10", String(anchor.x + ox), "20", String(anchor.y + oy), "30", "0");
    l.push("40", "0.0001", "1", payload);
    this._markerTail();
    this._writeV4cFallback(e, ox, oy, ly);
  }
  // C4b criterion 8 — REGION export, decided (owner, 2026-08-27): boundary
  // polylines PLUS a private carrier, TALLIED.
  //
  // A DXF REGION is an ACIS/SAT solid and we cannot write one (our own importer
  // already DROPS incoming ones — cadFidelityReport.js's REGION drop reason). So:
  //   - every ring goes out as a real closed LWPOLYLINE, which is what a
  //     third-party CAD sees and what it should see: the right geometry;
  //   - a marker TEXT names the group so OUR importer can rebuild the region.
  //
  // UNLIKE the V4c carrier, the marker does NOT carry the coordinates. A revcloud's
  // point list fits in a DXF group-1 string; a region's does not — group code 1 is
  // capped at 255 bytes, and a 48-gon ring alone blows past it. Duplicating
  // thousands of coordinates into a string a strict reader may reject is the wrong
  // trade when the LWPOLYLINEs beside it already hold exact geometry. The ASSOCIATION
  // rides in the LAYER NAME instead: each region gets its own `V4C$region$<n>` layer,
  // so the importer groups a marker with its rings by layer and ring ORDER is write
  // order (outer first, then holes). That is also why the counter is per-export and
  // not per-entity-id — an id can contain characters a layer name cannot.
  _writeRegion(e, ox, oy) {
    const l = this.lines;
    const rings = (Array.isArray(e.rings) ? e.rings : []).filter(
      (r) => Array.isArray(r) && r.length >= 3
    );
    if (!rings.length) return;
    const ly = this._regionLayerOf?.get(e) || "V4C$region$" + this._regionSeq++;
    this.warnings.push(
      `region "${e.id}" exported as ${(Array.isArray(e.rings) ? e.rings : []).length} boundary polyline(s) + a BranchCAD carrier: DXF REGION is an ACIS solid, which this engine cannot write. Other CAD tools will see outlines, not a filled region.`
    );
    const clean = (s) => String(s == null ? "" : s).replace(/[|=\r\n]/g, " ");
    const payload = `V4CENT|t=region|ly=${clean(e.layer)}|co=${clean(e.color)}|lt=${clean(e.linetype)}|n=${rings.length}`;
    const a = rings[0][0];
    this._markerHead(ly);
    l.push("10", String(a.x + ox), "20", String(a.y + oy), "30", "0");
    l.push("40", "0.0001", "1", payload);
    this._markerTail();
    for (const ring of rings) {
      this._cadHead("LWPOLYLINE", { layer: ly });
      l.push("90", String(ring.length), "70", "1");
      ring.forEach((p) => l.push("10", String(p.x + ox), "20", String(p.y + oy)));
    }
  }
  // Visible fallback geometry for foreign viewers (dropped by our own importer).
  _writeV4cFallback(e, ox, oy, ly) {
    const l = this.lines;
    const head = (type) => this._cadHead(type, { layer: ly });
    if (e.type === "ray" || e.type === "xline") {
      const L = Math.hypot(e.dx, e.dy) || 1;
      const ux = e.dx / L;
      const uy = e.dy / L;
      const K = 1e4;
      const a = e.type === "xline" ? { x: e.x - ux * K, y: e.y - uy * K } : { x: e.x, y: e.y };
      const b = { x: e.x + ux * K, y: e.y + uy * K };
      head("LINE");
      l.push("10", String(a.x + ox), "20", String(a.y + oy), "30", "0");
      l.push("11", String(b.x + ox), "21", String(b.y + oy), "31", "0");
    } else {
      const pts = e.points || [];
      if (pts.length >= 2) {
        head("LWPOLYLINE");
        l.push("90", String(pts.length), "70", e.closed ? "1" : "0");
        pts.forEach((p) => l.push("10", String(p.x + ox), "20", String(p.y + oy)));
      }
    }
  }
  // Write a hatch as a real DXF HATCH (boundary polyline path + named pattern).
  // Falls back to the cached tessellation as LINEs when there is no usable
  // boundary polygon, so the fill is always visible.
  _writeHatch(e, ox, oy) {
    const l = this.lines;
    const poly = e.cachedBoundary && e.cachedBoundary.length >= 3 ? e.cachedBoundary : null;
    if (!poly) {
      (e.cachedLines || []).forEach((s) => {
        this._cadHead("LINE", e);
        l.push("10", String(s.x1 + ox), "20", String(s.y1 + oy), "30", "0");
        l.push("11", String(s.x2 + ox), "21", String(s.y2 + oy), "31", "0");
      });
      return;
    }
    const holes = Array.isArray(e.cachedHoles) ? e.cachedHoles.filter((h) => Array.isArray(h) && h.length >= 3) : [];
    const paths = [poly, ...holes];
    const patDef = HATCH_PATTERNS[e.patternName];
    const solid = !!(patDef && patDef.solid);
    this._cadHead("HATCH", e);
    l.push("10", "0", "20", "0", "30", "0");
    l.push("210", "0", "220", "0", "230", "1");
    l.push("2", e.patternName || "EARTH");
    l.push("70", solid ? "1" : "0");
    l.push("71", "0");
    l.push("91", String(paths.length));
    for (const path of paths) {
      l.push("92", "2");
      l.push("72", "0");
      l.push("73", "1");
      l.push("93", String(path.length));
      path.forEach((p) => l.push("10", String(p.x + ox), "20", String(p.y + oy)));
      l.push("97", "0");
    }
    l.push("75", "0");
    l.push("76", solid ? "1" : "1");
    if (!solid) {
      l.push("52", String(e.patternAngle || 0));
      l.push("41", String(e.patternScale || 1));
      l.push("77", "0");
      this._writeHatchPattern(e, patDef);
    }
    l.push("98", "0");
  }
  // The hatch PATTERN DEFINITION — group 78 and one family of lines per entry.
  //
  // A DXF HATCH carries its own pattern rather than naming one the reader is
  // expected to own: `2 ANSI31` is a label, not a lookup key. Writing `78 0` meant
  // every pattern hatch we exported arrived with nothing to draw.
  //
  // Each family in HATCH_PATTERNS is {angle, offset, shift?}: an infinite set of
  // parallel lines at `angle`, spaced `offset` apart, the set displaced along its
  // own direction by `shift`. DXF wants, per family: 53 the angle, 43/44 a base
  // point, 45/46 the offset VECTOR between adjacent lines, and 79 the dash count.
  // The offset vector is perpendicular to the family (rotate the direction by 90°),
  // and the shift rides along it — the same decomposition cadHatch draws with.
  _writeHatchPattern(e, patDef) {
    const l = this.lines;
    const families = (patDef && Array.isArray(patDef.lines) ? patDef.lines : []).filter(Boolean);
    if (!families.length) {
      this.warnings.push(
        `HATCH ${e.id}: pattern "${e.patternName}" is not a known pattern \u2014 exported with no pattern definition, so it will draw as an outline`
      );
    }
    const scale = Number.isFinite(e.patternScale) && e.patternScale > 0 ? e.patternScale : 1;
    const rot = Number.isFinite(e.patternAngle) ? e.patternAngle : 0;
    l.push("78", String(families.length));
    for (const f of families) {
      const angle = (Number.isFinite(f.angle) ? f.angle : 0) + rot;
      const d = (Number.isFinite(f.offset) ? f.offset : 1) * scale;
      const a = angle * Math.PI / 180;
      const ux = Math.cos(a);
      const uy = Math.sin(a);
      const phase = (Number.isFinite(f.shift) ? f.shift : 0) * d;
      l.push("53", String(angle));
      l.push("43", String(-uy * phase), "44", String(ux * phase));
      l.push("45", String(-uy * d), "46", String(ux * d));
      l.push("79", "0");
    }
  }
  // Write a block instance as a DXF INSERT. The matching BLOCK definitions are
  // emitted in the BLOCKS section (see _writeCADBlocks, v4 P4).
  _writeInsertEntity(e, ox, oy) {
    const l = this.lines;
    const ip = e.insertionPoint || { x: 0, y: 0 };
    const attrs = Array.isArray(e.attributes) ? e.attributes : [];
    const insertHandle = this._cadHead("INSERT", e);
    if (attrs.length) l.push("66", "1");
    l.push("2", this._blockName(e.blockName || ""));
    l.push("10", String(ip.x + ox), "20", String(ip.y + oy), "30", "0");
    l.push("41", String(e.scaleX != null ? e.scaleX : 1));
    l.push("42", String(e.scaleY != null ? e.scaleY : 1));
    l.push("43", "1");
    l.push("50", String(e.rotation || 0));
    if (attrs.length) {
      for (const a of attrs) {
        this._cadHead("ATTRIB", e);
        l.push("10", String((a.x || 0) + ox), "20", String((a.y || 0) + oy), "30", "0");
        l.push("40", String(a.height || 1));
        l.push("1", String(a.value != null ? a.value : ""));
        l.push("50", String(a.rotation || 0));
        l.push("100", "AcDbAttribute");
        l.push("2", String(a.tag || ""));
        if (a.prompt) l.push("3", String(a.prompt));
        l.push("70", String(a.visible === false ? 1 : 0));
        l.push("73", "0");
        if (a.field && a.fieldLayout) {
          const bound = (this._doc?.layouts || []).find((lay) => lay && lay.id === a.fieldLayout);
          l.push("1001", XDATA_APP);
          l.push("1000", String(bound ? bound.name : a.fieldLayout).replace(/[\r\n]+/g, " "));
        }
      }
      const seqOwner = this._owner;
      this._owner = insertHandle;
      this._cadHead("SEQEND", e);
      this._owner = seqOwner;
    }
  }
  // Write one block attribute DEFINITION (ATTDEF) inside a BLOCK — block-local
  // coordinates, so no offset. Tag (2), default value (1), prompt (3), flags (70).
  // ATTDEF's group order was wrong, independently of the missing markers: group 50
  // (rotation) belongs to the TEXT part and sat after 2/70, and group 3 (prompt)
  // belongs to AcDbAttributeDefinition and sat before its marker. Both are now in
  // the reference's order — text body, then the attribute subclass.
  _writeAttdef(a) {
    const l = this.lines;
    this._cadHead("ATTDEF", { layer: "0" });
    l.push("10", String(a.x || 0), "20", String(a.y || 0), "30", "0");
    l.push("40", String(a.height || 1));
    l.push("1", String(a.default != null ? a.default : ""));
    l.push("50", String(a.rotation || 0));
    l.push("100", "AcDbAttributeDefinition");
    l.push("3", String(a.prompt || ""));
    l.push("2", String(a.tag || ""));
    l.push("70", String(a.visible === false ? 1 : 0));
    l.push("73", "0");
  }
  // Spec 13: a model-space TABLE is written EXPLODED — one LINE per grid segment
  // plus one TEXT per non-empty cell — mirroring _writeDimensionExploded's approach,
  // so it displays in any DXF reader. A real ACAD_TABLE object is explicitly out of
  // scope (v1 fidelity trade: the reimported table is plain lines + text, no longer
  // an editable grid). Geometry comes from the SAME tableLayout the renderers use,
  // mapped local→world via tableToWorld so the export matches the screen exactly.
  _writeTableExploded(e, ox, oy) {
    this._warnTextWeightLoss(e);
    const g = tableLayout(e);
    for (const [a, b] of g.gridSegs) {
      const wa = tableToWorld(e, a.x, a.y);
      const wb = tableToWorld(e, b.x, b.y);
      this._cadHead("LINE", e);
      this.lines.push("10", String(wa.x + ox), "20", String(wa.y + oy), "30", "0");
      this.lines.push("11", String(wb.x + ox), "21", String(wb.y + oy), "31", "0");
    }
    for (const [key, cell] of Object.entries(e.cells || {})) {
      if (!cell || !cell.text) continue;
      const [r, c] = key.split(",").map(Number);
      const pos = g.cellTextPos(r, c, cell.justify);
      if (!pos) continue;
      const wp = tableToWorld(e, pos.x, pos.y);
      this._cadHead("TEXT", e);
      this.lines.push("10", String(wp.x + ox), "20", String(wp.y + oy), "30", "0");
      this.lines.push(
        "40",
        String(g.textHeight),
        "1",
        this._textValue(cell.text, e),
        "50",
        String(e.rotation || 0)
      );
      this.lines.push("72", cell.justify === "C" ? "1" : cell.justify === "R" ? "2" : "0");
      this.lines.push("11", String(wp.x + ox), "21", String(wp.y + oy), "31", "0");
      this.lines.push("100", "AcDbText");
      this.lines.push("73", "2");
    }
  }
  // Dimensions are written as their constituent primitives (extension/dim LINEs,
  // SOLID arrowheads, centred TEXT) so they display in any DXF reader without a
  // DIMSTYLE table or dimension block. The in-app object stays a single editable
  // dimension entity; only the export is exploded.
  // Write a dimension as the primitives it draws as. The dimType dispatch — which
  // geometry function, and which of its fields are lines vs arcs — lives in
  // `dimensionPrimitives` (cadDimension/decompose.js), shared with EXPLODE
  // [BUG-A1-008], so a dimension cannot explode differently from the way it exports.
  // What stays HERE is only what is DXF-specific: a filled arrowhead becomes a SOLID
  // (which the exploder cannot express), and the arclength label's U+2312 prefix is
  // substituted so a strict reader never sees a non-Latin glyph.
  // [DXF-DIM-NATIVE-1] A real DIMENSION entity.
  //
  // The point of this, beyond validity: group 42 carries the MEASUREMENT and the
  // definition points carry the geometry, so AutoCAD re-derives the label from the
  // drawing rather than trusting a baked string. A stretched dimension then updates,
  // and a restyled one re-renders — neither of which loose LINE/SOLID/TEXT can do.
  // Group 1 is written ONLY for a user's text override; leaving it absent is what
  // keeps the dimension live.
  _writeNativeDimension(e, ox, oy, kind, blockName) {
    const l = this.lines;
    const g = dimensionPrimitives(e, this._dimStyleDefs, this._dimCurrentStyle, this._dimSizing);
    const P = (p) => ({ x: (p?.x ?? 0) + ox, y: (p?.y ?? 0) + oy });
    const push = (code, p) => {
      l.push(String(code), String(p.x), String(code + 10), String(p.y), String(code + 20), "0");
    };
    this._cadHead("DIMENSION", e);
    l.push("100", "AcDbDimension");
    l.push("280", "0");
    l.push("2", blockName);
    const c = e.center || { x: 0, y: 0 };
    const lp = e.leaderPoint || { x: 0, y: 0 };
    const r = Number.isFinite(e.radius) ? e.radius : 0;
    const ux = Math.hypot(lp.x - c.x, lp.y - c.y) || 1;
    const u = { x: (lp.x - c.x) / ux, y: (lp.y - c.y) / ux };
    switch (kind.type) {
      case 3:
        push(10, P({ x: c.x - u.x * r, y: c.y - u.y * r }));
        break;
      case 4:
        push(10, P(c));
        break;
      case 6:
        push(10, P(e.datum || { x: 0, y: 0 }));
        break;
      case 5: {
        const arc = g && Array.isArray(g.arcs) && g.arcs[0];
        if (arc) {
          const sweep = ((arc.endAngle - arc.startAngle) % 360 + 360) % 360;
          const mid = (arc.startAngle + sweep / 2) * Math.PI / 180;
          push(10, P({ x: arc.cx + arc.r * Math.cos(mid), y: arc.cy + arc.r * Math.sin(mid) }));
        } else {
          push(10, P(e.p2 || { x: 0, y: 0 }));
        }
        break;
      }
      default:
        push(10, P(e.linePoint || e.p2 || { x: 0, y: 0 }));
    }
    if (g && g.text) push(11, P(g.text.pos));
    let flags = kind.type | 32;
    if (g && g.text) flags |= 128;
    if (kind.type === 6 && e.axis === "x") flags |= 64;
    l.push("70", String(flags));
    l.push("71", "5");
    const measured = dimensionMeasure(e);
    if (Number.isFinite(measured) && kind.type !== 5) l.push("42", String(measured));
    if (e.textOverride) {
      l.push("1", this._textValue(String(e.textOverride).replace(/[\r\n]+/g, " "), e));
    }
    const styleName = this._dimStyleNameFor(e);
    if (styleName) l.push("3", this._dimStyleName(styleName));
    l.push("100", kind.chain[0]);
    switch (kind.type) {
      case 0:
      // rotated
      case 1:
        push(13, P(e.p1));
        push(14, P(e.p2));
        if (kind.type === 0) l.push("50", String(e.orientation === "vertical" ? 90 : 0));
        break;
      case 3:
        push(15, P({ x: c.x + u.x * r, y: c.y + u.y * r }));
        l.push("40", "0");
        break;
      case 4:
        push(15, P({ x: c.x + u.x * r, y: c.y + u.y * r }));
        l.push("40", "0");
        break;
      case 5:
        push(13, P(e.p1));
        push(14, P(e.p2));
        push(15, P(e.vertex));
        break;
      case 6:
        push(13, P(e.feature));
        push(14, P(e.leader));
        break;
      default:
        break;
    }
    for (const marker of kind.chain.slice(1)) l.push("100", marker);
  }
  // The DIMSTYLE name this dimension resolves against, guaranteed to be one this
  // export actually wrote — a group 3 naming a style with no record is the same
  // dangling reference as an entity naming a missing layer.
  _dimStyleNameFor(e) {
    const defs = this._dimStyleDefs || {};
    const has = (k) => Object.prototype.hasOwnProperty.call(defs, k);
    const named = e && e.dimStyle;
    if (named && has(named)) return named;
    if (this._dimCurrentStyle && has(this._dimCurrentStyle)) return this._dimCurrentStyle;
    const first = Object.keys(defs)[0];
    return first || "Standard";
  }
  _writeDimensionExploded(e, ox, oy) {
    const g = dimensionPrimitives(e, this._dimStyleDefs, this._dimCurrentStyle, this._dimSizing);
    if (!g) return;
    const line = (a, b) => {
      this._cadHead("LINE", e);
      this.lines.push("10", String(a.x + ox), "20", String(a.y + oy), "30", "0");
      this.lines.push("11", String(b.x + ox), "21", String(b.y + oy), "31", "0");
    };
    const solid = (t) => {
      this._cadHead("SOLID", e);
      this.lines.push("10", String(t[0].x + ox), "20", String(t[0].y + oy), "30", "0");
      this.lines.push("11", String(t[1].x + ox), "21", String(t[1].y + oy), "31", "0");
      this.lines.push("12", String(t[2].x + ox), "22", String(t[2].y + oy), "32", "0");
      this.lines.push("13", String(t[2].x + ox), "23", String(t[2].y + oy), "33", "0");
    };
    const circle = (c, r) => {
      this._cadHead("CIRCLE", e);
      this.lines.push("10", String(c.x + ox), "20", String(c.y + oy), "30", "0", "40", String(r));
    };
    const arrowDxf = (ar) => {
      if (!ar) return;
      if (ar.kind === "triangle") {
        if (ar.filled) {
          solid(ar.pts);
          return;
        }
        line(ar.pts[0], ar.pts[1]);
        line(ar.pts[1], ar.pts[2]);
        line(ar.pts[2], ar.pts[0]);
      } else if (ar.kind === "tick") {
        line(ar.seg[0], ar.seg[1]);
      } else if (ar.kind === "dot") {
        circle(ar.c, ar.r);
      }
    };
    for (const [a, b] of g.lines) line(a, b);
    for (const arc of g.arcs) {
      this._cadHead("ARC", e);
      this.lines.push("10", String(arc.cx + ox), "20", String(arc.cy + oy), "30", "0");
      this.lines.push("40", String(arc.r));
      this.lines.push("100", "AcDbArc");
      this.lines.push("50", String(arc.startAngle), "51", String(arc.endAngle));
    }
    g.arrows.forEach(arrowDxf);
    if (g.text) {
      const label = g.text.label.replace("\u2312 ", "ARC ");
      this._cadHead("TEXT", e);
      this.lines.push("10", String(g.text.pos.x + ox), "20", String(g.text.pos.y + oy), "30", "0");
      this.lines.push(
        "40",
        String(g.text.height),
        "1",
        this._textValue(label, e),
        "50",
        String(g.text.rotation || 0)
      );
      this.lines.push("72", "1");
      this.lines.push("11", String(g.text.pos.x + ox), "21", String(g.text.pos.y + oy), "31", "0");
      this.lines.push("100", "AcDbText");
      this.lines.push("73", "2");
    }
  }
  // Write a REAL MTEXT record (Spec 05) — not exploded: 10/20 insertion point at the
  // attachment position (71), 40 char height, 41 wrap width, 44 line-spacing factor,
  // 50 rotation, contents in 250-char group-3 chunks + a final group 1 (the layout
  // dxf-parser and AutoCAD both concatenate 3* then 1). Our anchor is always the
  // box's LEFT edge on the justify's vertical band, while DXF's insertion point sits
  // at the attachment's horizontal position too — so the anchor shifts by fx·width
  // along the box's width axis on the way out (mapMText inverts this on import).
  _writeMText(e, ox, oy) {
    const l = this.lines;
    this._warnTextWeightLoss(e);
    const g = layoutMText(e);
    const { fx } = justifyFactors(e.justify || "TL");
    const p = mtextToWorld(e, fx * g.width, 0);
    this._cadHead("MTEXT", e);
    l.push("10", String(p.x + ox), "20", String(p.y + oy), "30", "0");
    l.push("40", String(g.height), "41", String(g.width));
    l.push("71", String(mtextAttachment(e.justify)));
    const content = mtextEncode(e.text);
    const chunks = [];
    for (let i = 0; i < content.length; i += 250) chunks.push(content.slice(i, i + 250));
    if (!chunks.length) chunks.push("");
    for (let i = 0; i < chunks.length - 1; i++) l.push("3", chunks[i]);
    l.push("1", chunks[chunks.length - 1]);
    l.push("50", String(e.rotation || 0));
    l.push(
      "44",
      String(
        Number.isFinite(e.lineSpacing) && e.lineSpacing > 0 ? e.lineSpacing : MTEXT_DEFAULT_LINE_SPACING
      )
    );
  }
  // Write a LEADER (Spec 04) EXPLODED to primitives (mirrors _writeDimensionExploded):
  // an LWPOLYLINE for the segments + landing, a filled arrow (SOLID / LINEs / CIRCLE per
  // kind), and a TEXT for the note. A native DXF LEADER entity is out of scope v1.
  _writeLeaderExploded(e, ox, oy) {
    const g = leaderGeometry(e, this._dimStyleDefs, this._dimCurrentStyle, this._dimSizing);
    if (!g) return;
    const verts = [];
    for (const [a, b] of g.segs) {
      if (!verts.length) verts.push(a);
      verts.push(b);
    }
    if (g.landing) verts.push(g.landing[1]);
    if (verts.length >= 2) {
      this._cadHead("LWPOLYLINE", e);
      this.lines.push("90", String(verts.length), "70", "0");
      verts.forEach((p) => this.lines.push("10", String(p.x + ox), "20", String(p.y + oy)));
    }
    const line = (a, b) => {
      this._cadHead("LINE", e);
      this.lines.push("10", String(a.x + ox), "20", String(a.y + oy), "30", "0");
      this.lines.push("11", String(b.x + ox), "21", String(b.y + oy), "31", "0");
    };
    const solid = (t) => {
      this._cadHead("SOLID", e);
      this.lines.push("10", String(t[0].x + ox), "20", String(t[0].y + oy), "30", "0");
      this.lines.push("11", String(t[1].x + ox), "21", String(t[1].y + oy), "31", "0");
      this.lines.push("12", String(t[2].x + ox), "22", String(t[2].y + oy), "32", "0");
      this.lines.push("13", String(t[2].x + ox), "23", String(t[2].y + oy), "33", "0");
    };
    const ar = g.arrow;
    if (ar) {
      if (ar.kind === "triangle") {
        if (ar.filled) solid(ar.pts);
        else {
          line(ar.pts[0], ar.pts[1]);
          line(ar.pts[1], ar.pts[2]);
          line(ar.pts[2], ar.pts[0]);
        }
      } else if (ar.kind === "tick") {
        line(ar.seg[0], ar.seg[1]);
      } else if (ar.kind === "dot") {
        this._cadHead("CIRCLE", e);
        this.lines.push(
          "10",
          String(ar.c.x + ox),
          "20",
          String(ar.c.y + oy),
          "30",
          "0",
          "40",
          String(ar.r)
        );
      }
    }
    if (e.text) {
      this._cadHead("TEXT", e);
      this.lines.push("10", String(g.textPos.x + ox), "20", String(g.textPos.y + oy), "30", "0");
      this.lines.push("40", String(g.textHeight), "1", this._textValue(e.text, e), "50", "0");
      this.lines.push("72", g.textAlignRight ? "2" : "0");
      this.lines.push("11", String(g.textPos.x + ox), "21", String(g.textPos.y + oy), "31", "0");
      this.lines.push("100", "AcDbText");
      this.lines.push("73", "0");
    }
  }
  // ═════════════════════════════════════════════
  // HELPERS
  // ═════════════════════════════════════════════
  _extractSoilTypes(data) {
    const types = /* @__PURE__ */ new Set();
    if (data?.soilProfile?.profile) {
      data.soilProfile.profile.forEach((pt2) => {
        (pt2.layers || []).forEach((l) => types.add(l.soilType));
      });
    }
    if (data?.soilProfile?.boreholePositions) {
      data.soilProfile.boreholePositions.forEach((bh) => {
        (bh.layers || []).forEach((l) => types.add(l.soilType));
      });
    }
    return [...types];
  }
  _niceInterval(range, targetDivisions) {
    const raw = range / targetDivisions;
    const magnitude = Math.pow(10, Math.floor(Math.log10(raw)));
    const residual = raw / magnitude;
    let nice;
    if (residual <= 1.5) nice = 1;
    else if (residual <= 3) nice = 2;
    else if (residual <= 7) nice = 5;
    else nice = 10;
    return nice * magnitude;
  }
  // NOTE: the browser `download(dxfContent, filename)` helper deliberately does
  // NOT live here — triggering a file download needs the DOM, and this engine is
  // headless. Consumers own the delivery (download, HTTP response, fs write…).
};
var DXFExporter_default = DXFExporter;

// src/dxfExport.js
function cadModelToDXF(doc, opts) {
  return cadModelToDXFDetailed(doc, opts).dxf;
}
function cadModelToDXFDetailed(doc, opts) {
  const exporter = new DXFExporter_default();
  const dxf = exporter.exportCADModel(doc, opts);
  return { dxf, warnings: exporter.warnings || [] };
}

export {
  linearOrientation,
  ordinateAxis,
  makeDimension,
  baselineLinePoint,
  makeDimChain,
  chainStep,
  attachedDimensionIds,
  dimensionMeasure,
  angularMeasure,
  DIM_TEXT_K,
  DIM_TEXT_MIN,
  DIM_TEXT_MAX,
  autoTextHeight,
  PAPER_TEXT_MM,
  PAPER_WIDTH_MM,
  ANNO_SCALE_MIN,
  ANNO_SCALE_MAX,
  autoAnnotationScale,
  resolveAnnotationScale,
  annotationTextHeight,
  viewportAnnotationScale,
  documentAnnotationScale,
  sizingAnnoScale,
  isAnnotative,
  paperMmToModel,
  entityPaperHeight,
  syncAnnotativeEntity,
  annotativeStyleOf,
  syncAnnotativeHeights,
  migrateAnnotativeEntity,
  boxesOverlap,
  resolveTextCollision,
  CHAR_W_RATIO,
  textBox,
  deconflictLabels,
  STYLE_ONLY_FIELDS,
  LEGACY_CARRIER_FIELDS,
  DIM_OVERRIDE_FIELDS,
  DIM_OVERRIDE_KEYS,
  isDimOverrideKey,
  isValidDimOverride,
  sanitizeDimOverrides,
  dimOverrides,
  hasDimOverrides,
  withDimOverride,
  dimOverrideDisplay,
  dimOverrideFieldsFor,
  supportsDimOverrides,
  DIMSTYLE_DEFAULTS,
  resolveDimStyle,
  DIM_AUTOBASE_RATIO,
  autoDimBaseHeight,
  resolveUserDimScale,
  makeDimSizing,
  effectiveTextHeight,
  effectiveArrowSize,
  effectiveExtOffset,
  effectiveExtExtend,
  effectiveTextGap,
  effectiveBaselineSpacing,
  dimensionTextColor,
  formatArchitectural,
  formatMeasure,
  dimensionLabel,
  dimensionGeometry,
  dimTextOffset,
  angularGeometry,
  lineIntersection,
  angularFromTwoLines,
  arclengthGeometry,
  radialGeometry,
  ordinateGeometry,
  dimensionTextAnchor,
  arrowTriangle,
  arrowShape,
  dimensionPrimitives,
  arrowStrokes,
  distanceToDimension,
  leaderGeometry,
  APPROX_CHAR_WIDTH_FACTOR,
  justifyFactors,
  justifyOffset,
  MTEXT_DEFAULT_LINE_SPACING,
  approxMeasure,
  layoutMText,
  mtextToWorld,
  mtextLocal,
  mtextBoxCorners,
  mtextGripPoints,
  mtextWidthPatch,
  TABLE_MAX_CELLS,
  TABLE_CELL_KEY_RE,
  tableMinColWidth,
  tableMinRowHeight,
  tableLayout,
  tableToWorld,
  tableLocal,
  tableCorners,
  worldToTableCell,
  tableGripPoints,
  tableColEdgePatch,
  tableRowEdgePatch,
  tableScalePatch,
  DEFAULT_LAYER,
  CAD_FONTS,
  DEFAULT_FONT,
  ENTITY_TYPES,
  transformPoint,
  genId,
  resolveColor,
  getLayer,
  carryStyle,
  isFullEllipse,
  ellipseSweep,
  ellipsePoint,
  ellipseParamAt,
  ellipseQuadrants,
  ellipseToPolyline,
  bulgeToArc,
  splineDefiningPoints,
  isFitSpline,
  polygonPoints,
  revisionCloudBumps,
  revisionCloudArcs,
  splinePointAt,
  splineArcLengthParams,
  splineToPolyline,
  distance,
  pointToSegment,
  closestPointOnSegment,
  perpendicularFoot,
  pointInPolygon,
  lineLineIntersection,
  segSegIntersection,
  segCircleIntersections,
  circleCircleIntersections,
  tangentPointsToCircle,
  angleInArcSweep,
  onCirclePoint,
  arcMidAngle,
  BOOL_EPS,
  signedArea,
  ringArea,
  orientRing,
  segCuts,
  buildPolygons,
  orientRingSet,
  ringUnion,
  ringDifference,
  ringIntersection,
  IMAGE_MIN_EDGE,
  normalizeImageDef,
  imageDataUrl,
  imageDefFor,
  docAttachImageDef,
  orphanImageDefIds,
  normalizeImageEntity,
  hasImageFrame,
  imageCorners,
  imageWidth,
  imageHeight,
  imageRotationDeg,
  imageFlipped,
  imageFromPlacement,
  transformImageFrame,
  resizeImage,
  imageMatrix,
  entityVertices,
  xrefFrameBounds,
  entityBounds,
  documentBounds,
  framingBounds,
  translateEntity,
  cloneEntity,
  addPolylineVertex,
  removePolylineVertex,
  LINETYPE_DEFS,
  BUILTIN_LINETYPES,
  cloneDefs,
  linetypeToDash,
  resolveLinetypeName,
  resolveLinetypePattern,
  LINEWEIGHTS_MM,
  DEFAULT_LINEWEIGHT_MM,
  LINEWEIGHT_DISPLAY_PX_PER_MM,
  clampLineweight,
  resolveLineweightMm,
  lineweightToScreenPx,
  polylineWidthWorld,
  GEOTECH_BLOCKS,
  layerShown,
  layerFrozen,
  layerPlotted,
  layerLocked,
  freezeCurrentLayerError,
  currentLayerFrozenError,
  layerIndex,
  selectableEntity,
  selectableEntities,
  shownEntities,
  editableEntity,
  unfrozenEntities,
  lockedEntityIds,
  partitionLockedIds,
  SCALE_MIN,
  SCALE_MAX,
  clampScale2 as clampScale,
  normalizeCanvasRect,
  fitRectToCanvasRect,
  fitRectToViewport,
  sameView,
  zoomByFactor,
  zoomToScreenBox,
  fitExtents,
  fitSelection,
  easeInOutCubic,
  lerpView,
  PX_PER_MM,
  mmToPx,
  pxToMm,
  MODEL_UNIT_MM,
  modelUnitMm,
  PAPER_SIZES,
  paperDims,
  layoutPaperDims,
  viewportFrameMm,
  scaleViewportKeepingFrame,
  viewportPaperRectMm,
  viewportRectPx,
  viewportContentAffine,
  paperPxToModel,
  fitSheetView,
  screenToPaperPx,
  SHEET_ZOOM_MIN_FIT,
  SHEET_ZOOM_MAX_FIT,
  clampSheetView,
  cullToWorldRect,
  layerOverridesHash,
  defaultViewportForModel,
  parseScale,
  rotateVec,
  rectCenter,
  rectCornersRot,
  pointInRotRect,
  resizeRect,
  snapRect,
  formatScale,
  VIEWPORT_SCALE_PRESETS,
  CAPTURE_DPI,
  MAX_PAGE_MEGAPIXELS,
  captureDpiFor,
  viewportPrintMetresPerPixel,
  BUILTIN_TEMPLATE_ID,
  normalizeFieldTag,
  deriveSheetScale,
  templateBounds,
  placeTemplate,
  templateToDrawList,
  FIELD_CODES,
  fieldCodeLabel,
  isFieldCode,
  fieldLayoutFor,
  initialFieldValues,
  healFieldBindings,
  resolveField,
  refreshFields,
  SHEET_SYMBOL_TYPES,
  annotationRotatable,
  SCALEBAR_MIXED_TEXT,
  SCALEBAR_NONE_TEXT,
  SHEET_SYMBOL_PEN_MM,
  normalizeSheetSymbol,
  sheetScaleBinding,
  sheetScaleDenom,
  niceGroundLength,
  scaleBarSpec,
  scaleBarDrawList,
  northArrowDrawList,
  sheetTextBoxMm,
  annotationBoxMm,
  modelLayout,
  getLayout,
  DEFAULT_TITLE_FIELDS,
  addLayout,
  addAnnotation,
  updateAnnotation,
  removeAnnotation,
  updateLayout,
  deleteLayout,
  renameLayout,
  addViewport,
  updateViewport,
  removeViewport,
  DEFAULT_MODEL_MARGIN_MM,
  PLOT_SCALE_FIT,
  normalizeMargin,
  usableMargin,
  normalizePlotScale,
  normalizePageSetup,
  setPageSetup,
  TEXTSTYLE_DEFAULTS,
  RESERVED_STYLE_KEYS,
  isSafeStyleName,
  resolveTextStyle,
  effectiveEntityTextHeight,
  aci2int,
  int2aci,
  PLOT_STYLE_MODES,
  PLOT_STYLE_MAX_ENTRIES,
  emptyPlotStyleTable,
  normalizePlotStyleTable,
  plotPenFor,
  makePlotPenResolver,
  isMonoPlot,
  LAYER_NAME_MAX_LENGTH,
  layerNameError,
  layerNameKey,
  normalizeLayerName,
  layerRenameError,
  renameLayer,
  GEOREF_CRS_PATTERN,
  DEFAULT_GEOREF,
  isBasemapId,
  normalizeGeoref,
  sameGeoref,
  sameGeorefFrame,
  gisMode,
  GEOREF_MAX_BASEMAPS,
  addBasemap,
  updateBasemap,
  removeBasemap,
  clearBasemaps,
  setBasemapsVisible,
  worldToGrid,
  gridToWorld,
  formatGridCoord,
  roundOrigin,
  GROUP_NAME_MAX,
  normalizeGroupName,
  documentGroups,
  groupOf,
  groupById,
  uniqueGroupName,
  groupRefusal,
  createGroup,
  ungroup,
  renameGroup,
  setGroupSelectable,
  expandGroupSelection,
  pruneGroups,
  normalizeGroups,
  DRAWING_SETTINGS_KEYS,
  isDrawingSetting,
  defaultSettings,
  createBlankDocument,
  normalizeDocument,
  normalizeSettings,
  mapSplinePoints,
  rectCorners,
  asPolyline,
  mapDimensionPoints,
  mapHatchGeometry,
  rotateEntity,
  scaleEntity,
  mirrorEntity,
  rotateEntities,
  scaleEntities,
  mirrorEntities,
  worldToYDown,
  yDownToWorld,
  worldToYDownFrom,
  worldToScreen,
  screenToWorld,
  worldToYDownAngle,
  snapToGrid,
  fitView,
  zoomAt,
  formatCoord,
  entitySegments,
  entityOutlinePoints,
  entityLength,
  textBoxCorners,
  nearestOnEntity,
  intersectEntities,
  hatchFillsPoint,
  distanceToHatch,
  boundaryFromEntities,
  isClosedBoundary,
  polygonArea,
  regionRings,
  isBooleanOperand,
  makeRegions,
  explodeRegion,
  regionArea,
  regionFillsPoint,
  booleanRegions,
  unionRegions,
  subtractRegions,
  intersectRegions,
  entityInMarquee,
  entitiesInSelectionBox,
  entitiesThroughPoints,
  closestEntityWithin,
  distanceToEntity,
  distanceToXrefFrame,
  entitiesInLasso,
  quickSelectMatch,
  SNAP_LABELS,
  snapLabel,
  PRECISE_SNAP_TYPES,
  isPrecisePointSnap,
  findSnap,
  resolveDeferredTangent,
  offsetRefusal,
  offsetEntity,
  alignTransform,
  alignEntities,
  transformEntity,
  explodeAttributeLoss,
  reframeAttributes,
  unplaceAttribute,
  placeAttribute,
  placeBlockChild,
  drawnBlockChildren,
  explodeInsert,
  attachXref,
  detachXref,
  reloadXref,
  bindXref,
  xrefLayerVisible,
  HATCH_PATTERNS,
  DEFAULT_HATCH_PATTERN,
  HATCH_PATTERN_NAMES,
  HATCH_LINEWEIGHT_MIN,
  HATCH_LINEWEIGHT_MAX,
  DEFAULT_HATCH_LINEWEIGHT,
  clampHatchLineWeight,
  hatchLineWeight,
  tessellateHatch,
  findHatchIslands,
  findRegionBoundaryAt,
  findClosedBoundaryAt,
  chooseHatchBoundary,
  updateHatchCache,
  rebuildHatch,
  hatchSourceIds,
  makeHatch,
  paintedArea,
  hatchArea,
  RAD2DEG,
  LARGE,
  hexFromInt,
  lineweightFromDxf,
  normalizeLinetypeName,
  stripMText,
  IDENT,
  apply,
  matMul,
  insertMatrix,
  identityCtx,
  OCS_WORLD,
  sameExtrusion,
  statedExtrusion,
  ocsTransform,
  ocsCtx,
  unitScaleFromHeader,
  scaledCtx,
  entityContext,
  hatchRegionsDetailed,
  mapLine,
  mapPolyline,
  mapCircle,
  mapArc,
  mapEllipse,
  mapMText,
  mapText,
  mapPoint,
  mapSolid,
  mapSpline,
  mapInsert,
  mapHatchRegions,
  mapHatch,
  mapDimension,
  computeImportOffset,
  applyImportOffset,
  normalizeCoords,
  supplementParsedDxf,
  GEODATA_VERSION,
  GEODATA_COORD_LOCAL_GRID,
  GEODATA_COORD_PROJECTED_GRID,
  GEODATA_COORD_GEOGRAPHIC,
  GEODATA_DICT_ENTRY,
  MAX_DEFINITION_CHARS,
  chunkDefinition,
  joinDefinition,
  minimalCrsXml,
  crsCodeFromDefinition,
  planGeodata,
  writeGeodataObjects,
  readGeodata,
  DXFExporter_default,
  cadModelToDXF,
  cadModelToDXFDetailed
};
//# sourceMappingURL=chunk-B7AQMZVU.js.map